import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Astroid, Ban, BellRing, Bookmark, Check, ChevronDown, Circle, CircleAlert, CircleHelp, Clock3, Copy, ExternalLink, FileSearch, Folder, GalleryVerticalEnd, Import, Info, Layers, ListChecks, Loader2, LockKeyhole, Mic, MicOff, Navigation, Pin, Quote, RefreshCw, Save, Search, Send, Share2, ShieldCheck, SkipForward, Split, Square as StopSquare, SquareLibrary, SquarePen, TextAlignStart, ThumbsDown, ThumbsUp, Trash2, Pen, X, Paperclip } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, Contact, DocumentSearch, File, formatValue } from './FieldComponents';
import { useUploads } from './UploadManagerContext';
import Modal from './Modal';
import MoveFolderPicker from './MoveFolderPicker';
import RagReferenceContextSelect from './RagReferenceContextSelect';
import ProactiveAiPlanner from './ProactiveAiPlanner';
import ReadinessEvidenceReview, { getReadinessResultFromAnswer } from './ReadinessEvidenceReview';
import SourcedTimelineReview, { getSourcedTimelineFromAnswer } from './SourcedTimelineReview';
import TimelineEvidencePreview from './TimelineEvidencePreview';
import CaseEvidenceDigestReview, { getCaseEvidenceDigestFromAnswer } from './CaseEvidenceDigestReview';
import CaseAnalysisChatCard, { getCaseAnalysisChatCardData } from './CaseAnalysisChatCard';
import LibraryRagPdfPreview from './LibraryRagPdfPreview';
import { OutlookCitationReviewerPanel, OutlookCitationReviewTrigger, OutlookEmailCaseMatchLinks, OutlookEmailCitationCard, getOutlookCitationReviewTarget, getOutlookEmailCardModel, hasOutlookRawPreviewLabel, isOutlookCitation, withOutlookCitationReviewContext } from './OutlookCitationReviewer';
import { JaneCalendarPlannerPanel, SchedulingOrderCalendarDraftReview } from './RagCalendarDrafts';
import NativeRagTablePreview, { buildReportPreviewErrorState, buildReportPreviewLoadingState, buildReportPreviewReadyState, buildReportPreviewRequestPayload, buildSpreadsheetNativePreviewFromBrowserFile, getGeneratedReportDisplayText, getNativeTablePreviewsForMessage, getNativeTablePreviewRowLimit, getReportPreviewTargetsFromMessage, getReportPreviewTargetsFromReferences, normalizeRagPreviewFieldId, renderSpreadsheetChunkTable, shouldRenderSpreadsheetChunk } from './RagNativeTablePreview';
import { RAG_FINAL_RESPONSE_DRAFTING_LABEL, RAG_THINKING_LABEL, RagSidebarMark, RagThinkingIndicator } from './RagChrome';
import { appendRagPlannerStep, buildJaneTurnContractRows, buildRagPlannerStepsFromIntelligence, buildRagTraceRows, countRagPlannerItems, getJaneRunPlanFromMessage, getJaneTurnContractFromIntelligence, getRagPlannerStepsFromMessage, mergeJaneRunPlanProgress, normalizeJaneRunPlan, normalizeRagPlannerSteps, renderJaneRunInspector, renderJaneRunPlan, renderRagPlanner, renderRagResponseDetailsBreakdown, shouldCollapseJaneRunPlanByDefault, titleCaseRagTraceValue } from './RagResponseDetails';
import { RAG_MODEL_OPTIONS, normalizeRagModelKey } from './ragModelOptions';
import { createRagSilentReloadCoordinator, fetchRagMessagePage, ragMessagePageScope } from './ragMessageDelta';
import { getAnswerSourceCards, getSourceTypeLabel, mergeDisplayedSources } from './ragSourceUtils';
import { RAG_COMPONENT_VIEW_LABELS, getSavedRagComponentTextViewTypes, saveRagComponentTextViewTypes } from './ragComponentPreferences';
import { buildRagTransportFailure, fetchRagJsonWithTimeout, getNextRagStreamText, getRagStreamDelay, readJsonLineStream } from './ragStreamUtils';
import { bindOptimisticRagRequest, buildRagMobilePreviewMessages, formatDate, formatRagMessageDate, formatRagMessageTime, getRagDisplayTimeZone, getRagMessageAuthorLabel, getRagMessageDateKey, getRagMessagePreviewText, isLegacyRagRerunOpeningText, isRagMessageBoundToActiveRequest, isRagRerunMessage, mergeRagRerunMessages, normalizeRagMessages, parseRagMessageTimestampMs, shouldDisplayRagMessageTime, shouldReconcileStructuredRagAnswerImmediately } from './ragMessageUtils';
import { normalizePrivateFinancialArtifactUrl } from './ragArtifactUtils';
import { sanitizeRagHighlightedHtml } from './ragHighlightUtils';
import { getJaneUserId } from './ragIdentityUtils';
import { buildRagCancelTarget, createRagAgentV2OperationId, createRagAgentV2RuntimeIntent, createRagChatOperationContext, createRagClientSaveToken, createRagUploadContext, getAcceptedRagChatTitleUpdate, getRagTerminalPersistenceAction, isRagAgentV2SnapshotSettled, isRagChatOperationCurrent, isRagRunGenerationCurrent, isRagRuntimeIntentOwned, isRagUploadContextCurrent, normalizeRagAgentV2Acknowledgement, reconcileAcceptedRagChatTitle, reconcileAcceptedRagChatTitleValue, shouldAdvanceRagSessionForChatLoad, shouldFallbackFromJaneAgentStart } from './ragOperationGuards';
import { buildJanePromptContextSummary, buildRagSearchStatusMessage, extractWorkspaceCaseCandidate, filterReferencesForFreshTitleSearch, getJaneLegacyIntelligenceEndpoint, isJaneLegalDraftPresentation, isLegalDraftPrompt, isLikelyCaseBoundFollowupPrompt, normalizeCapabilityPrompt, normalizeJaneLegacyFallbackEndpoint, shouldUseDirectWorkspaceContinuation, shouldUseJaneAgentV2ForPrompt } from './ragPromptIntentUtils';
import { JANE_AGENT_LIBRARY, buildSavedJaneAgent, getJaneAgentSourceRequirementLabel, isJaneAgentSourceReady, loadPinnedJaneAgentIds, loadSavedJaneAgents, savePinnedJaneAgentIds, saveSavedJaneAgents } from './janeWorkflowLibrary';
import { buildCaseDocumentLocationUrl, buildPreviewDetailParts, buildRagRequestReferenceContext, buildRagSourceHistory, decodeRagTerminalText, getCaseNameForDisplay, getCaseReferenceFromAssistantMessage, getCaseSourceTitle, getFileExtension, getMessagePromptReferences, getPlainChunkText, getRagCitationHref, getRagReferenceDisplayLabel, getSafeRagInlineRecordUrl, getSafeRagNavigationUrl, getSourceFileId, isCaseSummaryCitation, replaceCaseNameToken, stripHtml, uniqueDisplayParts } from './ragReferenceUtils';
import { useNavVariant } from '../App';
import { parseCaseLeadSelection } from '../utils/navSearch';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import { getCookie, setCookie } from '../utils/cookies';
import { getContactInitialsClassName } from '../utils/colorUtils';
import '../utils/promiseWithResolversPolyfill';
import '../styles/LibraryDocuments.css';
import '../styles/ActivityFeed.css';
import DBLogo from "../resources/dblogo-white.png";
import SidebarLightLogo from "../resources/logo light.png";
import SidebarDarkLogo from "../resources/Daly & Black Logo White.png";

const API_BASE = 'https://api.casedb.co';

const RAG_SIDEBAR_COLLAPSE_MS = 340;
const RAG_MESSAGE_PAGE_SIZE = 40;
const RAG_CHAT_LOAD_TIMEOUT_MS = 30000;
const RAG_MESSAGE_VIRTUALIZE_THRESHOLD = 120;
const RAG_MESSAGE_VIRTUALIZE_ESTIMATED_HEIGHT = 220;
const RAG_MESSAGE_VIRTUALIZE_OVERSCAN_ROWS = 16;
const RAG_SAVE_REQUEST_TIMEOUT_MS = 30000;
const RAG_CANCEL_REQUEST_TIMEOUT_MS = 15000;
const RAG_CANCEL_RELEASE_POLL_MS = 400;
const JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY = 'persistent-jane-dock-prompt-draft';
const JANE_DOCK_PROMPT_CONSUMED_STORAGE_KEY = 'persistent-jane-dock-prompt-consumed';
const RAG_PLANNER_PREVIEW_CLOSED_COOKIE = 'jane_rag_planner_preview_closed';
const JANE_RUN_MODE_COOKIE = 'jane_rag_run_mode';
const JANE_BACKGROUND_HANDOFF_MODES = new Set([
    'background_draft_queued',
    'case_timeline_build_queued',
    'case_trial_analysis_queued',
    'jane_background_run_queued',
]);
const JANE_BACKGROUND_TASK_TYPES = new Set([
    'draft_background_job',
    'case_timeline_build',
    'case_trial_analysis',
    'jane_background_run',
]);
const JANE_BACKGROUND_RECEIPT_GRACE_MS = 30000;
const JANE_BACKGROUND_TERMINAL_HISTORY_LIMIT = 6;
const JANE_BACKGROUND_TERMINAL_STATUSES = new Set([
    'completed',
    'failed',
    'cancelled',
    'canceled',
]);
const JANE_PROVIDER_RECOVERY_PROVIDERS = Object.freeze({
    openai: 'OpenAI',
    gemini: 'Gemini',
    claude: 'Claude',
    ollama: 'the local model',
});
const JANE_PROVIDER_RECOVERY_FAILURE_CODES = new Set([
    'credit_balance_exhausted',
    'organization_spend_limit_exceeded',
    'project_spend_limit_exceeded',
    'organization_usage_limit_exceeded',
    'insufficient_quota',
    'rate_limit_exceeded',
    'slow_down',
    'server_is_overloaded',
    'missing_api_key',
    'not_configured',
    'deadline_exceeded',
    'transport_unavailable',
    'transport_failed',
    'provider_capacity',
    'provider_response_incomplete',
    'provider_http_error',
]);

const getJaneProviderRecovery = (run = null) => {
    const recovery = run?.metadata?.provider_recovery;
    const currentProvider = String(recovery?.current_provider || '').trim().toLowerCase();
    const nextProvider = String(recovery?.next_provider || '').trim().toLowerCase();
    const actionToken = String(recovery?.action_token || '').trim().toLowerCase();
    const failureCode = String(recovery?.failure_code || '').trim();
    const createdAt = String(recovery?.created_at_utc || '').trim();
    const createdAtMs = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(createdAt)
        ? Date.parse(createdAt) : Number.NaN;
    if (recovery?.version !== 1
        || !['awaiting_user', 'continuing'].includes(recovery?.status)
        || recovery?.action !== 'continue_with_next_provider'
        || recovery?.label !== 'Use another model instead of waiting'
        || !Object.prototype.hasOwnProperty.call(JANE_PROVIDER_RECOVERY_PROVIDERS, currentProvider)
        || !Object.prototype.hasOwnProperty.call(JANE_PROVIDER_RECOVERY_PROVIDERS, nextProvider)
        || currentProvider === nextProvider
        || !/^[a-f0-9]{48}$/.test(actionToken)
        || !JANE_PROVIDER_RECOVERY_FAILURE_CODES.has(failureCode)
        || !Number.isFinite(createdAtMs) || createdAtMs <= 0
        || new Date(createdAtMs).toISOString().replace('.000Z', 'Z') !== createdAt
    ) {
        return null;
    }
    return {
        action: recovery.action,
        actionToken,
        label: recovery.label,
        currentProvider,
        currentProviderLabel: JANE_PROVIDER_RECOVERY_PROVIDERS[currentProvider],
        nextProvider,
        nextProviderLabel: JANE_PROVIDER_RECOVERY_PROVIDERS[nextProvider],
        status: recovery.status,
    };
};

export const getJaneProviderRecoveryAction = (run = null) => {
    const recovery = getJaneProviderRecovery(run);
    const runId = Number(run?.id || 0);
    const taskType = String(run?.task?.task_type || run?.event_type || run?.task_type || '').trim();
    return recovery
        && Number.isSafeInteger(runId) && runId > 0
        && recovery.status === 'awaiting_user'
        && getJaneBackgroundRunStatus(run) === 'queued'
        && taskType === 'case_timeline_build'
        && String(run?.metadata?.timeline_extraction?.status || '').trim().toLowerCase() === 'waiting'
        ? recovery
        : null;
};

export const buildJaneProviderRecoveryRequest = (run = null, suppliedRecovery = null) => {
    const recovery = getJaneProviderRecoveryAction(run);
    if (!recovery || recovery.actionToken !== suppliedRecovery?.actionToken
        || recovery.nextProvider !== suppliedRecovery?.nextProvider
    ) return null;
    return {
        action: recovery.action,
        run_id: Number(run.id),
        action_token: recovery.actionToken,
    };
};

export const getJaneProviderRecoveryContinuation = (run = null) => {
    const recovery = getJaneProviderRecovery(run);
    const taskType = String(run?.task?.task_type || run?.event_type || run?.task_type || '').trim();
    return recovery && recovery.status === 'continuing'
        && taskType === 'case_timeline_build'
        && ['queued', 'running'].includes(getJaneBackgroundRunStatus(run))
        && String(run?.metadata?.run_plan?.current_step_id || '').trim() === 'classify-documents'
        ? recovery
        : null;
};

export const JaneProviderRecoveryAction = ({ run, busy = false, onContinue }) => {
    const recovery = getJaneProviderRecoveryAction(run);
    if (!recovery || typeof onContinue !== 'function') return null;
    return (
        <button
            type="button"
            className="action alt library-rag-provider-recovery-action"
            onClick={() => onContinue(run, recovery)}
            disabled={busy}
            aria-label={`${recovery.label}: continue with ${recovery.nextProviderLabel}`}
            title={`Continue this run with ${recovery.nextProviderLabel}`}
        >
            <RefreshCw size={14} className={busy ? 'spinner' : ''} aria-hidden="true" />
            <span>{busy ? `Continuing with ${recovery.nextProviderLabel}…` : recovery.label}</span>
        </button>
    );
};

const getJaneRequestTokens = (...values) => values
    .map(value => String(value || '').trim())
    .filter(Boolean);

export const getJanePublicOpening = (payload = null, expectedRequestToken = '', requireRequestBinding = false) => {
    const payloadRequestTokens = getJaneRequestTokens(payload?.request_token, payload?.metadata?.request_token);
    const payloadRequestToken = payloadRequestTokens[0] || '';
    const requestToken = String(expectedRequestToken || payloadRequestToken).trim();
    const candidates = [
        [payload?.public_opening, payloadRequestTokens],
        [payload?.intelligence?.public_opening, [
            ...payloadRequestTokens,
            ...getJaneRequestTokens(payload?.intelligence?.request_token, payload?.intelligence?.metadata?.request_token),
        ]],
        [payload?.answer?.public_opening, [
            ...payloadRequestTokens,
            ...getJaneRequestTokens(payload?.answer?.request_token, payload?.answer?.metadata?.request_token),
        ]],
        [payload?.metadata?.public_opening, payloadRequestTokens],
        [payload?.metadata?.intelligence?.public_opening, [
            ...payloadRequestTokens,
            ...getJaneRequestTokens(payload?.metadata?.intelligence?.request_token),
        ]],
    ];
    const matched = candidates.find(([candidate, envelopeRequestTokens]) => {
        const nestedRequestToken = String(candidate?.request_token || '').trim();
        const bindingTokens = [...envelopeRequestTokens, ...getJaneRequestTokens(nestedRequestToken)];
        return candidate?.origin === 'model'
            && typeof candidate?.text === 'string' && candidate.text.trim() !== ''
            && (requireRequestBinding
                ? requestToken !== ''
                    && bindingTokens.length > 0
                    && bindingTokens.every(token => token === requestToken)
                : (!requestToken || !candidate?.request_token || String(candidate.request_token) === requestToken));
    });
    const opening = matched?.[0];
    return opening ? { ...opening, text: opening.text.trim() } : null;
};

export const getJaneStreamOpening = (event = null, establishedRequestToken = '') => {
    const requestToken = String(establishedRequestToken || '').trim();
    if (!requestToken) return null;
    const eventRequestTokens = getJaneRequestTokens(event?.request_token, event?.metadata?.request_token);
    if (eventRequestTokens.some(token => token !== requestToken)) return null;
    return getJanePublicOpening(event, requestToken, true)
        || getJanePublicOpening(event?.payload, requestToken, true);
};

export const withJanePublicOpening = (intelligence = null, fallbackText = '', streamedOpening = null, requestToken = '') => {
    const requireRequestBinding = String(requestToken || '').trim() !== '';
    const publicOpening = getJanePublicOpening(intelligence, requestToken, requireRequestBinding)
        || getJanePublicOpening({ public_opening: streamedOpening }, requestToken, requireRequestBinding);
    const { public_opening: previousPublicOpening, ...publicIntelligence } = intelligence || {};
    const rejectedPreviousModelOpening = previousPublicOpening?.origin === 'model'
        && !getJanePublicOpening(intelligence, requestToken, requireRequestBinding);
    const legacyOpeningText = !requireRequestBinding && !rejectedPreviousModelOpening
        ? String(intelligence?.jane_opening_text || '').trim()
        : '';
    return {
        ...publicIntelligence,
        ...(publicOpening ? { public_opening: publicOpening } : {}),
        jane_opening_text: publicOpening?.text || legacyOpeningText || (!requireRequestBinding ? fallbackText : ''),
    };
};

const getJaneBackgroundRunStatus = (run = null) => String(run?.status || '').trim().toLowerCase();

const getJaneBackgroundRunTimestampMs = (run = null) => {
    const source = run?.run && typeof run.run === 'object' ? run.run : run;
    const values = [source?.updated_at, source?.finished_at, source?.started_at, source?.queued_at, source?.created_at];
    return values.reduce((latest, value) => {
        const rawValue = String(value || '').trim();
        if (!rawValue) return latest;
        const normalizedValue = rawValue.includes('T') ? rawValue : rawValue.replace(' ', 'T');
        const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
        const parsedValue = Date.parse(hasTimezone ? normalizedValue : `${normalizedValue}Z`);
        return Number.isFinite(parsedValue) ? Math.max(latest, parsedValue) : latest;
    }, 0);
};

export const getJaneBackgroundRunIdentity = (run = null) => {
    const taskType = String(run?.event_type || run?.task?.task_type || run?.task_type || 'background_job').trim();
    const runId = String(run?.id || run?.run_id || '').trim();
    return runId ? `${taskType}:${runId}` : '';
};

const mergeJaneBackgroundRun = (existing = null, incoming = null) => {
    if (!existing) return incoming;
    if (!incoming) return existing;
    const existingIsAuthoritative = existing._optimistic !== true;
    const incomingIsOptimistic = incoming._optimistic === true;
    const existingStatus = getJaneBackgroundRunStatus(existing);
    const incomingStatus = getJaneBackgroundRunStatus(incoming);
    const existingIsTerminal = JANE_BACKGROUND_TERMINAL_STATUSES.has(existingStatus);
    const incomingIsTerminal = JANE_BACKGROUND_TERMINAL_STATUSES.has(incomingStatus);
    const existingTimestamp = getJaneBackgroundRunTimestampMs(existing);
    const incomingTimestamp = getJaneBackgroundRunTimestampMs(incoming);
    let preferIncoming = true;
    if (existingIsAuthoritative && incomingIsOptimistic) {
        preferIncoming = false;
    } else if (!existingIsAuthoritative && !incomingIsOptimistic) {
        preferIncoming = true;
    } else if (existingIsTerminal !== incomingIsTerminal) {
        // Run terminality is durable. A late active/queued poll must never
        // revive a result that this client has already seen finish.
        preferIncoming = incomingIsTerminal;
    } else if (existingIsTerminal && incomingIsTerminal && existingStatus !== incomingStatus) {
        preferIncoming = false;
    } else if (existingTimestamp > 0 && incomingTimestamp > 0 && incomingTimestamp < existingTimestamp) {
        preferIncoming = false;
    }
    const overlay = preferIncoming ? incoming : existing;
    const base = preferIncoming ? existing : incoming;
    const previousPlan = preferIncoming ? existing?.metadata?.run_plan : incoming?.metadata?.run_plan;
    const nextPlan = preferIncoming ? incoming?.metadata?.run_plan : existing?.metadata?.run_plan;
    // Background polls contain full worker snapshots, not incremental trace
    // events. Reusing the foreground merge keeps obsolete receipt step IDs.
    const mergedPlan = nextPlan && typeof nextPlan === 'object' ? nextPlan : previousPlan;
    return {
        ...base,
        ...overlay,
        metadata: {
            ...(base.metadata || {}),
            ...(overlay.metadata || {}),
            ...(mergedPlan ? { run_plan: mergedPlan } : {}),
        },
        task: {
            ...(base.task || {}),
            ...(overlay.task || {}),
            metadata: {
                ...(base.task?.metadata || {}),
                ...(overlay.task?.metadata || {}),
            },
        },
        ...(base.run || overlay.run ? { run: { ...(base.run || {}), ...(overlay.run || {}) } } : {}),
        _optimistic: existing._optimistic === true && incoming._optimistic === true,
        _authoritativeSeenAt: Math.max(
            Number(existing._authoritativeSeenAt || 0),
            Number(incoming._authoritativeSeenAt || 0)
        ) || undefined,
    };
};

export const dedupeJaneBackgroundRuns = (runs = []) => {
    const runsByIdentity = new Map();
    (Array.isArray(runs) ? runs : []).forEach((run) => {
        const identity = getJaneBackgroundRunIdentity(run);
        if (!identity) return;
        runsByIdentity.set(identity, mergeJaneBackgroundRun(runsByIdentity.get(identity), run));
    });
    return Array.from(runsByIdentity.values());
};

export const upsertJaneBackgroundRun = (runs = [], incoming = null) => {
    const identity = getJaneBackgroundRunIdentity(incoming);
    if (!identity) return Array.isArray(runs) ? runs : [];
    const existingRuns = Array.isArray(runs) ? runs : [];
    const existingIndex = existingRuns.findIndex(run => getJaneBackgroundRunIdentity(run) === identity);
    if (existingIndex < 0) return [incoming, ...existingRuns];
    return existingRuns.map((run, index) => (
        index === existingIndex ? mergeJaneBackgroundRun(run, incoming) : run
    ));
};

export const reconcileJaneBackgroundRunPoll = (runs = [], authoritativeRuns = [], options = {}) => {
    const nowMs = Number(options.nowMs || Date.now());
    const activeChatId = Number(options.activeChatId || 0);
    const authoritativeWithSeenAt = dedupeJaneBackgroundRuns(
        (Array.isArray(authoritativeRuns) ? authoritativeRuns : []).map(run => ({
            ...run,
            _optimistic: false,
            _authoritativeSeenAt: nowMs,
        }))
    );
    const authoritativeIdentities = new Set(authoritativeWithSeenAt.map(getJaneBackgroundRunIdentity));
    const retainedRuns = (Array.isArray(runs) ? runs : []).filter((run) => {
        const identity = getJaneBackgroundRunIdentity(run);
        if (!identity) return false;
        const runChatId = Number(run?.chat_id || run?.task?.chat_id || 0);
        if (activeChatId && runChatId && activeChatId !== runChatId) return false;
        // Keep the prior snapshot long enough to merge the authoritative poll
        // by durable identity; dropping it here loses terminal and plan state.
        if (authoritativeIdentities.has(identity)) return true;
        if (JANE_BACKGROUND_TERMINAL_STATUSES.has(getJaneBackgroundRunStatus(run))) return true;
        const lastSeenAt = Number(
            run?._authoritativeSeenAt
            || run?._optimisticReceivedAt
            || getJaneBackgroundRunTimestampMs(run)
            || 0
        );
        return lastSeenAt > 0 && nowMs - lastSeenAt <= JANE_BACKGROUND_RECEIPT_GRACE_MS;
    });
    return authoritativeWithSeenAt.reduce(
        (nextRuns, run) => upsertJaneBackgroundRun(nextRuns, run),
        retainedRuns
    );
};

export const limitJaneBackgroundRunHistory = (runs = [], terminalLimit = JANE_BACKGROUND_TERMINAL_HISTORY_LIMIT) => {
    let retainedTerminalCount = 0;
    const normalizedTerminalLimit = Math.max(0, Number(terminalLimit) || 0);
    return (Array.isArray(runs) ? runs : []).filter((run) => {
        if (!JANE_BACKGROUND_TERMINAL_STATUSES.has(getJaneBackgroundRunStatus(run))) return true;
        retainedTerminalCount += 1;
        return retainedTerminalCount <= normalizedTerminalLimit;
    });
};

export const getJaneBackgroundReceipt = (payload = null) => {
    if (!payload || typeof payload !== 'object') return null;
    const intelligence = payload.intelligence && typeof payload.intelligence === 'object'
        ? payload.intelligence
        : {};
    const answer = payload.answer && typeof payload.answer === 'object'
        ? payload.answer
        : (intelligence.answer && typeof intelligence.answer === 'object' ? intelligence.answer : {});
    const proactive = payload.proactive_ai
        || intelligence.proactive_ai
        || answer.proactive_ai
        || intelligence.answer?.proactive_ai
        || null;
    if (!proactive || typeof proactive !== 'object') return null;
    const mode = String(answer.mode || intelligence.answer?.mode || payload.mode || intelligence.retrieval_mode || '').trim().toLowerCase();
    const modeTaskTypes = {
        background_draft_queued: 'draft_background_job',
        case_timeline_build_queued: 'case_timeline_build',
        case_trial_analysis_queued: 'case_trial_analysis',
        jane_background_run_queued: 'jane_background_run',
    };
    const taskType = String(proactive.task_type || modeTaskTypes[mode] || '').trim();
    const runId = Number(proactive.run_id || 0);
    if (runId <= 0 || (!JANE_BACKGROUND_HANDOFF_MODES.has(mode) && !JANE_BACKGROUND_TASK_TYPES.has(taskType))) {
        return null;
    }
    return { proactive, mode, taskType, answer, intelligence };
};

export const isJaneBackgroundHandoffPayload = (payload = null) => getJaneBackgroundReceipt(payload) !== null;

export const getJaneForegroundRunActivity = (message = null, answerOverride = null) => {
    const answer = answerOverride || message?.answer || null;
    const isAssistantStreaming = Boolean(message?.metadata?.streaming);
    const handedOffToBackground = isJaneBackgroundHandoffPayload({
        answer,
        intelligence: message?.metadata?.intelligence || null,
        mode: answer?.mode || message?.metadata?.intelligence?.retrieval_mode || '',
    });
    const runPlanStatus = String(
        message?.metadata?.runPlan?.status
        || message?.metadata?.intelligence?.run_plan?.status
        || ''
    ).trim().toLowerCase();
    const hasActiveRunPlan = !handedOffToBackground
        && ['queued', 'pending', 'running', 'active', 'in_progress'].includes(runPlanStatus);
    return {
        handedOffToBackground,
        hasActiveRunPlan,
        isLiveAssistantThinking: message?.role === 'assistant'
            && !handedOffToBackground
            && (Boolean(message?.pending) || isAssistantStreaming || hasActiveRunPlan),
    };
};

export const buildJaneBackgroundRunFromReceipt = (payload = null, chatId = null, nowMs = Date.now()) => {
    const receipt = getJaneBackgroundReceipt(payload);
    if (!receipt) return null;
    const { proactive, taskType, answer, intelligence } = receipt;
    const runId = Number(proactive.run_id || 0);
    const taskId = Number(proactive.task_id || 0);
    const summary = String(answer.summary || '').trim();
    const isDocumentChronology = /document chronology/i.test(summary)
        || /document chronology/i.test(String(proactive.task_title || ''));
    const fallbackTitle = taskType === 'case_timeline_build'
        ? (isDocumentChronology ? 'Sourced document chronology' : 'Full case timeline')
        : (taskType === 'case_trial_analysis'
            ? 'Case analysis'
            : (taskType === 'jane_background_run' ? 'Jane response' : 'Draft document'));
    const taskTitle = String(proactive.task_title || fallbackTitle).trim() || fallbackTitle;
    const receiptChatId = Number(proactive.chat_id || payload.chat_id || chatId || 0) || null;
    const runPlan = payload.run_plan || intelligence.run_plan || null;
    const receiptStatus = String(proactive.status || 'queued').trim().toLowerCase() || 'queued';
    const receiptRunPlan = runPlan && typeof runPlan === 'object' && receiptStatus === 'queued'
        ? { ...runPlan, status: 'queued' }
        : runPlan;
    return {
        id: runId,
        run_id: runId,
        task_id: taskId || null,
        chat_id: receiptChatId,
        event_type: taskType,
        status: receiptStatus,
        status_message: String(proactive.status_message || '').trim(),
        metadata: {
            ...(proactive.metadata && typeof proactive.metadata === 'object' ? proactive.metadata : {}),
            ...(getJanePublicOpening(payload) ? { public_opening: getJanePublicOpening(payload) } : {}),
            ...(receiptRunPlan && typeof receiptRunPlan === 'object' ? { run_plan: receiptRunPlan } : {}),
            route: proactive.route || undefined,
            request_token: proactive.request_token || undefined,
        },
        task: {
            id: taskId || null,
            chat_id: receiptChatId,
            task_type: taskType,
            title: taskTitle,
            metadata: {
                route: proactive.route || undefined,
            },
        },
        _optimistic: true,
        _receiptVisible: true,
        _optimisticReceivedAt: Number(nowMs) || Date.now(),
    };
};

export const getBackgroundDraftRunIdForMessage = (message) => Number(
    message?.metadata?.intelligence?.proactive_ai?.run_id
    || message?.metadata?.proactive_ai?.run_id
    || message?.answer?.proactive_ai?.run_id
    || 0
);

export const isJaneBackgroundHandoffShadowPrompt = (message = null) => (
    String(message?.role || '').trim().toLowerCase() === 'user'
    && String(message?.metadata?.background_handoff_anchor?.presentation || '').trim()
        === 'assistant_receipt_only'
    && String(message?.metadata?.background_handoff_anchor?.task_type || '').trim()
        === 'case_timeline_build'
);

export const filterBackgroundDraftRunsForMessages = (runs = [], messages = [], activeChatId = null) => {
    const visibleRunIds = new Set(
        (Array.isArray(messages) ? messages : [])
            .map(getBackgroundDraftRunIdForMessage)
            .filter(runId => Number.isFinite(runId) && runId > 0)
            .map(runId => String(runId))
    );
    const normalizedActiveChatId = Number(activeChatId || 0);
    return (Array.isArray(runs) ? runs : []).filter((run) => {
        if (run?.event_type === 'chat_reminder') return true;
        const runChatId = Number(run?.chat_id || run?.task?.chat_id || 0);
        if (normalizedActiveChatId > 0 && runChatId > 0 && normalizedActiveChatId !== runChatId) {
            return false;
        }
        if (visibleRunIds.has(String(run?.id || run?.run_id || 0))) return true;
        const runStatus = String(run?.status || '').trim().toLowerCase();
        if (normalizedActiveChatId > 0
            && runChatId === normalizedActiveChatId
            && ['queued', 'running', 'waiting', 'active', 'in_progress', 'saving', 'awaiting_permission'].includes(runStatus)
        ) {
            // Active runs returned by the chat-scoped, authorized API must be
            // visible even when the receipt message has not loaded yet (for
            // example after recovery or a second-tab handoff).
            return true;
        }
        return run?._receiptVisible === true
            && (!normalizedActiveChatId || !runChatId || normalizedActiveChatId === runChatId);
    });
};

export const getJaneBackgroundRunState = (run = null) => {
    const status = String(run?.status || 'queued').trim().toLowerCase();
    const statusMessage = String(run?.status_message || '').trim();
    const extractionStatus = String(run?.metadata?.timeline_extraction?.status || '').trim().toLowerCase();
    if (status === 'failed') return { key: 'failed', label: 'Failed' };
    if (status === 'completed') return { key: 'completed', label: 'Completed' };
    if (getJaneProviderRecoveryContinuation(run)) {
        return status === 'running' ? { key: 'running', label: 'Running' } : { key: 'queued', label: 'Queued' };
    }
    if (status === 'awaiting_permission'
        || extractionStatus === 'waiting'
        || (status === 'queued' && /^(?:waiting|retrying)\b/i.test(statusMessage))) {
        return { key: 'waiting', label: 'Waiting' };
    }
    if (['running', 'active', 'in_progress', 'saving'].includes(status)) {
        return { key: 'running', label: 'Running' };
    }
    return { key: 'queued', label: 'Queued' };
};

export const getJaneBackgroundRunPresentation = (run = null) => {
    const taskType = String(run?.task?.task_type || run?.event_type || '').trim();
    const state = getJaneBackgroundRunState(run);
    const fallbackTitle = taskType === 'case_timeline_build'
        ? 'Full case timeline'
        : (taskType === 'case_trial_analysis'
            ? 'Case analysis'
            : (taskType === 'jane_background_run' ? 'Jane response' : 'Draft document'));
    const title = (String(run?.task?.title || fallbackTitle)
        .replace(/^(?:(?:build(?:ing)?|draft(?:ing)?)\s+)+/i, '')
        .trim() || fallbackTitle).replace(/^./, firstCharacter => firstCharacter.toUpperCase());
    const suppliedStatus = sanitizeJaneStatusMessage(run?.status_message);
    const legacyTimelineStatus = taskType === 'case_timeline_build'
        && /^(?:building case timeline in the background|gathering case context|queued full timeline build)/i.test(suppliedStatus);
    const timelineTarget = /document chronology/i.test(title) ? 'the matching documents' : 'the case';
    let statusText = suppliedStatus;
    if (!statusText || legacyTimelineStatus) {
        if (taskType === 'case_timeline_build') {
            statusText = state.key === 'queued'
                ? `Ready to begin checking dated events across ${timelineTarget}.`
                : (state.key === 'running'
                    ? `Finding and verifying dated events across ${timelineTarget}…`
                    : (state.key === 'waiting'
                        ? 'Waiting for the required document text before continuing.'
                        : (state.key === 'completed'
                            ? 'The chronology result has been added to the chat.'
                            : 'The chronology did not complete.')));
        } else {
            statusText = state.key === 'queued'
                ? 'Ready to start in the background.'
                : (state.key === 'running'
                    ? 'Working in the background…'
                    : (state.key === 'waiting'
                        ? 'Waiting for the next required step.'
                        : (state.key === 'completed'
                            ? 'The result has been added to the chat.'
                            : 'The background task did not complete.')));
        }
    }
    return {
        title,
        stateKey: state.key,
        stateLabel: state.label,
        statusText,
        announcement: `${title} — ${state.label}. ${statusText}`,
    };
};

export const getJaneBackgroundRunPlan = (run = null, messages = []) => {
    const directPlan = run?.metadata?.run_plan || run?.run?.metadata?.run_plan || null;
    if (directPlan && typeof directPlan === 'object') {
        const state = getJaneBackgroundRunState(run);
        const stateAwarePlan = state.key === 'failed' ? { ...directPlan, status: 'failed' } : directPlan;
        return normalizeJaneRunPlan(stateAwarePlan, [], {
            pending: ['queued', 'running', 'waiting'].includes(state.key),
            complete: state.key === 'completed',
            failed: state.key === 'failed',
        });
    }
    const runId = Number(run?.id || run?.run_id || 0);
    const sourceMessage = (Array.isArray(messages) ? messages : []).find(
        message => getBackgroundDraftRunIdForMessage(message) === runId
    );
    return sourceMessage ? getJaneRunPlanFromMessage(sourceMessage) : null;
};

export const buildJaneBackgroundRunPlannerModel = (run = null, messages = []) => {
    const plan = getJaneBackgroundRunPlan(run, messages);
    return {
        collapsedByDefault: true,
        plan,
        steps: Array.isArray(plan?.steps) ? plan.steps : [],
    };
};

// Earlier workers stored every classification milestone as an instantaneous
// completed planner. Fold only that exact application contract; never infer
// phases from prose, merge different runs, or discard model-authored commentary.
export const foldJaneClassificationCheckpoints = (entries = []) => {
    const result = entries.map(entry => ({ ...entry }));
    const removed = new Set();
    result.slice().forEach((entry) => {
        const match = /^((?:case-timeline-build-\d+):classify-documents):checkpoint-(\d{1,2}):planner$/.exec(entry.id);
        if (entry.kind !== 'planner' || ![undefined, 'application'].includes(entry.origin) || !match || Number(match[2]) > 20
            || entry.run_plan?.id !== `${match[1]}:checkpoint-${match[2]}`
            || entry.run_plan?.current_step_id !== `classification-checkpoint-${match[2]}`) return;
        const legacyPlan = entry.run_plan;
        const legacyStep = legacyPlan.steps?.[0];
        const titleCounts = /^Document-type review: (\d{1,10}) of (\d{1,10}) decisions recorded$/.exec(entry.title);
        const detailCounts = /^(\d{1,10}) documents scanned in this completed batch; (\d{1,10}) recorded document-type decisions overall, including uncertain decisions\.$/.exec(legacyStep?.detail);
        const pendingCount = /^(\d{1,10}) documents remain scheduled for classification$/.exec(legacyStep?.meta?.[0]);
        if (entry.status !== 'complete' || legacyPlan.status !== 'complete' || legacyStep?.status !== 'complete'
            || typeof entry.started_at !== 'string' || !parseJaneTimestampMs(entry.started_at) || entry.finished_at !== entry.started_at
            || legacyPlan.default_collapsed !== true || !Array.isArray(legacyPlan.steps) || legacyPlan.steps.length !== 1
            || legacyStep.id !== legacyPlan.current_step_id || legacyStep.label !== entry.title || legacyPlan.title !== entry.title
            || !Array.isArray(legacyStep.meta) || legacyStep.meta.length !== 2 || legacyStep.meta[1] !== 'Page-text readiness is checked separately'
            || Object.keys(legacyStep).some(key => !['id', 'label', 'status', 'detail', 'meta'].includes(key))
            || Object.keys(legacyPlan).some(key => !['id', 'title', 'default_collapsed', 'current_step_id', 'status', 'steps'].includes(key))
            || Object.keys(entry).some(key => !['id', 'kind', 'origin', 'sequence', 'status', 'started_at', 'finished_at', 'title', 'run_plan'].includes(key))
            || !titleCounts || !detailCounts || !pendingCount || titleCounts[1] !== detailCounts[2]
            || !getJaneClassificationProgress({ current_step_id: 'classify-documents', classification_progress: {
                processed: Number(titleCounts[1]), total: Number(titleCounts[2]), pending: Number(pendingCount[1]),
                scanned: Number(detailCounts[1]), milestone: Number(match[2]),
            } })) return;
        const parentId = `${match[1]}:planner`;
        let parent = result.find(candidate => candidate.id === parentId && candidate.kind === 'planner');
        if (!parent) {
            parent = { id: parentId, kind: 'planner', sequence: entry.sequence, status: 'unknown',
                title: 'Review document-type classifications',
                run_plan: { id: match[1], current_step_id: 'classify-documents', status: 'unknown', steps: [] } };
            result.push(parent);
        }
        const updates = new Map((Array.isArray(parent.run_plan?.updates) ? parent.run_plan.updates : [])
            .filter(update => update && typeof update.id === 'string').map(update => [update.id, update]));
        const id = `${match[1]}:checkpoint-${match[2]}`;
        if (!updates.has(id)) updates.set(id, {
            id, observed_at: entry.finished_at || entry.started_at || null,
            title: entry.title || entry.run_plan?.title || 'Document-type progress recorded',
            detail: entry.run_plan?.steps?.[0]?.detail || '', meta: entry.run_plan?.steps?.[0]?.meta || [],
        });
        parent.run_plan = { ...parent.run_plan, updates: Array.from(updates.values()).slice(-21) };
        removed.add(entry.id);
    });
    return result.filter(entry => !removed.has(entry.id)).sort((a, b) => a.sequence - b.sequence);
};

export const buildJaneBackgroundWorkHistory = (run = null, message = null) => {
    const history = run?.metadata?.work_history || message?.metadata?.work_history || message?.metadata?.intelligence?.work_history;
    if (Array.isArray(history) && history.length > 0) {
        const entriesById = new Map();
        history.forEach((entry, index) => {
            if (!entry || typeof entry !== 'object') return;
            const id = String(entry.id || '').trim();
            if (!id) return;
            entriesById.set(id, { ...entry, id, sequence: Number.isFinite(Number(entry.sequence)) ? Number(entry.sequence) : index });
        });
        const entries = foldJaneClassificationCheckpoints(Array.from(entriesById.values()).sort((left, right) => left.sequence - right.sequence));
        const currentPlan = run?.metadata?.run_plan;
        const classification = getJaneClassificationProgress(currentPlan);
        // The current public plan can be newer than its last saved milestone.
        // Match the full phase ID before using its typed counters, never prose.
        if (classification) {
            const parent = entries.find(entry => entry.id === `${currentPlan.id}:classify-documents:planner`
                && entry.run_plan?.id === `${currentPlan.id}:classify-documents`);
            if (parent) parent.run_plan = { ...parent.run_plan, classification_progress: classification,
                ...(typeof currentPlan.updated_at_utc === 'string' ? { updated_at_utc: currentPlan.updated_at_utc } : {}) };
        }
        return entries;
    }
    const presentation = getJaneBackgroundRunPresentation(run);
    const plan = run?.metadata?.run_plan || message?.metadata?.runPlan || message?.metadata?.intelligence?.run_plan;
    const runId = String(run?.id || getBackgroundDraftRunIdForMessage(message) || 'work');
    return [
        { id: `${runId}:progress`, kind: 'progress', sequence: 0, text: presentation.statusText, status: run?.status },
        ...(plan && typeof plan === 'object' ? [{
            id: `${runId}:plan`, kind: 'planner', sequence: 1, title: presentation.title,
            status: run?.status, started_at: run?.started_at, finished_at: run?.finished_at,
            run_plan: plan,
        }] : []),
    ];
};

// Presentation only: older foreground messages did not publish work_history.
// Keep their reported plan/step identities and facts, without inventing work
// or treating the display wrapper as a durable background task receipt.
export const buildJaneForegroundWorkRun = (message = null) => {
    if (message?.role !== 'assistant' || message?.metadata?.streaming_trace_disabled
        || getJaneForegroundRunActivity(message).handedOffToBackground) return null;
    const plan = getJaneRunPlanFromMessage(message);
    const supplied = message?.metadata?.runPlan || message?.metadata?.intelligence?.run_plan;
    const rawStatus = String(supplied?.status || '').trim().toLowerCase();
    // The legacy top-level skipped state is not a background terminal enum.
    // Keep its existing final presentation rather than hiding its attribution.
    if (!plan || (plan.status === 'skipped' && !['cancelled', 'canceled'].includes(rawStatus))) return null;
    const status = ['waiting', 'cancelled', 'canceled', 'awaiting_permission'].includes(rawStatus)
        ? rawStatus : plan.status === 'complete' ? 'completed' : plan.status;
    const suppliedSteps = Array.isArray(supplied?.steps) ? supplied.steps : [];
    const steps = supplied && suppliedSteps.length === 0 && getRagPlannerStepsFromMessage(message).length === 0
        ? [] : plan.steps.map(step => {
            const original = suppliedSteps.find(candidate => candidate && candidate.id === step.id);
            return { ...original, ...step,
                ...(['waiting', 'cancelled', 'canceled', 'awaiting_permission', 'skipped'].includes(original?.status)
                    ? { status: original.status } : {}) };
        });
    const id = `foreground-${message.id}`;
    const startedMs = normalizeJaneElapsedMs(message?.metadata?.responsePlanStartedAt)
        || parseJaneTimestampMs(supplied?.started_at);
    const startedAt = startedMs ? new Date(startedMs).toISOString() : null;
    const finishedAt = message?.metadata?.responsePlanCompletedAt || supplied?.finished_at || null;
    const runPlan = { ...supplied, ...plan, steps, status };
    return {
        id, status, started_at: startedAt, finished_at: finishedAt,
        task: { title: plan.title },
        metadata: { run_plan: runPlan, work_history: [{
            id: `${id}:${plan.id || 'plan'}:planner`, kind: 'planner', sequence: 0,
            title: plan.title, status, started_at: startedAt, finished_at: finishedAt, run_plan: runPlan,
        }] },
    };
};

export const mergeJaneStreamWorkHistory = (history = [], event = {}, requestToken = '', nowMs = Date.now()) => {
    if (requestToken && event.request_token && String(event.request_token) !== String(requestToken)) return history;
    const progress = event.public_progress || event.payload?.public_progress || event.intelligence?.public_progress;
    const publicProgress = getJanePublicOpening({ public_opening: progress }, requestToken);
    const plan = ['planner', 'run_plan'].includes(event.type) ? event.run_plan || event.plan : null;
    const additions = [];
    if (publicProgress && typeof publicProgress.id === 'string' && publicProgress.id.trim()) {
        additions.push({ id: publicProgress.id, kind: 'progress', origin: 'model', text: publicProgress.text, status: 'complete' });
    }
    if (plan?.id && Array.isArray(plan.steps)) {
        plan.steps.forEach((step) => {
            const id = String(step?.id || '').trim();
            const label = sanitizeJaneStatusMessage(step?.label);
            const status = String(step?.status || 'pending').toLowerCase();
            if (!id || !label || (['pending', 'queued'].includes(status) && String(plan.current_step_id || '') !== id)) return;
            const planStatus = String(plan.status || '').toLowerCase();
            const stepStatus = ['complete', 'completed'].includes(planStatus) && !['failed', 'skipped', 'cancelled', 'canceled'].includes(status) ? 'complete'
                : ['failed', 'cancelled', 'canceled', 'awaiting_permission'].includes(planStatus) && ['running', 'active', 'in_progress', 'pending', 'queued'].includes(status) ? planStatus : status;
            const safeStep = { id, label, detail: sanitizeJaneStatusMessage(step.detail), status: stepStatus };
            additions.push({
                id: `${plan.id}:${id}:planner`, kind: 'planner', title: label, status: stepStatus,
                run_plan: { id: `${plan.id}:${id}`, title: label, status: stepStatus, current_step_id: id, steps: [safeStep] },
            });
        });
    }
    if (additions.length === 0) return history;
    const terminalStatuses = ['complete', 'completed', 'failed', 'cancelled', 'canceled', 'skipped', 'awaiting_permission'];
    const entries = history.filter(entry => !requestToken || !entry.request_token || String(entry.request_token) === String(requestToken));
    let sequence = Math.max(0, ...entries.map(entry => Number(entry.sequence) || 0));
    additions.forEach((entry) => {
        const index = entries.findIndex(previous => previous.id === entry.id);
        const previous = index >= 0 ? entries[index] : null;
        if (previous && terminalStatuses.includes(previous.status) && !terminalStatuses.includes(entry.status)) return;
        const next = {
            ...entry, sequence: previous?.sequence ?? ++sequence,
            started_at: previous?.started_at || new Date(nowMs).toISOString(),
            ...(requestToken ? { request_token: requestToken } : {}),
            ...(terminalStatuses.includes(entry.status) ? { finished_at: previous?.finished_at || new Date(nowMs).toISOString() } : {}),
        };
        if (index >= 0) entries[index] = next;
        else entries.push(next);
    });
    const bounded = entries.slice(-64);
    return JSON.stringify(bounded) === JSON.stringify(history) ? history : bounded;
};

export const shouldShowJaneAssistantAttribution = (message = null, run = null) => {
    if (message?.role !== 'assistant') return true;
    if (message.pending || message?.metadata?.streaming || message?.metadata?.pending_request === true) return false;
    if (run) return JANE_BACKGROUND_TERMINAL_STATUSES.has(getJaneBackgroundRunStatus(run));
    const status = String(message?.metadata?.runPlan?.status || message?.metadata?.intelligence?.run_plan?.status || '').toLowerCase();
    return !['queued', 'pending', 'running', 'active', 'in_progress', 'waiting', 'awaiting_permission'].includes(status);
};

export const getJaneBackgroundWorkType = (entry = {}) => {
    const suppliedType = String(entry.work_type || entry.run_plan?.work_type || '').trim().toLowerCase();
    const knownTypes = ['drafting', 'documents', 'verification', 'analysis', 'planning', 'search', 'publishing'];
    if (knownTypes.includes(suppliedType)) return suppliedType;
    const description = [suppliedType, entry.run_plan?.current_step_id, entry.title, entry.run_plan?.title, entry.run_plan?.mode]
        .map(value => String(value || '').toLowerCase()).join(' ');
    if (/draft|writ(?:e|ing)|compose|revision/.test(description)) return 'drafting';
    if (/verif|authoriz|citation|quality|guardrail|permission/.test(description)) return 'verification';
    if (/publish|post-result|save|deliver/.test(description)) return 'publishing';
    if (/document|source|file|retriev|load|inventory|extract/.test(description)) return 'documents';
    if (/search|find|discover/.test(description)) return 'search';
    if (/analy|synthesi|classif|chronology|timeline|event/.test(description)) return 'analysis';
    return 'planning';
};

const JANE_BACKGROUND_WORK_ICONS = {
    drafting: Pen,
    documents: FileSearch,
    verification: ShieldCheck,
    analysis: Layers,
    planning: ListChecks,
    search: Search,
    publishing: Save,
};

const getJaneWorkStepStatus = (status) => {
    const value = String(status || '').toLowerCase();
    if (['complete', 'completed'].includes(value)) return 'complete';
    if (['running', 'active', 'in_progress'].includes(value)) return 'running';
    if (['waiting', 'queued'].includes(value)) return 'waiting';
    if (['cancelled', 'canceled'].includes(value)) return 'cancelled';
    return ['pending', 'failed', 'skipped', 'awaiting_permission'].includes(value) ? value : 'unknown';
};

const JANE_WORK_STEP_STATUSES = {
    complete: { Icon: Check, label: 'Done' },
    running: { Icon: Loader2, label: 'In progress' },
    waiting: { Icon: Clock3, label: 'Waiting' },
    pending: { Icon: Circle, label: 'Not started' },
    failed: { Icon: CircleAlert, label: 'Failed' },
    cancelled: { Icon: Ban, label: 'Stopped' },
    skipped: { Icon: SkipForward, label: 'Skipped' },
    awaiting_permission: { Icon: LockKeyhole, label: 'Needs approval' },
    unknown: { Icon: CircleHelp, label: 'Status unavailable' },
};

export const JaneWorkStepStatusIcon = ({ status }) => {
    const key = getJaneWorkStepStatus(status);
    const { Icon } = JANE_WORK_STEP_STATUSES[key];
    return <Icon size={14} className={`library-rag-work-step-icon${key === 'running' ? ' spinner' : ''}`} data-step-status={key} aria-hidden="true" />;
};

// These counters describe classification decisions, never extracted pages or
// citation coverage. Do not derive counts or document types from model prose.
const JANE_CLASSIFICATION_FAILURE_LABELS = Object.freeze({
    credit_balance_exhausted: 'OpenAI credits exhausted',
    organization_spend_limit_exceeded: 'OpenAI organization spend limit reached',
    project_spend_limit_exceeded: 'OpenAI project spend limit reached',
    organization_usage_limit_exceeded: 'OpenAI organization usage limit reached',
    insufficient_quota: 'OpenAI quota exceeded',
    rate_limit_exceeded: 'OpenAI rate limit reached',
    slow_down: 'OpenAI rate limit reached',
    server_is_overloaded: 'OpenAI temporarily overloaded',
    missing_api_key: 'Classification credentials unavailable',
    invalid_model: 'Classification configuration needs attention',
    invalid_operation_ordinal: 'Classification configuration needs attention',
    invalid_call_ledger_sink: 'Classification configuration needs attention',
    invalid_tool_definition: 'Classification configuration needs attention',
    not_configured: 'Classification configuration needs attention',
    input_budget_exceeded: 'Classification input limit reached',
    request_budget_exceeded: 'Classification request budget reached',
    tool_budget_exceeded: 'Classification tool budget reached',
    response_budget_exceeded: 'Classification response limit reached',
    deadline_exceeded: 'Classification time budget reached',
    transport_unavailable: 'Classification transport unavailable',
    transport_failed: 'Classification transport error',
    provider_capacity: 'Classification request limited',
    provider_http_error: 'Classification request failed',
    request_encoding_failed: 'Classification request could not be prepared',
    classifier_input_invalid: 'Classification input could not be used',
    classifier_input_encoding_failed: 'Classification request could not be prepared',
    invalid_request: 'Classification request could not be used',
    invalid_provider_json: 'Classification result could not be used',
    provider_response_failed: 'Classification result could not be used',
    provider_response_incomplete: 'Classification result incomplete',
    invalid_response_shape: 'Classification result could not be used',
    no_function_call: 'Classification result could not be used',
    multiple_function_calls: 'Classification result could not be used',
    malformed_function_call: 'Classification result could not be used',
    unauthorized_function_call: 'Classification result could not be used',
    invalid_function_arguments: 'Classification result could not be used',
    invalid_answer_plan: 'Classification result could not be used',
    transport_contract_invalid: 'Classification result could not be used',
    classifier_protocol_invalid: 'Classification result could not be used',
    call_ledger_sink_failed: 'Classification diagnostic could not be recorded',
});

// Mirror the shared PHP diagnostic contract, never provider prose or a guessed
// normalization. History is retained as history, not a fallback current error.
const getJaneClassificationFailure = (value, historical = false) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)
        || typeof value.code !== 'string' || !Object.prototype.hasOwnProperty.call(JANE_CLASSIFICATION_FAILURE_LABELS, value.code)) return null;
    const hasHttpStatus = Number.isInteger(value.http_status) && value.http_status >= 100 && value.http_status <= 599;
    const requiredStatus = ['credit_balance_exhausted', 'organization_spend_limit_exceeded', 'project_spend_limit_exceeded',
        'organization_usage_limit_exceeded', 'insufficient_quota', 'rate_limit_exceeded', 'slow_down'].includes(value.code)
        ? 429 : value.code === 'server_is_overloaded' ? 503 : null;
    if (requiredStatus !== null && value.http_status !== requiredStatus) return null;
    const failure = { code: value.code, ...(hasHttpStatus ? { http_status: value.http_status } : {}) };
    if (historical) {
        if (!Number.isInteger(value.attempt) || value.attempt < 1 || value.attempt > 1000
            || typeof value.observed_at !== 'string' || !/^(?!0000)\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value.observed_at)) return null;
        const timestamp = Date.parse(value.observed_at);
        if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString() !== value.observed_at.replace('Z', '.000Z')) return null;
        failure.attempt = value.attempt;
        failure.observed_at = value.observed_at;
    }
    return failure;
};

const getJaneClassificationProgress = (plan) => {
    const progress = plan?.classification_progress;
    if (plan?.current_step_id !== 'classify-documents' || !progress
        || !['total', 'processed', 'pending', 'scanned', 'milestone'].every(key => Number.isInteger(progress[key]) && progress[key] >= 0 && progress[key] <= 1000000000)
        || progress.total <= 0 || progress.processed + progress.pending > progress.total
        || progress.scanned > progress.total || progress.milestone > 20) return null;
    const clean = Object.fromEntries(['total', 'processed', 'pending', 'scanned', 'milestone'].map(key => [key, progress[key]]));
    if (['available', 'unavailable', 'not_needed', 'unknown'].includes(progress.provider_status)) clean.provider_status = progress.provider_status;
    if (['provider_unavailable', 'provider_recovered', 'classification_pass_finished'].includes(progress.attention)) clean.attention = progress.attention;
    const failure = getJaneClassificationFailure(progress.classification_failure);
    const lastFailure = getJaneClassificationFailure(progress.classification_last_failure, true);
    if (clean.provider_status === 'unavailable' && clean.pending > 0 && failure
        && !['complete', 'failed', 'cancelled', 'awaiting_permission', 'skipped'].includes(getJaneWorkStepStatus(plan.status))) clean.classification_failure = failure;
    if (lastFailure) clean.classification_last_failure = lastFailure;
    return clean;
};

const getJaneClassificationWaitLabel = (plan) => {
    const progress = getJaneClassificationProgress(plan);
    return JANE_CLASSIFICATION_FAILURE_LABELS[progress?.classification_failure?.code]
        || (progress?.provider_status === 'unavailable' ? 'Waiting for a usable classification result' : 'Waiting for the next document-type review batch');
};

const getJaneClassificationPhaseTitle = (plan, status, latestUpdate, fallback, activityOverride = '') => {
    const progress = getJaneClassificationProgress(plan);
    if (!progress) return typeof latestUpdate?.title === 'string' && latestUpdate.title.trim() ? latestUpdate.title : fallback;
    const state = getJaneWorkStepStatus(status);
    const failureLabel = ['waiting', 'running'].includes(state)
        ? JANE_CLASSIFICATION_FAILURE_LABELS[progress.classification_failure?.code] : '';
    const activity = activityOverride || failureLabel || (state === 'waiting'
        ? (progress.provider_status === 'unavailable' ? 'Classification retry pending' : 'Waiting for the next classification batch')
        : state === 'complete' ? 'Document-type review finished'
            : ['failed', 'cancelled'].includes(state) ? 'Document-type review stopped'
                : state === 'running' ? 'Reviewing document types' : 'Document-type review');
    const evidenceGapCount = Math.max(0, progress.total - progress.processed - progress.pending);
    const terminal = ['complete', 'failed', 'cancelled'].includes(state);
    const progressSummary = state === 'complete' && progress.pending === 0
        ? `${progress.processed} decisions recorded${evidenceGapCount > 0 ? ` · ${evidenceGapCount} documents lacked classifiable evidence` : ''}`
        : `${progress.processed} decisions recorded · ${progress.pending} ${terminal ? 'unfinished' : 'scheduled'} · ${progress.total} documents in scope`;
    return `${activity} · ${progressSummary}`;
};

export const getJaneActiveWorkLabel = (run = null, entries = []) => {
    const status = getJaneBackgroundRunStatus(run);
    if (status === 'saving') return 'Saving the result';
    if (status === 'cancel_requested') return 'Stopping';
    if (!['queued', 'pending', 'running', 'active', 'in_progress', 'waiting'].includes(status)) return '';
    const providerContinuation = getJaneProviderRecoveryContinuation(run);
    if (providerContinuation) return `Continuing with ${providerContinuation.nextProviderLabel}`;
    const currentEntry = [...entries].reverse().find(entry => entry.kind === 'planner'
        && ['running', 'active', 'in_progress', 'waiting'].includes(String(entry.status || entry.run_plan?.status || '').toLowerCase()));
    const plan = currentEntry?.run_plan || run?.metadata?.run_plan;
    const phase = String(plan?.current_step_id || '').toLowerCase();
    if (status === 'waiting' || currentEntry?.status === 'waiting' || getJaneBackgroundRunState(run).key === 'waiting') {
        if (phase === 'classify-documents') return getJaneClassificationWaitLabel(plan);
        return phase === 'verify-extraction' ? 'Waiting for extraction' : 'Waiting for the next step';
    }
    if (['queued', 'pending'].includes(status)) return 'Waiting to start';
    if (phase === 'classify-documents') return getJaneClassificationProgress(plan)?.classification_failure
        ? getJaneClassificationWaitLabel(plan) : 'Checking document types';
    if (phase === 'verify-extraction') return 'Checking extracted text';
    const workType = currentEntry ? getJaneBackgroundWorkType(currentEntry) : plan ? getJaneBackgroundWorkType({ run_plan: plan }) : 'planning';
    return {
        drafting: 'Drafting', documents: 'Retrieving documents', verification: 'Verifying',
        analysis: 'Analyzing', search: 'Searching', publishing: 'Preparing the result', planning: 'Thinking',
    }[workType];
};

export const JaneBackgroundWorkHistory = ({ run, message = null, nowMs = Date.now(), actions = null }) => {
    const [expanded, setExpanded] = useState(null);
    const entries = buildJaneBackgroundWorkHistory(run, message);
    const runStatus = getJaneBackgroundRunStatus(run);
    const terminal = JANE_BACKGROUND_TERMINAL_STATUSES.has(runStatus);
    const finishedWork = terminal || runStatus === 'awaiting_permission';
    const isOpen = expanded ?? !finishedWork;
    const presentation = getJaneBackgroundRunPresentation(run);
    const providerContinuation = getJaneProviderRecoveryContinuation(run);
    const knownStarts = [parseJaneTimestampMs(run?.started_at), normalizeJaneElapsedMs(message?.metadata?.responsePlanStartedAt),
        ...entries.filter(entry => entry.kind === 'planner').map(entry => parseJaneTimestampMs(entry.started_at))].filter(Boolean);
    const startedAt = knownStarts.length ? Math.min(...knownStarts) : null;
    const finishedAt = parseJaneTimestampMs(run?.finished_at || message?.metadata?.responsePlanCompletedAt)
        || (finishedWork ? parseJaneTimestampMs(run?.updated_at) : null);
    const workedLabel = startedAt && finishedAt && finishedAt > startedAt ? `Worked for ${formatJaneElapsedDuration(finishedAt - startedAt)}` : 'Work history';
    const workHistoryAccessibleLabel = finishedWork ? `${workedLabel} · ${presentation.stateLabel}` : 'Work history';
    const activityLabel = getJaneActiveWorkLabel(run, entries);
    return (
        <>
        <details className={`library-rag-work-history ${finishedWork ? 'terminal' : 'active'}`} open={isOpen} data-background-run-id={String(run?.id || '')}>
            <summary aria-label={workHistoryAccessibleLabel} onClick={(event) => { event.preventDefault(); setExpanded(!isOpen); }}>
                <span>{workedLabel}</span>
                <span className="library-rag-work-history-state">{presentation.stateLabel}</span>
                <ChevronDown size={14} className="library-rag-work-chevron" aria-hidden="true" />
            </summary>
            <div className="library-rag-work-history-entries" role="log" aria-live="polite" aria-relevant="additions">
                {entries.map((entry) => {
                    if (entry.kind !== 'planner') {
                        const text = String(entry.text || entry.title || '').trim();
                        return text ? <p key={entry.id} className={entry.origin === 'model' ? 'library-rag-work-progress' : 'library-rag-work-app-status'} data-work-entry-id={entry.id} data-origin={entry.origin === 'model' ? 'model' : 'app_status'}>{text}</p> : null;
                    }
                    const rawStatus = String(entry.status || entry.run_plan?.status || runStatus).trim().toLowerCase();
                    const entryStatus = rawStatus === 'complete' ? 'completed' : rawStatus;
                    const effectiveStatus = finishedWork && ['running', 'active', 'in_progress', 'queued', 'pending', 'waiting'].includes(entryStatus)
                        ? runStatus : entryStatus;
                    const state = ['waiting', 'pending'].includes(effectiveStatus)
                        ? { label: 'Waiting' }
                        : ['cancelled', 'canceled', 'skipped'].includes(effectiveStatus)
                            ? { label: 'Stopped' }
                            : ['queued', 'running', 'active', 'in_progress', 'awaiting_permission', 'completed', 'failed'].includes(effectiveStatus)
                                ? getJaneBackgroundRunState({ status: effectiveStatus })
                                : { label: 'Status unavailable' };
                    const active = !finishedWork && ['running', 'active', 'in_progress'].includes(effectiveStatus);
                    const waiting = !finishedWork && effectiveStatus === 'waiting';
                    const plan = Array.isArray(entry.run_plan?.steps) && entry.run_plan.steps.length > 0
                        ? getJaneBackgroundRunPlan({ status: effectiveStatus, metadata: { run_plan: entry.run_plan } }) : null;
                    const startedAt = parseJaneTimestampMs(entry.started_at);
                    const finishedAt = parseJaneTimestampMs(entry.finished_at)
                        || (finishedWork && effectiveStatus !== entryStatus ? parseJaneTimestampMs(run?.finished_at || message?.metadata?.responsePlanCompletedAt || run?.updated_at) : null);
                    const elapsedMs = startedAt && (active || waiting ? nowMs : finishedAt)
                        ? Math.max(0, (active || waiting ? nowMs : finishedAt) - startedAt) : null;
                    const statusLabel = !elapsedMs ? state.label
                        : active ? `Running for ${formatJaneElapsedDuration(elapsedMs)}`
                            : waiting ? `Waiting · ${formatJaneElapsedDuration(elapsedMs)} elapsed`
                            : `${state.label} · ${formatJaneElapsedDuration(elapsedMs)}`;
                    const workType = getJaneBackgroundWorkType(entry);
                    const WorkIcon = JANE_BACKGROUND_WORK_ICONS[workType];
                    const updates = (Array.isArray(entry.run_plan?.updates) ? entry.run_plan.updates : [])
                        .filter(update => update && typeof update.id === 'string').slice(-21);
                    const classification = getJaneClassificationProgress(entry.run_plan);
                    const classificationPhase = entry.run_plan?.current_step_id === 'classify-documents';
                    const latestUpdate = updates[updates.length - 1];
                    const baseTitle = String(entry.title || entry.run_plan?.title || 'Task plan');
                    const phaseTitle = classificationPhase
                        ? getJaneClassificationPhaseTitle(
                            entry.run_plan,
                            effectiveStatus,
                            latestUpdate,
                            baseTitle,
                            providerContinuation ? `Continuing document-type review with ${providerContinuation.nextProviderLabel}` : ''
                        ) : baseTitle;
                    return (
                        <details key={entry.id} className={`library-rag-work-planner ${effectiveStatus}`} data-work-entry-id={entry.id}>
                            <summary>
                                <WorkIcon size={14} className="library-rag-work-type-icon" data-work-type={workType} aria-hidden="true" />
                                <span className="library-rag-work-planner-title">{phaseTitle}</span>
                                <span className="library-rag-work-history-state">{active && <Loader2 size={13} className="spinner library-rag-work-summary-spinner" aria-hidden="true" />}{statusLabel}</span>
                                <ChevronDown size={13} className="library-rag-work-chevron" aria-hidden="true" />
                            </summary>
                            {entry.text && <p className="library-rag-work-progress">{String(entry.text)}</p>}
                            {updates.length > 0 && <div className="library-rag-work-phase-updates" role="list" aria-label="Document-type review updates">
                                {updates.map(update => <div key={update.id} role="listitem" className="library-rag-work-batch" data-work-update-id={update.id}>
                                    <JaneWorkStepStatusIcon status="complete" />
                                    <span>{typeof update.title === 'string' && update.title.trim() ? update.title : 'Document-type progress recorded'}</span>
                                </div>)}
                            </div>}
                            <div className={`library-rag-work-phase-steps${classificationPhase ? ' library-rag-work-classification-steps' : ''}`} role="list">
                                {(plan?.steps || []).map((step) => {
                                    // Normalized legacy plans can lose stopped/permission enums.
                                    const rawStep = (Array.isArray(entry.run_plan?.steps)
                                        ? entry.run_plan.steps.find(candidate => candidate && candidate.id === step.id) : null) || step;
                                    const stepStatus = finishedWork && ['running', 'waiting', 'pending'].includes(getJaneWorkStepStatus(rawStep.status))
                                        ? effectiveStatus : rawStep.status;
                                    return <div key={step.id} className={`library-rag-work-phase-step ${getJaneWorkStepStatus(stepStatus)}`} role="listitem">
                                        <JaneWorkStepStatusIcon status={stepStatus} />
                                        <span><strong>{classificationPhase && classification
                                            ? (classification.pending > 0
                                                ? `${classification.pending} documents scheduled for classification`
                                                : (classification.total > classification.processed
                                                    ? `${classification.total - classification.processed} documents lacked classifiable evidence`
                                                    : `${classification.processed} document-type decisions recorded`))
                                            : step.label}</strong>
                                            {!classificationPhase && step.detail && <span className="library-rag-work-step-detail">{step.detail}</span>}
                                            {!classificationPhase && Array.isArray(rawStep.meta) && rawStep.meta.filter(value => typeof value === 'string').map((value, index) => <span key={index} className="library-rag-work-step-detail">{value}</span>)}
                                        </span>
                                        <span className="library-rag-work-history-state">{JANE_WORK_STEP_STATUSES[getJaneWorkStepStatus(stepStatus)].label}</span>
                                    </div>;
                                })}
                            </div>
                        </details>
                    );
                })}
            </div>
            {activityLabel && <div className="library-rag-work-activity" role="status" aria-live="polite" aria-atomic="true">
                <span className="library-rag-work-activity-label">{activityLabel}</span>
            </div>}
        </details>
        {actions}
        </>
    );
};

const JANE_RUN_MODE_OPTIONS = [
    { key: 'fast', label: 'Fast', description: 'Answer quickly with a focused review.' },
    { key: 'default', label: 'Default', description: 'Review the request and show concise progress.' },
    { key: 'long', label: 'Long', description: 'Take more time to check sources and details.' },
];
const RAG_WELCOME_GREETINGS = [
    'Let’s get started.',
    'What are we working on?',
    'Ready when you are.',
    'How can I help?',
    'Where should we begin?',
    'What should we look into?',
    'What can I help with today?',
    'Bring me up to speed.',
    'Tell me what you need.',
    'What’s on your mind?',
    'What would you like to review?',
    'Which case are we looking at?',
    'Send me a case, file, or question.',
    'I’m ready to help.',
    'Let’s find what you need.',
    'What should I summarize?',
    'What should I search for?',
    'Point me at the issue.',
    'What do you want to know?',
    'Let’s work through it.',
    'What are we reviewing?',
    'Ask me anything in CaseDB.',
    'What should I pull up?',
    'Let’s get oriented.',
    'What needs attention?',
    'How should we start?',
    'What’s the task?',
    'Ask me about a case.',
    'What are we trying to answer?'
];

const normalizeJaneElapsedMs = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const formatJaneElapsedDuration = (elapsedMs) => {
    const totalSeconds = Math.max(1, Math.round(Number(elapsedMs || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
};

const parseJaneTimestampMs = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return null;
    // MySQL DATETIME values arrive without a timezone even though the API and
    // worker timestamps are UTC. Parsing them as browser-local time can put a
    // run several hours in the future and leave the UI stuck at "Running".
    const normalizedValue = rawValue.includes('T') ? rawValue : rawValue.replace(' ', 'T');
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
    const parsedValue = Date.parse(hasTimezone ? normalizedValue : `${normalizedValue}Z`);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const buildJaneResponsePlanTimingMetadata = (payload = null, fallbackStartedAtMs = null) => {
    const startedAtMs = normalizeJaneElapsedMs(fallbackStartedAtMs) || null;
    const elapsedMs = normalizeJaneElapsedMs(
        payload?.elapsed_ms
        ?? payload?.elapsedMs
        ?? payload?.intelligence?.response_plan_elapsed_ms
        ?? payload?.intelligence?.responsePlanElapsedMs
        ?? (startedAtMs ? Date.now() - startedAtMs : null)
    );
    const metadata = {};
    if (startedAtMs) metadata.responsePlanStartedAt = startedAtMs;
    if (elapsedMs) metadata.responsePlanElapsedMs = elapsedMs;
    if (elapsedMs || payload?.completed_at || payload?.intelligence?.response_plan_completed_at) {
        metadata.responsePlanCompletedAt = payload?.completed_at || payload?.intelligence?.response_plan_completed_at || new Date().toISOString();
    }
    return metadata;
};

const getJaneResponsePlanElapsedMs = (message = null) => {
    const metadata = message?.metadata || {};
    const intelligence = metadata.intelligence || {};
    const elapsedMs = normalizeJaneElapsedMs(
        metadata.responsePlanElapsedMs
        ?? metadata.response_plan_elapsed_ms
        ?? intelligence.response_plan_elapsed_ms
        ?? intelligence.responsePlanElapsedMs
        ?? message?.elapsed_ms
    );
    if (elapsedMs) return elapsedMs;

    const startedAt = normalizeJaneElapsedMs(metadata.responsePlanStartedAt);
    const completedAt = parseJaneTimestampMs(metadata.responsePlanCompletedAt);
    if (startedAt && Number.isFinite(completedAt) && completedAt > startedAt) {
        return completedAt - startedAt;
    }
    return null;
};

const getJaneResponsePlanStartedAtMs = (message = null) => {
    const metadata = message?.metadata || {};
    const explicitStartedAt = normalizeJaneElapsedMs(metadata.responsePlanStartedAt);
    if (explicitStartedAt) return explicitStartedAt;
    const rawStartedAt = metadata.response_plan_started_at
        || metadata.intelligence?.response_plan_started_at
        || message?.created_at
        || '';
    return parseJaneTimestampMs(rawStartedAt);
};

const getJaneResponsePlanRunningSummary = (message = null, nowMs = Date.now()) => {
    const startedAtMs = getJaneResponsePlanStartedAtMs(message);
    return startedAtMs && nowMs > startedAtMs
        ? `Running for ${formatJaneElapsedDuration(nowMs - startedAtMs)}`
        : 'Running';
};

const getJaneResponsePlanCompleteSummary = (message = null) => {
    const elapsedMs = getJaneResponsePlanElapsedMs(message);
    return elapsedMs
        ? `Response plan completed in ${formatJaneElapsedDuration(elapsedMs)}`
        : 'Response plan complete';
};

export const projectJaneBackgroundRunsIntoMessages = (messages = [], runs = [], nowMs = Date.now()) => {
    const runsById = new Map(dedupeJaneBackgroundRuns(runs)
        .filter(run => Number(run?.id || 0) > 0)
        .map(run => [Number(run.id), run]));
    if (runsById.size === 0) return messages;
    let changed = false;
    const nextMessages = messages.map((message) => {
        if (message?.role !== 'assistant') return message;
        const runId = getBackgroundDraftRunIdForMessage(message);
        const run = runsById.get(runId);
        if (!run) return message;
        const metadata = message.metadata || {};
        const intelligence = metadata.intelligence || {};
        const runStatus = getJaneBackgroundRunStatus(run);
        const isTerminal = JANE_BACKGROUND_TERMINAL_STATUSES.has(runStatus);
        const existingPlan = metadata.runPlan || intelligence.run_plan || null;
        const taskRoute = String(run?.task?.metadata?.route || run?.task?.scope?.route || '').trim();
        const fallbackDiscoveryPlan = taskRoute === 'discovery_objection_review' ? {
            id: `discovery-objection-review-${runId}`,
            title: 'Discovery objection review',
            summary: runStatus === 'failed'
                ? 'The background review stopped before an attorney-review draft could be posted.'
                : 'Reviewing the selected written discovery request by request.',
            mode: 'discovery_objection_review',
            status: runStatus === 'completed' ? 'complete' : runStatus,
            source_count: Array.isArray(run?.task?.metadata?.reference_file_ids)
                ? run.task.metadata.reference_file_ids.length
                : 0,
            steps: [
                { id: 'load-sources', label: 'Load selected discovery files', detail: 'Use only the linked case and selected files.', status: runStatus === 'queued' ? 'running' : 'complete' },
                { id: 'extract-requests', label: 'Extract and preserve every request', detail: 'Keep source names, numbering, and request text intact.', status: runStatus === 'running' ? 'running' : (runStatus === 'completed' ? 'complete' : 'pending') },
                { id: 'draft-objections', label: 'Draft request-specific objections', detail: 'Leave substantive responses for attorney completion.', status: runStatus === 'completed' ? 'complete' : 'pending' },
                { id: 'verify-authorities', label: 'Verify authority support and guardrails', detail: 'Separate source support from citator-currentness review.', status: runStatus === 'completed' ? 'complete' : 'pending' },
                { id: 'attorney-output', label: 'Prepare the attorney-review draft', detail: 'Post the completed draft back into this chat.', status: runStatus === 'completed' ? 'complete' : 'pending' },
            ],
        } : null;
        // A supplied plan owns its narrative, step order, and identities. Receipt
        // steps may only fill in when the worker has not supplied a plan yet.
        const runPlan = run?.metadata?.run_plan || existingPlan || fallbackDiscoveryPlan;
        const nextPlan = runPlan && typeof runPlan === 'object' ? {
            ...runPlan,
            ...(isTerminal ? {
                status: runStatus === 'completed' ? 'complete' : runStatus,
                steps: (Array.isArray(runPlan.steps) ? runPlan.steps : []).map((step) => {
                    if (!step || typeof step !== 'object') return step;
                    if (runStatus === 'completed') return { ...step, status: 'complete' };
                    const stepStatus = String(step.status || '').trim().toLowerCase();
                    return ['running', 'active', 'in_progress'].includes(stepStatus)
                        ? { ...step, status: runStatus }
                        : step;
                }),
            } : {}),
        } : null;
        const completionAnswer = run?.metadata?.completion_answer;
        const publicOpening = getJanePublicOpening(run) || getJanePublicOpening({ answer: completionAnswer });
        const existingActionCandidates = [
            message?.answer?.confirmable_action,
            metadata.confirmable_action,
            intelligence.answer?.confirmable_action,
        ].filter(candidate => candidate && typeof candidate === 'object');
        const hasResolvedAction = existingActionCandidates.some(candidate => ['confirmed', 'declined'].includes(String(candidate.status || '').trim()))
            || Boolean(message?.answer?.generated_docx || message?.answer?.generated_file)
            || (Array.isArray(message?.files) && message.files.some(file => isRagGeneratedOutputSource(file)));
        const hasCompletionAnswer = !hasResolvedAction
            && completionAnswer && typeof completionAnswer === 'object'
            && String(completionAnswer.answer_text || '').trim() !== '';
        const startedAt = parseJaneTimestampMs(run.started_at || run.queued_at || run.created_at)
            || normalizeJaneElapsedMs(metadata.responsePlanStartedAt);
        const previouslyTerminal = ['complete', ...JANE_BACKGROUND_TERMINAL_STATUSES]
            .includes(String(metadata.backgroundRunStatus || existingPlan?.status || '').trim().toLowerCase());
        const finishedAt = isTerminal
            ? (parseJaneTimestampMs(run.finished_at)
                || (previouslyTerminal ? parseJaneTimestampMs(metadata.responsePlanCompletedAt) : null)
                || parseJaneTimestampMs(run.updated_at)
                || nowMs)
            : null;
        const nextMessage = {
            ...message,
            ...(hasCompletionAnswer ? {
                content: completionAnswer.answer_text,
                answer: { ...(message.answer || {}), ...completionAnswer },
            } : {}),
            ...((hasCompletionAnswer || isTerminal) ? { pending: false } : {}),
            metadata: {
                ...metadata,
                backgroundRunStatus: runStatus,
                ...(publicOpening ? { public_opening: publicOpening, janeOpeningText: publicOpening.text } : {}),
                ...(Array.isArray(run?.metadata?.work_history) ? { work_history: run.metadata.work_history } : {}),
                ...(nextPlan ? { runPlan: nextPlan } : {}),
                ...(startedAt ? { responsePlanStartedAt: startedAt } : {}),
                ...(finishedAt ? {
                    responsePlanCompletedAt: new Date(finishedAt).toISOString(),
                    ...(startedAt ? { responsePlanElapsedMs: Math.max(0, finishedAt - startedAt) } : {}),
                } : {}),
                ...((hasCompletionAnswer || isTerminal) ? { streaming: false, pending_request: false } : {}),
                intelligence: {
                    ...intelligence,
                    ...(nextPlan ? { run_plan: nextPlan } : {}),
                    ...(hasCompletionAnswer ? {
                        answer: completionAnswer,
                        retrieval_mode: completionAnswer.mode || intelligence.retrieval_mode,
                    } : {}),
                },
            },
        };
        // Include timing and structured completion data in equality: a finished
        // run can update the same message ID, text, and plan on a later poll.
        if (JSON.stringify(message) === JSON.stringify(nextMessage)) return message;
        changed = true;
        return nextMessage;
    });
    return changed ? nextMessages : messages;
};

export const shouldReloadJaneBackgroundOutput = (runs = [], messages = [], activeChatId = 0, latestMessageId = 0) => (
    Number(activeChatId) > 0 && runs.some((run) => {
        if (Number(run?.task?.chat_id || run?.chat_id || 0) !== Number(activeChatId)
            || !['completed', 'failed'].includes(getJaneBackgroundRunStatus(run))
            || !['case_timeline_build', 'jane_background_run', 'case_trial_analysis'].includes(String(run?.task?.task_type || run?.event_type || '').trim())) return false;
        const outputMessageId = Number(run?.output_chat_message_id || run?.metadata?.output_chat_message_id || 0);
        if (!(outputMessageId > 0)) return false;
        if (outputMessageId > Number(latestMessageId || 0)) return true;
        const outputMessage = messages.find(message => Number(message?.id || 0) === outputMessageId);
        // Workers can replace the receipt in place. A stable message ID is not
        // proof that this client has loaded its completed answer yet.
        const outputMode = String(outputMessage?.answer?.mode || outputMessage?.metadata?.intelligence?.retrieval_mode || '').trim().toLowerCase();
        return Boolean(outputMessage) && (Boolean(outputMessage.pending)
            || Boolean(outputMessage?.metadata?.streaming)
            || JANE_BACKGROUND_HANDOFF_MODES.has(outputMode));
    })
);

const getThemeSidebarLogo = () => getCookie('theme') === 'dark' ? SidebarDarkLogo : SidebarLightLogo;

const getRagReferenceChipLabelParts = (reference = {}) => {
    const label = getRagReferenceDisplayLabel(reference);
    const explicitExtension = String(reference?.extension || '').trim().replace(/^\./, '').toLowerCase();
    const extension = explicitExtension || getFileExtension(reference);
    if (!extension) return { label, stem: label, extension: '' };

    const dottedExtension = `.${extension}`;
    const normalizedLabel = String(label || '').trim();
    const lowerLabel = normalizedLabel.toLowerCase();
    const stem = lowerLabel.endsWith(dottedExtension)
        ? normalizedLabel.slice(0, -dottedExtension.length).trimEnd()
        : normalizedLabel;
    const displayLabel = lowerLabel.endsWith(dottedExtension) ? normalizedLabel : `${normalizedLabel} ${dottedExtension}`;

    return { label: displayLabel, stem: stem || normalizedLabel, extension: dottedExtension };
};

const renderRagReferenceChipLabel = (reference = {}) => {
    const { label, stem, extension } = getRagReferenceChipLabelParts(reference);
    return (
        <span className="library-rag-reference-chip-label" title={label}>
            <span className="library-rag-reference-chip-label-stem">{stem}</span>
            {extension && <span className="library-rag-reference-chip-label-extension">{extension}</span>}
        </span>
    );
};

const isRagGeneratedOutputSource = (source = {}) => {
    const marker = [
        source.source_type,
        source.sourceType,
        source.chunk_source,
        source.chunkSource,
        source.type,
        source.artifact_kind,
        source.generated_by,
        source.retrieval_mode,
    ].map(value => String(value || '').toLowerCase()).join(' ');

    return /\bgenerated_(?:draft|report|file|artifact|transcript)/.test(marker)
        || marker.includes('generated_draft_docx')
        || marker.includes('generated_transcript_file')
        || marker.includes('generated_report')
        || marker.includes('legal_document_draft');
};

const getRagSourceFileIdentity = (source = {}) => {
    const fileId = Number(source?.file_id || source?.id || source?.fileId || source?.document_id || 0);
    return Number.isFinite(fileId) && fileId > 0 ? fileId : null;
};

const hasRagSourceChunkTarget = (source = {}) => {
    const chunkIndex = Number(source?.chunk_index ?? source?.chunkIndex ?? source?.matchedChunk?.chunk_index);
    const registryId = Number(source?.registry_id ?? source?.registryId ?? source?.matchedChunk?.registry_id);
    return Number.isFinite(chunkIndex) && chunkIndex >= 0 && Number.isFinite(registryId) && registryId > 0;
};

const getRagSourcePhysicalPageTarget = (source = {}) => {
    if (source?.source_type !== 'document_text' || isRagGeneratedOutputSource(source)) return null;
    const positiveInteger = (value) => {
        if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
        const integer = Number(value);
        return Number.isSafeInteger(integer) && integer > 0 ? integer : null;
    };
    if (positiveInteger(source.file_id) === null) return null;
    for (const key of ['page', 'page_start', 'page_end']) {
        if (source[key] != null && positiveInteger(source[key]) === null) return null;
    }
    const start = positiveInteger(source.page_start ?? source.page);
    const end = positiveInteger(source.page_end ?? source.page ?? start);
    if (start === null || end === null || end < start) return null;
    const explicitPage = positiveInteger(source.page);
    if (explicitPage !== null && (explicitPage < start || explicitPage > end)) return null;
    // Physical pages do not establish a chunk identity or coordinate evidence.
    return { page_start: start, page_end: end };
};

const uniqueRagGeneratedOutputSources = (preferredSources = [], fallbackSources = []) => {
    const seenFileIds = new Set();
    const seenKeys = new Set();
    const outputSources = [];
    const addSource = (source) => {
        if (!source || !isRagGeneratedOutputSource(source)) return;
        const fileId = getRagSourceFileIdentity(source);
        if (fileId && seenFileIds.has(fileId)) return;
        const fallbackKey = String(source.source_id || source.url || source.title || source.file_name || source.label || '').trim().toLowerCase();
        if (!fileId && fallbackKey && seenKeys.has(fallbackKey)) return;
        if (fileId) seenFileIds.add(fileId);
        if (fallbackKey) seenKeys.add(fallbackKey);
        outputSources.push(source);
    };
    preferredSources.forEach(addSource);
    fallbackSources.forEach(addSource);
    return outputSources;
};

const normalizeJaneRunMode = (value) => {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
    if (['quick', 'fast'].includes(normalized)) return 'fast';
    if (['deep', 'full', 'thorough', 'background', 'long'].includes(normalized)) return 'long';
    return 'default';
};

const RAG_VISIBLE_PROVIDER_LABEL_PATTERN = '(?:local\\s+Ollama|Ollama|OpenAI|Claude|Anthropic|Gemini(?:\\s*[\\w.-]+)?|Gemma(?:\\s*\\d[\\w.-]*)?|Google\\s+AI|GPT-?\\s*\\d[\\w.-]*|GPT-[\\w.-]+|o\\d(?:-[\\w.-]+)?|Llama\\s*\\d?[\\w.-]*)';
const RAG_STATUS_ACTOR_PATTERN = `(?:Jane|${RAG_VISIBLE_PROVIDER_LABEL_PATTERN})`;
const RAG_VISIBLE_PROVIDER_LABEL_RE = new RegExp(`\\b${RAG_VISIBLE_PROVIDER_LABEL_PATTERN}\\b`, 'i');

const cleanRagStatusTarget = (value, fallback = 'the answer') => {
    const cleaned = String(value || '')
        .replace(/^[\s:–-]+/, '')
        .replace(/(?:\.{3}|…|\.)+$/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned || /^(?:now|response|answer|the response|the answer)$/i.test(cleaned)) return fallback;
    if (/^(?:the|a|an|your)\s+/i.test(cleaned)) return cleaned;
    return `the ${cleaned}`;
};

const sanitizeJaneStatusMessage = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const compact = raw.replace(/\s+/g, ' ');
    const providerActionMatch = compact.match(new RegExp(`^${RAG_STATUS_ACTOR_PATTERN}\\s+(?:is\\s+)?(drafting|generating|writing|preparing|building|searching|reading|reviewing|thinking|reasoning|answering)\\b(?:\\s+(.*))?$`, 'i'));
    if (providerActionMatch) {
        const action = String(providerActionMatch[1] || '').toLowerCase();
        const target = cleanRagStatusTarget(providerActionMatch[2] || '', action === 'searching' ? 'the case materials' : 'the answer');
        if (/matched sources|source documents|retrieval/i.test(target)) {
            return 'I’m organizing the matching documents now…';
        }
        if (/grounded answer|case materials/i.test(target)) {
            if (['drafting', 'writing', 'generating', 'preparing', 'building'].includes(action)) {
                return `I’m building ${target} now…`;
            }
        }
        if (['drafting', 'writing'].includes(action)) return `I’m drafting ${target} now…`;
        if (['generating', 'preparing', 'building'].includes(action)) return `I’m generating ${target} now…`;
        if (action === 'searching') return `I’m searching ${target} now…`;
        if (['reading', 'reviewing'].includes(action)) return `I’m reviewing ${target} now…`;
        return 'I’m thinking through the answer now…';
    }
    const withoutProviderSuffix = compact.replace(new RegExp(`\\s+(?:with|using|via)\\s+${RAG_VISIBLE_PROVIDER_LABEL_PATTERN}(?:\\s*\\([^)]*\\))?`, 'ig'), '').trim();
    if (withoutProviderSuffix && withoutProviderSuffix !== compact && !RAG_VISIBLE_PROVIDER_LABEL_RE.test(withoutProviderSuffix)) return withoutProviderSuffix;
    if (RAG_VISIBLE_PROVIDER_LABEL_RE.test(compact)) return 'I’m working through the answer now…';
    return raw;
};

const getJanePromptUnavailableAnswer = (payload = null) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const candidates = [payload.answer, payload.intelligence?.answer, payload];
    return candidates.find((answer) => {
        if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return false;
        const failureKind = String(answer.failure_kind || '').trim().toLowerCase();
        const errorCode = String(answer.error_code || '').trim().toLowerCase();
        const doneReason = String(answer.done_reason || '').trim().toLowerCase();
        const status = String(answer.status || '').trim().toLowerCase();
        const generatedBy = String(answer.generated_by || '').trim().toLowerCase();
        return failureKind === 'prompt_unavailable'
            || errorCode === 'prompt_unavailable'
            || doneReason === 'prompt_unavailable'
            || (status === 'failed' && generatedBy === 'casedb_prompt_unavailable');
    }) || null;
};

export const hasJanePromptUnavailableFailure = (payload = null) => Boolean(getJanePromptUnavailableAnswer(payload));

export const getJaneTerminalResponseDisposition = (payload = null) => {
    const failed = Boolean(payload?.success === false || hasJanePromptUnavailableFailure(payload));
    return {
        failed,
        runPlanStatus: failed ? 'failed' : 'complete',
        completionMessage: failed ? '' : 'I finished the response and I’m saving it to the chat.',
    };
};

const getJaneRequestFailureMessage = (failure = {}) => {
    const promptUnavailableAnswer = getJanePromptUnavailableAnswer(failure);
    if (promptUnavailableAnswer) {
        return String(promptUnavailableAnswer.answer_text || '').trim()
            || 'Jane could not start the model-backed part of this response because its reviewed instructions are temporarily unavailable. Please try again.';
    }
    const status = Number(failure?.http_status || failure?.status || 0);
    if (status === 401) return 'Your CaseDB session expired before Jane could finish. Refresh the page and try again.';
    if (status === 403) return 'Jane could not use that case, chat, or file because your account does not have access.';
    if (status === 429) return 'Jane is handling too many requests right now. Please wait a moment and try again.';
    if (failure?.retryable || status >= 500 || status === 0) {
        return 'Jane could not finish the response because the connection was interrupted. Your question was not saved as a completed answer. Please try again.';
    }
    const serverMessage = sanitizeJaneStatusMessage(failure?.message);
    return serverMessage || 'Jane could not complete that request. Please review the request and try again.';
};

const isJaneAgentV2Enabled = () => {
    const envValue = String(process.env.REACT_APP_JANE_AGENT_V2_ENABLED || '').trim().toLowerCase();
    if (['0', 'false', 'off', 'disabled'].includes(envValue)) return false;
    if (['1', 'true', 'on', 'enabled'].includes(envValue)) return true;
    if (typeof window === 'undefined') return false;
    const localValue = String(window.localStorage?.getItem('jane_agent_v2_enabled') || '').trim().toLowerCase();
    if (['0', 'false', 'off', 'disabled'].includes(localValue)) return false;
    if (window.CASEDB_JANE_AGENT_V2_ENABLED === false) return false;
    if (window.CASEDB_JANE_AGENT_V2_ENABLED === true) return true;
    const experimentalLocalValue = String(window.localStorage?.getItem('jane_agent_v2_experimental_enabled') || '').trim().toLowerCase();
    if (['1', 'true', 'on', 'enabled'].includes(experimentalLocalValue)) return true;
    return false;
};

const getJaneRunModeLabel = (mode) => JANE_RUN_MODE_OPTIONS.find(option => option.key === normalizeJaneRunMode(mode))?.label || 'Default';

const buildImmediateJanePlannerSteps = (mode, query, references = [], selectedSourcesOnly = false) => {
    const normalizedMode = normalizeJaneRunMode(mode);
    if (normalizedMode === 'fast') return [];
    const contextSummary = buildJanePromptContextSummary(references, selectedSourcesOnly);
    const steps = [
        {
            id: `local-start-${Date.now()}`,
            type: 'status',
            mode: 'local',
            message: normalizedMode === 'long'
                ? 'I’m reviewing your request and deciding what to check first.'
                : 'I’m reviewing your request and choosing what to check.',
            steps: [
                {
                    label: normalizedMode === 'long' ? 'Closer review' : 'Review',
                    values: [
                        normalizedMode === 'long'
                            ? 'I’ll take extra time to review the sources and verify important details.'
                            : 'I’ll check the relevant information before answering.',
                    ],
                },
            ],
        },
    ];
    if (contextSummary.labels.length > 0 || selectedSourcesOnly) {
        steps.push({
            id: `local-context-${Date.now()}`,
            type: 'status',
            mode: 'local',
            message: 'I’m reviewing the request context.',
            steps: [
                {
                    label: 'Context',
                    values: [
                        ...(contextSummary.labels.length > 0 ? contextSummary.labels : ['No selected files or case context']),
                        selectedSourcesOnly ? 'Use only this context' : 'Relevant CaseDB information is allowed',
                    ],
                },
            ],
        });
    }
    if (normalizedMode === 'long') {
        steps.push({
            id: `local-long-${Date.now()}`,
            type: 'status',
            mode: 'local',
            message: 'I’m taking a closer look before drafting the answer.',
            steps: [
                {
                    label: 'Review',
                    values: ['I’ll check the sources and important details before I answer.'],
                },
            ],
        });
    }
    return steps;
};

const buildImmediateJaneRunPlan = (mode, query, references = []) => {
    if (normalizeJaneRunMode(mode) === 'fast') return null;
    const prompt = String(query || '').toLowerCase();
    const isDiscoveryObjectionReview = /\b(?:written discovery|interrogator(?:y|ies)|rfps?|requests? for production|rfas?|requests? for admission)\b/.test(prompt)
        && /\bobjections?\b/.test(prompt);
    if (!isDiscoveryObjectionReview) return null;
    const sourceCount = buildJanePromptContextSummary(references).fileCount;
    return {
        id: 'discovery-objection-review-pending',
        title: 'Discovery objection review',
        summary: 'Reviewing the selected written discovery request by request.',
        mode: 'discovery_objection_review',
        status: 'running',
        source_count: sourceCount,
        steps: [
            { id: 'load-sources', label: 'Load selected discovery files', detail: 'Use only the linked case and selected files.', status: 'running' },
            { id: 'extract-requests', label: 'Extract and preserve every request', detail: 'Keep source names, numbering, and request text intact.', status: 'pending' },
            { id: 'draft-objections', label: 'Draft request-specific objections', detail: 'Leave substantive responses for attorney completion.', status: 'pending' },
            { id: 'verify-authorities', label: 'Verify authority support and guardrails', detail: 'Separate source support from citator-currentness review.', status: 'pending' },
            { id: 'attorney-output', label: 'Prepare the attorney-review draft', detail: 'Post the completed draft back into this chat.', status: 'pending' },
        ],
    };
};

const cleanRagChatTitle = (value) => String(value || '')
    .replace(/(?:\.\.\.|…)$/u, '')
    .replace(/[\s"'`“”‘’.:;!?…-]+$/u, '')
    .trim();

const getRagChatUpdateTime = (chat = {}) => {
    const rawValue = chat.last_message_at || chat.updated_at || chat.created_at || '';
    if (!rawValue) return 0;
    const timestamp = Date.parse(String(rawValue).replace(' ', 'T'));
    return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortRagChatsByUpdateTime = (chats = []) => [...chats].sort((left, right) => {
    const timeDifference = getRagChatUpdateTime(right) - getRagChatUpdateTime(left);
    if (timeDifference !== 0) return timeDifference;
    return Number(right?.id || 0) - Number(left?.id || 0);
});

const JANE_WORKSPACE_HANDOFF_STORAGE_KEY = 'jane-workspace-handoff';

const storeJaneWorkspaceHandoff = (caseId, prompt, actions = [], chatId = null) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(JANE_WORKSPACE_HANDOFF_STORAGE_KEY, JSON.stringify({
        caseId: Number(caseId),
        prompt,
        actions: Array.isArray(actions) ? actions : [],
        chatId: Number(chatId || 0) || null,
        createdAt: Date.now(),
        source: 'ai_route',
    }));
};

export const ChunkContextText = ({ contextChunk }) => {
    const text = stripHtml(contextChunk?.chunk_text);
    return text ? <div className="library-chunk-section-text">{text}</div> : null;
};

const getInlineCitationNumbers = (text = '') => {
    const numbers = new Set();
    const pattern = /\[([\d,\s]+)\]/g;
    let match;
    while ((match = pattern.exec(String(text || ''))) !== null) {
        String(match[1] || '').split(',').forEach((part) => {
            const value = part.trim();
            if (/^\d+$/.test(value)) numbers.add(value);
        });
    }
    return numbers;
};

const getOrderedInlineCitationNumbers = (text = '') => {
    const orderedNumbers = [];
    const seenNumbers = new Set();
    const pattern = /\[([\d,\s]+)\]/g;
    let match;
    while ((match = pattern.exec(String(text || ''))) !== null) {
        String(match[1] || '').split(',').forEach((part) => {
            const value = part.trim();
            if (/^\d+$/.test(value) && !seenNumbers.has(value)) {
                seenNumbers.add(value);
                orderedNumbers.push(value);
            }
        });
    }
    return orderedNumbers;
};

const normalizeRagAnswerText = (value = '') => String(value || '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');

const openRagEmailDraftWindow = (subject = '', body = '') => {
    if (typeof window === 'undefined') return;

    const cleanSubject = String(subject || '').trim();
    const cleanBody = normalizeRagAnswerText(body).trim();
    let mailto = `mailto:?subject=${encodeURIComponent(cleanSubject)}`;
    if (cleanBody) {
        mailto += `&body=${encodeURIComponent(cleanBody)}`;
    }
    window.open(mailto, '_blank');
};

const openSafeRagWindow = (url) => {
    const safeUrl = getSafeRagNavigationUrl(url);
    if (!safeUrl || typeof window === 'undefined') return false;
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
    return true;
};

const getDisplayedCitationsForAnswer = (citations = [], answerText = '') => {
    const citationList = Array.isArray(citations) ? citations : [];
    const orderedCitedNumbers = getOrderedInlineCitationNumbers(normalizeRagAnswerText(answerText));

    const citationsByOriginalNumber = new Map();
    citationList.forEach((citation, index) => {
        const fallbackNumber = String(index + 1);
        const citationNumber = String(citation?.original_number || citation?.number || fallbackNumber);
        citationsByOriginalNumber.set(citationNumber, { citation, originalNumber: citationNumber });
    });

    const displayedCitations = [];
    const displayedFileKeys = new Set();
    const addCitation = (number) => {
        const match = citationsByOriginalNumber.get(number);
        if (!match?.citation) return;
        const fileKey = String(match.citation.source_id || match.citation.file_id || match.citation.url || match.citation.file_name || match.originalNumber);
        if (displayedFileKeys.has(fileKey)) return;
        displayedFileKeys.add(fileKey);
        displayedCitations.push({
            ...match.citation,
            number: match.originalNumber,
            original_number: match.originalNumber,
        });
    };

    orderedCitedNumbers.forEach(addCitation);
    citationList.forEach((citation, index) => {
        addCitation(String(citation?.number || index + 1));
    });

    return displayedCitations.length > 0 ? displayedCitations : citationList;
};

const resolveInlineCitation = (number, citationMap, citations = [], groupSize = 1) => {
    const key = String(number || '').trim();
    if (citationMap.has(key)) return citationMap.get(key);
    if (groupSize === 1 && citations.length === 1) return citations[0];
    return null;
};

const renderInlineCitationLink = (number, citation, keyPrefix, matchIndex, onCitationOpen = null) => {
    const href = getRagCitationHref(citation);
    const displayNumber = citation?.number || number;
    const outlookReviewTarget = getOutlookCitationReviewTarget(citation);
    if (outlookReviewTarget) {
        return (
            <OutlookCitationReviewTrigger
                key={`${keyPrefix}-citation-${matchIndex}-${number}`}
                citation={citation}
                className="library-rag-inline-citation"
                title={citation?.title || citation?.file_name || `Outlook source ${number}`}
                aria-label={`Review cited Outlook email source ${displayNumber}`}
                onActivate={() => onCitationOpen?.(citation, { outlookView: 'pdf' })}
            >
                {displayNumber}
            </OutlookCitationReviewTrigger>
        );
    }
    if (!href) return number;
    return (
        <a
            key={`${keyPrefix}-citation-${matchIndex}-${number}`}
            className="library-rag-inline-citation"
            href={href}
            target="_blank"
            rel="noreferrer"
            title={citation?.file_name || `Source ${number}`}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onCitationOpen?.(citation);
            }}
        >
            {displayNumber}
        </a>
    );
};

export const renderInlineRagText = (text, citationMap, keyPrefix, onCitationOpen = null, citations = []) => {
    const value = String(text || '');
    const parts = [];
    const pattern = /\[([^\][\r\n]+)\]\((\/(?:case|contact)\/[1-9]\d*)\)|\*\*(.+?)\*\*|`([^`\n]+?)`|\[([\d,\s]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(value)) !== null) {
        if (match.index > lastIndex) {
            parts.push(decodeRagTerminalText(value.slice(lastIndex, match.index)));
        }

        if (match[1] !== undefined) {
            const href = getSafeRagInlineRecordUrl(match[2]);
            parts.push(
                <a
                    key={`${keyPrefix}-record-link-${match.index}`}
                    className="library-rag-inline-citation"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {decodeRagTerminalText(match[1])}
                </a>
            );
        } else if (match[3] !== undefined) {
            parts.push(
                <strong key={`${keyPrefix}-bold-${match.index}`}>
                    {renderInlineRagText(match[3], citationMap, `${keyPrefix}-bold-${match.index}`, onCitationOpen, citations)}
                </strong>
            );
        } else if (match[4] !== undefined) {
            parts.push(
                <span key={`${keyPrefix}-inline-label-${match.index}`} className="library-rag-inline-label">
                    {decodeRagTerminalText(match[4])}
                </span>
            );
        } else {
            const citationNumbers = String(match[5] || '')
                .split(',')
                .map(number => number.trim())
                .filter(number => /^\d+$/.test(number));
            if (citationNumbers.length > 0) {
                parts.push(
                    <span key={`${keyPrefix}-citation-group-${match.index}`} className="library-rag-inline-citation-group">
                        [
                        {citationNumbers.map((number, citationIndex) => {
                            const citation = resolveInlineCitation(number, citationMap, citations, citationNumbers.length);
                            return (
                                <React.Fragment key={`${keyPrefix}-citation-fragment-${match.index}-${number}-${citationIndex}`}>
                                    {citationIndex > 0 ? ', ' : ''}
                                    {renderInlineCitationLink(number, citation, keyPrefix, `${match.index}-${citationIndex}`, onCitationOpen)}
                                </React.Fragment>
                            );
                        })}
                        ]
                    </span>
                );
            } else {
                parts.push(match[0]);
            }
        }

        lastIndex = pattern.lastIndex;
    }

    if (lastIndex < value.length) {
        parts.push(decodeRagTerminalText(value.slice(lastIndex)));
    }

    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
};

const renderCitationLinkedText = (text, citations = [], keyPrefix = 'citation-text', onCitationOpen = null) => {
    const citationList = Array.isArray(citations) ? citations : [];
    const citationMap = new Map();
    citationList.forEach((citation) => {
        if (citation?.number) citationMap.set(String(citation.number), citation);
        if (citation?.original_number) citationMap.set(String(citation.original_number), citation);
    });

    return renderInlineRagText(text, citationMap, keyPrefix, onCitationOpen, citationList);
};

const RagAnswerText = ({ text, citations, keyPrefix, onCitationOpen }) => (
    renderCitationLinkedText(text, citations, keyPrefix, onCitationOpen)
);

const RagAnswerListItem = ({ text, citations, keyPrefix, onCitationOpen }) => (
    <li className="library-rag-answer-list-item">
        <RagAnswerText text={text} citations={citations} keyPrefix={keyPrefix} onCitationOpen={onCitationOpen} />
    </li>
);

const isChronologyTimelineLabel = (line = '') => cleanMalformedBoldMarkers(line).toLowerCase() === 'chronological timeline:';

const isChronologyTimelineItems = (items = []) => items.some(item => /^[A-Z][a-z]{2}\s+\d{4}\s+(?:—|--)/.test(String(item || '').trim()));

const normalizeMedicalChronologyEvents = (events = []) => {
    if (!Array.isArray(events)) return [];
    return events.map((event, index) => {
        const sourceKind = String(event?.source_kind || event?.sourceKind || '').trim().toLowerCase();
        const dateMissing = Boolean(event?.date_missing) || !event?.event_date;
        const dateLabel = String(event?.date_label || (dateMissing ? 'Date missing' : event?.event_date || 'Date missing')).trim();
        const title = String(event?.title || event?.provider_name || event?.event_type || `Medical event ${index + 1}`).trim();
        const summary = String(event?.summary || event?.plain_text || event?.source_excerpt || '').trim();
        const sourcePageStart = Number(event?.source_page_start || event?.page_start || 0);
        const sourcePageEnd = Number(event?.source_page_end || event?.page_end || sourcePageStart || 0);
        const safeSourcePageStart = Number.isFinite(sourcePageStart) && sourcePageStart > 0 ? sourcePageStart : null;
        const safeSourcePageEnd = Number.isFinite(sourcePageEnd) && sourcePageEnd > 0 ? sourcePageEnd : safeSourcePageStart;
        const sourcePageLabel = String(event?.source_page_label || (safeSourcePageStart ? (safeSourcePageEnd && safeSourcePageEnd !== safeSourcePageStart ? `Pages ${safeSourcePageStart}-${safeSourcePageEnd}` : `Page ${safeSourcePageStart}`) : '')).trim();
        return {
            ...event,
            id: String(event?.id || `medchron-event-${index}`),
            dateLabel,
            dateMissing,
            title,
            summary,
            sourceKind,
            eventType: String(event?.event_type || '').trim(),
            providerName: String(event?.provider_name || '').trim(),
            sourceName: String(event?.source_name || '').trim(),
            sourceFileId: Number(event?.source_file_id || event?.file_id || 0) || null,
            sourceRegistryId: Number(event?.source_registry_id || event?.registry_id || 0) || null,
            sourcePageStart: safeSourcePageStart,
            sourcePageEnd: safeSourcePageEnd,
            sourcePageLabel,
            sourceChunkIndex: Number.isFinite(Number(event?.source_chunk_index || event?.chunk_index))
                ? Number(event?.source_chunk_index || event?.chunk_index)
                : null,
            sourceExcerpt: String(event?.source_excerpt || '').trim(),
            reviewFlags: Array.isArray(event?.review_flags) ? event.review_flags.filter(Boolean).map(String) : [],
            activityId: Number(event?.activity_id || 0) || null,
            activityType: sourceKind === 'activity_feed' ? String(event?.activity_type || event?.type || '').trim() : '',
            activitySubject: sourceKind === 'activity_feed' ? String(event?.activity_subject || event?.subject || '').trim() : '',
            activityContent: sourceKind === 'activity_feed' ? String(event?.activity_content || event?.content || '').trim() : '',
            activityAuthorName: sourceKind === 'activity_feed' ? String(event?.activity_author_name || event?.author_name || '').trim() : '',
            activityCreatedAt: sourceKind === 'activity_feed' ? String(event?.activity_created_at || event?.creation_date || '').trim() : '',
            attachments: sourceKind === 'activity_feed' && Array.isArray(event?.attachments) ? event.attachments : [],
            needsReview: Boolean(event?.needs_review) || dateMissing,
        };
    });
};

const ChronologyActivityFeedAttachments = ({ attachments = [], activityType = '' }) => {
    const [hasOverflow, setHasOverflow] = useState(false);
    const navigate = useNavigate();

    if (!Array.isArray(attachments) || attachments.length === 0) {
        return null;
    }

    const visibleAttachments = hasOverflow ? attachments : attachments.slice(0, 5);
    const fallbackFolder = String(activityType || '').toLowerCase() !== 'calls'
        ? String(activityType || '').toLowerCase() === 'emails'
            ? 'Emails'
            : String(activityType || '').toLowerCase() === 'texts'
                ? 'Client Communication'
                : String(activityType || '').toLowerCase() === 'faxes'
                    ? 'Faxes'
                    : 'Notes'
        : 'Call Log';

    return (
        <div className="files">
            {visibleAttachments.map((file, index) => {
                const filePath = file?.file_path || file?.key || file?.storage_key || '';
                const normalizedPath = filePath.replace(/^cases\/\d+\//, '');
                const withoutName = normalizedPath.replace(/^{{Name}}\//, '');
                const folderFromPath = withoutName.includes('/')
                    ? withoutName.split('/').slice(0, -1).join('/')
                    : '';
                const targetFolder = folderFromPath || fallbackFolder;
                const fileId = file?.id || file?.file_id;
                const openFullView = () => {
                    if (!fileId) return;
                    openSafeRagWindow(`/file/${fileId}`);
                };

                return (
                    <File
                        file={file}
                        key={fileId || index}
                        onClick={openFullView}
                        menuOptions={[
                            {
                                label: 'Go to document location',
                                onClick: () => navigate(`${window.location.pathname}?section=3&folder=${targetFolder}`)
                            },
                            {
                                label: 'View full file',
                                onClick: openFullView,
                                disabled: !fileId
                            }
                        ]}
                    />
                );
            })}
            {attachments.length > 5 && (
                <div className="file small-text" onClick={(event) => { setHasOverflow(!hasOverflow); event.stopPropagation(); }}>
                    {hasOverflow ? 'show less' : `+${attachments.length - 5} more`}
                </div>
            )}
        </div>
    );
};

const ChronologyActivityFeedItem = ({ event }) => {
    const subject = String(event?.activitySubject || event?.title || 'Activity feed item').trim();
    const content = String(event?.activityContent || event?.summary || event?.sourceExcerpt || '').trim();
    const attachments = Array.isArray(event?.attachments) ? event.attachments : [];
    const activityType = String(event?.activityType || 'notes').trim();
    const normalizedFeedType = activityType !== '' ? activityType.toLowerCase().replace(/\s+/g, '-') : 'notes';
    const authorName = String(event?.activityAuthorName || 'Activity feed').trim();
    const dateLabel = String(event?.dateLabel || 'Entire case').trim();

    return (
        <div className={`activity-feed-item feed-type-${normalizedFeedType}`} aria-label="Activity feed item">
            <div className="activity-feed-item-header subtext">
                <span className="activity-feed-item-author">{authorName}</span>
                <span className="activity-feed-item-date">{dateLabel}</span>
                {event?.sourceName ? <span className="activity-feed-item-number">{event.sourceName}</span> : null}
            </div>
            <span className="activity-title">{subject}</span>
            {content ? (
                <div className="activity-feed-item-content" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', maxWidth: '100%', overflowWrap: 'break-word', overflowX: 'hidden' }}>
                    <div>{content}</div>
                </div>
            ) : null}
            <div className="activity-feed-item-type subtext">
                <span>{activityType || 'Activity feed'}</span>
            </div>
            <ChronologyActivityFeedAttachments attachments={attachments} activityType={event?.activityType || ''} />
        </div>
    );
};

const buildMedicalChronologySourcePreview = (event) => {
    const fileId = Number(event?.sourceFileId || 0);
    const pageStart = Number(event?.sourcePageStart || 0) || 1;
    if (!Number.isFinite(fileId) || fileId <= 0) {
        return null;
    }

    const pageEnd = Number(event?.sourcePageEnd || pageStart) || pageStart;
    return {
        fileId,
        fileName: event?.sourceName || `File ${fileId}`,
        caseId: null,
        caseName: '',
        storageKey: '',
        pageLabel: event?.sourcePageLabel || (pageEnd !== pageStart ? `Pages ${pageStart}-${pageEnd}` : `Page ${pageStart}`),
        pageNumber: pageStart,
        chunkIndex: Number.isFinite(Number(event?.sourceChunkIndex)) ? Number(event.sourceChunkIndex) : null,
        registryId: Number(event?.sourceRegistryId || 0) || null,
        singlePage: true,
        matchedChunk: {
            registry_id: Number(event?.sourceRegistryId || 0) || null,
            chunk_index: Number.isFinite(Number(event?.sourceChunkIndex)) ? Number(event.sourceChunkIndex) : null,
            page_start: pageStart,
            page_end: pageEnd,
            chunk_source: 'medical_chronology_source_text',
        },
    };
};

const getTimelineEvidenceSourceLabel = (sourceType = '') => {
    const normalized = String(sourceType || '').toLowerCase();
    if (normalized.includes('outlook')) return 'Outlook email';
    if (normalized.includes('feed') || normalized.includes('activity')) return 'Activity feed';
    if (normalized.includes('custom_field') || normalized.includes('field')) return 'Custom field';
    if (normalized.includes('fusion')) return 'Fusion field';
    if (normalized.includes('document') || normalized.includes('file')) return 'Document';
    if (normalized.includes('case')) return 'Case data';
    return 'Timeline source';
};

const buildTimelineEvidencePreview = (citation = {}) => {
    const sourceType = citation?.source_type || citation?.type || citation?.chunk_source || '';
    const title = String(citation?.label || citation?.file_name || citation?.title || getTimelineEvidenceSourceLabel(sourceType)).trim();
    const excerpt = String(citation?.quote || citation?.excerpt || citation?.description || citation?.text || '').trim();
    const meta = [
        citation?.case_name ? `Case: ${citation.case_name}` : '',
        citation?.case_id ? `Case #${citation.case_id}` : '',
        citation?.feed_update_id ? `Feed update #${citation.feed_update_id}` : '',
        citation?.field_id ? `Field #${citation.field_id}` : '',
        citation?.update_id ? `Update #${citation.update_id}` : '',
        citation?.fusion_name ? `Fusion: ${citation.fusion_name}` : '',
    ].filter(Boolean);
    return {
        title: title || 'Timeline source',
        sourceTypeLabel: getTimelineEvidenceSourceLabel(sourceType),
        isOutlookEmail: String(sourceType || '').toLowerCase().includes('outlook'),
        excerpt,
        meta,
        url: getRagCitationHref(citation),
        citationNumber: citation?.number || citation?.citation_number || citation?.original_number || null,
    };
};

const normalizeMedicalChronologySourceTable = (event) => {
    const fieldValues = Array.isArray(event?.fieldValues)
        ? event.fieldValues
        : (Array.isArray(event?.field_values) ? event.field_values : []);
    const normalizedFieldValues = fieldValues
        .map((fieldValue, index) => {
            const label = String(fieldValue?.name || fieldValue?.field_name || fieldValue?.label || '').trim();
            const value = String(fieldValue?.value ?? fieldValue?.field_value ?? '').trim();
            if (!label || !value) return null;
            return {
                key: String(fieldValue?.key || fieldValue?.field_id || normalizeRagPreviewFieldId(label) || `field-${index}`),
                label,
                value,
            };
        })
        .filter(Boolean);
    if (normalizedFieldValues.length > 0) {
        return {
            label: String(event?.sourceName || event?.source_name || 'Case data').trim(),
            columns: normalizedFieldValues.map((fieldValue, index) => ({ key: fieldValue.key || `field-${index}`, label: fieldValue.label })),
            rows: [normalizedFieldValues.map((fieldValue) => fieldValue.value)],
        };
    }

    const table = event?.sourceTable || event?.source_table || null;
    const rawColumns = Array.isArray(table?.columns) ? table.columns : [];
    const rawRows = Array.isArray(table?.rows) ? table.rows : [];
    let columns = rawColumns
        .map((column, index) => ({
            key: String(column?.key || column?.label || `column-${index}`),
            label: String(column?.label || column?.name || column || `Column ${index + 1}`),
        }))
        .filter((column) => column.label.trim() !== '');
    const hasValueKeyRows = rawRows.some((row) => row && typeof row === 'object' && !Array.isArray(row) && row.value !== undefined && row.key !== undefined);
    const normalizedColumnLabels = columns.map((column) => String(column.label || column.key || '').trim().toLowerCase().replace(/[_\s-]+/g, ' '));
    const looksLikeMetadataTable = hasValueKeyRows && normalizedColumnLabels.some(label => label === 'key') && normalizedColumnLabels.some(label => label === 'type');

    if (looksLikeMetadataTable) {
        columns = [
            { key: 'value', label: 'Value' },
            { key: 'key', label: 'Key' },
        ];
    }

    const rows = rawRows
        .map((row) => {
            if (Array.isArray(row)) return row.map((cell) => String(cell ?? '').trim());
            if (row && typeof row === 'object') {
                return columns.map((column) => String(row[column.key] ?? row[column.label] ?? '').trim());
            }
            return [String(row ?? '').trim()];
        })
        .filter((row) => row.some((cell) => cell !== ''));

    if (columns.length > 0 && rows.length > 0) {
        return {
            label: String(table?.label || event?.sourceName || 'Source data').trim(),
            columns,
            rows,
        };
    }

    const sourceKind = String(event?.source_kind || event?.sourceKind || '').toLowerCase();
    const caseDataText = String(event?.sourceExcerpt || event?.source_excerpt || event?.summary || '').trim();
    if (sourceKind === 'case_data' && caseDataText.includes(':')) {
        const parsedFields = caseDataText
            .split(/;\s*/)
            .map((part, index) => {
                const separatorIndex = part.indexOf(':');
                if (separatorIndex <= 0) return null;
                const label = part.slice(0, separatorIndex).trim();
                const value = part.slice(separatorIndex + 1).trim();
                if (!label || !value) return null;
                return { key: `case-data-${index}`, label, value };
            })
            .filter(Boolean);
        if (parsedFields.length > 1) {
            return {
                label: String(event?.sourceName || event?.source_name || 'Case data').trim(),
                columns: parsedFields.map((field) => ({ key: field.key, label: field.label })),
                rows: [parsedFields.map((field) => field.value)],
            };
        }
    }

    const fallbackText = String(event?.summary || event?.sourceExcerpt || event?.sourceName || 'Source data').trim();
    return {
        label: String(table?.label || event?.sourceName || 'Source data').trim(),
        columns: [{ key: 'source', label: 'Source data' }],
        rows: [[fallbackText || 'No structured data was returned.']],
    };
};

const getMedicalChronologyForMessage = (message) => {
    const answer = message?.answer || message?.metadata?.intelligence?.answer || null;
    const chronology = answer?.medical_chronology || message?.metadata?.intelligence?.answer?.medical_chronology || null;
    const events = normalizeMedicalChronologyEvents(chronology?.events || []);
    if (!chronology || events.length === 0) return null;
    return {
        ...chronology,
        events,
        overview: chronology.overview || {},
        overviewText: String(chronology?.overview?.overview_text || answer?.summary || '').trim(),
    };
};

export const MedicalChronologyReview = ({ chronology }) => {
    const chronologyEventsSignature = useMemo(() => JSON.stringify(chronology?.events || []), [chronology?.events]);
    const events = useMemo(() => normalizeMedicalChronologyEvents(chronology?.events || []), [chronologyEventsSignature]);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const selectedEvent = useMemo(() => events.find(event => event.id === selectedEventId) || events[0] || null, [events, selectedEventId]);
    const selectedEventPreview = useMemo(() => (selectedEvent ? buildMedicalChronologySourcePreview(selectedEvent) : null), [selectedEvent]);
    const selectedEventTable = useMemo(() => (selectedEvent ? normalizeMedicalChronologySourceTable(selectedEvent) : null), [selectedEvent]);
    const selectedEventIsActivityFeed = selectedEvent?.sourceKind === 'activity_feed';

    if (events.length === 0) return null;
    const selectedEventTableColumns = Array.isArray(selectedEventTable?.columns) ? selectedEventTable.columns : [];
    const selectedEventTableRows = Array.isArray(selectedEventTable?.rows) ? selectedEventTable.rows : [];
    const selectedEventTablePreview = selectedEventTable ? {
        kind: 'report',
        status: 'ready',
        title: selectedEventTable.label || 'Source data',
        meta: selectedEvent?.sourceName || selectedEvent?.eventType || 'Source data',
        columns: selectedEventTableColumns,
        rows: selectedEventTableRows,
    } : null;
    const overviewText = String(chronology?.overview?.overview_text || chronology?.overviewText || '').trim();
    const missingDateCount = events.filter(event => event.dateMissing).length;
    return (
        <section className="library-rag-medchron-review inline" aria-label="Medical chronology review">
            <div className="library-rag-medchron-calendar" role="list" aria-label="Medical chronology events">
                <div className="library-rag-medchron-calendar-head">
                    <span>{events.length} event{events.length === 1 ? '' : 's'}</span>
                    {missingDateCount > 0 ? <span>{missingDateCount} missing date{missingDateCount === 1 ? '' : 's'}</span> : null}
                </div>
                {events.map((event) => {
                    const active = selectedEvent && event.id === selectedEvent.id;
                    return (
                        <div key={event.id} role="listitem" className="library-rag-medchron-item">
                            <button
                                type="button"
                                className={`library-rag-medchron-row${active ? ' active' : ''}${event.dateMissing ? ' missing-date' : ''}`}
                                onClick={() => setSelectedEventId(event.id)}
                                aria-pressed={active}
                            >
                                <span className="library-rag-medchron-date">{event.dateLabel}</span>
                                <span className="library-rag-medchron-card-title">{event.title}</span>
                            </button>
                        </div>
                    );
                })}
            </div>
            <aside className="library-rag-medchron-detail" aria-label="Medical chronology event detail">
                <div className="library-rag-medchron-detail-eyebrow">{selectedEvent ? 'Selected event' : 'Case overview'}</div>
                {selectedEventIsActivityFeed ? (
                    <ChronologyActivityFeedItem event={selectedEvent} />
                ) : selectedEventPreview ? (
                    <>
                        <h4>{selectedEvent?.title || 'Case overview'}</h4>
                        <div className="library-rag-medchron-detail-meta">
                            <span>{selectedEvent?.dateLabel || 'Entire case'}</span>
                            {selectedEvent?.eventType ? <span>{selectedEvent.eventType}</span> : null}
                            {selectedEvent?.sourceName ? <span>{selectedEvent.sourceName}</span> : null}
                            {selectedEvent?.sourcePageLabel ? <span>{selectedEvent.sourcePageLabel}</span> : null}
                        </div>
                    <div className="library-rag-medchron-source-preview" aria-label="Source page preview">
                        <LibraryRagPdfPreview key={`medchron-preview-${selectedEvent.id}-${selectedEventPreview.pageNumber || 0}`} preview={selectedEventPreview} apiBase={API_BASE} />
                    </div>
                    </>
                ) : (
                    <>
                        <h4>{selectedEvent?.title || 'Case overview'}</h4>
                        <div className="library-rag-medchron-detail-meta">
                            <span>{selectedEvent?.dateLabel || 'Entire case'}</span>
                            {selectedEvent?.eventType ? <span>{selectedEvent.eventType}</span> : null}
                            {selectedEvent?.sourceName ? <span>{selectedEvent.sourceName}</span> : null}
                            {selectedEvent?.sourcePageLabel ? <span>{selectedEvent.sourcePageLabel}</span> : null}
                        </div>
                    <div className="library-rag-medchron-source-preview">
                        <NativeRagTablePreview preview={selectedEventTablePreview} />
                    </div>
                    </>
                )}
                {selectedEvent?.reviewFlags?.length > 0 ? (
                    <div className="library-rag-medchron-flags">
                        {selectedEvent.reviewFlags.map((flag, flagIndex) => (
                            <span key={`medchron-flag-${flagIndex}`}>{flag}</span>
                        ))}
                    </div>
                ) : null}
                {overviewText ? (
                    <section className="library-rag-medchron-overview" aria-label="Entire case overview">
                        <h4>Entire case overview</h4>
                        <p>{overviewText}</p>
                    </section>
                ) : null}
            </aside>
        </section>
    );
};

const parseChronologyTimelineItem = (value = '') => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    const match = text.match(/^(.+?)\s+(?:—|--)\s+(.+?)\.?$/);
    if (!match) return null;

    const dateLabel = match[1].trim();
    const parts = match[2].split(';').map(part => part.trim()).filter(Boolean);
    const parsed = {
        dateLabel,
        updateText: parts[0] || '',
        signals: [],
        summary: '',
        other: [],
        originalText: text,
    };

    parts.slice(1).forEach((part) => {
        const lowerPart = part.toLowerCase();
        if (lowerPart.startsWith('signals:')) {
            parsed.signals = part.slice(part.indexOf(':') + 1).split(',').map(signal => signal.trim()).filter(Boolean);
            return;
        }
        if (lowerPart.startsWith('summary:') || lowerPart.startsWith('examples:')) {
            parsed.summary = part.slice(part.indexOf(':') + 1).trim();
            return;
        }
        parsed.other.push(part);
    });

    return parsed;
};

const ChronologyTimelineItem = ({ text, citations, keyPrefix, onCitationOpen }) => {
    const parsedItem = parseChronologyTimelineItem(text);

    return (
        <div className="library-rag-chronology-card">
            {parsedItem ? (
                <>
                    <div className="library-rag-chronology-header">
                        <span className="library-rag-chronology-date">{parsedItem.dateLabel}</span>
                        {parsedItem.updateText ? <span className="library-rag-chronology-count">{parsedItem.updateText}</span> : null}
                    </div>
                    {parsedItem.signals.length > 0 ? (
                        <div className="library-rag-chronology-tags" aria-label="Signals">
                            {parsedItem.signals.map((signal, signalIndex) => (
                                <span key={`signal-${signalIndex}`} className="library-rag-chronology-tag">{signal}</span>
                            ))}
                        </div>
                    ) : null}
                    {parsedItem.summary ? (
                        <div className="library-rag-chronology-examples">
                            {renderCitationLinkedText(parsedItem.summary, citations, `${keyPrefix}-examples`, onCitationOpen)}
                        </div>
                    ) : null}
                    {parsedItem.other.length > 0 ? (
                        <div className="library-rag-chronology-other">
                            {renderCitationLinkedText(parsedItem.other.join('; '), citations, `${keyPrefix}-other`, onCitationOpen)}
                        </div>
                    ) : null}
                </>
            ) : renderCitationLinkedText(text, citations, keyPrefix, onCitationOpen)}
        </div>
    );
};

export const ChronologyTimeline = ({ items, citations, messageId, nodeIndex, onCitationOpen }) => {
    return (
        <div className="library-rag-chronology-timeline">
            {items.map((item, itemIndex) => (
                <div key={`chronology-item-${itemIndex}`} className="library-rag-chronology-item">
                    <ChronologyTimelineItem text={item} citations={citations} keyPrefix={`rag-answer-chronology-${messageId}-${nodeIndex}-${itemIndex}`} onCitationOpen={onCitationOpen} />
                    {itemIndex < items.length - 1 ? <div className="library-rag-chronology-connector" aria-hidden="true" /> : null}
                </div>
            ))}
        </div>
    );
};

const renderChronologyTimelineNode = (items, citations, messageId, nodeIndex, onCitationOpen) => (
    <ChronologyTimeline
        key={`rag-answer-chronology-${messageId}-${nodeIndex}`}
        items={items}
        citations={citations}
        messageId={messageId}
        nodeIndex={nodeIndex}
        onCitationOpen={onCitationOpen}
    />
);

const JaneCapabilityExplorer = ({ answer, onUseExample }) => {
    const registry = answer?.capability_registry || {};
    const capabilities = Array.isArray(registry.capabilities) ? registry.capabilities.filter(Boolean) : [];
    const primitives = Array.isArray(answer?.tool_runtime_registry?.capabilities) ? answer.tool_runtime_registry.capabilities.filter(Boolean) : [];
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredCapabilities = capabilities
        .map((capability, index) => ({ capability, index }))
        .filter(({ capability }) => {
            if (!normalizedSearch) return true;
            const haystack = [capability?.name, capability?.description, ...(Array.isArray(capability?.examples) ? capability.examples : [])].join(' ').toLowerCase();
            return haystack.includes(normalizedSearch);
        });
    const selectedCapability = capabilities[selectedIndex] || filteredCapabilities[0]?.capability || capabilities[0] || null;
    const selectedExamples = Array.isArray(selectedCapability?.examples) ? selectedCapability.examples.filter(Boolean) : [];

    if (capabilities.length === 0) return null;

    return (
        <div className="library-rag-capability-explorer">
            <div className="library-rag-capability-hero">
                <div>
                    <span className="library-rag-capability-eyebrow">Jane Tools</span>
                    <h4>Here is what {registry.product || 'Jane'} can do right now.</h4>
                    <p>Select a tool to see examples, then drop a starter prompt into the composer.</p>
                </div>
                <div className="library-rag-capability-count">{capabilities.length} workflows</div>
            </div>
            <div className="library-rag-capability-search">
                <Search size={14} />
                <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Find a tool or workflow" />
            </div>
            <div className="library-rag-capability-layout">
                <div className="library-rag-capability-list" role="listbox" aria-label="Jane workflows">
                    {filteredCapabilities.length === 0 ? (
                        <div className="library-rag-capability-empty">No tools match that search.</div>
                    ) : filteredCapabilities.map(({ capability, index }) => (
                        <button
                            key={`${capability?.name || 'capability'}-${index}`}
                            type="button"
                            className={index === selectedIndex ? 'active' : ''}
                            onClick={() => setSelectedIndex(index)}
                        >
                            <span>{capability?.name || 'Jane capability'}</span>
                            <small>{Array.isArray(capability?.examples) ? `${capability.examples.length} example${capability.examples.length === 1 ? '' : 's'}` : 'Examples'}</small>
                        </button>
                    ))}
                </div>
                {selectedCapability && (
                    <section className="library-rag-capability-detail" aria-label={`${selectedCapability.name || 'Capability'} examples`}>
                        <div className="library-rag-capability-detail-header">
                            <div>
                                <span className="library-rag-capability-detail-kicker">Selected tool</span>
                                <h5>{selectedCapability.name || 'Jane capability'}</h5>
                            </div>
                        </div>
                        <p>{selectedCapability.description || 'This workflow is available in Jane.'}</p>
                        {selectedExamples.length > 0 && (
                            <div className="library-rag-capability-examples">
                                <span>Try one of these</span>
                                {selectedExamples.map((example, exampleIndex) => (
                                    <button
                                        key={`${selectedCapability.name || 'capability'}-example-${exampleIndex}`}
                                        type="button"
                                        onClick={() => onUseExample?.(String(example))}
                                    >
                                        <span>{example}</span>
                                        <ArrowUp size={13} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </div>
            {primitives.length > 0 && (
                <details className="library-rag-capability-primitives">
                    <summary>Tool authoring primitives</summary>
                    <div>
                        {primitives.slice(0, 18).map((primitive) => (
                            <span key={primitive.id || primitive.name}>{primitive.id || primitive.name}</span>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
};

const isMarkdownTableSeparator = (line = '') => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const parseMarkdownTableRow = (line = '') => {
    const trimmed = line.trim();
    if (!trimmed.includes('|')) return [];
    return trimmed
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map(cell => cell.trim());
};

const parseMarkdownHeading = (line = '') => {
    const match = line.match(/^\s{0,3}(#{1,4})\s+(.+?)\s*#*\s*$/);
    if (!match) return null;
    return { level: Math.min(match[1].length, 4), text: match[2] };
};

const normalizeMarkdownListItemText = (text = '') => String(text || '').replace(/^\s*(?:[*-]\s+)+/, '').trim();

const parseMarkdownListItem = (line = '') => {
    const unordered = line.match(/^\s{0,3}[*-]\s+(.+)$/);
    if (unordered) return { type: 'ul', text: normalizeMarkdownListItemText(unordered[1]) };
    const ordered = line.match(/^\s{0,3}(\d+)[.)]\s+(.+)$/);
    if (ordered) return { type: 'ol', number: Number(ordered[1]), text: normalizeMarkdownListItemText(ordered[2]) };
    return null;
};

const getOutlookCitationForAnswerListItem = (text = '', citations = []) => {
    const citationNumbers = getOrderedInlineCitationNumbers(text);
    if (citationNumbers.length !== 1) return null;
    const citationNumber = String(citationNumbers[0]);
    const citation = (Array.isArray(citations) ? citations : []).find((source, index) => (
        String(source?.original_number || source?.number || index + 1) === citationNumber
    ));
    return citation && isOutlookCitation(citation) ? citation : null;
};

const withOutlookAnswerItemSummary = (citation = {}, itemText = '') => {
    if (String(citation.summary || citation.email_summary || '').trim()) return citation;
    const summaryMatch = String(itemText || '').match(/\bSummary\s*:\s*(.+?)(?:\s*\[[\d,\s]+\]\s*)?$/i);
    if (!summaryMatch?.[1] || hasOutlookRawPreviewLabel(summaryMatch[1])) return citation;
    return {
        ...citation,
        summary: cleanMalformedBoldMarkers(summaryMatch[1]),
    };
};

const getOutlookCitationIdentity = (citation = {}) => String(
    citation.source_id
    || citation.original_number
    || citation.number
    || ''
);

const normalizeOutlookAnswerLines = (text = '', citations = []) => {
    const rawLines = normalizeRagAnswerText(text).split('\n');
    const mergedLines = [];

    rawLines.forEach((line) => {
        const trimmed = String(line || '').trim();
        const outlookCitation = getOutlookCitationForAnswerListItem(trimmed, citations);
        if (/^Preview\s*:/i.test(trimmed) && outlookCitation) {
            return;
        }
        if (/^Summary\s*:/i.test(trimmed) && outlookCitation) {
            let previousIndex = mergedLines.length - 1;
            while (previousIndex >= 0 && String(mergedLines[previousIndex] || '').trim() === '') previousIndex -= 1;
            const previousItem = previousIndex >= 0 ? parseMarkdownListItem(mergedLines[previousIndex]) : null;
            const previousCitation = previousItem
                ? getOutlookCitationForAnswerListItem(previousItem.text, citations)
                : null;
            if (
                previousItem
                && previousCitation
                && getOutlookCitationIdentity(previousCitation) === getOutlookCitationIdentity(outlookCitation)
            ) {
                mergedLines[previousIndex] = `${mergedLines[previousIndex]} · ${trimmed}`;
                return;
            }
        }
        mergedLines.push(line);
    });

    return mergedLines.filter((line, lineIndex) => {
        if (String(line || '').trim() !== '') return true;
        let previousIndex = lineIndex - 1;
        while (previousIndex >= 0 && String(mergedLines[previousIndex] || '').trim() === '') previousIndex -= 1;
        let nextIndex = lineIndex + 1;
        while (nextIndex < mergedLines.length && String(mergedLines[nextIndex] || '').trim() === '') nextIndex += 1;
        const previousItem = previousIndex >= 0 ? parseMarkdownListItem(mergedLines[previousIndex]) : null;
        const nextItem = nextIndex < mergedLines.length ? parseMarkdownListItem(mergedLines[nextIndex]) : null;
        return !(
            previousItem
            && nextItem
            && getOutlookCitationForAnswerListItem(previousItem.text, citations)
            && getOutlookCitationForAnswerListItem(nextItem.text, citations)
        );
    });
};

const isStandaloneMarkdownBullet = (line = '') => {
    const normalized = String(line || '').replace(/\u00a0/g, ' ').trim();
    return normalized === '*' || normalized === '-' || normalized === '\u2022';
};

const isMarkdownMarkerOnlyLine = (line = '') => /^\s*(?:\*\s*){2,}\s*$/.test(String(line || ''));

const isMarkdownThematicBreak = (line = '') => /^\s{0,3}(?:\*\s*){3,}$/.test(String(line || ''));

const cleanMalformedBoldMarkers = (value = '') => String(value || '')
    .replace(/^\s*\*{3,}(?=\S)/, '')
    .replace(/\*\*([^*\n:]{1,90}:)\*\*/g, '$1')
    .replace(/^\s*\*\*\s*/, '')
    .replace(/\*\*(?=\s|$)/g, '')
    .replace(/:\s*\*\*\s*/g, ': ')
    .replace(/\s*\*{3,}\s*$/g, '')
    .replace(/\s*\*\*\s*$/, '')
    .trim();

const parseMalformedBoldHeading = (line = '') => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('**')) return null;
    const cleaned = cleanMalformedBoldMarkers(trimmed);
    if (!cleaned || cleaned.includes(':')) return null;
    return cleaned;
};

const parseLabeledSummaryLine = (line = '') => {
    const cleaned = cleanMalformedBoldMarkers(line);
    const match = cleaned.match(/^([^:]{2,70}):\s*(.*)$/);
    if (!match) return null;
    return { label: match[1].trim(), value: match[2].trim() };
};

const parseInlineLabeledSummary = (line = '') => {
    const trimmed = cleanMalformedBoldMarkers(line);
    if (!trimmed || !trimmed.includes(';')) return null;

    const headerMatch = trimmed.match(/^([^:]{2,80}):\s*(.+)$/);
    if (!headerMatch) return null;

    const header = headerMatch[1].trim();
    const items = headerMatch[2]
        .split(';')
        .map(item => item.trim())
        .filter(Boolean)
        .map((item) => {
            const itemMatch = item.match(/^([^:]{2,70}):\s*(.*)$/);
            return itemMatch ? { label: itemMatch[1].trim(), value: itemMatch[2].trim() } : null;
        })
        .filter(Boolean);

    return items.length >= 2 ? { header, items } : null;
};

const renderLabeledSummaryNode = ({ label, value }, citations, keyPrefix, onCitationOpen) => (
    <p key={keyPrefix}>
        <strong>{renderCitationLinkedText(`${label}:`, citations, `${keyPrefix}-label`, onCitationOpen)}</strong>
        {value ? ' ' : ''}
        {value ? renderCitationLinkedText(value, citations, `${keyPrefix}-value`, onCitationOpen) : null}
    </p>
);

const parseMarkdownListOrLabeledSummaryLine = (line = '') => {
    const listItem = parseMarkdownListItem(line);
    const text = listItem ? listItem.text : line;
    return parseLabeledSummaryLine(text);
};

const renderLabeledSummaryListNode = (title, items, citations, messageId, summaryIndex, onCitationOpen) => (
    <div key={`rag-answer-malformed-summary-${messageId}-${summaryIndex}`} className="library-rag-answer-summary-list">
        <div className="library-rag-answer-summary-title">
            {renderCitationLinkedText(title, citations, `rag-answer-malformed-title-${messageId}-${summaryIndex}`, onCitationOpen)}
        </div>
        <ul className="library-rag-answer-list">
            {items.map((item, itemIndex) => (
                <li key={`malformed-summary-item-${itemIndex}`}>
                    <strong>{renderCitationLinkedText(`${item.label}:`, citations, `rag-answer-malformed-label-${messageId}-${summaryIndex}-${itemIndex}`, onCitationOpen)}</strong>
                    {item.value ? ' ' : ''}
                    {item.value ? renderCitationLinkedText(item.value, citations, `rag-answer-malformed-value-${messageId}-${summaryIndex}-${itemIndex}`, onCitationOpen) : null}
                </li>
            ))}
        </ul>
    </div>
);

const OutlookEmailAnswerList = ({ items, citations, onCitationOpen }) => {
    const entries = items.map((item, itemIndex) => {
        const citation = getOutlookCitationForAnswerListItem(item.text, citations);
        if (!citation) return null;
        return {
            key: getOutlookCitationIdentity(citation) || `outlook-email-${itemIndex}`,
            citation: withOutlookAnswerItemSummary(citation, item.text),
            sourceNumber: citation.number || citation.original_number || getOrderedInlineCitationNumbers(item.text)[0],
        };
    }).filter(Boolean);
    return (
        <div className="library-rag-outlook-answer-list">
            {entries.length > 0 && (
                <ul className="library-rag-answer-list outlook-email-list">
                    {entries.map((entry, entryIndex) => (
                        <li key={`${entry.key}-${entryIndex}`} className="library-rag-answer-outlook-email-item">
                            <OutlookEmailCitationCard
                                truncateSummary={false}
                                citation={entry.citation}
                                sourceNumber={entry.sourceNumber}
                                onOpen={(source, outlookTarget) => onCitationOpen?.(source, {
                                    outlookTarget,
                                    outlookView: 'pdf',
                                })}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const OUTLOOK_DIGEST_SECTION_MATCHERS = {
    executive: /^executive summary$/i,
    action: /^needs your response\s*\/\s*action\s*\((\d+)\)$/i,
    important: /^important updates\s*\((\d+)\)$/i,
    low: /^low[-\u2010-\u2015]?priority\s*\/\s*no action\s*\((\d+)\)$/i,
    supporting: /^supporting emails$/i,
};

const getOutlookDigestSourceEntries = (citations = []) => {
    const seen = new Set();
    return (Array.isArray(citations) ? citations : []).reduce((entries, citation, citationIndex) => {
        if (!isOutlookCitation(citation)) return entries;
        const identity = getOutlookCitationIdentity(citation)
            || String(citation?.citation_handle || citation?.number || citation?.original_number || citationIndex);
        if (seen.has(identity)) return entries;
        seen.add(identity);
        entries.push({
            citation,
            identity,
            sourceNumber: citation?.number || citation?.original_number || citationIndex + 1,
        });
        return entries;
    }, []);
};

const parseOutlookDigestSectionBody = (lines = []) => lines.reduce((section, line) => {
    const item = parseMarkdownListItem(line);
    if (item) {
        section.items.push(item.text);
        return section;
    }
    const text = cleanMalformedBoldMarkers(line);
    if (text) section.notes.push(text);
    return section;
}, { items: [], notes: [] });

const parseOutlookDigestSummary = (lines = [], citations = []) => {
    const headingRows = lines
        .map((line, index) => ({ index, heading: parseMarkdownHeading(line) }))
        .filter(row => row.heading);
    const titleRow = headingRows.find(row => /^outlook email (?:digest|summary)$/i.test(row.heading.text)) || null;

    const required = ['executive', 'action', 'important', 'low', 'supporting'];
    const sectionRows = {};
    let cursor = titleRow?.index ?? -1;
    for (const sectionKey of required) {
        let row = null;
        for (const candidate of headingRows) {
            if (candidate.index <= cursor) continue;
            if (!OUTLOOK_DIGEST_SECTION_MATCHERS[sectionKey].test(candidate.heading.text)) continue;
            row = candidate;
            break;
        }
        if (!row) return null;
        sectionRows[sectionKey] = row;
        cursor = row.index;
    }

    const orderedIndexes = required.map(sectionKey => sectionRows[sectionKey].index);
    if (!orderedIndexes.every((sectionIndex, index) => index === 0 || sectionIndex > orderedIndexes[index - 1])) {
        return null;
    }

    const countFor = sectionKey => {
        const match = sectionRows[sectionKey].heading.text.match(OUTLOOK_DIGEST_SECTION_MATCHERS[sectionKey]);
        return Number(match?.[1]);
    };
    const counts = {
        action: countFor('action'),
        important: countFor('important'),
        low: countFor('low'),
    };
    if (!Object.values(counts).every(count => Number.isSafeInteger(count) && count >= 0)) return null;

    const sourceEntries = getOutlookDigestSourceEntries(citations);
    const totalCount = counts.action + counts.important + counts.low;
    if (sourceEntries.length === 0 || totalCount !== sourceEntries.length) return null;

    const caseDbMatchesRow = headingRows.find(row => (
        row.index > sectionRows.supporting.index
        && /^casedb matches$/i.test(row.heading.text)
    )) || null;
    const supportingEndIndexCandidate = lines.findIndex((line, lineIndex) => (
        lineIndex > sectionRows.supporting.index
        && (Boolean(parseMarkdownHeading(line)) || /^Limitations:?$/i.test(String(line || '').trim()))
    ));
    const supportingEndIndex = supportingEndIndexCandidate >= 0 ? supportingEndIndexCandidate : lines.length;
    const caseDbMatchesEndIndexCandidate = caseDbMatchesRow ? lines.findIndex((line, lineIndex) => (
        lineIndex > caseDbMatchesRow.index
        && (Boolean(parseMarkdownHeading(line)) || /^Limitations:?$/i.test(String(line || '').trim()))
    )) : -1;
    const digestEndIndex = caseDbMatchesRow
        ? (caseDbMatchesEndIndexCandidate >= 0 ? caseDbMatchesEndIndexCandidate : lines.length)
        : supportingEndIndex;
    const bodyFor = (sectionKey, nextSectionKey) => parseOutlookDigestSectionBody(
        lines.slice(sectionRows[sectionKey].index + 1, sectionRows[nextSectionKey].index)
    );
    const preExecutiveLines = lines
        .slice((titleRow?.index ?? -1) + 1, sectionRows.executive.index)
        .filter(line => !parseMarkdownHeading(line));
    const executive = parseOutlookDigestSectionBody([
        ...preExecutiveLines,
        ...lines.slice(sectionRows.executive.index + 1, sectionRows.action.index),
    ]);

    return {
        title: titleRow ? cleanMalformedBoldMarkers(titleRow.heading.text) : 'Outlook email digest',
        counts,
        totalCount,
        executive,
        action: bodyFor('action', 'important'),
        important: bodyFor('important', 'low'),
        low: bodyFor('low', 'supporting'),
        supporting: parseOutlookDigestSectionBody(lines.slice(
            sectionRows.supporting.index + 1,
            caseDbMatchesRow?.index ?? supportingEndIndex
        )),
        sourceEntries,
        remainderText: digestEndIndex < lines.length ? lines.slice(digestEndIndex).join('\n').trim() : '',
    };
};

const OutlookDigestText = ({ lines = [], citations, keyPrefix, onCitationOpen }) => (
    <>
        {lines.map((line, lineIndex) => (
            <p key={`${keyPrefix}-${lineIndex}`}>
                <RagAnswerText
                    text={line}
                    citations={citations}
                    keyPrefix={`${keyPrefix}-${lineIndex}`}
                    onCitationOpen={onCitationOpen}
                />
            </p>
        ))}
    </>
);

const getOutlookDigestItemSourceEntries = (item = '', citations = []) => {
    const citationList = Array.isArray(citations) ? citations : [];
    const seen = new Set();

    return getOrderedInlineCitationNumbers(item).reduce((entries, citationNumber) => {
        const citation = citationList.find((source, sourceIndex) => (
            String(source?.original_number || source?.number || sourceIndex + 1) === String(citationNumber)
        ));
        if (!citation || !isOutlookCitation(citation)) return entries;
        const identity = getOutlookCitationIdentity(citation) || String(citationNumber);
        if (seen.has(identity)) return entries;
        seen.add(identity);
        entries.push({
            citation,
            identity,
            model: getOutlookEmailCardModel(citation),
            sourceNumber: citation?.number || citation?.original_number || citationNumber,
        });
        return entries;
    }, []);
};

const formatOutlookDigestSourceDate = (value = '') => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
};

const OutlookDigestSourceIdentity = ({ entry }) => {
    const model = entry.model || getOutlookEmailCardModel(entry.citation);
    const recipients = model.recipients.join('; ');
    const counterparty = model.direction === 'Sent' && recipients
        ? `To ${recipients}`
        : (model.sender ? `From ${model.sender}` : (recipients ? `To ${recipients}` : ''));
    const sourceDate = formatOutlookDigestSourceDate(model.timestamp);
    const metadata = [counterparty, sourceDate].filter(Boolean).join(' · ');

    return (
        <div className="library-rag-outlook-digest-item-source">
            <span className="library-rag-outlook-digest-item-source-number">[{entry.sourceNumber}]</span>
            <span className="library-rag-outlook-digest-item-source-subject" title={model.subject}>
                {model.subject}
            </span>
            {metadata ? (
                <span className="library-rag-outlook-digest-item-source-meta" title={metadata}>{metadata}</span>
            ) : null}
            <OutlookEmailCaseMatchLinks citation={entry.citation} compact />
        </div>
    );
};

const OutlookDigestItemSources = ({ item, citations }) => {
    const entries = getOutlookDigestItemSourceEntries(item, citations);
    if (entries.length === 0) return null;

    return (
        <div className="library-rag-outlook-digest-item-sources" role="group" aria-label="Related emails">
            {entries.map(entry => <OutlookDigestSourceIdentity entry={entry} key={entry.identity} />)}
        </div>
    );
};

const OutlookDigestList = ({ items = [], ordered = false, citations, keyPrefix, onCitationOpen }) => {
    if (items.length === 0) return null;
    const ListTag = ordered ? 'ol' : 'ul';
    return (
        <ListTag className="library-rag-outlook-digest-list">
            {items.map((item, itemIndex) => (
                <li key={`${keyPrefix}-${itemIndex}`}>
                    <div className="library-rag-outlook-digest-item-copy">
                        <RagAnswerText
                            text={item}
                            citations={citations}
                            keyPrefix={`${keyPrefix}-${itemIndex}`}
                            onCitationOpen={onCitationOpen}
                        />
                    </div>
                    <OutlookDigestItemSources item={item} citations={citations} />
                </li>
            ))}
        </ListTag>
    );
};

const OutlookDigestSection = ({ kind, title, count, section, ordered = false, citations, messageId, onCitationOpen }) => (
    <section className={`library-rag-outlook-digest-section ${kind}`} aria-labelledby={`outlook-digest-${kind}-${messageId}`}>
        <div className="library-rag-outlook-digest-section-heading">
            <h4 id={`outlook-digest-${kind}-${messageId}`}>{title}</h4>
            <span aria-label={`${count} ${title.toLowerCase()}`}>{count}</span>
        </div>
        <OutlookDigestList
            items={section.items}
            ordered={ordered}
            citations={citations}
            keyPrefix={`outlook-digest-${kind}-item-${messageId}`}
            onCitationOpen={onCitationOpen}
        />
        <div className="library-rag-outlook-digest-notes">
            <OutlookDigestText
                lines={section.notes}
                citations={citations}
                keyPrefix={`outlook-digest-${kind}-note-${messageId}`}
                onCitationOpen={onCitationOpen}
            />
        </div>
    </section>
);

const OutlookDigestSummary = ({ digest, citations, messageId, onCitationOpen }) => {
    const [showAllSources, setShowAllSources] = useState(false);
    const [showAllCaseMatches, setShowAllCaseMatches] = useState(false);
    const visibleSources = showAllSources ? digest.sourceEntries : digest.sourceEntries.slice(0, 6);
    const hasMoreSources = digest.sourceEntries.length > 6;
    const caseMatchSources = digest.sourceEntries.map(entry => ({
        ...entry,
        model: getOutlookEmailCardModel(entry.citation),
    })).filter(entry => entry.model.caseMatches.length > 0);
    const visibleCaseMatchSources = showAllCaseMatches ? caseMatchSources : caseMatchSources.slice(0, 6);
    const hasMoreCaseMatches = caseMatchSources.length > 6;
    const supportingPreview = digest.sourceEntries.slice(0, 2).map((entry) => {
        const model = getOutlookEmailCardModel(entry.citation);
        return [model.sender, model.subject].filter(Boolean).join(' — ');
    }).filter(Boolean);
    if (digest.sourceEntries.length > supportingPreview.length) {
        supportingPreview.push(`+${digest.sourceEntries.length - supportingPreview.length} more`);
    }

    useEffect(() => {
        setShowAllSources(false);
        setShowAllCaseMatches(false);
    }, [digest.sourceEntries.length]);

    return (
        <section className="library-rag-outlook-digest" aria-labelledby={`outlook-digest-title-${messageId}`}>
            <header className="library-rag-outlook-digest-header">
                <h3 id={`outlook-digest-title-${messageId}`}>{digest.title}</h3>
                <div className="library-rag-outlook-digest-overview">
                    <OutlookDigestText
                        lines={[...digest.executive.notes, ...digest.executive.items]}
                        citations={citations}
                        keyPrefix={`outlook-digest-overview-${messageId}`}
                        onCitationOpen={onCitationOpen}
                    />
                </div>
                <dl className="library-rag-outlook-digest-metrics">
                    <div className="needs-response"><dt>Needs action</dt><dd>{digest.counts.action}</dd></div>
                    <div className="important"><dt>Important</dt><dd>{digest.counts.important}</dd></div>
                    <div className="low"><dt>No action</dt><dd>{digest.counts.low}</dd></div>
                </dl>
            </header>

            <OutlookDigestSection
                kind="needs-response"
                title="Needs your response"
                count={digest.counts.action}
                section={digest.action}
                ordered
                citations={citations}
                messageId={messageId}
                onCitationOpen={onCitationOpen}
            />
            <OutlookDigestSection
                kind="important"
                title="Important updates"
                count={digest.counts.important}
                section={digest.important}
                citations={citations}
                messageId={messageId}
                onCitationOpen={onCitationOpen}
            />

            {caseMatchSources.length > 0 ? (
                <section
                    className="library-rag-outlook-digest-case-matches"
                    aria-labelledby={`outlook-digest-case-matches-${messageId}`}
                >
                    <div className="library-rag-outlook-digest-section-heading">
                        <h4 id={`outlook-digest-case-matches-${messageId}`}>CaseDB matches</h4>
                        <span aria-label={`${caseMatchSources.length} matched emails`}>{caseMatchSources.length}</span>
                    </div>
                    <ul className="library-rag-outlook-digest-case-match-list">
                        {visibleCaseMatchSources.map(entry => (
                            <li key={`case-match-${entry.identity}`}>
                                <OutlookDigestSourceIdentity entry={entry} />
                            </li>
                        ))}
                    </ul>
                    {hasMoreCaseMatches ? (
                        <button
                            type="button"
                            className="action tertiary library-rag-outlook-digest-source-toggle"
                            onClick={() => setShowAllCaseMatches(current => !current)}
                            aria-expanded={showAllCaseMatches}
                        >
                            {showAllCaseMatches ? 'Show fewer matches' : `Show all ${caseMatchSources.length} matched emails`}
                        </button>
                    ) : null}
                </section>
            ) : null}

            <OutlookDigestSection
                kind="low"
                title="Low-priority / no action"
                count={digest.counts.low}
                section={digest.low}
                citations={citations}
                messageId={messageId}
                onCitationOpen={onCitationOpen}
            />

            <details className="library-rag-outlook-digest-supporting">
                <summary>
                    <span className="library-rag-outlook-digest-supporting-label">
                        <SquareLibrary size={16} aria-hidden="true" />
                        <span className="library-rag-outlook-digest-supporting-copy">
                            <span>Supporting emails</span>
                            {supportingPreview.length > 0 ? (
                                <span className="library-rag-outlook-digest-supporting-preview">
                                    {supportingPreview.join(' · ')}
                                </span>
                            ) : null}
                        </span>
                    </span>
                    <span className="library-rag-outlook-digest-summary-count">{digest.totalCount}</span>
                    <ChevronDown size={16} aria-hidden="true" />
                </summary>
                <div className="library-rag-outlook-digest-disclosure-body">
                    <div className="library-rag-outlook-digest-notes">
                        <OutlookDigestText
                            lines={digest.supporting.notes}
                            citations={citations}
                            keyPrefix={`outlook-digest-supporting-note-${messageId}`}
                            onCitationOpen={onCitationOpen}
                        />
                    </div>
                    <ul className="library-rag-answer-list outlook-email-list library-rag-outlook-digest-sources">
                        {visibleSources.map((entry, sourceIndex) => (
                            <li key={`${entry.identity}-${sourceIndex}`} className="library-rag-answer-outlook-email-item">
                                <OutlookEmailCitationCard
                                    truncateSummary={false}
                                    citation={entry.citation}
                                    sourceNumber={entry.sourceNumber}
                                    onOpen={(source, outlookTarget) => onCitationOpen?.(source, {
                                        outlookTarget,
                                        outlookView: 'pdf',
                                    })}
                                />
                            </li>
                        ))}
                    </ul>
                    {hasMoreSources ? (
                        <button
                            type="button"
                            className="action tertiary library-rag-outlook-digest-source-toggle"
                            onClick={() => setShowAllSources(current => !current)}
                            aria-expanded={showAllSources}
                        >
                            {showAllSources ? 'Show fewer emails' : `Show all ${digest.sourceEntries.length} emails`}
                        </button>
                    ) : null}
                </div>
            </details>

        </section>
    );
};

export const renderRagAnswerContent = (text, citations, messageId, onCitationOpen, answerMode = '') => {
    const lines = normalizeOutlookAnswerLines(text, citations);
    const outlookDigest = parseOutlookDigestSummary(lines, citations);
    if (outlookDigest) {
        const digestNodes = [
            <OutlookDigestSummary
                key={`outlook-digest-${messageId}`}
                digest={outlookDigest}
                citations={citations}
                messageId={messageId}
                onCitationOpen={onCitationOpen}
            />,
        ];
        if (outlookDigest.remainderText) {
            digestNodes.push(...renderRagAnswerContent(
                outlookDigest.remainderText,
                citations,
                `${messageId}-outlook-digest-remainder`,
                onCitationOpen,
                answerMode
            ));
        }
        return digestNodes;
    }
    const nodes = [];
    let index = 0;
    let quoteBlockLines = null;
    let quoteClosingCharacter = '';
    let activeHeading = '';

    const flushQuoteBlock = (complete = false) => {
        if (!quoteBlockLines || quoteBlockLines.length === 0) {
            quoteBlockLines = null;
            return;
        }

        const blockIndex = nodes.length;
        if (!complete) {
            // Streaming or malformed quoted edits are still readable, and must
            // not absorb later headings/lists into an unfinished quote card.
            nodes.push(...renderRagAnswerContent(
                quoteBlockLines.join('\n'), citations,
                `${messageId}-unfinished-quote-${blockIndex}`, onCitationOpen
            ));
            quoteBlockLines = null;
            return;
        }
        const normalizedLines = quoteBlockLines.map((quoteLine, quoteLineIndex) => {
            let content = quoteLine;
            if (quoteLineIndex === 0) content = content.replace(/^\s*["“]/, '');
            if (quoteLineIndex === quoteBlockLines.length - 1) content = content.replace(/["”]\s*$/, '');
            return content;
        });

        nodes.push(
            <div key={`rag-answer-quote-${messageId}-${blockIndex}`} className="library-rag-answer-quote">
                {normalizedLines.map((quoteLine, quoteLineIndex) => (
                    quoteLine.trim() === '' ? (
                        <p key={`rag-answer-quote-line-${messageId}-${blockIndex}-${quoteLineIndex}`}>&nbsp;</p>
                    ) : (
                        <p key={`rag-answer-quote-line-${messageId}-${blockIndex}-${quoteLineIndex}`}>
                            <RagAnswerText text={cleanMalformedBoldMarkers(quoteLine)} citations={citations} keyPrefix={`rag-answer-quote-line-${messageId}-${blockIndex}-${quoteLineIndex}`} onCitationOpen={onCitationOpen} />
                        </p>
                    )
                ))}
            </div>
        );
        quoteBlockLines = null;
    };

    while (index < lines.length) {
        const line = lines[index];
        const nextLine = lines[index + 1] || '';

        if (isMarkdownMarkerOnlyLine(line)) {
            flushQuoteBlock();
            if (isMarkdownThematicBreak(line)) {
                nodes.push(<hr key={`rag-answer-rule-${messageId}-${index}`} className="library-rag-answer-rule" />);
            }
            index += 1;
            continue;
        }

        if (quoteBlockLines !== null) {
            quoteBlockLines.push(line);
            if (line.trimEnd().endsWith(quoteClosingCharacter)) {
                flushQuoteBlock(true);
            }
            index += 1;
            continue;
        }

        // This card is the existing edited-text presentation, not a generic
        // quotation renderer. Ordinary quoted answers keep their punctuation.
        if (answerMode === 'language_editing_answer' && /^["“]/.test(line.trim())) {
            const trimmed = line.trim();
            quoteClosingCharacter = trimmed.startsWith('“') ? '”' : '"';
            const closesOnSameLine = trimmed.length > 1 && trimmed.endsWith(quoteClosingCharacter);
            const hasInlineQuote = trimmed.slice(1).includes(quoteClosingCharacter);
            if (closesOnSameLine || !hasInlineQuote) {
                quoteBlockLines = [line];
                if (closesOnSameLine) flushQuoteBlock(true);
                index += 1;
                continue;
            }
        }

        const malformedBoldHeading = parseMalformedBoldHeading(line);
        if (malformedBoldHeading) {
            const items = [];
            let nextIndex = index + 1;
            while (nextIndex < lines.length) {
                const nextCandidate = lines[nextIndex];
                if (nextCandidate.trim() === '') {
                    nextIndex += 1;
                    continue;
                }
                if (parseMalformedBoldHeading(nextCandidate) || parseMarkdownHeading(nextCandidate)) break;
                const labeledLine = parseMarkdownListOrLabeledSummaryLine(nextCandidate);
                if (!labeledLine) break;
                items.push(labeledLine);
                nextIndex += 1;
            }

            if (items.length > 0) {
                const summaryIndex = nodes.length;
                nodes.push(renderLabeledSummaryListNode(malformedBoldHeading, items, citations, messageId, summaryIndex, onCitationOpen));
                index = nextIndex;
                continue;
            }
        }

        if (line.includes('|') && isMarkdownTableSeparator(nextLine)) {
            const header = parseMarkdownTableRow(line);
            const rows = [];
            index += 2;

            while (index < lines.length && lines[index].includes('|') && lines[index].trim() !== '') {
                rows.push(parseMarkdownTableRow(lines[index]));
                index += 1;
            }

            if (header.length >= 2 && rows.length > 0) {
                const tableIndex = nodes.length;
                nodes.push(
                    <div key={`rag-answer-table-${messageId}-${tableIndex}`} className="library-rag-answer-table-wrap">
                        <table className="library-rag-answer-table">
                            <thead>
                                <tr>
                                    {header.map((cell, cellIndex) => (
                                        <th key={`head-${cellIndex}`}>{renderCitationLinkedText(cell, citations, `rag-answer-table-head-${messageId}-${tableIndex}-${cellIndex}`, onCitationOpen)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rowIndex) => (
                                    <tr key={`row-${rowIndex}`}>
                                        {header.map((_, cellIndex) => (
                                            <td key={`cell-${rowIndex}-${cellIndex}`}>{renderCitationLinkedText(row[cellIndex] || '', citations, `rag-answer-table-cell-${messageId}-${tableIndex}-${rowIndex}-${cellIndex}`, onCitationOpen)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
                continue;
            }
        }

        const listItem = parseMarkdownListItem(line);
        if (listItem) {
            const listType = listItem.type;
            const items = [];
            while (index < lines.length) {
                const nextItem = parseMarkdownListItem(lines[index]);
                if (!nextItem || nextItem.type !== listType) break;
                items.push(nextItem);
                index += 1;
            }
            const previousLine = lines[index - items.length - 1] || '';
            const itemTexts = items.map(item => item.text);
            if (isChronologyTimelineLabel(previousLine) || isChronologyTimelineItems(itemTexts)) {
                nodes.push(renderChronologyTimelineNode(itemTexts, citations, messageId, nodes.length, onCitationOpen));
                continue;
            }
            const ListTag = listType;
            const listProps = listType === 'ol' && Number.isFinite(items[0]?.number)
                ? { start: items[0].number }
                : {};
            const outlookCitations = items.map(item => getOutlookCitationForAnswerListItem(item.text, citations));
            const isOutlookInventorySection = /^(?:messages|all messages|email messages|matched messages)$/i.test(String(activeHeading || '').trim());
            const hasOutlookEmailItems = isOutlookInventorySection && outlookCitations.some(Boolean);
            const allItemsAreOutlookEmails = outlookCitations.length > 0 && outlookCitations.every(Boolean);
            if (isOutlookInventorySection && allItemsAreOutlookEmails) {
                nodes.push(
                    <OutlookEmailAnswerList
                        key={`rag-answer-outlook-list-${messageId}-${nodes.length}`}
                        items={items}
                        citations={citations}
                        onCitationOpen={onCitationOpen}
                    />
                );
                continue;
            }
            nodes.push(
                <ListTag key={`rag-answer-list-${messageId}-${nodes.length}`} className={`library-rag-answer-list${hasOutlookEmailItems ? ' outlook-email-list' : ''}`} {...listProps}>
                    {items.map((item, itemIndex) => {
                        const outlookCitation = outlookCitations[itemIndex];
                        if (!isOutlookInventorySection || !outlookCitation) {
                            return <RagAnswerListItem key={`item-${itemIndex}`} text={item.text} citations={citations} keyPrefix={`rag-answer-list-${messageId}-${nodes.length}-${itemIndex}`} onCitationOpen={onCitationOpen} />;
                        }
                        const emailCitation = withOutlookAnswerItemSummary(outlookCitation, item.text);
                        const sourceNumber = outlookCitation.number || outlookCitation.original_number || getOrderedInlineCitationNumbers(item.text)[0];
                        return (
                            <li key={`outlook-email-item-${itemIndex}`} className="library-rag-answer-outlook-email-item">
                                <OutlookEmailCitationCard
                                    truncateSummary={false}
                                    citation={emailCitation}
                                    sourceNumber={sourceNumber}
                                    onOpen={(source, outlookTarget) => onCitationOpen?.(source, {
                                        outlookTarget,
                                        outlookView: 'pdf',
                                    })}
                                />
                            </li>
                        );
                    })}
                </ListTag>
            );
            continue;
        }

        const heading = parseMarkdownHeading(line);
        if (heading) {
            activeHeading = heading.text;
            const HeadingTag = `h${Math.min(heading.level + 2, 6)}`;
            nodes.push(
                <HeadingTag key={`rag-answer-heading-${messageId}-${index}`} className="library-rag-answer-heading">
                    {renderCitationLinkedText(heading.text, citations, `rag-answer-heading-${messageId}-${index}`, onCitationOpen)}
                </HeadingTag>
            );
            index += 1;
            continue;
        }

        const inlineSummary = parseInlineLabeledSummary(line);
        if (inlineSummary) {
            const summaryIndex = nodes.length;
            nodes.push(
                <div key={`rag-answer-summary-list-${messageId}-${summaryIndex}`} className="library-rag-answer-summary-list">
                    <div className="library-rag-answer-summary-title">
                        {renderCitationLinkedText(inlineSummary.header, citations, `rag-answer-summary-title-${messageId}-${summaryIndex}`, onCitationOpen)}
                    </div>
                    <ul className="library-rag-answer-list">
                        {inlineSummary.items.map((item, itemIndex) => (
                            <li key={`summary-item-${itemIndex}`}>
                                <strong>{renderCitationLinkedText(`${item.label}:`, citations, `rag-answer-summary-label-${messageId}-${summaryIndex}-${itemIndex}`, onCitationOpen)}</strong>
                                {item.value ? ' ' : ''}
                                {item.value ? renderCitationLinkedText(item.value, citations, `rag-answer-summary-value-${messageId}-${summaryIndex}-${itemIndex}`, onCitationOpen) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            );
            index += 1;
            continue;
        }

        const boldLabeledLine = /^\s*\*\*/.test(line) ? parseLabeledSummaryLine(line) : null;
        if (boldLabeledLine) {
            nodes.push(renderLabeledSummaryNode(boldLabeledLine, citations, `rag-answer-labeled-line-${messageId}-${index}`, onCitationOpen));
            index += 1;
            continue;
        }

        const cleanedMarkerLine = cleanMalformedBoldMarkers(line);
        if (isStandaloneMarkdownBullet(line) || cleanedMarkerLine === '*' || cleanedMarkerLine === '') {
            index += 1;
            continue;
        }

        if (line.trim() !== '') {
            const cleanedLine = cleanedMarkerLine;
            nodes.push(
                <p key={`rag-answer-line-${messageId}-${index}`}>
                    <RagAnswerText text={cleanedLine} citations={citations} keyPrefix={`rag-answer-line-${messageId}-${index}`} onCitationOpen={onCitationOpen} />
                </p>
            );
        }
        index += 1;
    }

    flushQuoteBlock();

    return nodes;
};

export const AnimatedRagAnswerContent = ({ text, citations, messageId, onCitationOpen, answerMode = '', animate = false, complete = true, onAnimationComplete = null }) => {
    const normalizedText = String(text || '');
    const initialText = animate && normalizedText !== '' ? getNextRagStreamText('', normalizedText) : normalizedText;
    const [displayText, setDisplayText] = useState(initialText);
    const displayTextRef = useRef(initialText);
    const targetTextRef = useRef(normalizedText);
    const timerRef = useRef(null);
    const animationVersionRef = useRef(0);
    const completionNotifiedRef = useRef(false);
    const onAnimationCompleteRef = useRef(onAnimationComplete);

    useEffect(() => {
        displayTextRef.current = displayText;
    }, [displayText]);

    useEffect(() => {
        targetTextRef.current = normalizedText;
    }, [normalizedText]);

    useEffect(() => {
        onAnimationCompleteRef.current = onAnimationComplete;
    }, [onAnimationComplete]);

    useEffect(() => {
        animationVersionRef.current += 1;
        const animationVersion = animationVersionRef.current;

        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (!animate) {
            completionNotifiedRef.current = false;
            displayTextRef.current = normalizedText;
            setDisplayText(normalizedText);
            return undefined;
        }

        const syncTarget = () => {
            const targetText = targetTextRef.current;
            const currentText = displayTextRef.current;
            if (currentText === targetText) {
                if (complete && !completionNotifiedRef.current) {
                    completionNotifiedRef.current = true;
                    onAnimationCompleteRef.current?.();
                }
                timerRef.current = null;
                return;
            }

            completionNotifiedRef.current = false;

            const remainingText = targetText.startsWith(currentText) ? targetText.slice(currentText.length) : targetText;
            const nextText = getNextRagStreamText(currentText, targetText);

            displayTextRef.current = nextText;
            setDisplayText(nextText);

            if (nextText !== targetText && animationVersionRef.current === animationVersion) {
                timerRef.current = window.setTimeout(syncTarget, getRagStreamDelay(nextText, remainingText.length));
            } else {
                if (complete && !completionNotifiedRef.current) {
                    completionNotifiedRef.current = true;
                    onAnimationCompleteRef.current?.();
                }
                timerRef.current = null;
            }
        };

        timerRef.current = window.setTimeout(syncTarget, 0);

        return () => {
            animationVersionRef.current += 1;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [animate, complete, normalizedText, messageId]);

    return renderRagAnswerContent(displayText, citations, messageId, onCitationOpen, answerMode);
};

const AI = ({ user }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { dispatch } = useUploads();
    const navVariant = useNavVariant();
    const userId = getJaneUserId(user);
    const hasJaneUserIdentity = userId !== null;
    const ragMessageTimeZone = getRagDisplayTimeZone(user);

    const [files, setFiles] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, pageSize: 25, totalCount: 0 });
    const [loading, setLoading] = useState(false);
    const [ragChats, setRagChats] = useState([]);
    const [activeRagChatId, setActiveRagChatId] = useState(null);
    const [ragMessages, setRagMessages] = useState([]);
    const [janeElapsedNow, setJaneElapsedNow] = useState(() => Date.now());
    const [ragMessagesHasMoreBefore, setRagMessagesHasMoreBefore] = useState(false);
    const [ragMessagesLoadingOlder, setRagMessagesLoadingOlder] = useState(false);
    const [ragActiveRequest, setRagActiveRequest] = useState(null);
    const ragInlineRerunRef = useRef(null);
    const [ragRerunErrorByMessageId, setRagRerunErrorByMessageId] = useState({});
    const [ragCanceling, setRagCanceling] = useState(false);
    const [ragCancelError, setRagCancelError] = useState('');
    // Live progress belongs in the message, never an automatically opened aside.
    const [ragPlannerPreviewClosed, setRagPlannerPreviewClosed] = useState(true);
    const [ragIncomingAnswerMessageIds, setRagIncomingAnswerMessageIds] = useState(() => new Set());
    const [ragBackgroundDraftRuns, setRagBackgroundDraftRuns] = useState([]);
    const [ragBackgroundDraftBusyRunIds, setRagBackgroundDraftBusyRunIds] = useState(() => new Set());
    const [ragBackgroundDraftErrorByRunId, setRagBackgroundDraftErrorByRunId] = useState({});
    const [ragChatLoading, setRagChatLoading] = useState(false);
    const [ragSaving, setRagSaving] = useState(false);
    const [ragPersistenceStateByMessageId, setRagPersistenceStateByMessageId] = useState({});
    const [ragThreadAtBottom, setRagThreadAtBottom] = useState(true);
    const [ragActiveRailMessageId, setRagActiveRailMessageId] = useState(null);
    const [ragTopRailMessageId, setRagTopRailMessageId] = useState(null);
    const [ragChatSearch, setRagChatSearch] = useState('');
    const [ragModelKey, setRagModelKey] = useState('auto');
    const [janeRunMode, setJaneRunMode] = useState(() => normalizeJaneRunMode(getCookie(JANE_RUN_MODE_COOKIE) || 'default'));
    const [editingRagChatTitle, setEditingRagChatTitle] = useState(false);
    const [ragChatTitleDraft, setRagChatTitleDraft] = useState('');
    const [ragChatSettingsSaving, setRagChatSettingsSaving] = useState(false);
    const [showRagSourceHistory, setShowRagSourceHistory] = useState(false);
    const [ragResponseDetailsMessageId, setRagResponseDetailsMessageId] = useState(null);
    const [janeTraceCollapsedByMessageId, setJaneTraceCollapsedByMessageId] = useState(() => new Map());
    const [ragRunInspectorByMessageId, setRagRunInspectorByMessageId] = useState(() => new Map());
    const [ragRunInspectorLoadingMessageIds, setRagRunInspectorLoadingMessageIds] = useState(() => new Set());
    const [ragRunInspectorErrorByMessageId, setRagRunInspectorErrorByMessageId] = useState(() => new Map());
    const [ragCalendarPanelMessageId, setRagCalendarPanelMessageId] = useState(null);
    const [ragReferences, setRagReferences] = useState([]);
    const [ragReferenceDraft, setRagReferenceDraft] = useState('');
    const [ragReferenceContext, setRagReferenceContext] = useState(null);
    const [showRagDocumentPicker, setShowRagDocumentPicker] = useState(false);
    const [showRagReferenceMenu, setShowRagReferenceMenu] = useState(false);
    const [showJaneAgentLibrary, setShowJaneAgentLibrary] = useState(false);
    const [janeAgentLibraryCategory, setJaneAgentLibraryCategory] = useState('All');
    const [savedJaneAgents, setSavedJaneAgents] = useState(() => loadSavedJaneAgents());
    const [pinnedJaneAgentIds, setPinnedJaneAgentIds] = useState(() => loadPinnedJaneAgentIds());
    const [backendJaneAgents, setBackendJaneAgents] = useState([]);
    const [janeAgentLibraryLoading, setJaneAgentLibraryLoading] = useState(false);
    const [janeAgentLibraryError, setJaneAgentLibraryError] = useState('');
    const [ragPreflightExpanded, setRagPreflightExpanded] = useState(false);
    const [chunkSearchDraft, setChunkSearchDraft] = useState('');
    const [ragPromptImproving, setRagPromptImproving] = useState(false);
    const [ragPromptImproveStatus, setRagPromptImproveStatus] = useState('');
    const [ragRunMenuOpen, setRagRunMenuOpen] = useState(false);
    const [ragUsePageContext, setRagUsePageContext] = useState(true);
    const [ragUsePriorChat, setRagUsePriorChat] = useState(true);
    const [ragSelectedSourcesOnly, setRagSelectedSourcesOnly] = useState(false);
    const [chunkSearchQuery, setChunkSearchQuery] = useState('');
    const [ragSidebarCollapsed, setRagSidebarCollapsed] = useState(false);
    const [ragSidebarTextHidden, setRagSidebarTextHidden] = useState(false);
    const [ragMobileNavOpen, setRagMobileNavOpen] = useState(false);
    const [ragMobileActionChatId, setRagMobileActionChatId] = useState(null);
    const [ragMobilePreviewMessagesByChatId, setRagMobilePreviewMessagesByChatId] = useState({});
    const [ragMobilePreviewLoadingChatId, setRagMobilePreviewLoadingChatId] = useState(null);
    const [ragMobileUserActionMessageId, setRagMobileUserActionMessageId] = useState(null);
    const [isRagMobileViewport, setIsRagMobileViewport] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
    const [ragSidebarLogo, setRagSidebarLogo] = useState(getThemeSidebarLogo);
    const [ragWelcomeGreeting] = useState(() => RAG_WELCOME_GREETINGS[Math.floor(Math.random() * RAG_WELCOME_GREETINGS.length)] || RAG_WELCOME_GREETINGS[0]);
    const [copiedUserMessageId, setCopiedUserMessageId] = useState(null);
    const [editingRagMessageId, setEditingRagMessageId] = useState(null);
    const [editingRagDraft, setEditingRagDraft] = useState('');
    const [pendingRagEdit, setPendingRagEdit] = useState(null);
    const [pendingRagRedo, setPendingRagRedo] = useState(null);
    const [pendingRagChatDelete, setPendingRagChatDelete] = useState(null);
    const [ragChatDeleteBusy, setRagChatDeleteBusy] = useState(false);
    const [ragChatDeleteError, setRagChatDeleteError] = useState('');
    const [workspaceNavigationPendingMessageIds, setWorkspaceNavigationPendingMessageIds] = useState(() => new Set());
    const [confirmableActionBusyMessageIds, setConfirmableActionBusyMessageIds] = useState(() => new Set());
    const [confirmableActionDecliningMessageIds, setConfirmableActionDecliningMessageIds] = useState(() => new Set());
    const confirmableActionBusyRef = useRef(new Set());
    const [confirmableActionErrorByMessageId, setConfirmableActionErrorByMessageId] = useState({});
    const [confirmableActionEdits, setConfirmableActionEdits] = useState(() => new Map());
    const [ragFolderPickerActionMessageId, setRagFolderPickerActionMessageId] = useState(null);
    const [forkingRagMessageId, setForkingRagMessageId] = useState(null);
    const [ragForkErrorByMessageId, setRagForkErrorByMessageId] = useState({});
    const [ragBranchActionBusy, setRagBranchActionBusy] = useState(false);
    const [ragBranchActionError, setRagBranchActionError] = useState('');
    const [ragFeedbackSavingByMessageId, setRagFeedbackSavingByMessageId] = useState({});
    const [ragFeedbackStatusByMessageId, setRagFeedbackStatusByMessageId] = useState({});
    const [expandedCitationMessages, setExpandedCitationMessages] = useState(() => new Set());
    const [expandedPromptReferenceMessages, setExpandedPromptReferenceMessages] = useState(() => new Set());
    const [textViewRagComponentTypes, setTextViewRagComponentTypes] = useState(() => getSavedRagComponentTextViewTypes(getCookie));
    const [ragTablePreviewCache, setRagTablePreviewCache] = useState({});
    const [chunkPreview, setChunkPreview] = useState(null);
    const [outlookCitationReview, setOutlookCitationReview] = useState(null);
    const [timelineEvidencePreview, setTimelineEvidencePreview] = useState(null);
    const [ragDraftPreviewMessageId, setRagDraftPreviewMessageId] = useState(null);
    const [sharingRagChat, setSharingRagChat] = useState(null);
    const [shareUsers, setShareUsers] = useState([]);
    const [shareSearch, setShareSearch] = useState('');
    const [selectedShareUserIds, setSelectedShareUserIds] = useState([]);
    const [showShareUserDropdown, setShowShareUserDropdown] = useState(false);
    const [shareHighlightIndex, setShareHighlightIndex] = useState(0);
    const [shareChatStatus, setShareChatStatus] = useState('');
    const [shareChatSaving, setShareChatSaving] = useState(false);
    const [ragComposerHeight, setRagComposerHeight] = useState(56);
    const [ragActiveRequestHeight, setRagActiveRequestHeight] = useState(0);
    const [ragRenderedMessageRange, setRagRenderedMessageRange] = useState({ start: 0, end: Infinity });
    const [ragChatFilesUploading, setRagChatFilesUploading] = useState(false);
    const [ragSpeechSupported] = useState(() => typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition));
    const [ragListening, setRagListening] = useState(false);
    const [ragSpeechStatus, setRagSpeechStatus] = useState('');

    useEffect(() => {
        setOutlookCitationReview(null);
    }, [activeRagChatId]);

    useEffect(() => {
        if (!outlookCitationReview) return;
        if (
            showRagSourceHistory
            || chunkPreview
            || timelineEvidencePreview
            || ragDraftPreviewMessageId !== null
            || ragCalendarPanelMessageId !== null
            || ragResponseDetailsMessageId !== null
        ) {
            setOutlookCitationReview(null);
        }
    }, [chunkPreview, outlookCitationReview, ragCalendarPanelMessageId, ragDraftPreviewMessageId, ragResponseDetailsMessageId, showRagSourceHistory, timelineEvidencePreview]);

    const hasFinancialWorkbookInProgress = useMemo(() => ragMessages.some((message) => {
        const status = String(
            message?.answer?.financial_workbook_job?.status
            || message?.metadata?.financial_workbook_job?.status
            || ''
        ).trim().toLowerCase();
        return ['queued', 'retry', 'running', 'persisting'].includes(status);
    }), [ragMessages]);

    useEffect(() => {
        const hasRunningJanePlan = ragMessages.some((message) => {
            const status = String(message?.metadata?.runPlan?.status || message?.metadata?.intelligence?.run_plan?.status || '').trim().toLowerCase();
            return message?.pending || message?.metadata?.streaming || ['queued', 'pending', 'running', 'active', 'in_progress', 'waiting'].includes(status);
        });
        const hasRunningBackgroundPlan = ragBackgroundDraftRuns.some(run => ['running', 'active', 'in_progress'].includes(getJaneBackgroundRunStatus(run)));
        if (!hasRunningJanePlan && !hasRunningBackgroundPlan) return undefined;
        setJaneElapsedNow(Date.now());
        const intervalId = window.setInterval(() => setJaneElapsedNow(Date.now()), 1000);
        return () => window.clearInterval(intervalId);
    }, [ragMessages, ragBackgroundDraftRuns]);

    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const ragThreadRef = useRef(null);
    const ragComposerRef = useRef(null);
    const ragActiveRequestBannerRef = useRef(null);
    const ragOlderMessagesSentinelRef = useRef(null);
    const ragReferenceMenuRef = useRef(null);
    const ragThreadRailOverviewRef = useRef(null);
    const requestSequenceRef = useRef(0);
    const ragChatLoadSequenceRef = useRef(0);
    const ragForegroundChatLoadRef = useRef(null);
    const ragOlderMessagesRequestRef = useRef(null);
    const ragMessagePageCacheRef = useRef(null);
    const ragMessagePageEpochRef = useRef(0);
    const ragMessagePageActorId = getJaneUserId(user);
    const ragMessagePageActorRef = useRef(ragMessagePageActorId);
    if (ragMessagePageActorRef.current !== ragMessagePageActorId) {
        ragForegroundChatLoadRef.current = null;
        ragMessagePageCacheRef.current = null;
        ragMessagePageEpochRef.current += 1;
        ragMessagePageActorRef.current = ragMessagePageActorId;
    }
    const ragSilentReloadCoordinatorRef = useRef(null);
    if (!ragSilentReloadCoordinatorRef.current) ragSilentReloadCoordinatorRef.current = createRagSilentReloadCoordinator();
    const requestControllerRef = useRef(null);
    const ragCancelPromiseRef = useRef(null);
    const ragSessionRef = useRef(0);
    const ragRunGenerationRef = useRef(0);
    const ragRuntimeIntentRef = useRef(null);
    const activeRagChatIdRef = useRef(null);
    const ragUploadOperationRef = useRef(0);
    const ragSaveOperationRef = useRef(0);
    const ragUrlReadyRef = useRef(false);
    const ragUrlHydratingRef = useRef(false);
    const ragPreserveScrollRef = useRef(null);
    const ragSkipNextAutoScrollRef = useRef(false);
    const ragShouldAutoScrollRef = useRef(true);
    const ragLatestMessageIdRef = useRef(0);
    const ragLatestChatUpdatedAtRef = useRef('');
    const ragLocalTurnSavingRef = useRef(false);
    const ragMobileLongPressTimerRef = useRef(null);
    const ragMobileLongPressTriggeredRef = useRef(false);
    const ragMobileUserLongPressTimerRef = useRef(null);
    const ragSpeechRecognitionRef = useRef(null);
    const ragTablePreviewRequestsRef = useRef(new Set());
    const ragPendingDockPromptDraftRef = useRef(null);
    const ragPendingDockPromptSubmitRef = useRef(null);
    const ragExternalRequestPreviewSignatureRef = useRef('');
    const ragIncomingAnswerMessageIdsRef = useRef(new Set());
    const ragDeferredSavedMessagesRef = useRef(null);
    const ragPersistenceRetryRef = useRef(new Map());
    const ragRunControlBackgroundBusyRef = useRef(false);

    useEffect(() => {
        setRagMessagesLoadingOlder(false);
        return () => {
            const request = ragOlderMessagesRequestRef.current;
            request?.controller.abort();
            if (request) window.clearTimeout(request.timeoutId);
            ragOlderMessagesRequestRef.current = null;
        };
    }, [activeRagChatId, ragMessagePageActorId]);

    const getOwnedRagRuntimeIntent = (chatId = activeRagChatIdRef.current) => (
        isRagRuntimeIntentOwned(ragRuntimeIntentRef.current, {
            runGeneration: ragRunGenerationRef.current, actorId: getJaneUserId(user), chatId: Number(chatId || 0),
        }) ? ragRuntimeIntentRef.current : null
    );

    useEffect(() => {
        activeRagChatIdRef.current = activeRagChatId;
    }, [activeRagChatId]);

    const applyAcceptedRagChatTitle = (receipt, fallbackChatId = null) => {
        const update = getAcceptedRagChatTitleUpdate({ receipt, fallbackChatId });
        if (!update) return null;
        setRagChats(prev => {
            const next = reconcileAcceptedRagChatTitle(prev, update);
            return next === prev ? prev : sortRagChatsByUpdateTime(next);
        });
        if (Number(activeRagChatIdRef.current || 0) === Number(update.chatId)) {
            setRagChatTitleDraft(current => reconcileAcceptedRagChatTitleValue(current, update.title));
        }
        return update;
    };

    const invalidateRagPendingSaveState = () => {
        ragSaveOperationRef.current += 1;
        ragLocalTurnSavingRef.current = false;
        setRagSaving(false);
    };

    const clearRagPersistenceFailure = (messageId) => {
        const key = String(messageId || '');
        if (!key) return;
        ragPersistenceRetryRef.current.delete(key);
        setRagPersistenceStateByMessageId(prev => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const recordRagPersistenceFailure = (messageId, retryEntry, message = '') => {
        const key = String(messageId || '');
        if (!key) return;
        ragPersistenceRetryRef.current.set(key, retryEntry);
        setRagPersistenceStateByMessageId(prev => ({
            ...prev,
            [key]: {
                status: 'failed',
                message: String(message || 'This response is visible here but was not saved to the chat.').trim(),
            },
        }));
    };
    const captureRagChatOperationContext = (chatId = activeRagChatIdRef.current || activeRagChatId) => createRagChatOperationContext({
        chatId,
        sessionId: ragSessionRef.current,
        runGeneration: ragRunGenerationRef.current,
    });
    const isCurrentRagChatOperation = (context) => isRagChatOperationCurrent(context, {
        activeChatId: activeRagChatIdRef.current || activeRagChatId,
        sessionId: ragSessionRef.current,
        runGeneration: ragRunGenerationRef.current,
    });

    const markRagIncomingAnswerMessage = (messageId) => {
        const key = String(messageId || '');
        if (!key) return;
        if (ragIncomingAnswerMessageIdsRef.current.has(key)) return;
        const next = new Set(ragIncomingAnswerMessageIdsRef.current);
        next.add(key);
        ragIncomingAnswerMessageIdsRef.current = next;
        setRagIncomingAnswerMessageIds(next);
    };

    const clearRagIncomingAnswerMessage = (messageId) => {
        const key = String(messageId || '');
        if (!key) return;
        if (!ragIncomingAnswerMessageIdsRef.current.has(key)) return;
        const next = new Set(ragIncomingAnswerMessageIdsRef.current);
        next.delete(key);
        ragIncomingAnswerMessageIdsRef.current = next;
        setRagIncomingAnswerMessageIds(next);
        if (next.size === 0 && ragDeferredSavedMessagesRef.current) {
            const deferredSnapshot = ragDeferredSavedMessagesRef.current;
            ragDeferredSavedMessagesRef.current = null;
            const snapshotMatchesCurrentChat = Number(deferredSnapshot.chatId || 0) === Number(activeRagChatIdRef.current || 0);
            const snapshotMatchesSession = Number(deferredSnapshot.sessionId) === Number(ragSessionRef.current);
            const snapshotMatchesRun = Number(deferredSnapshot.runGeneration) === Number(ragRunGenerationRef.current);
            if (snapshotMatchesCurrentChat && snapshotMatchesSession && snapshotMatchesRun) {
                window.setTimeout(() => setRagMessages(previous => {
                    if (Number(deferredSnapshot.chatId) !== Number(activeRagChatIdRef.current)
                        || Number(deferredSnapshot.sessionId) !== Number(ragSessionRef.current)
                        || Number(deferredSnapshot.runGeneration) !== Number(ragRunGenerationRef.current)) return previous;
                    return deferredSnapshot.rerunAssistantMessageId
                        ? mergeRagRerunMessages(previous, deferredSnapshot.messages, deferredSnapshot.rerunAssistantMessageId)
                        : deferredSnapshot.messages;
                }), 0);
            }
        }
    };

    const resetRagIncomingAnswerState = () => {
        ragIncomingAnswerMessageIdsRef.current = new Set();
        ragDeferredSavedMessagesRef.current = null;
        setRagIncomingAnswerMessageIds(new Set());
    };

    useEffect(() => {
        ragIncomingAnswerMessageIdsRef.current = ragIncomingAnswerMessageIds;
    }, [ragIncomingAnswerMessageIds]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const routeDraft = location.state?.janeDockPromptDraft;
        const routeDraftChatId = Number(location.state?.janeDockPromptChatId || 0) || null;
        const rawDraft = window.sessionStorage.getItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
        if (!rawDraft && !String(routeDraft || '').trim()) return;

        try {
            const parsedDraft = rawDraft ? JSON.parse(rawDraft) : {};
            const draftText = String(parsedDraft?.draft || routeDraft || '').trim();
            const draftChatId = Number(parsedDraft?.chatId || routeDraftChatId || 0) || null;
            const draftCreatedAt = Number(parsedDraft?.createdAt || 0) || 0;
            const draftKey = `${draftChatId || 'chat'}:${draftCreatedAt || 'time'}:${draftText}`;
            const consumedDraftKey = window.sessionStorage.getItem(JANE_DOCK_PROMPT_CONSUMED_STORAGE_KEY);
            if (consumedDraftKey && consumedDraftKey === draftKey) {
                window.sessionStorage.removeItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
                ragPendingDockPromptDraftRef.current = null;
                ragPendingDockPromptSubmitRef.current = null;
                return;
            }
            if (!draftText) {
                window.sessionStorage.removeItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
                ragPendingDockPromptDraftRef.current = null;
                ragPendingDockPromptSubmitRef.current = null;
                return;
            }

            ragPendingDockPromptDraftRef.current = { draftText, chatId: draftChatId };
            ragPendingDockPromptSubmitRef.current = { draftText, chatId: draftChatId, draftKey, submitted: false };
            const urlChatId = getRagChatIdFromUrl();
            if (draftChatId && urlChatId && String(draftChatId) !== String(urlChatId)) {
                return;
            }

            if (!chunkSearchDraft) {
                setChunkSearchDraft(draftText);
                window.requestAnimationFrame(() => inputRef.current?.focus());
            }

            window.sessionStorage.removeItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
        } catch (error) {
            window.sessionStorage.removeItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
            ragPendingDockPromptDraftRef.current = null;
            ragPendingDockPromptSubmitRef.current = null;
        }
    }, [chunkSearchDraft, location.pathname, location.state]);

    const getRagChatIdFromUrl = () => {
        if (typeof window === 'undefined') return null;
        const params = new URLSearchParams(window.location.search);
        const rawChatId = params.get('chat_id') || params.get('rag_chat_id');
        const chatId = Number(rawChatId);
        return Number.isFinite(chatId) && chatId > 0 ? String(Math.trunc(chatId)) : null;
    };

    const isJaneAgentLibraryUrl = () => {
        if (typeof window === 'undefined') return false;
        return /\/ai\/library\/?$/i.test(window.location.pathname) || new URLSearchParams(window.location.search).has('library');
    };

    const getCurrentPageCaseReference = () => {
        if (typeof window === 'undefined') return null;
        const pathMatch = window.location.pathname.match(/\/case\/(\d+)/i);
        const params = new URLSearchParams(window.location.search);
        const rawCaseId = pathMatch?.[1] || params.get('case_id') || '';
        const caseId = Number(rawCaseId);
        if (!Number.isFinite(caseId) || caseId <= 0) return null;
        const normalizedCaseId = Math.trunc(caseId);
        return {
            id: `page-case-${normalizedCaseId}`,
            type: 'case',
            label: `Current page case #${normalizedCaseId}`,
            case_id: normalizedCaseId,
            source: 'page_context'
        };
    };

    const syncRagChatUrl = (chatId) => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const normalizedChatId = Number(chatId);

        if (Number.isFinite(normalizedChatId) && normalizedChatId > 0) {
            params.set('chat_id', String(Math.trunc(normalizedChatId)));
        } else {
            params.delete('chat_id');
        }
        params.delete('rag_chat_id');
        params.delete('library');

        const nextSearch = params.toString();
        const currentSearch = window.location.search.replace(/^\?/, '');
        if (nextSearch === currentSearch) return;

        const nextPathname = window.location.pathname.replace(/\/library\/?$/i, '') || '/ai';
        navigate({
            pathname: nextPathname,
            search: nextSearch ? `?${nextSearch}` : '',
            hash: window.location.hash,
        }, { replace: true });
    };

    const syncJaneAgentLibraryUrl = () => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        params.delete('chat_id');
        params.delete('rag_chat_id');
        params.delete('library');
        const nextSearch = params.toString();
        const basePathname = window.location.pathname.replace(/\/library\/?$/i, '') || '/ai';
        const nextPathname = `${basePathname.replace(/\/$/u, '')}/library`;
        const currentSearch = window.location.search.replace(/^\?/, '');
        if (nextSearch === currentSearch && window.location.pathname === nextPathname) return;
        navigate({
            pathname: nextPathname,
            search: nextSearch ? `?${nextSearch}` : '',
            hash: window.location.hash,
        }, { replace: true });
    };

    const isRagThreadScrolledToBottom = (thread) => {
        if (!thread) return true;
        return thread.scrollHeight - thread.scrollTop - thread.clientHeight <= 24;
    };

    const updateRagThreadBottomState = () => {
        const atBottom = isRagThreadScrolledToBottom(ragThreadRef.current);
        ragShouldAutoScrollRef.current = atBottom;
        setRagThreadAtBottom(atBottom);
    };

    const markRagThreadShouldAutoScroll = () => {
        ragShouldAutoScrollRef.current = true;
        setRagThreadAtBottom(true);
    };

    const scrollRagThreadToBottom = () => {
        const thread = ragThreadRef.current;
        if (!thread) return;
        thread.scrollTo({ top: thread.scrollHeight, behavior: 'smooth' });
        ragShouldAutoScrollRef.current = true;
        setRagThreadAtBottom(true);
    };

    const forceRagThreadToBottom = (behavior = 'auto') => {
        const thread = ragThreadRef.current;
        if (!thread) return;
        const top = thread.scrollHeight;
        if (behavior === 'smooth') {
            thread.scrollTo({ top, behavior: 'smooth' });
        } else {
            thread.scrollTop = top;
        }
        ragShouldAutoScrollRef.current = true;
        setRagThreadAtBottom(true);
    };

    const scheduleRagThreadToBottom = (frames = 3) => {
        ragShouldAutoScrollRef.current = true;
        setRagThreadAtBottom(true);
        const run = (remaining) => {
            window.requestAnimationFrame(() => {
                forceRagThreadToBottom('auto');
                if (remaining > 0) run(remaining - 1);
            });
        };
        run(Math.max(0, frames));
    };

    const updateRagRenderedMessageRange = () => {
        const messageCount = ragMessages.length;
        if (messageCount <= RAG_MESSAGE_VIRTUALIZE_THRESHOLD) {
            setRagRenderedMessageRange({ start: 0, end: Infinity });
            return;
        }

        const thread = ragThreadRef.current;
        if (!thread) return;

        const estimatedHeight = RAG_MESSAGE_VIRTUALIZE_ESTIMATED_HEIGHT;
        const overscan = RAG_MESSAGE_VIRTUALIZE_OVERSCAN_ROWS;
        const viewportStart = Math.max(0, Math.floor(thread.scrollTop / estimatedHeight));
        const viewportRows = Math.max(1, Math.ceil(thread.clientHeight / estimatedHeight));
        const nextStart = Math.max(0, viewportStart - overscan);
        const nextEnd = Math.min(messageCount - 1, viewportStart + viewportRows + overscan);

        setRagRenderedMessageRange((prev) => (
            prev.start === nextStart && prev.end === nextEnd
                ? prev
                : { start: nextStart, end: nextEnd }
        ));
    };

    const updateRagTopRailMessage = () => {
        const thread = ragThreadRef.current;
        if (!thread) return;
        const userMessages = Array.from(thread.querySelectorAll('.library-rag-message.user[data-rag-message-id]'));
        if (userMessages.length === 0) {
            setRagTopRailMessageId(null);
            return;
        }
        const screenMidpoint = window.innerHeight / 2;
        const currentMessage = userMessages.reduce((best, element) => {
            const box = element.getBoundingClientRect();
            const distance = Math.abs((box.top + (box.height / 2)) - screenMidpoint);
            if (!best || distance < best.distance) {
                return { element, distance };
            }
            return best;
        }, null);
        const nextMessageId = currentMessage?.element?.getAttribute('data-rag-message-id') || null;
        setRagTopRailMessageId(nextMessageId);
    };

    const getRagRailMessagePreview = (message) => String(message?.content || '')
        .replace(/https?:\/\/(?:www\.)?casedb\.co\/(?:case|file|lead|document)\/\d+/gi, '')
        .replace(/https?:\/\/(?:www\.)?casedb\.co\/reports\?[^\s]+/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

    const ragUserMessageRailItems = ragMessages
        .filter((message) => (
            message.role === 'user'
            && !isJaneBackgroundHandoffShadowPrompt(message)
            && getRagRailMessagePreview(message) !== ''
        ))
        .map((message, index) => ({
            id: String(message.id),
            label: getRagRailMessagePreview(message),
            index,
        }));

    const scrollRagRailOverviewToCurrent = () => {
        const overview = ragThreadRailOverviewRef.current;
        if (!overview) return;
        const currentId = String(ragTopRailMessageId || ragActiveRailMessageId || '');
        const currentItem = currentId
            ? overview.querySelector(`[data-rag-rail-message-id="${CSS.escape(currentId)}"]`)
            : overview.querySelector('.library-rag-thread-rail-overview-item.current, .library-rag-thread-rail-overview-item.active');
        if (currentItem) currentItem.scrollIntoView({ block: 'nearest' });
    };

    const handleRagRailJump = (messageId) => {
        const thread = ragThreadRef.current;
        if (!thread) return;
        const target = thread.querySelector(`[data-rag-message-id="${CSS.escape(String(messageId))}"]`);
        if (!target) return;
        setRagActiveRailMessageId(String(messageId));
        setRagTopRailMessageId(String(messageId));
        ragShouldAutoScrollRef.current = false;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        setRagActiveRailMessageId(null);
        setRagTopRailMessageId(null);
    }, [activeRagChatId, ragMessages.length]);

    useEffect(() => {
        document.title = 'Jane AI | CaseDB';
        const initialRagChatId = getRagChatIdFromUrl();
        loadRagChats();
        if (initialRagChatId) {
            ragUrlHydratingRef.current = true;
            loadRagChat(initialRagChatId).finally(() => {
                ragUrlHydratingRef.current = false;
                ragUrlReadyRef.current = true;
            });
        } else {
            ragUrlReadyRef.current = true;
        }
        const refreshSidebarLogo = () => setRagSidebarLogo(getThemeSidebarLogo());
        const themeObserver = new MutationObserver(refreshSidebarLogo);
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        window.addEventListener('focus', refreshSidebarLogo);
        window.addEventListener('storage', refreshSidebarLogo);
        const mobileViewportQuery = window.matchMedia('(max-width: 900px)');
        const handleMobileViewportChange = (event) => setIsRagMobileViewport(event.matches);
        setIsRagMobileViewport(mobileViewportQuery.matches);
        if (mobileViewportQuery.addEventListener) {
            mobileViewportQuery.addEventListener('change', handleMobileViewportChange);
        } else if (mobileViewportQuery.addListener) {
            mobileViewportQuery.addListener(handleMobileViewportChange);
        }
        return () => {
            if (requestControllerRef.current) {
                requestControllerRef.current.abort();
            }
            requestSequenceRef.current += 1;
            ragRunGenerationRef.current += 1;
            ragRuntimeIntentRef.current = null;
            if (ragSpeechRecognitionRef.current) {
                try { ragSpeechRecognitionRef.current.stop(); } catch (error) {}
                ragSpeechRecognitionRef.current = null;
            }
            themeObserver.disconnect();
            window.removeEventListener('focus', refreshSidebarLogo);
            window.removeEventListener('storage', refreshSidebarLogo);
            if (mobileViewportQuery.removeEventListener) {
                mobileViewportQuery.removeEventListener('change', handleMobileViewportChange);
            } else if (mobileViewportQuery.removeListener) {
                mobileViewportQuery.removeListener(handleMobileViewportChange);
            }
        };
    }, []);

    useEffect(() => {
        if (!ragUrlReadyRef.current || ragUrlHydratingRef.current) return;
        if (activeRagChatId) {
            syncRagChatUrl(activeRagChatId);
        } else if (!getRagChatIdFromUrl()) {
            syncRagChatUrl(null);
        }
    }, [activeRagChatId]);

    useEffect(() => {
        const targetMap = new Map();
        ragMessages
            .flatMap((message, messageIndex) => {
                const targetsForMessage = [];
                getReportPreviewTargetsFromMessage(message).forEach((target) => targetsForMessage.push(target));
                getReportPreviewTargetsFromReferences(getMessagePromptReferences(message, ragMessages[messageIndex + 1]))
                    .forEach((target) => targetsForMessage.push(target));
                return targetsForMessage;
            })
            .filter(Boolean)
            .forEach((target) => {
                if (!targetMap.has(target.key)) targetMap.set(target.key, target);
            });
        const targets = Array.from(targetMap.values())
            .filter((target) => !ragTablePreviewCache[target.key] && !ragTablePreviewRequestsRef.current.has(target.key));

        if (targets.length === 0) return;

        targets.forEach((target) => ragTablePreviewRequestsRef.current.add(target.key));
        setRagTablePreviewCache(prev => {
            const next = { ...prev };
            targets.forEach((target) => {
                next[target.key] = buildReportPreviewLoadingState(target);
            });
            return next;
        });

        targets.forEach(async (target) => {
            try {
                const response = await fetch(`${API_BASE}/reports.php`, {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    credentials: 'include',
                    body: JSON.stringify(buildReportPreviewRequestPayload(target, getNativeTablePreviewRowLimit())),
                });
                const data = await response.json().catch(() => ({}));

                if (!response.ok || !data?.success) throw new Error('Could not load report preview.');
                setRagTablePreviewCache(prev => ({
                    ...prev,
                    [target.key]: buildReportPreviewReadyState(target, data),
                }));
            } catch (error) {
                setRagTablePreviewCache(prev => ({
                    ...prev,
                    [target.key]: buildReportPreviewErrorState(target, error),
                }));
            } finally {
                ragTablePreviewRequestsRef.current.delete(target.key);
            }
        });
    }, [ragMessages, ragTablePreviewCache]);

    useEffect(() => {
        const thread = ragThreadRef.current;
        if (!thread) return;

        const handleScroll = () => {
            updateRagThreadBottomState();
            updateRagTopRailMessage();
            updateRagRenderedMessageRange();
        };
        handleScroll();
        thread.addEventListener('scroll', handleScroll, { passive: true });
        return () => thread.removeEventListener('scroll', handleScroll);
    }, [activeRagChatId]);

    useEffect(() => {
        updateRagRenderedMessageRange();
    }, [ragMessages.length, ragChatLoading]);

    useEffect(() => {
        window.requestAnimationFrame(updateRagTopRailMessage);
    }, [ragMessages]);

    useLayoutEffect(() => {
        const thread = ragThreadRef.current;
        if (!thread) return;

        if (ragSkipNextAutoScrollRef.current) {
            const previousScroll = ragPreserveScrollRef.current;
            ragSkipNextAutoScrollRef.current = false;
            ragPreserveScrollRef.current = null;
            if (previousScroll) {
                thread.scrollTop = thread.scrollHeight - previousScroll.scrollHeight + previousScroll.scrollTop;
            }
            updateRagThreadBottomState();
            return;
        }

        if (!ragShouldAutoScrollRef.current) {
            setRagThreadAtBottom(isRagThreadScrolledToBottom(thread));
            return;
        }

        thread.scrollTop = thread.scrollHeight;
        ragShouldAutoScrollRef.current = true;
        setRagThreadAtBottom(true);
    }, [ragMessages, loading]);

    useEffect(() => {
        const root = ragThreadRef.current;
        const sentinel = ragOlderMessagesSentinelRef.current;
        if (!root || !sentinel || !activeRagChatId || !ragMessagesHasMoreBefore || ragMessagesLoadingOlder || ragChatLoading) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) loadOlderRagMessages();
        }, { root, rootMargin: '160px 0px 0px 0px', threshold: 0.01 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [activeRagChatId, ragMessagesHasMoreBefore, ragMessagesLoadingOlder, ragChatLoading, ragMessages]);

    useEffect(() => {
        ragLatestMessageIdRef.current = Math.max(0, ...ragMessages.map(message => Number(message.id)).filter(id => Number.isFinite(id)));
    }, [ragMessages]);

    const getExternalRagRequestMessageIds = (activeRequest) => {
        const rawKey = String(activeRequest?.request_token || `${activeRequest?.chat_id || activeRagChatId || 'chat'}-${activeRequest?.started_at || 'active'}`);
        const safeKey = rawKey.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 96) || 'active';
        return {
            userMessageId: `external-rag-user-${safeKey}`,
            assistantMessageId: `external-rag-assistant-${safeKey}`,
        };
    };

    const getExternalRagRequestPreviewSignature = (activeRequest) => [
        activeRequest?.request_token || '',
        activeRequest?.chat_id || '',
        activeRequest?.status || '',
        activeRequest?.status_message || '',
        activeRequest?.answer_preview || '',
        activeRequest?.query_text || '',
        activeRequest?.user_id || '',
        activeRequest?.user_name || '',
        activeRequest?.started_at || '',
    ].map(value => String(value).trim()).join('|');

    const isSameRagActiveRequest = (left, right) => getExternalRagRequestPreviewSignature(left) === getExternalRagRequestPreviewSignature(right);

    const syncExternalRagRequestPreview = (activeRequest) => {
        if (!activeRequest) {
            clearExternalRagRequestPreview();
            return;
        }
        const nextSignature = getExternalRagRequestPreviewSignature(activeRequest);
        const { userMessageId, assistantMessageId } = getExternalRagRequestMessageIds(activeRequest);
        const queryText = String(activeRequest.query_text || '').trim();
        const isLegalDraftActiveRequest = isLegalDraftPrompt(queryText);
        const statusText = sanitizeJaneStatusMessage(activeRequest.status_message);
        const previewText = isLegalDraftActiveRequest ? '' : sanitizeJaneStatusMessage(activeRequest.answer_preview);
        const assistantText = previewText || statusText;
        const createdAt = activeRequest.started_at || new Date().toISOString();
        const updatedAt = activeRequest.updated_at || new Date().toISOString();

        const externalUserMessage = queryText ? {
            id: userMessageId,
            role: 'user',
            content: queryText,
            author_name: activeRequest.user_name || activeRequest.name || activeRequest.user || '',
            user_id: activeRequest.user_id || null,
            externalRequest: true,
            pending: false,
            created_at: createdAt,
            metadata: { external_generation: true, active_request: activeRequest }
        } : null;
        const externalAssistantMessage = assistantText ? {
            id: assistantMessageId,
            role: 'assistant',
            content: assistantText,
            externalRequest: true,
            pending: false,
            created_at: updatedAt,
            answer: { answer_text: assistantText, citations: [], source_descriptions: [] },
            sources: [],
            metadata: { external_generation: true, active_request: activeRequest }
        } : null;

        setRagMessages(prev => {
            const durableRequestMessageExists = prev.some(message => (
                !message.externalRequest
                && isRagMessageBoundToActiveRequest(message, activeRequest)
            ));
            if (durableRequestMessageExists) {
                ragExternalRequestPreviewSignatureRef.current = '';
                return prev.some(message => message.externalRequest)
                    ? prev.filter(message => !message.externalRequest)
                    : prev;
            }
            if (nextSignature === ragExternalRequestPreviewSignatureRef.current) return prev;
            ragExternalRequestPreviewSignatureRef.current = nextSignature;
            const withoutExternal = prev.filter(message => !message.externalRequest);
            if (!externalUserMessage && !externalAssistantMessage && withoutExternal.length === prev.length) {
                return prev;
            }
            const nextMessages = withoutExternal.length === prev.length ? [...withoutExternal] : withoutExternal;
            if (externalUserMessage) nextMessages.push(externalUserMessage);
            if (externalAssistantMessage) nextMessages.push(externalAssistantMessage);
            return nextMessages;
        });
    };

    const clearExternalRagRequestPreview = () => {
        if (!ragExternalRequestPreviewSignatureRef.current) {
            return;
        }
        ragExternalRequestPreviewSignatureRef.current = '';
        setRagMessages(prev => {
            if (!prev.some(message => message.externalRequest)) return prev;
            return prev.filter(message => !message.externalRequest);
        });
    };

    useEffect(() => {
        const composer = ragComposerRef.current;
        if (!composer || typeof ResizeObserver === 'undefined') return;

        const updateComposerHeight = () => {
            setRagComposerHeight(Math.ceil(composer.getBoundingClientRect().height || 56));
            if (ragShouldAutoScrollRef.current) {
                scheduleRagThreadToBottom(1);
            } else {
                window.requestAnimationFrame(updateRagThreadBottomState);
            }
        };
        updateComposerHeight();
        const observer = new ResizeObserver(updateComposerHeight);
        observer.observe(composer);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const banner = ragActiveRequestBannerRef.current;
        if (!banner) {
            setRagActiveRequestHeight(0);
            return;
        }

        const updateBannerHeight = () => setRagActiveRequestHeight(Math.ceil(banner.getBoundingClientRect().height || 0));
        updateBannerHeight();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(updateBannerHeight);
        observer.observe(banner);
        return () => observer.disconnect();
    }, [ragActiveRequest, loading, ragCanceling, ragCancelError]);

    useEffect(() => {
        if (!activeRagChatId || ragChatLoading) return;

        let cancelled = false;
        let statusPollInFlight = false;
        let statusPollController = null;
        const statusPollSessionId = ragSessionRef.current;
        const statusPollRunGeneration = ragRunGenerationRef.current;
        const pollChatStatus = async () => {
            if (cancelled || statusPollInFlight) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && !ragActiveRequest) return;
            statusPollInFlight = true;
            const controller = new AbortController();
            statusPollController = controller;
            const timeoutId = window.setTimeout(() => controller.abort(), RAG_CHAT_LOAD_TIMEOUT_MS);
            try {
                const params = new URLSearchParams({ action: 'status', chat_id: String(activeRagChatId) });
                const response = await fetch(`${API_BASE}/library-rag-chat.php?${params.toString()}`, { credentials: 'include', signal: controller.signal });
                const data = await response.json();
                if (cancelled || controller.signal.aborted
                    || statusPollSessionId !== ragSessionRef.current
                    || !isRagRunGenerationCurrent(statusPollRunGeneration, ragRunGenerationRef.current)
                    || !data.success) return;
                const activeRequest = data.active_request || null;
                const hadActiveRequest = !!ragActiveRequest;
                setRagActiveRequest(prev => (isSameRagActiveRequest(prev, activeRequest) ? prev : activeRequest));
                if (!activeRequest && !getOwnedRagRuntimeIntent()) setRagCancelError('');
                if (activeRequest && !loading && !ragLocalTurnSavingRef.current) {
                    syncExternalRagRequestPreview(activeRequest);
                } else if (!activeRequest) {
                    clearExternalRagRequestPreview();
                }
                const latestServerMessageId = Number(data.latest_message_id || 0);
                const latestLoadedMessageId = ragLatestMessageIdRef.current;
                const localTurnSaving = ragLocalTurnSavingRef.current;
                const serverChatUpdatedAt = String(data.chat?.updated_at || data.chat?.last_message_at || data.chat?.created_at || '');
                const chatChangedInPlace = serverChatUpdatedAt !== ''
                    && ragLatestChatUpdatedAtRef.current !== ''
                    && serverChatUpdatedAt !== ragLatestChatUpdatedAtRef.current;
                if (!activeRequest && latestServerMessageId > 0 && !localTurnSaving) setRagSaving(false);
                if (!activeRequest && hadActiveRequest && !loading && !localTurnSaving) {
                    loadRagChat(activeRagChatId, { silent: true });
                    return;
                }
                const shouldReloadForMessageChange = !loading && !localTurnSaving && latestServerMessageId > latestLoadedMessageId;
                const shouldReloadForInPlaceUpdate = !loading && !localTurnSaving && chatChangedInPlace;
                if (shouldReloadForMessageChange || shouldReloadForInPlaceUpdate) {
                    if (!activeRequest) loadRagChat(activeRagChatId, { silent: true });
                }
            } catch (error) {
                if (!cancelled && !controller.signal.aborted) console.error('Error polling RAG chat status:', error);
            } finally {
                window.clearTimeout(timeoutId);
                if (statusPollController === controller) statusPollController = null;
                statusPollInFlight = false;
            }
        };

        pollChatStatus();
        const intervalId = window.setInterval(pollChatStatus, ragActiveRequest || hasFinancialWorkbookInProgress ? 2500 : 10000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            statusPollController?.abort();
        };
    }, [activeRagChatId, ragChatLoading, loading, ragActiveRequest, hasFinancialWorkbookInProgress]);

    const ragReferenceBrowsingActive = showRagReferenceMenu || showRagDocumentPicker;

    const maybeReloadRagChatForBackgroundOutput = (runs = []) => {
        if (!activeRagChatId || loading || ragLocalTurnSavingRef.current) return;
        if (shouldReloadJaneBackgroundOutput(runs, ragMessages, activeRagChatId, ragLatestMessageIdRef.current)) {
            loadRagChat(activeRagChatId, { silent: true });
        }
    };

    // Project only reconciled snapshots so stale polls cannot revive a
    // terminal message or overwrite progress with an older worker plan.
    useEffect(() => {
        setRagMessages(current => projectJaneBackgroundRunsIntoMessages(current, ragBackgroundDraftRuns));
    }, [ragBackgroundDraftRuns, ragMessages]);

    useEffect(() => {
        maybeReloadRagChatForBackgroundOutput(ragBackgroundDraftRuns);
    }, [ragBackgroundDraftRuns]);

    useEffect(() => {
        let cancelled = false;
        let backgroundRunsPollInFlight = false;
        let backgroundRunsPollController = null;
        const loadBackgroundDraftRuns = async () => {
            if (cancelled || backgroundRunsPollInFlight) return;
            if (ragReferenceBrowsingActive) return;
            backgroundRunsPollInFlight = true;
            const controller = new AbortController();
            backgroundRunsPollController = controller;
            const timeoutId = window.setTimeout(() => controller.abort(), RAG_CHAT_LOAD_TIMEOUT_MS);
            try {
                const activeParams = new URLSearchParams({ limit: '80', status: 'active', view: 'progress' });
                const completedParams = new URLSearchParams({ limit: '40', status: 'completed', view: 'progress' });
                const [activeResponse, completedResponse] = await Promise.all([
                    fetch(`${API_BASE}/proactive-ai-tasks.php?${activeParams.toString()}`, { credentials: 'include', signal: controller.signal }),
                    fetch(`${API_BASE}/proactive-ai-tasks.php?${completedParams.toString()}`, { credentials: 'include', signal: controller.signal }),
                ]);
                const activePayload = await activeResponse.json().catch(() => ({}));
                const completedPayload = await completedResponse.json().catch(() => ({}));
                if (cancelled || controller.signal.aborted || !activeResponse.ok || !activePayload?.success) return;
                const taskList = [
                    ...(Array.isArray(activePayload.tasks) ? activePayload.tasks : []),
                    ...(completedResponse.ok && completedPayload?.success && Array.isArray(completedPayload.tasks) ? completedPayload.tasks : []),
                ];
                const runs = [];
                taskList.forEach((task) => {
                    if (task?.task_type === 'chat_reminder' && ['active', 'completed'].includes(String(task?.status || ''))) {
                        const latestRun = Array.isArray(task.runs) ? task.runs[0] : null;
                        const completedRecently = task.status === 'completed' && latestRun && ['completed', 'failed'].includes(String(latestRun.status || '')) && (Date.now() - new Date(latestRun.finished_at || latestRun.updated_at || task.updated_at || task.last_completed_at || task.created_at || Date.now()).getTime()) < 30 * 60 * 1000;
                        if (task.status === 'active' || completedRecently) {
                            runs.push({ id: `task-${task.id}`, task_id: task.id, status: task.status === 'active' ? 'scheduled' : String(latestRun?.status || 'completed'), event_type: 'chat_reminder', task, metadata: task.metadata || {}, run: latestRun, next_run_at: task.next_run_at });
                        }
                        return;
                    }
                    if (['draft_background_job', 'case_timeline_build', 'jane_background_run', 'case_trial_analysis'].includes(String(task?.task_type || '')) && Number(task?.chat_id || 0) === Number(activeRagChatId || 0)) {
                        (Array.isArray(task.runs) ? task.runs : []).forEach((run) => {
                            const status = String(run?.status || '').trim();
                            if (!['queued', 'running', 'awaiting_permission', 'completed', 'failed'].includes(status)) return;
                            runs.push({ ...run, event_type: String(task?.task_type || 'background_job'), task });
                        });
                    }
                });
                const uniqueRuns = dedupeJaneBackgroundRuns(runs);
                const statusOrder = { awaiting_permission: 0, scheduled: 1, running: 2, queued: 3, failed: 4, completed: 5 };
                uniqueRuns.sort((left, right) => (statusOrder[left.status] ?? 9) - (statusOrder[right.status] ?? 9) || Number(right.task_id || right.id || 0) - Number(left.task_id || left.id || 0));
                setRagBackgroundDraftRuns((previousRuns) => {
                    const reconciledRuns = reconcileJaneBackgroundRunPoll(previousRuns, uniqueRuns, {
                        activeChatId: activeRagChatId,
                    });
                    reconciledRuns.sort((left, right) => (statusOrder[left.status] ?? 9) - (statusOrder[right.status] ?? 9) || Number(right.task_id || right.id || 0) - Number(left.task_id || left.id || 0));
                    return limitJaneBackgroundRunHistory(reconciledRuns);
                });
            } catch (error) {
                if (!cancelled && !controller.signal.aborted) console.error('Error loading background draft runs:', error);
            } finally {
                window.clearTimeout(timeoutId);
                // Promise.all can reject while the other compact read is pending.
                controller.abort();
                if (backgroundRunsPollController === controller) backgroundRunsPollController = null;
                backgroundRunsPollInFlight = false;
            }
        };

        loadBackgroundDraftRuns();
        const intervalId = window.setInterval(loadBackgroundDraftRuns, 5000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            backgroundRunsPollController?.abort();
        };
    }, [activeRagChatId, ragReferenceBrowsingActive]);

    const getBackgroundDraftRunBusyKey = (runId) => String(runId || '');

    const setBackgroundDraftRunBusy = (runId, busy) => {
        const busyKey = getBackgroundDraftRunBusyKey(runId);
        setRagBackgroundDraftBusyRunIds(prev => {
            const next = new Set(prev);
            if (busy) {
                next.add(busyKey);
            } else {
                next.delete(busyKey);
            }
            return next;
        });
    };

    const refreshBackgroundDraftRuns = async () => {
        const refreshChatId = Number(activeRagChatId || 0);
        try {
            const activeParams = new URLSearchParams({ limit: '80', status: 'active', view: 'progress' });
            const completedParams = new URLSearchParams({ limit: '40', status: 'completed', view: 'progress' });
            const [activeResponse, completedResponse] = await Promise.all([
                fetch(`${API_BASE}/proactive-ai-tasks.php?${activeParams.toString()}`, { credentials: 'include' }),
                fetch(`${API_BASE}/proactive-ai-tasks.php?${completedParams.toString()}`, { credentials: 'include' }),
            ]);
            const activePayload = await activeResponse.json().catch(() => ({}));
            const completedPayload = await completedResponse.json().catch(() => ({}));
            if (refreshChatId !== Number(activeRagChatIdRef.current || 0)
                || !activeResponse.ok || !activePayload?.success) return;
            const taskList = [
                ...(Array.isArray(activePayload.tasks) ? activePayload.tasks : []),
                ...(completedResponse.ok && completedPayload?.success && Array.isArray(completedPayload.tasks) ? completedPayload.tasks : []),
            ];
            const runs = [];
            taskList.forEach((task) => {
                if (task?.task_type === 'chat_reminder' && ['active', 'completed'].includes(String(task?.status || ''))) {
                    const latestRun = Array.isArray(task.runs) ? task.runs[0] : null;
                    const completedRecently = task.status === 'completed' && latestRun && ['completed', 'failed'].includes(String(latestRun.status || '')) && (Date.now() - new Date(latestRun.finished_at || latestRun.updated_at || task.updated_at || task.last_completed_at || task.created_at || Date.now()).getTime()) < 30 * 60 * 1000;
                    if (task.status === 'active' || completedRecently) {
                        runs.push({ id: `task-${task.id}`, task_id: task.id, status: task.status === 'active' ? 'scheduled' : String(latestRun?.status || 'completed'), event_type: 'chat_reminder', task, metadata: task.metadata || {}, run: latestRun, next_run_at: task.next_run_at });
                    }
                    return;
                }
                if (['draft_background_job', 'case_timeline_build', 'jane_background_run', 'case_trial_analysis'].includes(String(task?.task_type || '')) && Number(task?.chat_id || 0) === Number(activeRagChatId || 0)) {
                    (Array.isArray(task.runs) ? task.runs : []).forEach((run) => {
                        if (['queued', 'running', 'awaiting_permission', 'completed', 'failed'].includes(String(run?.status || '').trim())) {
                            runs.push({ ...run, event_type: String(task?.task_type || 'background_job'), task });
                        }
                    });
                }
            });
            const uniqueRuns = dedupeJaneBackgroundRuns(runs);
            const statusOrder = { awaiting_permission: 0, scheduled: 1, running: 2, queued: 3, failed: 4, completed: 5 };
            uniqueRuns.sort((left, right) => (statusOrder[left.status] ?? 9) - (statusOrder[right.status] ?? 9) || Number(right.task_id || right.id || 0) - Number(left.task_id || left.id || 0));
            setRagBackgroundDraftRuns((previousRuns) => {
                const reconciledRuns = reconcileJaneBackgroundRunPoll(previousRuns, uniqueRuns, {
                    activeChatId: activeRagChatId,
                });
                reconciledRuns.sort((left, right) => (statusOrder[left.status] ?? 9) - (statusOrder[right.status] ?? 9) || Number(right.task_id || right.id || 0) - Number(left.task_id || left.id || 0));
                return limitJaneBackgroundRunHistory(reconciledRuns);
            });
        } catch (error) {
            console.error('Error refreshing background draft runs:', error);
        }
    };

    const buildSavedBackgroundDraftMessageUpdate = (message, run, payload) => {
        const file = payload?.file || run?.metadata?.generated_file || null;
        const runId = Number(run?.id || payload?.run_id || 0);
        const fileName = String(file?.file_name || file?.name || 'draft document').trim();
        const folderLabel = String(file?.display_folder_path || file?.folder_path || 'the case draft folder').trim();
        const formatInlineLabel = (value) => {
            const text = String(value || '').trim();
            return text ? `\`${text.replace(/`/g, "'")}\`` : '';
        };
        const savedText = `Saved ${formatInlineLabel(fileName)} to ${formatInlineLabel(folderLabel)}.`;
        const existingAnswer = message?.answer || {};
        const existingMetadata = message?.metadata || {};
        const existingIntelligence = existingMetadata.intelligence || {};
        const existingProactive = existingIntelligence.proactive_ai || existingMetadata.proactive_ai || existingAnswer.proactive_ai || {};
        const nextProactive = {
            ...existingProactive,
            run_id: runId || existingProactive.run_id,
            task_id: Number(run?.task_id || existingProactive.task_id || 0) || existingProactive.task_id,
            task_type: existingProactive.task_type || 'draft_background_job',
            status: 'completed',
            status_message: run?.status_message || 'Draft saved to CaseDB files.',
            saved_file: file,
            saved_at: new Date().toISOString(),
        };
        const nextAnswer = {
            ...existingAnswer,
            mode: 'background_draft_saved',
            answer_text: savedText,
            summary: 'Background draft saved.',
            generated_file: file,
            proactive_ai: nextProactive,
        };
        const nextMetadata = {
            ...existingMetadata,
            intelligence: {
                ...existingIntelligence,
                answer: nextAnswer,
                proactive_ai: nextProactive,
            },
            proactive_ai: nextProactive,
        };
        const existingFiles = Array.isArray(message?.files) ? message.files : [];
        const nextFiles = file
            ? [...existingFiles.filter(existingFile => Number(existingFile?.file_id || existingFile?.id || 0) !== Number(file?.file_id || file?.id || 0)), file]
            : existingFiles;
        return { content: savedText, answer: nextAnswer, metadata: nextMetadata, files: nextFiles };
    };

    const handleSaveBackgroundDraftRun = async (run) => {
        if (!hasJaneUserIdentity) return;
        const runId = Number(run?.id || 0);
        if (!runId) return;
        setBackgroundDraftRunBusy(runId, true);
        setRagBackgroundDraftErrorByRunId(prev => {
            if (!prev[String(runId)]) return prev;
            const next = { ...prev };
            delete next[String(runId)];
            return next;
        });
        const saveController = new AbortController();
        const saveTimeout = window.setTimeout(() => saveController.abort(), RAG_SAVE_REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(`${API_BASE}/proactive-ai-tasks.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                signal: saveController.signal,
                body: JSON.stringify({ action: 'save_draft_run', run_id: runId }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not save draft.');
            }
            const updatedRun = payload.run || { ...run, status: 'completed', status_message: 'Draft saved to CaseDB files.', metadata: { ...(run.metadata || {}), generated_file: payload.file || null } };
            setRagBackgroundDraftRuns(prev => prev.map(item => Number(item.id) === runId ? { ...item, ...updatedRun, task: item.task || run.task } : item));
            const sourceMessage = ragMessages.find(message => getBackgroundDraftRunIdForMessage(message) === runId);
            if (sourceMessage) {
                const update = buildSavedBackgroundDraftMessageUpdate(sourceMessage, updatedRun, payload);
                setRagMessages(prev => prev.map(message => (
                    String(message.id) === String(sourceMessage.id)
                        ? { ...message, content: update.content, answer: update.answer, metadata: update.metadata, files: update.files }
                        : message
                )));
                await persistRagMessageUpdate({
                    chatId: sourceMessage.chat_id || activeRagChatId,
                    messageId: sourceMessage.id,
                    content: update.content,
                    answer: update.answer,
                    metadata: update.metadata,
                    files: update.files,
                });
            }
            await refreshBackgroundDraftRuns();
        } catch (error) {
            console.error('Error saving background draft:', error);
            setRagBackgroundDraftErrorByRunId(prev => ({
                ...prev,
                [String(runId)]: error?.name === 'AbortError'
                    ? 'Saving took too long. Check the case files, then try again if the draft is not there.'
                    : 'The draft could not be saved. Please try again.',
            }));
        } finally {
            window.clearTimeout(saveTimeout);
            setBackgroundDraftRunBusy(runId, false);
        }
    };

    const handleContinueBackgroundRunWithNextProvider = async (run, recovery) => {
        if (!hasJaneUserIdentity) return;
        const request = buildJaneProviderRecoveryRequest(run, recovery);
        const runId = Number(request?.run_id || 0);
        if (!request || !runId) return;
        setBackgroundDraftRunBusy(runId, true);
        setRagBackgroundDraftErrorByRunId(prev => {
            if (!prev[String(runId)]) return prev;
            const next = { ...prev };
            delete next[String(runId)];
            return next;
        });
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), RAG_SAVE_REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(`${API_BASE}/proactive-ai-tasks.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                signal: controller.signal,
                body: JSON.stringify(request),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Jane could not continue with another model.');
            }
            setRagBackgroundDraftRuns(prev => prev.map(item => Number(item?.id || 0) === runId ? {
                ...item,
                status_message: payload?.status_message || item.status_message,
                metadata: {
                    ...(item.metadata || {}),
                    provider_recovery: {
                        ...(item.metadata?.provider_recovery || {}),
                        status: 'continuing',
                    },
                },
            } : item));
            await refreshBackgroundDraftRuns();
        } catch (error) {
            console.error('Error continuing Jane run with another model:', error);
            setRagBackgroundDraftErrorByRunId(prev => ({
                ...prev,
                [String(runId)]: error?.name === 'AbortError'
                    ? 'The model change took too long. Wait for the run status to refresh before trying again.'
                    : (String(error?.message || '').trim() || 'Jane could not continue with another model.'),
            }));
        } finally {
            window.clearTimeout(timeoutId);
            setBackgroundDraftRunBusy(runId, false);
        }
    };

    const handleDismissBackgroundDraftRun = async (run) => {
        if (!hasJaneUserIdentity) return;
        const runId = Number(run?.id || 0);
        if (!runId) return;
        setBackgroundDraftRunBusy(runId, true);
        try {
            await fetch(`${API_BASE}/proactive-ai-tasks.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({ action: 'dismiss_run', run_id: runId }),
            });
            setRagBackgroundDraftRuns(prev => prev.filter(item => Number(item.id) !== runId));
        } catch (error) {
            console.error('Error dismissing background draft:', error);
        } finally {
            setBackgroundDraftRunBusy(runId, false);
        }
    };

    const handleCancelProactiveEvent = async (eventItem) => {
        if (!hasJaneUserIdentity) return;
        const taskId = Number(eventItem?.task_id || eventItem?.task?.id || 0);
        if (!taskId) return;
        const busyKey = eventItem?.id || `task-${taskId}`;
        setBackgroundDraftRunBusy(busyKey, true);
        try {
            await fetch(`${API_BASE}/proactive-ai-tasks.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({ action: 'cancel', id: taskId }),
            });
            setRagBackgroundDraftRuns(prev => prev.filter(item => Number(item?.task_id || item?.task?.id || 0) !== taskId));
            await refreshBackgroundDraftRuns();
        } catch (error) {
            console.error('Error cancelling proactive event:', error);
        } finally {
            setBackgroundDraftRunBusy(busyKey, false);
        }
    };

    useEffect(() => {
        if (!ragSidebarCollapsed) {
            setRagSidebarTextHidden(false);
            return;
        }

        const hideTimer = setTimeout(() => setRagSidebarTextHidden(true), RAG_SIDEBAR_COLLAPSE_MS);
        return () => clearTimeout(hideTimer);
    }, [ragSidebarCollapsed]);

    useEffect(() => {
        if (!showRagReferenceMenu) return;

        const handleClickOutside = (event) => {
            if (ragReferenceMenuRef.current && !ragReferenceMenuRef.current.contains(event.target)) {
                setShowRagReferenceMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showRagReferenceMenu]);

    useEffect(() => {
        if (!ragMobileActionChatId) return;

        const handleMobileActionClickOutside = (event) => {
            const target = event.target;
            if (target?.closest?.('.library-rag-mobile-chat-popover') || target?.closest?.('.library-rag-chat-list-row.action-open')) return;
            setRagMobileActionChatId(null);
        };

        document.addEventListener('pointerdown', handleMobileActionClickOutside);
        return () => document.removeEventListener('pointerdown', handleMobileActionClickOutside);
    }, [ragMobileActionChatId]);

    useEffect(() => {
        if (!ragMobileUserActionMessageId) return;

        const handleMobileUserActionClickOutside = (event) => {
            if (event.target?.closest?.('.library-rag-user-actions')) return;
            setRagMobileUserActionMessageId(null);
        };

        document.addEventListener('pointerdown', handleMobileUserActionClickOutside);
        return () => document.removeEventListener('pointerdown', handleMobileUserActionClickOutside);
    }, [ragMobileUserActionMessageId]);

    useEffect(() => {
        if (!sharingRagChat) return;

        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(`${API_BASE}/user.php?users=true&time=${Date.now()}`, { credentials: 'include' });
                const data = await response.json();
                if (!cancelled && Array.isArray(data.users)) setShareUsers(data.users);
            } catch (error) {
                console.error('Error loading users for chat sharing:', error);
                if (!cancelled) setShareChatStatus('Could not load users. Try again.');
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sharingRagChat]);

    useEffect(() => {
        const shouldShowLibrary = isJaneAgentLibraryUrl();
        setShowJaneAgentLibrary(prev => prev === shouldShowLibrary ? prev : shouldShowLibrary);
        if (shouldShowLibrary) {
            if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('library') && !/\/ai\/library\/?$/i.test(window.location.pathname)) {
                syncJaneAgentLibraryUrl();
            }
            setActiveRagChatId(null);
            setEditingRagChatTitle(false);
            setRagChatTitleDraft('');
            setShowRagSourceHistory(false);
            setShowRagReferenceMenu(false);
            setShowRagDocumentPicker(false);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!showJaneAgentLibrary || backendJaneAgents.length > 0 || janeAgentLibraryLoading) return;

        let cancelled = false;
        setJaneAgentLibraryLoading(true);
        setJaneAgentLibraryError('');

        (async () => {
            try {
                const response = await fetch(`${API_BASE}/jane-agent-library.php`, { credentials: 'include' });
                const data = await response.json();
                if (!response.ok || !data?.success) {
                    throw new Error(data?.message || 'Could not load the Jane Agent Library.');
                }
                const agents = Array.isArray(data.agents) ? data.agents : [];
                if (!cancelled) {
                    setBackendJaneAgents(agents.filter(agent => agent && typeof agent === 'object'));
                    if (Array.isArray(data.bookmarked_agent_ids)) {
                        const serverIds = data.bookmarked_agent_ids.map(String).filter(Boolean);
                        const localIds = loadPinnedJaneAgentIds();
                        const bookmarkedIds = serverIds.length > 0 ? serverIds : localIds;
                        setPinnedJaneAgentIds(bookmarkedIds);
                        savePinnedJaneAgentIds(bookmarkedIds);
                        if (serverIds.length === 0 && localIds.length > 0) {
                            syncJaneAgentBookmarks(localIds);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading Jane Agent Library:', error);
                if (!cancelled) {
                    setJaneAgentLibraryError(error.message || 'Could not load the Jane Agent Library.');
                }
            } finally {
                if (!cancelled) {
                    setJaneAgentLibraryLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [backendJaneAgents.length, janeAgentLibraryLoading, showJaneAgentLibrary]);

    useEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;

        const styles = window.getComputedStyle(textarea);
        const fontSize = parseFloat(styles.fontSize) || 14;
        const lineHeight = parseFloat(styles.lineHeight) || fontSize * 1.45;
        const paddingTop = parseFloat(styles.paddingTop) || 0;
        const paddingBottom = parseFloat(styles.paddingBottom) || 0;
        const minHeight = parseFloat(styles.minHeight) || 52;
        const maxHeight = (lineHeight * 10.5) + paddingTop + paddingBottom;

        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const nextHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        window.requestAnimationFrame(updateRagThreadBottomState);
    }, [chunkSearchDraft, ragReferences.length]);

    const loadRagChats = async () => {
        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php?action=list`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) setRagChats(sortRagChatsByUpdateTime(data.chats || []));
        } catch (error) {
            console.error('Error loading RAG chats:', error);
        }
    };

    const loadRagChat = (chatId, options = {}) => {
        if (!chatId) return;
        // Cancellation needs the result of a fresh, successful authorized GET.
        // Ordinary background reloads retain their existing coalesced cache path.
        if (options.requireSnapshot) return performRagChatLoad(chatId, options);
        const sameChatSilent = Boolean(options?.silent) && Number(chatId) === Number(activeRagChatId);
        if (!sameChatSilent) return performRagChatLoad(chatId, options);
        const sessionId = ragSessionRef.current;
        const generation = ragRunGenerationRef.current;
        const actorId = ragMessagePageActorRef.current;
        const key = `${actorId}:${chatId}:${sessionId}:${generation}`;
        return ragSilentReloadCoordinatorRef.current.run(key, () => performRagChatLoad(chatId, options), () => (
            sessionId === ragSessionRef.current && generation === ragRunGenerationRef.current
            && actorId === ragMessagePageActorRef.current
            && Number(chatId) === Number(activeRagChatIdRef.current)
            && !ragForegroundChatLoadRef.current
            && !requestControllerRef.current && !ragLocalTurnSavingRef.current
        ));
    };

    const performRagChatLoad = async (chatId, options = {}) => {
        if (!chatId) return;
        const silent = Boolean(options?.silent);
        const switchingChats = Number(chatId) !== Number(activeRagChatId);
        const useSilentReload = silent && !switchingChats;
        // Polling may notice the durable request placeholders before the active
        // response stream has returned. Never let that silent refresh abort the
        // stream or advance its session id; doing so leaves "Starting response…"
        // in the chat and discards the queued answer and run plan.
        if (useSilentReload && (ragForegroundChatLoadRef.current || ragOlderMessagesRequestRef.current || requestControllerRef.current || ragLocalTurnSavingRef.current)) return;
        // Only loads that actually start may supersede an older fetch. An
        // intentionally skipped poll must not invalidate an in-flight load and
        // strand its loading state.
        const chatLoadId = ++ragChatLoadSequenceRef.current;
        const preserveDockPromptDraft = Boolean(String(ragPendingDockPromptDraftRef.current?.draftText || '').trim());
        const shouldAdvanceSession = shouldAdvanceRagSessionForChatLoad({ silent, switchingChats });
        if (shouldAdvanceSession) {
            if (requestControllerRef.current) requestControllerRef.current.abort();
            ragOlderMessagesRequestRef.current?.controller.abort();
            ragRuntimeIntentRef.current = null;
            setRagCancelError('');
            setRagCanceling(false);
            resetRagIncomingAnswerState();
            requestSequenceRef.current += 1;
            ragSessionRef.current += 1;
            ragRunGenerationRef.current += 1;
            ragForegroundChatLoadRef.current = null;
            ragMessagePageCacheRef.current = null;
            ragMessagePageEpochRef.current += 1;
            invalidateRagPendingSaveState();
        }
        const sessionId = ragSessionRef.current;
        const generation = ragRunGenerationRef.current;
        const cacheEpoch = ragMessagePageEpochRef.current;
        const actorId = ragMessagePageActorRef.current;
        if (!useSilentReload) ragForegroundChatLoadRef.current = { chatLoadId };
        const pageScope = ragMessagePageScope({ actorId, chatId: Number(chatId), sessionId, generation, epoch: cacheEpoch, limit: RAG_MESSAGE_PAGE_SIZE });
        const isCurrentPageLoad = () => sessionId === ragSessionRef.current
            && generation === ragRunGenerationRef.current && cacheEpoch === ragMessagePageEpochRef.current
            && actorId === ragMessagePageActorRef.current
            && chatLoadId === ragChatLoadSequenceRef.current
            && (!useSilentReload || (Number(chatId) === Number(activeRagChatIdRef.current)
                && !requestControllerRef.current && !ragLocalTurnSavingRef.current));
        if (!useSilentReload) {
            setRagChatLoading(true);
            setRagMessagesHasMoreBefore(false);
            setRagMessagesLoadingOlder(false);
            setRagActiveRequest(null);
            setChunkPreview(null);
            setFiles([]);
            setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
        }
        if (switchingChats) setRagChatFilesUploading(false);
        if (switchingChats && !preserveDockPromptDraft) {
            setChunkSearchDraft('');
            setRagReferenceDraft('');
            setRagReferences([]);
            setRagReferenceContext(null);
            setShowRagReferenceMenu(false);
            setShowRagDocumentPicker(false);
        }
        try {
            const params = new URLSearchParams({
                action: 'get',
                chat_id: String(chatId),
                messages_limit: String(RAG_MESSAGE_PAGE_SIZE),
            });
            const page = await fetchRagMessagePage({
                fetchPage: async (requestParams) => {
                    const controller = new AbortController();
                    const timeoutId = window.setTimeout(() => controller.abort(), RAG_CHAT_LOAD_TIMEOUT_MS);
                    try {
                        const response = await fetch(`${API_BASE}/library-rag-chat.php?${requestParams.toString()}`, { credentials: 'include', signal: controller.signal });
                        const data = await response.json();
                        return response.ok ? data : { success: false };
                    } finally {
                        window.clearTimeout(timeoutId);
                    }
                },
                params,
                cache: ragMessagePageCacheRef.current,
                context: { chatId: Number(chatId), limit: RAG_MESSAGE_PAGE_SIZE, beforeMessageId: 0, scope: pageScope },
                isCurrent: isCurrentPageLoad,
            });
            if (!page || !isCurrentPageLoad()) return;
            const data = page.data;
            // A current denial/failure invalidates reusable raw private bodies.
            ragMessagePageCacheRef.current = page.cache;
            if (data.success) {
                const loadedChatId = data.chat?.id || chatId;
                ragLatestChatUpdatedAtRef.current = String(data.chat?.updated_at || data.chat?.last_message_at || data.chat?.created_at || ragLatestChatUpdatedAtRef.current || '');
                if (!useSilentReload || ragThreadAtBottom) {
                    markRagThreadShouldAutoScroll();
                }
                setActiveRagChatId(loadedChatId);
                activeRagChatIdRef.current = loadedChatId;
                if (data.chat?.model_key) setRagModelKey(normalizeRagModelKey(data.chat.model_key));
                setEditingRagChatTitle(false);
                setRagChatTitleDraft(data.chat?.title || '');
                syncRagChatUrl(loadedChatId);
                const normalizedMessages = normalizeRagMessages(data.messages || []);
                setRagMessages(normalizedMessages);
                const activeRequest = data.active_request || null;
                setRagActiveRequest(prev => (isSameRagActiveRequest(prev, activeRequest) ? prev : activeRequest));
                const runtimeIntent = getOwnedRagRuntimeIntent(loadedChatId);
                if (runtimeIntent && isRagAgentV2SnapshotSettled({
                    snapshot: data, acknowledgement: runtimeIntent.acknowledgement, actorId: getJaneUserId(user),
                })) {
                    ragRuntimeIntentRef.current = null;
                    setRagCancelError('');
                }
                const activeRequestHasDurableMessages = activeRequest && normalizedMessages.some((message) => (
                    isRagMessageBoundToActiveRequest(message, activeRequest)
                ));
                if (activeRequest && !loading && !activeRequestHasDurableMessages) {
                    syncExternalRagRequestPreview(activeRequest);
                } else if (!activeRequest) {
                    clearExternalRagRequestPreview();
                }
                setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
                const lastAssistant = [...(data.messages || [])].reverse().find((message) => message.role === 'assistant');
                setFiles(lastAssistant?.files || []);
                setChunkSearchQuery(lastAssistant?.query_text || '');
                return data;
            } else {
                syncRagChatUrl(null);
            }
        } catch (error) {
            if (isCurrentPageLoad()) ragMessagePageCacheRef.current = null;
            console.error('Error loading RAG chat:', error);
        } finally {
            if (ragForegroundChatLoadRef.current?.chatLoadId === chatLoadId) ragForegroundChatLoadRef.current = null;
            if (switchingChats && preserveDockPromptDraft) {
                ragPendingDockPromptDraftRef.current = null;
            }
            if (sessionId === ragSessionRef.current && chatLoadId === ragChatLoadSequenceRef.current && !useSilentReload) setRagChatLoading(false);
        }
    };

    const loadOlderRagMessages = async () => {
        if (!activeRagChatId || ragOlderMessagesRequestRef.current || ragMessagesLoadingOlder || ragChatLoading || !ragMessagesHasMoreBefore) return;
        const oldestMessage = ragMessages.find(message => Number.isFinite(Number(message.id)) && Number(message.id) > 0);
        if (!oldestMessage) return;

        const sessionId = ragSessionRef.current;
        const generation = ragRunGenerationRef.current;
        const actorId = ragMessagePageActorRef.current;
        const chatId = Number(activeRagChatId);
        const controller = new AbortController();
        const request = { controller, timeoutId: window.setTimeout(() => controller.abort(), RAG_CHAT_LOAD_TIMEOUT_MS) };
        ragOlderMessagesRequestRef.current = request;
        const isScopeCurrent = () => !controller.signal.aborted && sessionId === ragSessionRef.current
            && generation === ragRunGenerationRef.current && actorId === ragMessagePageActorRef.current
            && chatId === Number(activeRagChatIdRef.current);
        const isCurrent = () => ragOlderMessagesRequestRef.current === request && isScopeCurrent();
        setRagMessagesLoadingOlder(true);
        try {
            const params = new URLSearchParams({
                action: 'get',
                chat_id: String(chatId),
                messages_limit: String(RAG_MESSAGE_PAGE_SIZE),
                before_message_id: String(oldestMessage.id),
            });
            const response = await fetch(`${API_BASE}/library-rag-chat.php?${params.toString()}`, { credentials: 'include', signal: controller.signal });
            const data = await response.json();
            if (!isCurrent() || !response.ok || !data.success || Number(data.chat?.id) !== chatId) return;
            const olderMessages = normalizeRagMessages(data.messages || []);
            const thread = ragThreadRef.current;
            if (thread) {
                ragPreserveScrollRef.current = { scrollHeight: thread.scrollHeight, scrollTop: thread.scrollTop };
                ragSkipNextAutoScrollRef.current = true;
            }
            setRagMessages(prev => {
                if (!isScopeCurrent()) return prev;
                const existingIds = new Set(prev.map(message => String(message.id)));
                const uniqueOlder = olderMessages.filter(message => !existingIds.has(String(message.id)));
                if (uniqueOlder.length === 0) {
                    ragSkipNextAutoScrollRef.current = false;
                    ragPreserveScrollRef.current = null;
                    return prev;
                }
                return uniqueOlder.length > 0 ? [...uniqueOlder, ...prev] : prev;
            });
            setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
        } catch (error) {
            if (isCurrent()) console.error('Error loading older RAG messages:', error);
        } finally {
            window.clearTimeout(request.timeoutId);
            if (ragOlderMessagesRequestRef.current === request) {
                ragOlderMessagesRequestRef.current = null;
                if (sessionId === ragSessionRef.current && actorId === ragMessagePageActorRef.current) setRagMessagesLoadingOlder(false);
            }
        }
    };

    const loadChunkMatches = async (page = 1, queryOverride = null, options = {}) => {
        const requestId = ++requestSequenceRef.current;
        const requestGeneration = ragRunGenerationRef.current;
        const requestActorId = getJaneUserId(user);
        if (requestControllerRef.current) requestControllerRef.current.abort();

        const requestController = new AbortController();
        requestControllerRef.current = requestController;
        const trimmedQuery = typeof queryOverride === 'string' ? queryOverride.trim() : chunkSearchQuery.trim();

        if (trimmedQuery.length < 2) {
            setFiles([]);
            setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
            setLoading(false);
            return null;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({
                mode: 'firm-wide',
                q: trimmedQuery,
                page: page.toString(),
                page_size: Math.min(pagination.pageSize, 25).toString(),
                use_hybrid: '1',
                use_vector: '1'
            });
            if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
                const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (browserTimeZone) {
                    params.set('user_timezone', browserTimeZone);
                }
            }
            const browserNow = new Date();
            params.set('user_now_iso', browserNow.toISOString());
            params.set('user_now_offset_minutes', String(-browserNow.getTimezoneOffset()));
            params.set('model_key', normalizeRagModelKey(options.modelKeyOverride || ragModelKey));
            const requestJaneRunMode = normalizeJaneRunMode(options.janeRunModeOverride || janeRunMode);
            params.set('jane_run_mode', requestJaneRunMode);
            if (options.streamAnswer) {
                params.set('stream', '1');
            }
            const requestChatId = options.chatIdOverride || activeRagChatId;
            if (requestChatId) {
                params.set('chat_id', String(requestChatId));
            }
            if (Number(options.rerunMessageId || 0) > 0) {
                params.set('rerun_message_id', String(Number(options.rerunMessageId)));
            }
            if (String(options.rerunRequestToken || '').trim()) {
                params.set('rerun_request_token', String(options.rerunRequestToken).trim());
            }
            if (Number(options.reuseAssistantMessageId || 0) > 0) {
                params.set('reuse_assistant_message_id', String(Number(options.reuseAssistantMessageId)));
            }
            const selectedSourcesOnly = Boolean(options.selectedSourcesOnly);
            if (!ragUsePriorChat || selectedSourcesOnly) {
                params.set('suppress_prior_chat', '1');
            }
            const requestReferences = filterReferencesForFreshTitleSearch(
                Array.isArray(options.promptReferences) ? options.promptReferences : ragReferences,
                trimmedQuery
            );
            const referenceSelection = Number(options.rerunMessageId || 0) > 0
                ? null : parseCaseLeadSelection(ragReferenceContext?.value || '');
            const promptReferenceCaseId = requestReferences.find(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) > 0) || null;
            const fileReferenceCaseIds = Array.from(new Set(requestReferences
                .map(reference => Number(reference?.case_id || 0))
                .filter(referenceCaseId => Number.isFinite(referenceCaseId) && referenceCaseId > 0)));
            const selectedCaseId = Number(options.caseIdOverride || 0) > 0
                ? Number(options.caseIdOverride)
                : referenceSelection?.type === 'case' && referenceSelection.id
                ? referenceSelection.id
                : Number(promptReferenceCaseId?.case_id || promptReferenceCaseId?.id || (fileReferenceCaseIds.length === 1 ? fileReferenceCaseIds[0] : 0));
            if (selectedCaseId) {
                params.set('case_id', String(selectedCaseId));
            }
            if (options.confirmedCaseId) {
                params.set('confirmed_case_id', '1');
            }
            const requestReferenceContext = buildRagRequestReferenceContext(requestReferences);
            const referenceFileIds = requestReferences
                .map(reference => Number(reference.file_id))
                .filter(fileId => Number.isFinite(fileId) && fileId > 0);
            if (referenceFileIds.length > 0) {
                params.set('file_ids', Array.from(new Set(referenceFileIds)).join(','));
            }
            if (requestReferenceContext.length > 0) {
                params.set('references_json', JSON.stringify(requestReferenceContext.slice(0, 20)));
            }
            if (selectedSourcesOnly) {
                params.set('selected_sources_only', '1');
                params.set('retrieval_scope', 'selected');
            }

            const isReplacementContinuation = Number(options.reuseAssistantMessageId || 0) > 0;
            let intelligenceEndpoint = getJaneLegacyIntelligenceEndpoint({
                query: trimmedQuery,
                runMode: requestJaneRunMode,
                rerunMessageId: options.rerunMessageId,
                isReplacementContinuation,
            });
            let requestUrl = `${API_BASE}/${intelligenceEndpoint}?${params}`;
            // Reruns consume the one-time same-chat reservation for the exact
            // existing response. The legacy endpoints understand that token;
            // agent v2 must not bypass it.
            const shouldUseJaneAgentV2 = shouldUseJaneAgentV2ForPrompt({
                query: trimmedQuery,
                runMode: requestJaneRunMode,
                rerunMessageId: options.rerunMessageId,
                isReplacementContinuation,
                agentV2Enabled: isJaneAgentV2Enabled(),
            });
            if (shouldUseJaneAgentV2) {
                const clientOperationId = createRagAgentV2OperationId();
                const intent = createRagAgentV2RuntimeIntent({
                    runGeneration: requestGeneration,
                    actorId: requestActorId,
                    chatId: Number(requestChatId || 0),
                    clientOperationId,
                });
                if (!intent) throw new Error('Jane could not establish a safe submission identity.');
                // Synchronous ownership must exist before the asynchronous POST:
                // React state alone cannot protect Stop while admission is pending.
                ragRuntimeIntentRef.current = intent;
                const agentPayload = { ...Object.fromEntries(params.entries()), client_operation_id: clientOperationId };
                const agentStartResponse = await fetch(`${API_BASE}/jane-agent-runs.php`, {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    credentials: 'include',
                    signal: requestController.signal,
                    body: JSON.stringify(agentPayload),
                });
                const agentStartData = await agentStartResponse.json().catch(() => null);
                if (requestId !== requestSequenceRef.current || requestController.signal.aborted
                    || requestGeneration !== ragRunGenerationRef.current || ragRuntimeIntentRef.current !== intent) return null;
                const acknowledgement = normalizeRagAgentV2Acknowledgement({
                    receipt: agentStartData, clientOperationId, chatId: Number(requestChatId || 0), actorId: requestActorId,
                });
                if (acknowledgement) {
                    ragRuntimeIntentRef.current = createRagAgentV2RuntimeIntent({
                        runGeneration: requestGeneration, actorId: requestActorId,
                        chatId: Number(requestChatId || 0), clientOperationId, acknowledgement,
                    });
                }
                if (agentStartResponse.ok && agentStartData?.success && acknowledgement) {
                    applyAcceptedRagChatTitle(agentStartData, requestChatId);
                    options.onStreamEvent?.({
                        type: 'request_lock', ...acknowledgement, runtime: 'agent_v2',
                        endpoint: 'jane-agent-stream.php', user_id: requestActorId,
                        status_message: 'The background run is queued. I’m waiting for its first update.',
                    });
                    intelligenceEndpoint = 'jane-agent-stream.php';
                    const streamParams = new URLSearchParams({ run_id: acknowledgement.run_id });
                    requestUrl = `${API_BASE}/${intelligenceEndpoint}?${streamParams.toString()}`;
                } else if (!acknowledgement && !agentStartData?.run_id && !agentStartData?.request_token
                    && shouldFallbackFromJaneAgentStart(agentStartData)) {
                    ragRuntimeIntentRef.current = null;
                    console.warn('Jane agent v2 unavailable; falling back to a compatible Jane route.', agentStartData?.message || agentStartResponse.status);
                    intelligenceEndpoint = normalizeJaneLegacyFallbackEndpoint(agentStartData?.fallback_endpoint);
                    requestUrl = `${API_BASE}/${intelligenceEndpoint}?${params}`;
                } else {
                    // A failed dispatch may already own and persist this exact
                    // turn. Surface its safe failure contract; retrying through
                    // the legacy route here would create duplicate messages and
                    // could repeat a mutating tool action.
                    return agentStartData && typeof agentStartData === 'object'
                        ? {
                            success: false,
                            runtime: 'agent_v2',
                            run_id: acknowledgement?.run_id || null,
                            request_token: acknowledgement?.request_token || '',
                            error_code: String(agentStartData.error_code || 'jane_agent_start_failed'),
                            http_status: Number(agentStartResponse.status || 0) || null,
                            retryable: Boolean(agentStartData.retryable),
                            persisted_message: Boolean(agentStartData.persisted_message),
                            safe_to_retry_from_message: Boolean(agentStartData.safe_to_retry_from_message),
                            chat_id: Number(agentStartData.chat_id || 0) || null,
                            user_message_id: Number(agentStartData.user_message_id || 0) || null,
                            assistant_message_id: Number(agentStartData.assistant_message_id || 0) || null,
                            message: String(agentStartData.message || 'Jane could not start this response. Please try again.'),
                        }
                        : buildRagTransportFailure({
                            status: agentStartResponse.status,
                            errorCode: 'jane_agent_start_failed',
                        });
                }
            }
            const requestUsesProtectedJaneRuntime = intelligenceEndpoint === 'jane-loop.php'
                || intelligenceEndpoint === 'library-intelligence-search.php';
            if (requestUsesProtectedJaneRuntime) {
                requestUrl = `${API_BASE}/${intelligenceEndpoint}`;
            }
            const response = await fetch(requestUrl, {
                ...(requestUsesProtectedJaneRuntime ? {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    body: JSON.stringify({
                        ...(intelligenceEndpoint === 'library-intelligence-search.php' ? { action: 'run' } : {}),
                        ...Object.fromEntries(params.entries()),
                    }),
                } : {}),
                credentials: 'include',
                signal: requestController.signal
            });
            let data = null;
            if (options.streamAnswer) {
                data = await readJsonLineStream(response, options.onStreamEvent, requestController.signal);
            } else {
                try {
                    data = await response.json();
                } catch (error) {
                    data = buildRagTransportFailure({
                        status: response.status,
                        errorCode: response.ok ? 'jane_invalid_response' : 'jane_http_error',
                    });
                }
                if (!response.ok && data?.success !== false) {
                    data = buildRagTransportFailure({ status: response.status, errorCode: 'jane_http_error' });
                }
            }

            if (requestId !== requestSequenceRef.current || requestController.signal.aborted) return null;

            if (data.success) {
                setFiles(data.files || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalPages: data.pagination?.totalPages || 1,
                    totalCount: data.pagination?.totalCount || 0
                }));
                return data;
            }

            console.error('Failed to load RAG matches:', data?.error_code || data?.message || 'unknown_error');
            return data && typeof data === 'object'
                ? { ...data, success: false }
                : buildRagTransportFailure();
        } catch (error) {
            if (requestId !== requestSequenceRef.current) return null;
            if (error.name === 'AbortError') return null;
            console.error('Error loading RAG matches:', error);
            return buildRagTransportFailure();
        } finally {
            if (requestControllerRef.current === requestController) requestControllerRef.current = null;
            if (requestId === requestSequenceRef.current) setLoading(false);
        }
    };

    const handleNewRagChat = () => {
        if (requestControllerRef.current) requestControllerRef.current.abort();
        ragRuntimeIntentRef.current = null;
        setRagCancelError('');
        setRagCanceling(false);
        resetRagIncomingAnswerState();
        requestSequenceRef.current += 1;
        ragSessionRef.current += 1;
        ragRunGenerationRef.current += 1;
        ragForegroundChatLoadRef.current = null;
        ragMessagePageCacheRef.current = null;
        ragMessagePageEpochRef.current += 1;
        invalidateRagPendingSaveState();
        setRagMobileNavOpen(false);
        setRagMobileActionChatId(null);
        setRagMobileUserActionMessageId(null);
        setLoading(false);
        setRagChatLoading(false);
        setRagChatFilesUploading(false);
        activeRagChatIdRef.current = null;
        setActiveRagChatId(null);
        setRagModelKey('auto');
        setEditingRagChatTitle(false);
        setRagChatTitleDraft('');
        setShowRagSourceHistory(false);
        setShowJaneAgentLibrary(false);
        syncRagChatUrl(null);
        setRagMessages([]);
        setRagMessagesHasMoreBefore(false);
        setRagMessagesLoadingOlder(false);
        setRagActiveRequest(null);
        setChunkSearchDraft('');
        setChunkSearchQuery('');
        setRagReferences([]);
        setRagReferenceDraft('');
        setRagReferenceContext(null);
        setShowRagDocumentPicker(false);
        setShowRagReferenceMenu(false);
        setRagSpeechStatus('');
        setRagListening(false);
        if (ragSpeechRecognitionRef.current) {
            try { ragSpeechRecognitionRef.current.stop(); } catch (error) {}
            ragSpeechRecognitionRef.current = null;
        }
        setChunkPreview(null);
        setFiles([]);
        setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
    };

    const handleRagChatSelect = (chatId) => {
        setRagMobileNavOpen(false);
        setRagMobileActionChatId(null);
        setRagMobileUserActionMessageId(null);
        setShowJaneAgentLibrary(false);
        loadRagChat(chatId);
    };

    const clearRagMobileLongPressTimer = () => {
        if (ragMobileLongPressTimerRef.current) {
            window.clearTimeout(ragMobileLongPressTimerRef.current);
            ragMobileLongPressTimerRef.current = null;
        }
    };

    const fetchRagMobileChatPreview = async (chat) => {
        const chatId = Number(chat?.id);
        if (!Number.isFinite(chatId) || chatId <= 0) return;
        const cacheKey = String(chatId);
        if (ragMobilePreviewMessagesByChatId[cacheKey]) return;

        setRagMobilePreviewLoadingChatId(chatId);
        try {
            const params = new URLSearchParams({
                action: 'get',
                chat_id: String(chatId),
                messages_limit: '6',
            });
            const response = await fetch(`${API_BASE}/library-rag-chat.php?${params.toString()}`, { credentials: 'include' });
            const data = await response.json();
            if (!data.success) return;
            setRagMobilePreviewMessagesByChatId(prev => ({
                ...prev,
                [cacheKey]: buildRagMobilePreviewMessages(data.messages || []),
            }));
        } catch (error) {
            console.error('Error loading mobile RAG chat preview:', error);
        } finally {
            setRagMobilePreviewLoadingChatId(currentId => Number(currentId) === chatId ? null : currentId);
        }
    };

    const openRagMobileChatActions = (chat) => {
        setRagMobileActionChatId(chat.id);
        fetchRagMobileChatPreview(chat);
    };

    const getRagMobilePreviewMessageText = (message) => {
        const text = getRagMessagePreviewText(message);
        if (!text) return '';
        return text.length > 160 ? `${text.slice(0, 160).trim()}…` : text;
    };

    const handleMobileChatPressStart = (chat) => {
        clearRagMobileLongPressTimer();
        ragMobileLongPressTriggeredRef.current = false;
        ragMobileLongPressTimerRef.current = window.setTimeout(() => {
            ragMobileLongPressTriggeredRef.current = true;
            openRagMobileChatActions(chat);
        }, 520);
    };

    const handleMobileChatPressEnd = () => {
        clearRagMobileLongPressTimer();
    };

    const handleMobileChatClick = (event, chat) => {
        if (ragMobileLongPressTriggeredRef.current || Number(ragMobileActionChatId) === Number(chat.id)) {
            event.preventDefault();
            event.stopPropagation();
            ragMobileLongPressTriggeredRef.current = false;
            return;
        }
        handleRagChatSelect(chat.id);
    };

    const clearRagMobileUserLongPressTimer = () => {
        if (ragMobileUserLongPressTimerRef.current) {
            window.clearTimeout(ragMobileUserLongPressTimerRef.current);
            ragMobileUserLongPressTimerRef.current = null;
        }
    };

    const openRagMobileUserActions = (message) => {
        setRagMobileUserActionMessageId(message.id);
    };

    const handleMobileUserMessagePressStart = (message) => {
        clearRagMobileUserLongPressTimer();
        ragMobileUserLongPressTimerRef.current = window.setTimeout(() => {
            openRagMobileUserActions(message);
        }, 520);
    };

    const handleMobileUserMessagePressEnd = () => {
        clearRagMobileUserLongPressTimer();
    };

    const getRagChatPreviewText = (chat) => {
        const preview = String(chat?.last_message || chat?.last_message_preview || chat?.preview || chat?.query_text || '').trim();
        if (preview) return preview.length > 130 ? `${preview.slice(0, 130).trim()}…` : preview;
        const dateValue = chat?.last_message_at || chat?.updated_at || chat?.created_at;
        return dateValue ? `Last activity ${formatDate(dateValue)}` : 'No recent message preview available.';
    };

    const handleRequestDeleteRagChat = (event, chat) => {
        event?.stopPropagation?.();
        const chatId = Number(chat?.id);
        if (!Number.isFinite(chatId) || chatId <= 0) return;
        setPendingRagChatDelete(chat);
        setRagChatDeleteError('');
        setRagMobileActionChatId(null);
    };

    const handleCancelDeleteRagChat = () => {
        if (ragChatDeleteBusy) return;
        setPendingRagChatDelete(null);
        setRagChatDeleteError('');
    };

    const handleConfirmDeleteRagChat = async () => {
        if (!hasJaneUserIdentity || ragChatDeleteBusy) return;
        const chat = pendingRagChatDelete;
        const chatId = Number(chat?.id);
        if (!Number.isFinite(chatId) || chatId <= 0) return;

        setRagChatDeleteBusy(true);
        setRagChatDeleteError('');
        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({ action: 'delete', chat_id: chatId })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.message || 'Could not delete this chat.');

            setRagChats(prev => prev.filter((item) => Number(item.id) !== chatId));
            if (Number(activeRagChatId) === chatId) {
                handleNewRagChat();
            }
            setPendingRagChatDelete(null);
            setRagChatDeleteError('');
        } catch (error) {
            console.error('Error deleting RAG chat:', error);
            setRagChatDeleteError(error.message || 'Could not delete this chat. Please try again.');
        } finally {
            setRagChatDeleteBusy(false);
        }
    };

    const handleOpenShareRagChat = (event, chat) => {
        event?.stopPropagation?.();
        const existingSharedUserIds = Array.isArray(chat?.shared_user_ids)
            ? chat.shared_user_ids
            : (Array.isArray(chat?.shared_users) ? chat.shared_users.map(shareUser => shareUser.id) : []);
        setSharingRagChat(chat);
        setShareSearch('');
        setSelectedShareUserIds(existingSharedUserIds.map(Number).filter(id => Number.isFinite(id) && id > 0));
        setShowShareUserDropdown(false);
        setShareHighlightIndex(0);
        setShareChatStatus('');
    };

    const updateRagChatSettings = async (updates) => {
        if (!hasJaneUserIdentity) return null;
        const chatId = Number(activeRagChatId);
        if (!Number.isFinite(chatId) || chatId <= 0) return null;
        setRagChatSettingsSaving(true);
        try {
            const currentChat = ragChats.find((chat) => Number(chat.id) === chatId) || {};
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update',
                    chat_id: chatId,
                    title: updates.title ?? currentChat.title ?? 'New chat',
                    model_key: normalizeRagModelKey(updates.model_key ?? ragModelKey),
                })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.message || 'Failed to update chat');
            if (data.chat) {
                setRagChats(prev => sortRagChatsByUpdateTime(prev.map((chat) => Number(chat.id) === chatId ? { ...chat, ...data.chat } : chat)));
                setRagModelKey(normalizeRagModelKey(data.chat.model_key));
                setRagChatTitleDraft(data.chat.title || '');
            }
            return data.chat || null;
        } catch (error) {
            console.error('Error updating RAG chat settings:', error);
            return null;
        } finally {
            setRagChatSettingsSaving(false);
        }
    };

    const handleRagModelChange = (event) => {
        const nextModelKey = normalizeRagModelKey(event.target.value);
        setRagModelKey(nextModelKey);
        if (activeRagChatId) updateRagChatSettings({ model_key: nextModelKey });
    };

    const handleJaneRunModeChange = (event) => {
        const nextMode = normalizeJaneRunMode(event.target.value);
        setJaneRunMode(nextMode);
        setCookie(JANE_RUN_MODE_COOKIE, nextMode, 365);
    };

    const handleRagChatTitleSubmit = async () => {
        const trimmedTitle = ragChatTitleDraft.trim();
        if (!activeRagChatId || !trimmedTitle) {
            setEditingRagChatTitle(false);
            return;
        }
        await updateRagChatSettings({ title: trimmedTitle });
        setEditingRagChatTitle(false);
    };

    const handleCloseShareRagChat = () => {
        if (shareChatSaving) return;
        setSharingRagChat(null);
        setShareSearch('');
        setSelectedShareUserIds([]);
        setShowShareUserDropdown(false);
        setShareHighlightIndex(0);
        setShareChatStatus('');
    };

    const getFilteredShareUsers = () => {
        const query = shareSearch.trim().toLowerCase();
        return shareUsers.filter((shareUser) => {
            const id = Number(shareUser.id);
            const name = String(shareUser.name || '').toLowerCase();
            const username = String(shareUser.user || '').toLowerCase();
            return id > 0
                && id !== Number(userId)
                && !selectedShareUserIds.includes(id)
                && (!query || name.includes(query) || username.includes(query) || String(id) === query);
        }).slice(0, 15);
    };

    const addShareUser = (shareUser) => {
        const id = Number(shareUser?.id);
        if (!Number.isFinite(id) || id <= 0 || id === Number(userId)) return;
        setSelectedShareUserIds(prev => prev.includes(id) ? prev : [...prev, id]);
        setShareSearch('');
        setShowShareUserDropdown(false);
        setShareHighlightIndex(0);
        setShareChatStatus('');
    };

    const removeShareUser = (id) => {
        setSelectedShareUserIds(prev => prev.filter(value => value !== id));
    };

    const handleShareRagChatSubmit = async (event) => {
        event.preventDefault();
        if (!hasJaneUserIdentity) return;
        const chatId = Number(sharingRagChat?.id);
        const uniqueUserIds = Array.from(new Set(selectedShareUserIds));
        const hasExistingShares = (Array.isArray(sharingRagChat?.shared_user_ids) && sharingRagChat.shared_user_ids.length > 0)
            || (Array.isArray(sharingRagChat?.shared_users) && sharingRagChat.shared_users.length > 0);

        if (!Number.isFinite(chatId) || chatId <= 0) return;
        if (uniqueUserIds.length === 0 && !hasExistingShares) {
            setShareChatStatus('Enter at least one user ID.');
            return;
        }

        setShareChatSaving(true);
        setShareChatStatus('Saving share settings…');
        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({ action: 'share', chat_id: chatId, user_ids: uniqueUserIds })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.message || 'Failed to share chat');
            if (data.chat) {
                setRagChats(prev => sortRagChatsByUpdateTime(prev.map((chat) => Number(chat.id) === chatId ? { ...chat, ...data.chat } : chat)));
                setSharingRagChat(data.chat);
                setSelectedShareUserIds((data.chat.shared_user_ids || []).map(Number).filter(id => Number.isFinite(id) && id > 0));
            }
            setShareChatStatus(uniqueUserIds.length > 0
                ? `Shared with ${uniqueUserIds.length} user${uniqueUserIds.length === 1 ? '' : 's'}.`
                : 'Sharing removed.');
            loadRagChats();
            setTimeout(() => {
                setSharingRagChat(null);
                setShareSearch('');
                setSelectedShareUserIds([]);
                setShareChatStatus('');
            }, 900);
        } catch (error) {
            console.error('Error sharing RAG chat:', error);
            setShareChatStatus(error.message || 'Failed to share chat.');
        } finally {
            setShareChatSaving(false);
        }
    };

    const handleCancelRagResponse = async () => {
        if (!hasJaneUserIdentity) return false;
        if (ragCancelPromiseRef.current) return ragCancelPromiseRef.current;

        let cancellationGeneration = null;
        const cancelPromise = (async () => {
            const inlineRerun = ragInlineRerunRef.current?.generation === ragRunGenerationRef.current
                && ragInlineRerunRef.current?.chatId === Number(activeRagChatIdRef.current)
                ? ragInlineRerunRef.current : null;
            const activeRequest = inlineRerun ? {
                ...ragActiveRequest,
                chat_id: inlineRerun.chatId,
                request_token: inlineRerun.requestToken,
                user_id: userId,
            } : ragActiveRequest;
            const restoreCancelledMessages = messages => messages.flatMap(message => {
                if (inlineRerun && String(message.id) === String(inlineRerun.snapshot.id)) {
                    return [inlineRerun.snapshot];
                }
                return message.pending ? [] : [message];
            });
            const requestChatId = Number(activeRequest?.chat_id || activeRagChatIdRef.current || activeRagChatId || getRagChatIdFromUrl() || 0);
            const actorId = getJaneUserId(user);
            const runtimeIntent = getOwnedRagRuntimeIntent(requestChatId);
            const target = buildRagCancelTarget({
                activeRequest, runtimeIntent, runGeneration: ragRunGenerationRef.current,
                actorId, chatId: Number(activeRagChatIdRef.current || 0), fallbackChatId: requestChatId,
            });
            const hadLocalRequest = Boolean(requestControllerRef.current || loading);
            setRagCanceling(true);
            setRagCancelError('');

            if (requestControllerRef.current) requestControllerRef.current.abort();
            requestControllerRef.current = null;
            resetRagIncomingAnswerState();
            requestSequenceRef.current += 1;
            ragSessionRef.current += 1;
            ragRunGenerationRef.current += 1;
            cancellationGeneration = ragRunGenerationRef.current;
            const cancellationSession = ragSessionRef.current;
            const isCurrentCancellation = () => cancellationGeneration === ragRunGenerationRef.current
                && cancellationSession === ragSessionRef.current && actorId === ragMessagePageActorRef.current
                && requestChatId === Number(activeRagChatIdRef.current || 0);
            // Retain the same operation across local stream cancellation. An
            // unresolved acknowledgement is not proof that nothing persisted.
            if (runtimeIntent) {
                ragRuntimeIntentRef.current = createRagAgentV2RuntimeIntent({
                    runGeneration: cancellationGeneration, actorId,
                    chatId: runtimeIntent.chat_id || 0, originChatId: runtimeIntent.origin_chat_id,
                    clientOperationId: runtimeIntent.client_operation_id,
                    acknowledgement: runtimeIntent.acknowledgement,
                });
            }
            ragForegroundChatLoadRef.current = null;
            ragMessagePageCacheRef.current = null;
            ragMessagePageEpochRef.current += 1;
            invalidateRagPendingSaveState();
            setLoading(false);

            if (inlineRerun) setRagMessages(restoreCancelledMessages);

            if (target.runtime === 'held') {
                setRagCancelError('Jane has not confirmed this submission’s run identity. Stopping is unconfirmed; no new request was sent. Reopen the chat to check its saved status.');
                return false;
            }

            // A request that never reached a saved chat has no server lock to
            // release. Aborting the browser request is sufficient in that case.
            if (!Number.isInteger(requestChatId) || requestChatId <= 0) {
                setRagActiveRequest(null);
                setRagMessages(restoreCancelledMessages);
                return hadLocalRequest;
            }

            const cancelController = new AbortController();
            const cancelDeadline = Date.now() + RAG_CANCEL_REQUEST_TIMEOUT_MS;
            const cancelTimeout = window.setTimeout(() => cancelController.abort(), RAG_CANCEL_REQUEST_TIMEOUT_MS);
            try {
                const cancelResponse = await fetch(`${API_BASE}/${target.endpoint}`, {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    credentials: 'include',
                    signal: cancelController.signal,
                    body: JSON.stringify(target.payload)
                });
                const cancelData = await cancelResponse.json().catch(() => ({}));
                if (!isCurrentCancellation()) return false;
                if (!cancelResponse.ok || !cancelData?.success) {
                    throw new Error('Jane could not confirm that the response stopped.');
                }

                if (target.runtime === 'agent_v2') {
                    const snapshot = await loadRagChat(requestChatId, { silent: true, requireSnapshot: true });
                    if (!isCurrentCancellation()) return false;
                    if (!isRagAgentV2SnapshotSettled({ snapshot, acknowledgement: target.payload, actorId })) {
                        throw new Error('Jane received the stop request, but its saved terminal status could not be verified. Reopen the chat before starting another response.');
                    }
                    ragRuntimeIntentRef.current = null;
                    setRagActiveRequest(null);
                    setRagCancelError('');
                    return true;
                }

                let activeRequestAfterCancel = cancelData.active_request || null;
                while (activeRequestAfterCancel && Date.now() < cancelDeadline) {
                    await new Promise(resolve => window.setTimeout(resolve, RAG_CANCEL_RELEASE_POLL_MS));
                    const params = new URLSearchParams({ action: 'status', chat_id: String(requestChatId) });
                    const statusResponse = await fetch(`${API_BASE}/library-rag-chat.php?${params.toString()}`, {
                        credentials: 'include',
                        signal: cancelController.signal,
                    });
                    const statusData = await statusResponse.json().catch(() => ({}));
                    if (!isCurrentCancellation()) return false;
                    if (!statusResponse.ok || !statusData?.success) {
                        throw new Error('Jane could not confirm that the response stopped.');
                    }
                    activeRequestAfterCancel = statusData.active_request || null;
                }

                if (activeRequestAfterCancel) {
                    setRagActiveRequest(activeRequestAfterCancel);
                    throw new Error('Jane is still finishing the current response. Wait a moment, then try stopping it again.');
                }

                setRagActiveRequest(null);
                setRagMessages(restoreCancelledMessages);
                if (inlineRerun) ragInlineRerunRef.current = null;
                setRagCancelError('');
                return true;
            } catch (error) {
                if (!isCurrentCancellation()) return false;
                console.error('Error cancelling RAG request:', error);
                const friendlyMessage = error?.name === 'AbortError'
                    ? 'Stopping took too long to confirm. Try again before starting another response.'
                    : (String(error?.message || '').trim() || 'Jane could not confirm that the response stopped. Try again.');
                setRagCancelError(friendlyMessage);
                return false;
            } finally {
                window.clearTimeout(cancelTimeout);
            }
        })();

        ragCancelPromiseRef.current = cancelPromise;
        try {
            return await cancelPromise;
        } finally {
            if (ragCancelPromiseRef.current === cancelPromise) ragCancelPromiseRef.current = null;
            if (cancellationGeneration === ragRunGenerationRef.current) setRagCanceling(false);
        }
    };

    const copyTextToClipboard = async (text) => {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    };

    const handleCopyUserMessage = async (message) => {
        const text = message?.content || message?.query_text || '';
        if (!text) return;

        try {
            await copyTextToClipboard(text);
            setCopiedUserMessageId(message.id);
            setTimeout(() => setCopiedUserMessageId(currentId => currentId === message.id ? null : currentId), 1200);
        } catch (error) {
            console.error('Failed to copy user message:', error);
        }
    };

    const toggleRagComponentTextView = (componentType) => {
        if (!componentType) return;
        setTextViewRagComponentTypes((previous) => {
            const next = new Set(previous);
            const key = String(componentType);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            saveRagComponentTextViewTypes(setCookie, next);
            return next;
        });
    };

    const requestSavedRagChatFork = async (message, action) => {
        if (!hasJaneUserIdentity) return null;
        const messageId = Number(message?.id);
        if (!activeRagChatId || !Number.isFinite(messageId) || messageId <= 0) return null;
        if (action !== 'fork_before_user_message_for_edit') {
            throw new Error('Jane could not create the new chat. Please try again.');
        }

        const { data } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                action,
                chat_id: activeRagChatId,
                message_id: messageId
            })
        }, {
            timeoutMs: RAG_SAVE_REQUEST_TIMEOUT_MS,
            failureMessage: 'Jane could not create the new chat. Please try again.',
            timeoutMessage: 'Jane took too long to create the new chat. Please try again.',
        });
        if (!data?.success) throw new Error('Jane could not create the new chat. Please try again.');
        return data;
    };

    const forkSavedRagChatForEdit = async (message) => requestSavedRagChatFork(message, 'fork_before_user_message_for_edit');

    const reserveSavedRagMessageRerun = async (chatId, userMessage, assistantMessage) => {
        const userMessageId = Number(userMessage?.id);
        const assistantMessageId = Number(assistantMessage?.id);
        if (!hasJaneUserIdentity || !Number.isInteger(chatId) || chatId <= 0
            || !Number.isInteger(userMessageId) || userMessageId <= 0
            || !Number.isInteger(assistantMessageId) || assistantMessageId <= 0) {
            throw new Error('invalid_rerun_target');
        }
        const { data } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                action: 'rerun_from_message',
                chat_id: chatId,
                message_id: userMessageId,
                assistant_message_id: assistantMessageId,
            }),
        }, {
            timeoutMs: RAG_SAVE_REQUEST_TIMEOUT_MS,
            failureMessage: 'Jane could not prepare this response for rerunning. Please try again.',
            timeoutMessage: 'Preparing this rerun took too long. Reopen this chat before trying again.',
        });
        if (!data?.success || Number(data.chat?.id) !== chatId
            || Number(data.rerun_message_id) !== userMessageId
            || Number(data.rerun_assistant_message_id) !== assistantMessageId
            || !String(data.rerun_request_token || '').trim()) {
            throw new Error('invalid_rerun_reservation');
        }
        return data;
    };

    const handleEditUserMessage = async (message, messageIndex) => {
        if (!message || message.role !== 'user') return;
        if (requestControllerRef.current || loading || ragSaving) return;

        setEditingRagMessageId(message.id);
        setEditingRagDraft(message.content || message.query_text || '');
        setPendingRagEdit(null);
        setPendingRagRedo(null);
        setRagBranchActionError('');
        setChunkPreview(null);
    };

    const handleCancelInlineRagEdit = () => {
        setEditingRagMessageId(null);
        setEditingRagDraft('');
        setPendingRagEdit(null);
    };

    const handleInlineRagEditSubmit = (event, message, messageIndex) => {
        event.preventDefault();
        if (!message || message.role !== 'user') return;

        const editedPrompt = editingRagDraft.trim();
        const originalPrompt = (message.content || message.query_text || '').trim();
        if (editedPrompt.length < 2) return;
        if (editedPrompt === originalPrompt) {
            handleCancelInlineRagEdit();
            return;
        }

        setPendingRagEdit({
            message,
            messageIndex,
            prompt: editedPrompt,
            references: Array.isArray(message.references) ? message.references : [],
            selectedSourcesOnly: Boolean(message.metadata?.selected_sources_only),
        });
        setPendingRagRedo(null);
        setRagBranchActionError('');
    };

    const handleCancelRagEditConfirm = () => {
        if (ragBranchActionBusy) return;
        setPendingRagEdit(null);
        setRagBranchActionError('');
    };

    const handleCancelRagRedoConfirm = () => {
        if (ragBranchActionBusy) return;
        setPendingRagRedo(null);
        setRagBranchActionError('');
    };

    const getWorkspaceNavigationResolutionText = (navigationDetails, fallbackText = '') => {
        const baseText = String(fallbackText || navigationDetails?.assistantText || navigationDetails?.summary || '').trim();
        const summary = String(navigationDetails?.summary || '').trim();
        if (!summary) return baseText;
        const target = summary
            .replace(/^open\s+/i, '')
            .replace(/\.$/, '')
            .trim();
        const followupText = target ? `Opening ${target} now.` : `Opening ${navigationDetails.caseLabel} now.`;
        return [baseText, followupText].filter(Boolean).join('\n\n');
    };

    const buildResolvedWorkspaceNavigationMessage = (currentMessage, navigationDetails, resolution) => {
        const existingMetadata = currentMessage.metadata || {};
        const existingIntelligence = existingMetadata.intelligence || {};
        const existingWorkspaceNavigation = existingMetadata.workspace_navigation || existingIntelligence.workspace_navigation || {};
        const existingAnswer = currentMessage.answer || { answer_text: currentMessage.content || '', citations: [], source_descriptions: [] };
        const baseAnswerText = String(existingWorkspaceNavigation.assistant_text || existingAnswer.answer_text || currentMessage.content || navigationDetails.assistantText || navigationDetails.summary || '').trim();
        const nextAnswerText = resolution.status === 'confirmed'
            ? getWorkspaceNavigationResolutionText(navigationDetails, baseAnswerText)
            : baseAnswerText;
        const permissionState = resolution.status === 'confirmed' ? 'granted' : 'dismissed';
        const nextWorkspaceNavigation = {
            ...existingWorkspaceNavigation,
            route: 'workspace_navigation',
            case_id: navigationDetails.caseId,
            case_name: navigationDetails.caseName || null,
            prompt: resolution.prompt,
            title: navigationDetails.title || existingWorkspaceNavigation.title || `Open the ${navigationDetails.caseLabel} case?`,
            subject: navigationDetails.subject || existingWorkspaceNavigation.subject || 'Open case',
            summary: navigationDetails.summary || existingWorkspaceNavigation.summary || nextAnswerText,
            assistant_text: existingWorkspaceNavigation.assistant_text || baseAnswerText,
            actions: Array.isArray(navigationDetails.actions) ? navigationDetails.actions : (Array.isArray(existingWorkspaceNavigation.actions) ? existingWorkspaceNavigation.actions : []),
            route_reason: navigationDetails.routeReason || existingWorkspaceNavigation.route_reason || 'workspace_navigation',
            status: resolution.status,
            permission_state: permissionState,
            confirmed_by: resolution.confirmedBy,
            confirmed_by_user_id: resolution.confirmedByUserId,
            confirmed_at: resolution.confirmedAt,
        };
        const nextSession = {
            ...(existingIntelligence.session || {}),
            status: resolution.status,
            permission_state: permissionState,
            case_id: navigationDetails.caseId,
            case_name: navigationDetails.caseName || null,
        };

        return {
            content: nextAnswerText,
            answer: {
                ...existingAnswer,
                answer_text: nextAnswerText,
            },
            metadata: {
                ...existingMetadata,
                intelligence: {
                    ...existingIntelligence,
                    session: nextSession,
                    workspace_navigation: nextWorkspaceNavigation,
                },
                workspace_navigation: nextWorkspaceNavigation,
            },
        };
    };

    const persistRagMessageUpdate = async ({ chatId, messageId, content, answer, metadata, files = [] }) => {
        const numericChatId = Number(chatId || activeRagChatId || 0);
        const numericMessageId = Number(messageId || 0);
        if (!hasJaneUserIdentity || !Number.isFinite(numericChatId) || numericChatId <= 0 || !Number.isFinite(numericMessageId) || numericMessageId <= 0) {
            if (messageId) {
                recordRagPersistenceFailure(messageId, {
                    type: 'update_message',
                    args: { chatId, messageId, content, answer, metadata, files },
                }, !hasJaneUserIdentity ? 'Your CaseDB session could not be verified. Refresh the page before retrying.' : 'This update could not be saved yet.');
            }
            return null;
        }
        const operationContext = captureRagChatOperationContext(numericChatId);
        if (!isCurrentRagChatOperation(operationContext)) return null;
        const updateController = new AbortController();
        const updateTimeout = window.setTimeout(() => updateController.abort(), RAG_SAVE_REQUEST_TIMEOUT_MS);

        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                signal: updateController.signal,
                body: JSON.stringify({
                    action: 'update_message',
                    chat_id: numericChatId,
                    message_id: numericMessageId,
                    content,
                    answer,
                    files,
                    metadata,
                }),
            });
            const responseText = await response.text();
            let data = null;
            try { data = responseText ? JSON.parse(responseText) : null; } catch (error) {}
            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Could not update Jane message.');
            }
            clearRagPersistenceFailure(messageId);
            if (!isCurrentRagChatOperation(operationContext)) return null;
            if (Array.isArray(data.messages)) {
                setRagMessages(normalizeRagMessages(data.messages));
                setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
            }
            if (data.chat?.updated_at || data.chat?.last_message_at || data.chat?.created_at) {
                ragLatestChatUpdatedAtRef.current = String(data.chat.updated_at || data.chat.last_message_at || data.chat.created_at || ragLatestChatUpdatedAtRef.current || '');
            }
            if (data.chat?.id) setActiveRagChatId(data.chat.id);
            return data;
        } catch (error) {
            console.error('Error updating Jane message:', error);
            recordRagPersistenceFailure(messageId, {
                type: 'update_message',
                args: { chatId: numericChatId, messageId: numericMessageId, content, answer, metadata, files },
            }, error?.name === 'AbortError'
                ? 'Saving took too long. This update is still visible here, but it has not been saved to the chat.'
                : 'This update is visible here but was not saved to the chat.');
            return null;
        } finally {
            window.clearTimeout(updateTimeout);
        }
    };

    const getConfirmableActionDisplayTitle = (action) => {
        if (!action || typeof action !== 'object') return 'Confirm this action?';
        const type = String(action.type || '').trim();
        const rawTitle = String(action.title || '').trim();
        const rawSubject = String(action.subject || '').trim();
        if (type === 'save_report' && rawSubject) return `Save ${rawSubject}?`;
        if (type === 'save_legal_draft_docx') return rawTitle || 'Save draft DOCX?';
        return rawTitle || 'Confirm this action?';
    };

    const getDeclinedConfirmableActionText = (action) => {
        const actionType = String(action?.type || '').trim();
        if (actionType === 'save_report') return 'Report was not saved.';
        if (actionType === 'save_legal_draft_docx') return 'Draft was not saved. No files were changed.';
        if (actionType === 'generate_financial_period_workbook') return 'Workbook was not generated.';
        if (actionType.startsWith('save_')) return 'Nothing was saved.';
        return 'Action canceled. No changes were made.';
    };

    const getConfirmableActionDisplaySubject = (action) => {
        if (!action || typeof action !== 'object') return 'Confirm action';
        const rawSubject = String(action.subject || '').trim();
        if (rawSubject) return rawSubject;
        const confirmLabel = String(action.confirm_label || '').trim();
        return confirmLabel || String(action.title || 'Confirm action').trim() || 'Confirm action';
    };

    const renderPermissionActionIcon = (actionType) => {
        const normalizedType = String(actionType || '').trim();
        const iconProps = { size: 15, strokeWidth: 2.1, 'aria-hidden': 'true' };
        if (normalizedType === 'workspace_navigation') return <Navigation {...iconProps} />;
        if (normalizedType === 'save_legal_draft_docx') return <Save {...iconProps} />;
        if (normalizedType === 'save_report') return <Import {...iconProps} />;
        return <Save {...iconProps} />;
    };

    const getConfirmableActionDetails = (message) => {
        if (!message) return null;
        const metadata = message.metadata || {};
        const intelligence = metadata.intelligence || {};
        const actionCandidates = [
            message.answer?.confirmable_action,
            metadata.confirmable_action,
            intelligence.answer?.confirmable_action,
        ].filter(candidate => candidate && typeof candidate === 'object');
        const action = actionCandidates.find(candidate => ['confirmed', 'declined'].includes(String(candidate.status || '').trim()))
            || actionCandidates.find(candidate => candidate.result || candidate.generated_docx)
            || actionCandidates[0]
            || null;
        if (!action || typeof action !== 'object') return null;
        const type = String(action.type || '').trim();
        if (!type) return null;
        const status = String(action.status || 'pending_permission').trim() || 'pending_permission';
        const generatedFile = message.answer?.generated_docx
            || action.result
            || (Array.isArray(message.files) ? message.files.find(file => Number(file?.file_id || file?.id || 0) > 0) : null);
        const inferredDraftFileName = type === 'save_legal_draft_docx'
            ? String(action.subject || action.payload?.filename || generatedFile?.file_name || generatedFile?.name || '').trim()
            : '';
        const displayAction = inferredDraftFileName && !String(action.subject || '').trim()
            ? { ...action, subject: inferredDraftFileName }
            : action;
        const defaultConfirmLabel = type === 'save_legal_draft_docx' ? 'Save DOCX' : 'Confirm';
        return {
            ...displayAction,
            type,
            status,
            title: getConfirmableActionDisplayTitle(displayAction),
            subject: getConfirmableActionDisplaySubject(displayAction),
            summary: String(action.summary || '').trim(),
            confirmLabel: String(action.confirm_label || defaultConfirmLabel).trim() || defaultConfirmLabel,
            cancelLabel: String(action.cancel_label || 'Cancel').trim() || 'Cancel',
            pendingText: String(action.pending_text || 'Awaiting user action.').trim() || 'Awaiting user action.',
        };
    };

    const getConfirmableActionPayloadForMessage = (message, actionDetails) => {
        const editKey = String(message?.id || '');
        return confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
    };

    const getLegalDraftPreviewPayload = (message, actionDetails) => {
        if (actionDetails?.type !== 'save_legal_draft_docx' || actionDetails?.status !== 'pending_permission') return null;
        const payload = getConfirmableActionPayloadForMessage(message, actionDetails);
        const draftText = String(payload?.draft_text || message?.answer?.draft_text || '').trim();
        if (!draftText) return null;
        return { ...payload, draft_text: draftText };
    };

    const buildGeneratedDraftDocxPreview = (message, actionDetails = null, overrides = {}) => {
        const answer = overrides.answer || message?.answer || {};
        const metadata = overrides.metadata || message?.metadata || {};
        const files = Array.isArray(overrides.files) ? overrides.files : (Array.isArray(message?.files) ? message.files : []);
        const draftIntent = answer?.draft_intent || actionDetails?.payload?.draft_intent || metadata?.draft_intent || {};
        const documentType = String(draftIntent?.document_type || answer?.document_type || actionDetails?.subject || 'legal draft').trim() || 'legal draft';
        const generatedFileCandidates = [
            answer?.generated_docx,
            answer?.generated_file,
            actionDetails?.result,
            actionDetails?.generated_docx,
            actionDetails?.payload?.generated_docx,
            metadata?.confirmable_action?.result,
            metadata?.confirmable_action?.generated_docx,
            ...files.filter((file) => {
                const sourceLabel = String(file?.source_type || file?.sourceType || file?.chunk_source || file?.chunkSource || '').toLowerCase();
                const name = String(file?.file_name || file?.name || file?.filename || '').toLowerCase();
                return sourceLabel.includes('generated_draft_docx') || (name.endsWith('.docx') && /draft|brief|motion|pleading|letter|memo|memorandum/i.test(name));
            }),
        ].filter(Boolean);
        const generatedFile = generatedFileCandidates.find((file) => Number(file?.file_id || file?.id || file?.fileId || file?.document_id || 0) > 0);
        if (!generatedFile) return null;
        const fileId = Number(generatedFile.file_id || generatedFile.id || generatedFile.fileId || generatedFile.document_id || 0);
        const fileName = String(generatedFile.file_name || generatedFile.name || generatedFile.filename || generatedFile.display_name || '').trim() || `${documentType}.docx`;
        const sourceType = generatedFile.source_type || generatedFile.sourceType || 'generated_draft_docx';
        return {
            fileId,
            fileName,
            caseId: generatedFile.case_id || answer?.case_id || draftIntent?.case_id || null,
            caseName: generatedFile.case_name || answer?.case_name || draftIntent?.case_name || '',
            storageKey: generatedFile.storage_key || generatedFile.key || generatedFile.file_path || generatedFile.path || '',
            pageLabel: '',
            chunkIndex: null,
            registryId: generatedFile.registry_id || null,
            matchedChunk: null,
            sourceType,
            chunkSource: generatedFile.chunk_source || generatedFile.chunkSource || sourceType,
        };
    };

    const cloneConfirmableFieldReview = (fieldReview) => {
        if (!fieldReview || typeof fieldReview !== 'object') return null;
        return {
            ...fieldReview,
            rows: Array.isArray(fieldReview.rows) ? fieldReview.rows.map(row => ({ ...row })) : [],
            sections: Array.isArray(fieldReview.sections)
                ? fieldReview.sections.map(section => ({
                    ...section,
                    rows: Array.isArray(section.rows) ? section.rows.map(row => ({ ...row })) : [],
                }))
                : [],
        };
    };

    const updateConfirmableFieldReviewRow = (fieldReview, rowIndex, updater) => {
        if (!fieldReview || typeof updater !== 'function') return fieldReview;
        const applyUpdate = (row) => {
            const candidateIndex = Number(row?.row_index);
            if ((Number.isFinite(candidateIndex) && candidateIndex === rowIndex) || (!Number.isFinite(candidateIndex) && false)) {
                return updater(row);
            }
            return row;
        };
        return {
            ...fieldReview,
            rows: Array.isArray(fieldReview.rows) ? fieldReview.rows.map((row, index) => (index === rowIndex ? updater(row) : row)) : [],
            sections: Array.isArray(fieldReview.sections)
                ? fieldReview.sections.map(section => ({
                    ...section,
                    rows: Array.isArray(section.rows) ? section.rows.map(applyUpdate) : [],
                }))
                : [],
        };
    };

    const getConfirmableFieldIncludedCount = (payload) => {
        const fieldWrites = Array.isArray(payload?.field_writes) ? payload.field_writes : [];
        return fieldWrites.filter(write => write && write.include !== false).length;
    };

    const filterConfirmableFieldPayloadForSubmit = (payload) => {
        if (!payload || !Array.isArray(payload.field_writes)) return payload;
        const includedIndexes = new Set();
        const fieldWrites = payload.field_writes
            .map((write, index) => ({ write, index }))
            .filter(({ write, index }) => {
                const included = write && write.include !== false;
                if (included) includedIndexes.add(index);
                return included;
            })
            .map(({ write }) => {
                const { include, ...cleanWrite } = write;
                return cleanWrite;
            });
        const fieldReview = payload.field_review && typeof payload.field_review === 'object'
            ? {
                ...payload.field_review,
                rows: Array.isArray(payload.field_review.rows)
                    ? payload.field_review.rows.filter((row, index) => includedIndexes.has(Number.isFinite(Number(row?.row_index)) ? Number(row.row_index) : index))
                    : [],
                sections: Array.isArray(payload.field_review.sections)
                    ? payload.field_review.sections.map(section => ({
                        ...section,
                        rows: Array.isArray(section.rows)
                            ? section.rows.filter((row) => includedIndexes.has(Number(row?.row_index)))
                            : [],
                    })).filter(section => Array.isArray(section.rows) && section.rows.length > 0)
                    : [],
            }
            : null;
        return {
            ...payload,
            field_writes: fieldWrites,
            ...(fieldReview ? { field_review: fieldReview } : {}),
        };
    };

    const getConfirmableActionForSubmit = (message, actionDetails) => {
        const editKey = String(message?.id || '');
        const editedPayload = confirmableActionEdits.get(editKey);
        const payloadForSubmit = filterConfirmableFieldPayloadForSubmit(editedPayload || actionDetails?.payload || {});
        if (!editedPayload && payloadForSubmit === (actionDetails?.payload || {})) return actionDetails;
        const { payload_hash, idempotency_key, ...unsealedAction } = actionDetails;
        return {
            ...unsealedAction,
            payload: payloadForSubmit,
            edited_payload: true,
        };
    };

    const mergeAnswerArrayByFingerprint = (baseList, extraList) => {
        const merged = [];
        const seen = new Set();
        [...(Array.isArray(baseList) ? baseList : []), ...(Array.isArray(extraList) ? extraList : [])].forEach((item) => {
            if (!item || typeof item !== 'object') return;
            const fingerprint = [
                item.file_id || item.id || '',
                item.url || item.file_path || item.storage_key || item.key || '',
                item.source_id || item.citation_number || item.number || '',
                item.title || item.file_name || item.name || '',
            ].join('|');
            if (seen.has(fingerprint)) return;
            seen.add(fingerprint);
            merged.push(item);
        });
        return merged;
    };

    const stripPendingReportSaveCopy = (value) => String(value || '')
        .replace(/\n{2,}I have not saved it yet\.\s*Confirm below to create the report\.\s*$/i, '')
        .trim();

    const mergeConfirmedLegalDraftAnswer = (existingAnswer, responseAnswer, actionDetails, messageContent = '') => {
        const confirmedAction = responseAnswer.confirmable_action || {
            ...actionDetails,
            status: 'confirmed',
            permission_state: 'granted',
            payload: null,
        };
        const existingAnswerText = String(existingAnswer.answer_text || messageContent || '').trim();
        const responseAnswerText = String(responseAnswer.answer_text || '').trim();
        let nextAnswerText = responseAnswerText || existingAnswerText;
        if (actionDetails?.type === 'save_report' && existingAnswerText) {
            const preservedAnswerText = stripPendingReportSaveCopy(existingAnswerText);
            const backendAlreadyPreservedAnswer = preservedAnswerText
                && responseAnswerText.toLowerCase().includes(preservedAnswerText.toLowerCase());
            nextAnswerText = backendAlreadyPreservedAnswer
                ? responseAnswerText
                : [preservedAnswerText, responseAnswerText].filter(Boolean).join('\n\n');
        }
        return {
            ...existingAnswer,
            ...responseAnswer,
            answer_text: nextAnswerText,
            citations: mergeAnswerArrayByFingerprint(existingAnswer.citations, responseAnswer.citations),
            source_descriptions: mergeAnswerArrayByFingerprint(existingAnswer.source_descriptions, responseAnswer.source_descriptions),
            confirmable_action: confirmedAction,
        };
    };

    const handleConfirmableFieldProposalChange = (message, actionDetails, rowIndex, nextValue) => {
        const editKey = String(message?.id || '');
        if (!editKey) return;
        const basePayload = confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
        const fieldWrites = Array.isArray(basePayload.field_writes) ? basePayload.field_writes.map(write => ({ ...write })) : [];
        let fieldReview = cloneConfirmableFieldReview(basePayload.field_review);
        if (!fieldWrites[rowIndex]) return;
        fieldWrites[rowIndex] = {
            ...fieldWrites[rowIndex],
            value: nextValue,
            proposed_value: nextValue,
        };
        if (fieldReview) {
            fieldReview = updateConfirmableFieldReviewRow(fieldReview, rowIndex, (row) => ({
                ...row,
                proposed_value: nextValue,
            }));
        }
        const nextPayload = {
            ...basePayload,
            field_writes: fieldWrites,
            ...(fieldReview ? { field_review: fieldReview } : {}),
        };
        setConfirmableActionEdits(prev => {
            const next = new Map(prev);
            next.set(editKey, nextPayload);
            return next;
        });
    };

    const handleConfirmableFieldContactChange = (message, actionDetails, rowIndex, contact) => {
        const editKey = String(message?.id || '');
        if (!editKey) return;
        const basePayload = confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
        const fieldWrites = Array.isArray(basePayload.field_writes) ? basePayload.field_writes.map(write => ({ ...write })) : [];
        let fieldReview = cloneConfirmableFieldReview(basePayload.field_review);
        if (!fieldWrites[rowIndex]) return;
        const contactId = contact && typeof contact === 'object' ? Number(contact.id || contact.contact_id || 0) : Number(contact || 0);
        const contactLabel = contact && typeof contact === 'object'
            ? String(contact.full_name || contact.contact_name || contact.name || contact.company_name || '').trim()
            : '';
        const nextValue = contactId > 0 ? String(contactId) : '';
        const nextInclude = contactId > 0;
        fieldWrites[rowIndex] = {
            ...fieldWrites[rowIndex],
            value: nextValue,
            proposed_value: nextValue,
            proposed_display_value: contactLabel || nextValue,
            field_type: 'contact',
            field_type_id: 8,
            create_contact_if_missing: false,
            contact_name: '',
            proposed_contact: null,
            include: nextInclude,
        };
        if (fieldReview) {
            fieldReview = updateConfirmableFieldReviewRow(fieldReview, rowIndex, (row) => ({
                ...row,
                proposed_value: nextValue,
                proposed_display_value: contactLabel || nextValue,
                field_type: 'contact',
                field_type_id: 8,
                create_contact_if_missing: false,
                contact_name: '',
                proposed_contact: null,
                include: nextInclude,
            }));
        }
        const nextPayload = {
            ...basePayload,
            field_writes: fieldWrites,
            ...(fieldReview ? { field_review: fieldReview } : {}),
        };
        setConfirmableActionEdits(prev => {
            const next = new Map(prev);
            next.set(editKey, nextPayload);
            return next;
        });
    };

    const isConfirmableContactFieldType = (fieldTypeId, fieldType) => {
        const numericTypeId = Number(fieldTypeId || 0);
        const normalizedType = String(fieldType || '').trim().toLowerCase();
        return numericTypeId === 8 || ['contact', 'person'].includes(normalizedType);
    };

    const handleConfirmableFieldTargetChange = (message, actionDetails, rowIndex, selectedFieldId) => {
        const editKey = String(message?.id || '');
        const numericFieldId = Number(selectedFieldId || 0);
        if (!editKey || !numericFieldId) return;
        const basePayload = confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
        const fieldWrites = Array.isArray(basePayload.field_writes) ? basePayload.field_writes.map(write => ({ ...write })) : [];
        let fieldReview = cloneConfirmableFieldReview(basePayload.field_review);
        const row = fieldReview?.rows?.[rowIndex] || {};
        const options = Array.isArray(row.field_options) ? row.field_options : [];
        const resolvedOptions = Array.isArray(fieldReview?.resolved_fields) ? fieldReview.resolved_fields : [];
        const selectedOption = options.find(option => Number(option.field_id || option.id || 0) === numericFieldId)
            || resolvedOptions.find(option => Number(option.field_id || option.id || 0) === numericFieldId)
            || null;
        const nextLabel = String(selectedOption?.label || selectedOption?.name || fieldWrites[rowIndex]?.label || fieldWrites[rowIndex]?.field_name || row.label || `Field ${rowIndex + 1}`);
        const nextFieldTypeId = Number(selectedOption?.field_type_id || 0);
        const nextFieldType = isConfirmableContactFieldType(nextFieldTypeId, selectedOption?.field_type) ? 'contact' : '';
        const nextValue = nextFieldType === 'contact' && !/^\d+$/.test(String(fieldWrites[rowIndex]?.value || fieldWrites[rowIndex]?.proposed_value || ''))
            ? ''
            : (fieldWrites[rowIndex]?.value ?? fieldWrites[rowIndex]?.proposed_value ?? '');
        if (!fieldWrites[rowIndex]) return;
        fieldWrites[rowIndex] = {
            ...fieldWrites[rowIndex],
            field_id: numericFieldId,
            custom_field_id: numericFieldId,
            field_name: nextLabel,
            label: nextLabel,
            field_type_id: nextFieldTypeId,
            field_type: nextFieldType,
            value: nextValue,
            proposed_value: nextValue,
            include: nextFieldType === 'contact' && !nextValue ? false : fieldWrites[rowIndex].include,
        };
        if (fieldReview) {
            fieldReview = updateConfirmableFieldReviewRow(fieldReview, rowIndex, (reviewRow) => ({
                ...reviewRow,
                field_id: numericFieldId,
                label: nextLabel,
                field_type_id: nextFieldTypeId,
                field_type: nextFieldType,
                proposed_value: nextValue,
                include: nextFieldType === 'contact' && !nextValue ? false : reviewRow.include,
            }));
        }
        const nextPayload = {
            ...basePayload,
            field_writes: fieldWrites,
            ...(fieldReview ? { field_review: fieldReview } : {}),
        };
        setConfirmableActionEdits(prev => {
            const next = new Map(prev);
            next.set(editKey, nextPayload);
            return next;
        });
    };

    const handleConfirmableFieldIncludeChange = (message, actionDetails, rowIndex, include) => {
        const editKey = String(message?.id || '');
        if (!editKey) return;
        const basePayload = confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
        const fieldWrites = Array.isArray(basePayload.field_writes) ? basePayload.field_writes.map(write => ({ ...write })) : [];
        let fieldReview = cloneConfirmableFieldReview(basePayload.field_review);
        if (!fieldWrites[rowIndex]) return;
        fieldWrites[rowIndex] = {
            ...fieldWrites[rowIndex],
            include: Boolean(include),
        };
        if (fieldReview) {
            fieldReview = updateConfirmableFieldReviewRow(fieldReview, rowIndex, (row) => ({
                ...row,
                include: Boolean(include),
            }));
        }
        const nextPayload = {
            ...basePayload,
            field_writes: fieldWrites,
            ...(fieldReview ? { field_review: fieldReview } : {}),
        };
        setConfirmableActionEdits(prev => {
            const next = new Map(prev);
            next.set(editKey, nextPayload);
            return next;
        });
    };

    const renderConfirmableFieldReview = (message, actionDetails) => {
        const payload = getConfirmableActionPayloadForMessage(message, actionDetails);
        const review = payload?.field_review && typeof payload.field_review === 'object' ? payload.field_review : null;
        const fieldWrites = Array.isArray(payload?.field_writes) ? payload.field_writes : [];
        const resolvedFieldOptions = Array.isArray(review?.resolved_fields) ? review.resolved_fields : [];
        const rows = Array.isArray(review?.rows) && review.rows.length > 0
            ? review.rows
            : fieldWrites.map((write, index) => ({
                row_index: index,
                label: write.label || write.field_name || `Field ${index + 1}`,
                current_value: write.current_value || '',
                proposed_value: write.value ?? write.proposed_value ?? '',
                proposed_display_value: write.proposed_display_value || '',
                extracted_value: write.extracted_value || '',
                create_contact_if_missing: Boolean(write.create_contact_if_missing),
                proposed_contact: write.proposed_contact || null,
                field_type_id: write.field_type_id || '',
                field_type: write.field_type || '',
                contact_match: write.contact_match || null,
                confidence: write.confidence || '',
                source: write.source || null,
                add_item: write.add_item || 0,
                storage_scope: write.storage_scope || (Number(write.add_item || 0) === 1 ? 'grouped' : 'standalone'),
                include: write.include !== false,
                existing_matches: Array.isArray(write.existing_matches) ? write.existing_matches : [],
            }));
        if (rows.length === 0) return null;
        const pendingNewItem = Boolean(review?.pending_new_item || payload?.pending_new_item || payload?.create_group);
        const groupId = review?.group_id ?? payload?.matched_group_id ?? null;
        const reviewSections = Array.isArray(review?.sections) && review.sections.length > 0
            ? review.sections.map((section, sectionIndex) => ({
                key: String(section.key || `section-${sectionIndex}`),
                label: String(section.label || (section.storage_scope === 'grouped' ? 'Grouped field item' : 'Standalone case fields')),
                description: String(section.description || ''),
                storage_scope: String(section.storage_scope || ''),
                pending_new_item: Boolean(section.pending_new_item),
                group_id: section.group_id ?? null,
                current_values: section.current_values || {},
                rows: Array.isArray(section.rows) ? section.rows : [],
            })).filter(section => section.rows.length > 0)
            : [];
        const displaySections = reviewSections.length > 0
            ? reviewSections
            : [{
                key: 'all',
                label: pendingNewItem ? 'New grouped item' : (groupId ? `Group ${groupId}` : 'Field updates'),
                description: '',
                storage_scope: pendingNewItem || groupId ? 'grouped' : 'standalone',
                pending_new_item: pendingNewItem,
                group_id: groupId,
                current_values: review?.current_group_values || {},
                rows,
            }];
        const totalRows = displaySections.reduce((count, section) => count + section.rows.length, 0);
        return (
            <div className="library-rag-field-review" aria-label="Proposed field updates">
                <div className="library-rag-field-review-header">
                    <span>{displaySections.length > 1 ? 'Separated field updates' : displaySections[0]?.label}</span>
                    <small>{totalRows} proposed update{totalRows === 1 ? '' : 's'}</small>
                </div>
                <div className="library-rag-field-review-sections">
                    {displaySections.map((section, sectionIndex) => {
                        const currentValues = Object.values(section.current_values || {})
                            .filter(value => value && typeof value === 'object' && String(value.label || value.field_name || '').trim())
                            .slice(0, 8);
                        return (
                            <section key={`field-review-section-${message.id}-${section.key}-${sectionIndex}`} className={`library-rag-field-review-section ${section.storage_scope === 'grouped' ? 'grouped' : 'standalone'}`}>
                                <div className="library-rag-field-review-section-heading">
                                    <span>{section.label}</span>
                                    <small>{section.storage_scope === 'grouped' ? (section.pending_new_item ? 'New add-item group' : (section.group_id ? `Group ${section.group_id}` : 'Grouped')) : 'Non-grouped fields'}</small>
                                </div>
                                {section.description ? <p>{section.description}</p> : null}
                                {currentValues.length > 0 ? (
                                    <div className="library-rag-field-review-current-group" aria-label="Current section values">
                                        {currentValues.map((value, valueIndex) => (
                                            <span key={`field-review-current-${message.id}-${section.key}-${valueIndex}`}>
                                                <strong>{String(value.label || value.field_name || `Field ${valueIndex + 1}`)}</strong>
                                                {String(value.value || value.raw_value || '').trim() || 'blank'}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                                <div className="library-rag-field-review-list">
                                    {section.rows.map((row, sectionRowIndex) => {
                        const rowIndexValue = Number(row.row_index);
                        const rowIndex = Number.isFinite(rowIndexValue) && rowIndexValue >= 0 ? rowIndexValue : sectionRowIndex;
                        const write = fieldWrites[rowIndex] || {};
                        const source = row.source || write.source || {};
                        const sourceLabel = String(source.file_name || source.label || source.source_type || '').trim();
                        const excerpt = String(source.excerpt || '').trim();
                        const proposedValue = write.value ?? write.proposed_value ?? row.proposed_value ?? '';
                        const proposedDisplayValue = String(write.proposed_display_value || row.proposed_display_value || '').trim();
                        const extractedValue = String(write.extracted_value || row.extracted_value || '').trim();
                        const createContactIfMissing = Boolean(write.create_contact_if_missing || row.create_contact_if_missing);
                        const proposedContact = write.proposed_contact || row.proposed_contact || null;
                        const contactMatchStatus = String(write.contact_match?.status || row.contact_match?.status || '').trim();
                        const fieldOptions = Array.isArray(row.field_options) ? row.field_options.filter(option => Number(option.field_id || option.id || 0) > 0) : [];
                        const selectedFieldId = Number(write.field_id || write.custom_field_id || row.field_id || 0);
                        const selectedFieldOption = fieldOptions.find(option => Number(option.field_id || option.id || 0) === selectedFieldId)
                            || resolvedFieldOptions.find(option => Number(option.field_id || option.id || 0) === selectedFieldId)
                            || null;
                        const fieldTypeId = Number(write.field_type_id || row.field_type_id || selectedFieldOption?.field_type_id || 0);
                        const fieldType = String(write.field_type || row.field_type || selectedFieldOption?.field_type || '').toLowerCase();
                        const isContactField = isConfirmableContactFieldType(fieldTypeId, fieldType);
                        const proposedValueText = String(proposedValue ?? '').trim();
                        const contactSelectedValue = isContactField && /^\d+$/.test(proposedValueText) ? proposedValueText : '';
                        const contactExtractedValue = isContactField && extractedValue ? extractedValue : (isContactField && proposedValueText && !contactSelectedValue ? proposedValueText : '');
                        const rowIncluded = (write.include ?? row.include) !== false;
                        const existingMatches = Array.isArray(row.existing_matches)
                            ? row.existing_matches
                            : (Array.isArray(write.existing_matches) ? write.existing_matches : []);
                        const existingMatchText = existingMatches.length > 0
                            ? existingMatches.map(match => {
                                const matchGroupId = Number(match.group_id || 0);
                                const matchValue = String(match.value || '').trim();
                                return `${matchGroupId > 0 ? `group ${matchGroupId}` : 'non-grouped field'}${matchValue ? `: ${matchValue}` : ''}`;
                            }).join('; ')
                            : '';
                        return (
                            <div key={`field-review-${message.id}-${section.key}-${rowIndex}`} className={`library-rag-field-review-row${rowIncluded || isContactField ? '' : ' excluded'}`}>
                                <span className="library-rag-field-review-label">
                                    <strong>{String(row.label || write.label || write.field_name || `Field ${rowIndex + 1}`)}</strong>
                                    {row.confidence ? <small>{String(row.confidence)} confidence</small> : null}
                                </span>
                                <div className="library-rag-field-review-row-top">
                                </div>
                                {fieldOptions.length > 1 ? (
                                    <select
                                        value={selectedFieldId || ''}
                                        onChange={(event) => handleConfirmableFieldTargetChange(message, actionDetails, rowIndex, event.target.value)}
                                        disabled={!rowIncluded || confirmableActionBusyMessageIds.has(String(message.id))}
                                        aria-label={`Target field for ${String(row.label || write.label || write.field_name || `field ${rowIndex + 1}`)}`}
                                    >
                                        {fieldOptions.map((option) => {
                                            const optionFieldId = Number(option.field_id || option.id || 0);
                                            return (
                                                <option key={`field-review-option-${message.id}-${rowIndex}-${optionFieldId}`} value={optionFieldId}>
                                                    {String(option.label || option.name || `Field ${optionFieldId}`)}{option.section_id ? ` · section ${option.section_id}` : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : null}
                                <span className="library-rag-field-review-current">
                                    Current: {String(row.current_value || '').trim() || 'blank'}
                                </span>
                                {existingMatchText ? (
                                    <span className="library-rag-field-review-existing">
                                        Similar value already exists in {existingMatchText}
                                    </span>
                                ) : null}
                                {isContactField ? (
                                    <div className="library-rag-field-review-contact">
                                        {contactExtractedValue && !contactSelectedValue ? (
                                            <span className="library-rag-field-review-contact-hint">
                                                Extracted: {contactExtractedValue}. Select the matching contact before saving.
                                            </span>
                                        ) : null}
                                        {contactMatchStatus && contactMatchStatus !== 'resolved_name' && contactMatchStatus !== 'resolved_id' ? (
                                            <span className="library-rag-field-review-contact-hint">
                                                Contact match: {contactMatchStatus}
                                            </span>
                                        ) : null}
                                        {createContactIfMissing && proposedContact ? (
                                            <span className="library-rag-field-review-contact-hint">
                                                Will create contact on save: {String(proposedContact.full_name || proposedContact.company_name || contactExtractedValue)}
                                            </span>
                                        ) : null}
                                        <Contact
                                            selectedContact={contactSelectedValue}
                                            setSelectedContact={(contact) => handleConfirmableFieldContactChange(message, actionDetails, rowIndex, contact)}
                                            disabled={confirmableActionBusyMessageIds.has(String(message.id))}
                                            noNewContacts
                                        />
                                        {proposedDisplayValue && contactSelectedValue ? (
                                            <span className="library-rag-field-review-contact-hint">Saving contact: {proposedDisplayValue}</span>
                                        ) : null}
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        value={String(proposedValue ?? '')}
                                        onChange={(event) => handleConfirmableFieldProposalChange(message, actionDetails, rowIndex, event.target.value)}
                                        disabled={!rowIncluded || confirmableActionBusyMessageIds.has(String(message.id))}
                                    />
                                )}
                                <div className='library-rag-field-review-row-bottom'>
                                    {sourceLabel || excerpt ? (
                                        <span className="library-rag-field-review-source">
                                            {sourceLabel ? `Source: ${sourceLabel}` : 'Source excerpt'}
                                            {excerpt ? ` - ${excerpt}` : ''}
                                        </span>
                                    ) : null}
                                    <Checkbox
                                        mini
                                        value={rowIncluded}
                                        onChange={() => handleConfirmableFieldIncludeChange(message, actionDetails, rowIndex, !rowIncluded)}
                                        label={rowIncluded ? 'Save this field' : 'Do not save'}
                                    />
                                </div>
                            </div>
                        );
                    })}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </div>
        );
    };

    const getWorkspaceCaseSelectionDetails = (message) => {
        if (!message) return null;
        const metadata = message.metadata || {};
        const intelligence = metadata.intelligence || {};
        const caseSelection = metadata.case_selection || intelligence.case_selection || null;
        const caseMatches = Array.isArray(metadata.case_matches)
            ? metadata.case_matches
            : (Array.isArray(caseSelection?.case_matches)
                ? caseSelection.case_matches
                : (Array.isArray(intelligence.case_matches) ? intelligence.case_matches : []));
        const originalPrompt = String(metadata.original_prompt || caseSelection?.prompt || intelligence.original_prompt || intelligence.query || '').trim();
        const status = String(metadata.workspace_case_selection_status || caseSelection?.status || 'pending_permission').trim() || 'pending_permission';
        if (!caseSelection && !metadata.workspace_case_selection_required && caseMatches.length === 0) return null;
        const answerMode = String(message.answer?.mode || intelligence.answer?.mode || intelligence.retrieval_mode || '').trim();
        const inferredKind = answerMode === 'case_name_confirmation' ? 'case_name_confirmation' : 'workspace_navigation';
        return {
            caseMatches,
            originalPrompt,
            status,
            kind: String(caseSelection?.kind || metadata.case_selection_kind || inferredKind).trim() || inferredKind,
            title: String(caseSelection?.title || 'Which case ID or name should I use?').trim() || 'Which case ID or name should I use?',
            subject: String(caseSelection?.subject || 'Enter case ID or name').trim() || 'Enter case ID or name',
            summary: String(caseSelection?.summary || 'Choose the case ID or name to continue this workspace action.').trim(),
            pendingText: String(caseSelection?.pending_text || 'Awaiting case ID or name.').trim() || 'Awaiting case ID or name.',
        };
    };

    const getPendingRagActionTarget = (messages = []) => {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (!message || message.role !== 'assistant') continue;

            const workspaceNavigationDetails = getWorkspaceNavigationDetails(message);
            if (workspaceNavigationDetails?.status === 'pending_permission' || workspaceNavigationPendingMessageIds.has(String(message.id))) {
                return { type: 'workspace_navigation', message, details: workspaceNavigationDetails };
            }

            const confirmableActionDetails = getConfirmableActionDetails(message);
            if (confirmableActionDetails?.status === 'pending_permission') {
                return { type: 'confirmable_action', message, details: confirmableActionDetails };
            }

            const workspaceCaseSelectionDetails = getWorkspaceCaseSelectionDetails(message);
            if (workspaceCaseSelectionDetails?.status === 'pending_permission' && workspaceCaseSelectionDetails.caseMatches.length > 0) {
                return { type: 'workspace_case_selection', message, details: workspaceCaseSelectionDetails };
            }
        }
        return null;
    };

    const handleConfirmWorkspaceCaseSelection = async (message, selectedCase) => {
        if (!hasJaneUserIdentity) return;
        const caseSelectionDetails = getWorkspaceCaseSelectionDetails(message);
        const selectedCaseId = Number(selectedCase?.case_id || selectedCase?.id || 0);
        if (!caseSelectionDetails || !selectedCaseId) return;
        const messageKey = String(message?.id || '');
        const numericMessageId = Number(message?.id || 0);
        if (!messageKey || !Number.isInteger(numericMessageId) || numericMessageId <= 0 || confirmableActionBusyRef.current.has(messageKey)) return;
        const actionChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        const operationContext = captureRagChatOperationContext(actionChatId);
        if (!Number.isFinite(actionChatId) || actionChatId <= 0 || !isCurrentRagChatOperation(operationContext)) return;

        const originalPrompt = String(caseSelectionDetails.originalPrompt || message?.content || '').trim();
        if (!originalPrompt) return;
        const confirmedBy = String(user?.name || user?.user_name || user?.display_name || 'You').trim() || 'You';
        confirmableActionBusyRef.current.add(messageKey);

        setConfirmableActionErrorByMessageId(prev => {
            if (!prev[messageKey]) return prev;
            const next = { ...prev };
            delete next[messageKey];
            return next;
        });

        setConfirmableActionBusyMessageIds(prev => {
            const next = new Set(prev);
            next.add(String(message.id));
            return next;
        });

        try {
            const selectedCaseName = String(selectedCase.case_name || selectedCase.name || `Case ${selectedCaseId}`).trim();
            if (caseSelectionDetails.kind !== 'workspace_navigation') {
                const caseReference = {
                    id: `case-${selectedCaseId}`,
                    type: 'case',
                    label: selectedCaseName,
                    case_id: selectedCaseId,
                    url: selectedCase.url || `https://casedb.co/case/${selectedCaseId}`,
                    source: 'case_selection',
                };
                const confirmedAt = new Date().toISOString();
                const existingMetadata = message.metadata || {};
                const existingIntelligence = existingMetadata.intelligence || {};
                const resolvedCaseSelection = {
                    ...(existingMetadata.case_selection || existingIntelligence.case_selection || {}),
                    status: 'confirmed',
                    selected_case_id: selectedCaseId,
                    selected_case_name: selectedCaseName,
                    confirmed_by: confirmedBy,
                    confirmed_by_user_id: userId,
                    confirmed_at: confirmedAt,
                };
                const confirmedAnswer = {
                    ...(message.answer || {}),
                    mode: 'case_selection_confirmed',
                    answer_text: `Using ${selectedCaseName} for this request.`,
                    citations: [],
                    source_descriptions: [],
                    case_selection: resolvedCaseSelection,
                };
                const confirmedMetadata = {
                    ...existingMetadata,
                    workspace_case_selection_required: false,
                    workspace_case_selection_status: 'confirmed',
                    case_selection_kind: caseSelectionDetails.kind,
                    case_selection: resolvedCaseSelection,
                    selected_case_id: selectedCaseId,
                    selected_case_name: selectedCaseName,
                    confirmed_by: confirmedBy,
                    confirmed_by_user_id: userId,
                    confirmed_at: confirmedAt,
                    intelligence: {
                        ...existingIntelligence,
                        case_selection: resolvedCaseSelection,
                        answer: {
                            ...(existingIntelligence.answer || {}),
                            mode: 'case_selection_confirmed',
                            case_selection: resolvedCaseSelection,
                        },
                    },
                };
                const persistedSelection = await persistRagMessageUpdate({
                    chatId: actionChatId,
                    messageId: message.id,
                    content: `Using ${selectedCaseName} for this request.`,
                    answer: confirmedAnswer,
                    metadata: confirmedMetadata,
                    files: message.files || [],
                });
                if (!persistedSelection || !isCurrentRagChatOperation(operationContext)) {
                    if (isCurrentRagChatOperation(operationContext)) {
                        clearRagPersistenceFailure(message.id);
                        setConfirmableActionErrorByMessageId(prev => ({
                            ...prev,
                            [messageKey]: 'CaseDB could not save that case choice. Please try again.',
                        }));
                    }
                    return;
                }
                setRagReferenceContext({ id: `case-${selectedCaseId}`, label: selectedCaseName, value: `case:${selectedCaseId}`, source: 'case_selection' });
                setRagReferences(prev => {
                    const next = prev.filter(reference => !(reference?.type === 'case' && Number(reference.case_id || reference.id) === selectedCaseId));
                    return [caseReference, ...next];
                });
                setRagMessages(prev => prev.map(currentMessage => (
                    String(currentMessage.id) === String(message.id)
                        ? {
                            ...currentMessage,
                            content: `Using ${selectedCaseName} for this request.`,
                            answer: confirmedAnswer,
                            metadata: confirmedMetadata,
                        }
                        : currentMessage
                )));
                await executeRagPrompt(originalPrompt, [caseReference], {
                    appendUserMessage: false,
                    caseIdOverride: selectedCaseId,
                    clearComposer: true,
                    confirmedCaseId: true,
                    force: true,
                    chatIdOverride: actionChatId,
                    replaceAssistantMessageId: message.id,
                    selectedSourcesOnly: false,
                });
                return;
            }

            const workspaceConfirmedAt = new Date().toISOString();
            const workspaceExistingMetadata = message.metadata || {};
            const workspaceExistingIntelligence = workspaceExistingMetadata.intelligence || {};
            const workspaceResolvedCaseSelection = {
                ...(workspaceExistingMetadata.case_selection || workspaceExistingIntelligence.case_selection || {}),
                status: 'confirmed',
                selected_case_id: selectedCaseId,
                selected_case_name: selectedCaseName,
                confirmed_by: confirmedBy,
                confirmed_by_user_id: userId,
                confirmed_at: workspaceConfirmedAt,
            };
            const nextMetadata = {
                ...workspaceExistingMetadata,
                workspace_case_selection_required: false,
                workspace_case_selection_status: 'confirmed',
                case_selection: workspaceResolvedCaseSelection,
                selected_case_id: selectedCaseId,
                selected_case_name: selectedCaseName,
                confirmed_by: confirmedBy,
                confirmed_by_user_id: userId,
                confirmed_at: workspaceConfirmedAt,
                intelligence: {
                    ...workspaceExistingIntelligence,
                    case_selection: workspaceResolvedCaseSelection,
                    answer: {
                        ...(workspaceExistingIntelligence.answer || {}),
                        mode: 'case_selection_confirmed',
                        case_selection: workspaceResolvedCaseSelection,
                    },
                },
            };

            const planResponse = await fetch(`${API_BASE}/jane-workspace.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    case_id: selectedCaseId,
                    confirmed_case_id: true,
                    prompt: originalPrompt,
                    current_path: `${window.location.pathname}${window.location.search}`,
                }),
            });
            const planData = await planResponse.json();
            if (!isCurrentRagChatOperation(operationContext)) return;
            if (!planResponse.ok || !planData?.success || planData.mode !== 'workspace_navigation') {
                throw new Error(planData?.message || 'Could not prepare the selected case workspace action.');
            }
            const workspaceNavigation = buildWorkspaceNavigationMessage(planData, originalPrompt);
            const workspacePlannerSteps = Array.isArray(planData.steps) ? planData.steps : [];
            const workspaceRouteTrace = {
                route: 'workspace_navigation',
                route_reason: workspaceNavigation.workspaceNavigation.route_reason || 'workspace_navigation',
                query_context: {
                    case_id: workspaceNavigation.caseId,
                    case_name: workspaceNavigation.caseName || selectedCaseName || null,
                    selected_sources_only: false,
                    reference_file_ids: [],
                },
                tools: Array.isArray(planData.tools_used) ? planData.tools_used : [],
                evidence: { retrieval_mode: 'workspace_navigation' },
            };
            const workspaceAnswer = workspaceNavigation.answerPayload;
            const workspaceMetadata = {
                ...nextMetadata,
                plannerSteps: workspacePlannerSteps,
                workspace_navigation: workspaceNavigation.workspaceNavigation,
                intelligence: {
                    ...(planData.intelligence || {}),
                    ...(nextMetadata.intelligence || {}),
                    summary: workspaceNavigation.summary,
                    answer: workspaceAnswer,
                    actions: Array.isArray(planData.actions) ? planData.actions : [],
                    session: planData.session || workspaceNavigation.workspaceNavigation,
                    route_trace: workspaceRouteTrace,
                    plannerSteps: workspacePlannerSteps,
                    workspace_navigation: workspaceNavigation.workspaceNavigation,
                    case_selection: workspaceResolvedCaseSelection,
                },
            };
            const persistedWorkspaceSelection = await persistRagMessageUpdate({
                chatId: actionChatId,
                messageId: message.id,
                content: workspaceNavigation.assistantText,
                answer: workspaceAnswer,
                metadata: workspaceMetadata,
                files: message.files || [],
            });
            if (!persistedWorkspaceSelection || !isCurrentRagChatOperation(operationContext)) {
                if (isCurrentRagChatOperation(operationContext)) {
                    clearRagPersistenceFailure(message.id);
                    setConfirmableActionErrorByMessageId(prev => ({
                        ...prev,
                        [messageKey]: 'CaseDB could not save that case choice. Please try again.',
                    }));
                }
                return;
            }
            setWorkspaceNavigationPendingMessageIds(prev => {
                const next = new Set(prev);
                next.add(String(message.id));
                return next;
            });
            setRagMessages(prev => prev.map(currentMessage => (
                String(currentMessage.id) === String(message.id)
                    ? {
                        ...currentMessage,
                        content: workspaceNavigation.assistantText,
                        answer: workspaceAnswer,
                        metadata: workspaceMetadata,
                    }
                    : currentMessage
            )));
        } catch (error) {
            console.error('Error confirming workspace case selection:', error);
            if (isCurrentRagChatOperation(operationContext)) {
                setConfirmableActionErrorByMessageId(prev => ({
                    ...prev,
                    [messageKey]: error?.message || 'CaseDB could not continue with that case. Please try again.',
                }));
            }
        } finally {
            confirmableActionBusyRef.current.delete(messageKey);
            setConfirmableActionBusyMessageIds(prev => {
                const next = new Set(prev);
                next.delete(String(message.id));
                return next;
            });
        }
    };

    const handleConfirmConfirmableAction = async (message, options = {}) => {
        if (!hasJaneUserIdentity) return;
        const actionDetails = getConfirmableActionDetails(message);
        if (!actionDetails || actionDetails.status !== 'pending_permission') return;
        const actionChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        const operationContext = captureRagChatOperationContext(actionChatId);
        if (!Number.isFinite(actionChatId) || actionChatId <= 0 || !isCurrentRagChatOperation(operationContext)) return;
        const numericMessageId = Number(message?.id || 0);
        const hasPersistedMessageId = Number.isFinite(numericMessageId) && numericMessageId > 0;
        if (!hasPersistedMessageId || ragSaving) return;
        const messageKey = String(message.id);
        if (confirmableActionBusyRef.current.has(messageKey)) return;
        confirmableActionBusyRef.current.add(messageKey);

        setConfirmableActionErrorByMessageId(prev => {
            if (!prev[messageKey]) return prev;
            const next = { ...prev };
            delete next[messageKey];
            return next;
        });

        setConfirmableActionBusyMessageIds(prev => {
            const next = new Set(prev);
            next.add(messageKey);
            return next;
        });
        const confirmationController = new AbortController();
        const confirmationTimeout = window.setTimeout(() => confirmationController.abort(), RAG_SAVE_REQUEST_TIMEOUT_MS);

        try {
            const actionForSubmit = getConfirmableActionForSubmit(message, actionDetails);
            const response = await fetch(`${API_BASE}/library-intelligence-search.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                signal: confirmationController.signal,
                body: JSON.stringify({
                    action: 'confirm_staged_action',
                    chat_id: actionChatId,
                    message_id: hasPersistedMessageId ? numericMessageId : null,
                    model_key: normalizeRagModelKey(ragModelKey),
                    tool_action: actionForSubmit,
                    destination_override: options.destinationOverride || undefined,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) {
                const requestError = new Error(data?.message || 'Could not complete the staged action.');
                requestError.responseStatus = response.status;
                requestError.responseReason = String(data?.reason || '');
                throw requestError;
            }
            if (!isCurrentRagChatOperation(operationContext)) return;

            const existingAnswer = message.answer || { answer_text: message.content || '', citations: [], source_descriptions: [] };
            const responseAnswer = data.answer || {};
            const nextAnswer = mergeConfirmedLegalDraftAnswer(existingAnswer, responseAnswer, actionDetails, message.content || '');
            const nextContent = String(nextAnswer.answer_text || message.content || '').trim();
            const existingIntelligence = message.metadata?.intelligence || null;
            const responseIntelligence = data.intelligence || null;
            const nextIntelligence = {
                ...(existingIntelligence || {}),
                ...(responseIntelligence || {}),
                plan: {
                    ...(existingIntelligence?.plan || {}),
                    ...(responseIntelligence?.plan || {}),
                },
                answer: nextAnswer,
            };
            const completedRunPlan = mergeJaneRunPlanProgress(
                responseIntelligence?.run_plan || message.metadata?.runPlan || existingIntelligence?.run_plan || null,
                {
                    status: 'complete',
                    summary: actionDetails.type === 'save_legal_draft_docx'
                        ? 'The selected save action is complete.'
                        : 'The requested action is complete.',
                    steps: [],
                }
            );
            nextIntelligence.run_plan = completedRunPlan;
            const nextMetadata = {
                ...(message.metadata || {}),
                pending_request: false,
                streaming: false,
                status_message: actionDetails.type === 'save_legal_draft_docx'
                    ? 'The selected save action is complete.'
                    : 'The requested action is complete.',
                runPlan: completedRunPlan,
                intelligence: nextIntelligence,
                confirmable_action: nextAnswer.confirmable_action || {
                    ...actionDetails,
                    status: 'confirmed',
                    payload: null,
                },
            };
            const nextFiles = mergeAnswerArrayByFingerprint(message.files, Array.isArray(data.files) ? data.files : []);
            const shouldOpenGeneratedDraftPreview = actionDetails.type === 'save_legal_draft_docx' && String(ragDraftPreviewMessageId) === String(message.id);
            const generatedDraftPreview = shouldOpenGeneratedDraftPreview
                ? buildGeneratedDraftDocxPreview(
                    { ...message, answer: nextAnswer, files: nextFiles, metadata: nextMetadata },
                    { ...actionDetails, result: nextAnswer.generated_docx || responseAnswer.generated_docx || actionDetails.result || null },
                    { answer: nextAnswer, files: nextFiles, metadata: nextMetadata }
                )
                : null;

            setRagMessages(prev => prev.map((currentMessage) => (
                String(currentMessage.id) === String(message.id)
                    ? {
                        ...currentMessage,
                        content: nextContent,
                        pending: false,
                        answer: nextAnswer,
                        files: nextFiles,
                        sources: nextAnswer.citations || currentMessage.sources || [],
                        metadata: nextMetadata,
                    }
                    : currentMessage
            )));

            // Financial workbook confirmation is committed by the same backend
            // request that creates the durable job. A second generic message
            // update can race the worker and replace its newer job result with
            // the browser's older queued snapshot.
            const confirmationPersistedByBackend = actionDetails.type === 'generate_financial_period_workbook';
            if (hasPersistedMessageId && !confirmationPersistedByBackend) {
                await persistRagMessageUpdate({
                    chatId: actionChatId,
                    messageId: numericMessageId,
                    content: nextContent,
                    answer: nextAnswer,
                    metadata: nextMetadata,
                    files: nextFiles,
                });
            }
            clearRagIncomingAnswerMessage(message.id);
            setConfirmableActionEdits(prev => {
                const next = new Map(prev);
                next.delete(String(message.id));
                return next;
            });
            if (generatedDraftPreview) {
                setShowRagSourceHistory(false);
                setTimelineEvidencePreview(null);
                setRagResponseDetailsMessageId(null);
                setRagCalendarPanelMessageId(null);
                setRagDraftPreviewMessageId(null);
                setChunkPreview(null);
                window.requestAnimationFrame(() => setChunkPreview(generatedDraftPreview));
            }
        } catch (error) {
            console.error('Error confirming staged Jane action:', error);
            const confirmationTimedOut = error?.name === 'AbortError';
            const responseReason = String(error?.responseReason || '');
            const safeReportConflictReasons = new Set([
                'persisted_action_not_found',
                'persisted_action_missing',
                'persisted_action_unsealed',
                'staged_action_expired',
            ]);
            const actionableReportConflict = actionDetails.type === 'save_report'
                && Number(error?.responseStatus || 0) === 409
                && (
                    /^analytics_(?:case|report|reconciliation)/.test(responseReason)
                    || safeReportConflictReasons.has(responseReason)
                );
            const friendlyReportConflictMessage = responseReason === 'staged_action_expired'
                ? 'This save request expired. Run the financial analysis again, then save the new report.'
                : String(error?.message || '').trim().slice(0, 280);
            const saveErrorMessage = actionableReportConflict
                ? friendlyReportConflictMessage
                : 'The report could not be saved. Please try again.';
            setConfirmableActionErrorByMessageId(prev => ({
                ...prev,
                [messageKey]: confirmationTimedOut
                    ? 'This is taking longer than expected. Refresh the chat to check whether it finished before trying again.'
                    : (actionDetails.type === 'save_report'
                        ? saveErrorMessage
                        : 'Jane could not complete this action. Please try again.'),
            }));
            if (confirmationTimedOut) {
                loadRagChat(actionChatId, { silent: true }).catch((loadError) => {
                    console.error('Error refreshing Jane action after timeout:', loadError);
                });
            }
        } finally {
            window.clearTimeout(confirmationTimeout);
            confirmableActionBusyRef.current.delete(messageKey);
            setConfirmableActionBusyMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
        }
    };

    const handleCancelConfirmableAction = async (message) => {
        if (!hasJaneUserIdentity || ragSaving) return;
        const numericMessageId = Number(message?.id || 0);
        const messageKey = String(message?.id || '');
        const actionChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        const operationContext = captureRagChatOperationContext(actionChatId);
        if (!messageKey
            || !Number.isInteger(numericMessageId)
            || numericMessageId <= 0
            || !Number.isInteger(actionChatId)
            || actionChatId <= 0
            || !isCurrentRagChatOperation(operationContext)
            || confirmableActionBusyRef.current.has(messageKey)) return;
        const actionDetails = getConfirmableActionDetails(message);
        if (!actionDetails || actionDetails.status !== 'pending_permission') return;
        confirmableActionBusyRef.current.add(messageKey);
        setConfirmableActionBusyMessageIds(prev => new Set(prev).add(messageKey));
        setConfirmableActionDecliningMessageIds(prev => new Set(prev).add(messageKey));
        setConfirmableActionErrorByMessageId(prev => ({ ...prev, [messageKey]: '' }));
        const declinedBy = String(user?.name || user?.user_name || user?.display_name || 'You').trim() || 'You';
        const declinedAction = {
            ...actionDetails,
            status: 'declined',
            permission_state: 'dismissed',
            confirmed_by: declinedBy,
            confirmed_by_user_id: userId,
            confirmed_at: new Date().toISOString(),
            payload: null,
        };
        const existingAnswer = message.answer || { answer_text: message.content || '', citations: [], source_descriptions: [] };
        const nextContent = String(existingAnswer.answer_text || message.content || '').trim();
        const nextAnswer = {
            ...existingAnswer,
            answer_text: nextContent,
            confirmable_action: declinedAction,
        };
        const completedRunPlan = mergeJaneRunPlanProgress(
            message.metadata?.runPlan || message.metadata?.intelligence?.run_plan || null,
            {
                status: 'complete',
                summary: 'Save canceled. No files were changed.',
                steps: [],
            }
        );
        const nextMetadata = {
            ...(message.metadata || {}),
            pending_request: false,
            streaming: false,
            status_message: 'Save canceled. No files were changed.',
            runPlan: completedRunPlan,
            intelligence: {
                ...(message.metadata?.intelligence || {}),
                answer: nextAnswer,
                run_plan: completedRunPlan,
            },
            confirmable_action: declinedAction,
        };

        try {
            const persistedDecline = await persistRagMessageUpdate({
                chatId: actionChatId,
                messageId: numericMessageId,
                content: nextContent,
                answer: nextAnswer,
                metadata: nextMetadata,
                files: message.files || [],
            });
            if (!persistedDecline || !isCurrentRagChatOperation(operationContext)) {
                clearRagPersistenceFailure(message.id);
                throw new Error('decline_not_saved');
            }
            setRagMessages(prev => prev.map((currentMessage) => (
                String(currentMessage.id) === String(message.id)
                    ? { ...currentMessage, content: nextContent, pending: false, answer: nextAnswer, metadata: nextMetadata }
                    : currentMessage
            )));
            clearRagIncomingAnswerMessage(message.id);
        } catch (error) {
            console.error('Error canceling staged Jane action:', error);
            clearRagPersistenceFailure(message.id);
            setConfirmableActionErrorByMessageId(prev => ({
                ...prev,
                [messageKey]: 'The action could not be canceled. Nothing was changed. Please try again.',
            }));
        } finally {
            confirmableActionBusyRef.current.delete(messageKey);
            setConfirmableActionBusyMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
            setConfirmableActionDecliningMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
        }
    };

    const handleConfirmWorkspaceNavigation = async (message) => {
        if (!hasJaneUserIdentity || ragSaving) return;
        const navigationDetails = getWorkspaceNavigationDetails(message);
        if (!navigationDetails) return;
        const numericMessageId = Number(message?.id || 0);
        const messageKey = String(message?.id || '');
        const actionChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        const operationContext = captureRagChatOperationContext(actionChatId);
        if (!messageKey
            || !Number.isInteger(numericMessageId)
            || numericMessageId <= 0
            || !Number.isInteger(actionChatId)
            || actionChatId <= 0
            || !isCurrentRagChatOperation(operationContext)
            || confirmableActionBusyRef.current.has(messageKey)) return;
        confirmableActionBusyRef.current.add(messageKey);
        setConfirmableActionBusyMessageIds(prev => new Set(prev).add(messageKey));

        try {
            const confirmedBy = 'You';
            const confirmedAt = new Date().toISOString();
            const prompt = String(navigationDetails.prompt || navigationDetails.summary || '').trim();
            const workspaceCaseId = Number(navigationDetails.caseId);
            const resolution = {
                status: 'confirmed',
                confirmedBy,
                confirmedByUserId: userId,
                confirmedAt,
                prompt,
            };
            const resolvedMessage = buildResolvedWorkspaceNavigationMessage(message, navigationDetails, resolution);

            const persistedNavigation = await persistRagMessageUpdate({
                chatId: actionChatId,
                messageId: numericMessageId,
                content: resolvedMessage.content,
                answer: resolvedMessage.answer,
                metadata: resolvedMessage.metadata,
                files: message.files || [],
            });
            if (!persistedNavigation || !isCurrentRagChatOperation(operationContext)) return;

            setRagMessages(prev => prev.map((currentMessage) => {
                if (String(currentMessage.id) !== String(message.id)) return currentMessage;
                const nextResolvedMessage = buildResolvedWorkspaceNavigationMessage(currentMessage, navigationDetails, resolution);
                return { ...currentMessage, ...nextResolvedMessage };
            }));

            setWorkspaceNavigationPendingMessageIds(prev => {
                const next = new Set(prev);
                next.delete(String(message.id));
                return next;
            });

            storeJaneWorkspaceHandoff(workspaceCaseId, prompt, navigationDetails.actions || [], actionChatId);
            window.setTimeout(() => {
                if (!isCurrentRagChatOperation(operationContext)) return;
                navigate(`/case/${workspaceCaseId}?jane_open=1&jane_handoff=workspace`, {
                    state: { janeWorkspacePrompt: prompt, janeWorkspaceCaseId: workspaceCaseId, janeWorkspaceCaseName: navigationDetails.caseName || navigationDetails.caseLabel || `Case ${workspaceCaseId}` },
                });
            }, 0);
        } finally {
            confirmableActionBusyRef.current.delete(messageKey);
            setConfirmableActionBusyMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
        }
    };

    const handleCancelWorkspaceNavigation = async (message) => {
        if (!hasJaneUserIdentity || ragSaving) return;
        const numericMessageId = Number(message?.id || 0);
        const messageKey = String(message?.id || '');
        const actionChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        const operationContext = captureRagChatOperationContext(actionChatId);
        if (!messageKey
            || !Number.isInteger(numericMessageId)
            || numericMessageId <= 0
            || !Number.isInteger(actionChatId)
            || actionChatId <= 0
            || !isCurrentRagChatOperation(operationContext)
            || confirmableActionBusyRef.current.has(messageKey)) return;
        const navigationDetails = getWorkspaceNavigationDetails(message);
        if (!navigationDetails) return;
        confirmableActionBusyRef.current.add(messageKey);
        setConfirmableActionBusyMessageIds(prev => new Set(prev).add(messageKey));
        setConfirmableActionDecliningMessageIds(prev => new Set(prev).add(messageKey));
        setConfirmableActionErrorByMessageId(prev => ({ ...prev, [messageKey]: '' }));
        const dismissedAt = new Date().toISOString();
        const dismissedBy = 'You';
        const prompt = String(navigationDetails.prompt || navigationDetails.summary || '').trim();
        const resolution = {
            status: 'declined',
            confirmedBy: dismissedBy,
            confirmedByUserId: userId,
            confirmedAt: dismissedAt,
            prompt,
        };
        const resolvedMessage = buildResolvedWorkspaceNavigationMessage(message, navigationDetails, resolution);

        try {
            const persistedDecline = await persistRagMessageUpdate({
                chatId: actionChatId,
                messageId: numericMessageId,
                content: resolvedMessage.content,
                answer: resolvedMessage.answer,
                metadata: resolvedMessage.metadata,
                files: message.files || [],
            });
            if (!persistedDecline || !isCurrentRagChatOperation(operationContext)) {
                clearRagPersistenceFailure(message.id);
                throw new Error('navigation_decline_not_saved');
            }
            setRagMessages(prev => prev.map((currentMessage) => {
                if (String(currentMessage.id) !== String(message.id)) return currentMessage;
                const nextResolvedMessage = buildResolvedWorkspaceNavigationMessage(currentMessage, navigationDetails, resolution);
                return { ...currentMessage, ...nextResolvedMessage };
            }));
            setWorkspaceNavigationPendingMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
        } catch (error) {
            console.error('Error canceling Jane navigation:', error);
            clearRagPersistenceFailure(message.id);
            setConfirmableActionErrorByMessageId(prev => ({
                ...prev,
                [messageKey]: 'The navigation could not be canceled. Nothing was changed. Please try again.',
            }));
        } finally {
            confirmableActionBusyRef.current.delete(messageKey);
            setConfirmableActionBusyMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
            setConfirmableActionDecliningMessageIds(prev => {
                const next = new Set(prev);
                next.delete(messageKey);
                return next;
            });
        }
    };

    const handleConfirmRagEdit = async () => {
        if (!pendingRagEdit || requestControllerRef.current || loading || ragSaving || ragBranchActionBusy) return;
        const editRequest = pendingRagEdit;
        const { message, messageIndex, prompt, references, selectedSourcesOnly } = editRequest;
        const sourceChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        if (!Number.isInteger(sourceChatId) || sourceChatId <= 0) {
            setRagBranchActionError('Jane could not identify the current chat. Refresh the page and try again.');
            return;
        }
        const sourceSessionId = ragSessionRef.current;
        const sourceRunGeneration = ragRunGenerationRef.current;
        setRagBranchActionBusy(true);
        setRagBranchActionError('');
        let replacementMessages = ragMessages.slice(0, messageIndex);
        let targetChatId = sourceChatId;
        try {
            const data = await forkSavedRagChatForEdit(message);
            if (sourceSessionId !== ragSessionRef.current
                || !isRagRunGenerationCurrent(sourceRunGeneration, ragRunGenerationRef.current)
                || Number(activeRagChatIdRef.current || 0) !== sourceChatId) {
                throw new Error('active_chat_changed');
            }
            if (!data?.chat?.id) throw new Error('fork_missing_chat');
            if (data?.messages) {
                replacementMessages = normalizeRagMessages(data.messages || []);
                setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
            }
            if (data?.chat?.id) {
                targetChatId = data.chat.id;
                activeRagChatIdRef.current = data.chat.id;
                setActiveRagChatId(data.chat.id);
            }
            resetRagIncomingAnswerState();
            requestSequenceRef.current += 1;
            ragSessionRef.current += 1;
            ragRunGenerationRef.current += 1;
            ragForegroundChatLoadRef.current = null;
            ragMessagePageCacheRef.current = null;
            ragMessagePageEpochRef.current += 1;
            invalidateRagPendingSaveState();
            setLoading(false);
            setPendingRagEdit(null);
            setPendingRagRedo(null);
            setEditingRagMessageId(null);
            setEditingRagDraft('');
            setShowRagReferenceMenu(false);
            setFiles([]);
            setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
            loadRagChats();
            setRagMessages(replacementMessages);
            setRagBackgroundDraftRuns(prev => filterBackgroundDraftRunsForMessages(prev, replacementMessages, targetChatId));
            await executeRagPrompt(prompt, references, { clearComposer: false, force: true, chatIdOverride: targetChatId, replacementMessages, selectedSourcesOnly });
        } catch (error) {
            console.error('Failed to edit saved RAG chat:', error);
            setRagBranchActionError(error?.isSafeForDisplay && error?.message
                ? error.message
                : 'Jane could not start the edited chat. Your edit is still here. Please try again.');
        } finally {
            setRagBranchActionBusy(false);
        }
    };

    const handleRedoAssistantResponse = async (messageIndex) => {
        if (requestControllerRef.current || loading || ragSaving) return;

        const userMessageIndex = (() => {
            for (let index = messageIndex - 1; index >= 0; index -= 1) {
                if (ragMessages[index]?.role === 'user') return index;
            }
            return -1;
        })();
        if (userMessageIndex < 0) return;

        const userMessage = ragMessages[userMessageIndex];
        const prompt = (userMessage.content || userMessage.query_text || '').trim();
        if (prompt.length < 2) return;

        setPendingRagRedo({
            messageIndex,
            userMessageIndex,
            userMessageId: Number(userMessage.id),
            assistantMessageId: Number(ragMessages[messageIndex]?.id),
            chatId: Number(activeRagChatIdRef.current || activeRagChatId || 0),
            prompt,
        });
        setRagBranchActionError('');
    };

    const handleConfirmRagRedo = async () => {
        if (!pendingRagRedo) return;
        if (requestControllerRef.current || loading || ragSaving || ragBranchActionBusy) return;

        const { userMessageId, assistantMessageId, chatId: requestedChatId, prompt } = pendingRagRedo;
        const userMessage = ragMessages.find(message => Number(message.id) === userMessageId && message.role === 'user');
        const assistantMessage = ragMessages.find(message => Number(message.id) === assistantMessageId && message.role === 'assistant');
        if (!userMessage || !assistantMessage || !Number.isInteger(assistantMessageId) || assistantMessageId <= 0) {
            setRagBranchActionError('Save this response before rerunning it. Reopen the chat if it is already saved.');
            return;
        }

        const references = Array.isArray(userMessage.references) ? userMessage.references : [];
        const selectedSourcesOnly = Boolean(userMessage.metadata?.selected_sources_only);
        const sourceChatId = Number(activeRagChatIdRef.current || activeRagChatId || 0);
        if (!Number.isInteger(sourceChatId) || sourceChatId <= 0 || sourceChatId !== requestedChatId) {
            setRagBranchActionError('Jane could not identify the current chat. Refresh the page and try again.');
            return;
        }
        const sourceSessionId = ragSessionRef.current;
        const sourceRunGeneration = ragRunGenerationRef.current;
        setRagBranchActionBusy(true);
        setRagBranchActionError('');
        try {
            const rerunData = await reserveSavedRagMessageRerun(sourceChatId, userMessage, assistantMessage);
            if (sourceSessionId !== ragSessionRef.current
                || !isRagRunGenerationCurrent(sourceRunGeneration, ragRunGenerationRef.current)
                || Number(activeRagChatIdRef.current || 0) !== sourceChatId) {
                throw new Error('active_chat_changed');
            }
            resetRagIncomingAnswerState();
            requestSequenceRef.current += 1;
            ragSessionRef.current += 1;
            ragRunGenerationRef.current += 1;
            ragForegroundChatLoadRef.current = null;
            ragMessagePageCacheRef.current = null;
            ragMessagePageEpochRef.current += 1;
            invalidateRagPendingSaveState();
            setLoading(false);
            setPendingRagRedo(null);
            setPendingRagEdit(null);
            setEditingRagMessageId(null);
            setEditingRagDraft('');
            setShowRagReferenceMenu(false);
            setChunkPreview(null);
            setFiles([]);
            setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
            setWorkspaceNavigationPendingMessageIds(previous => {
                const next = new Set(previous);
                next.delete(String(assistantMessageId));
                return next;
            });
            setRagRerunErrorByMessageId(previous => ({ ...previous, [String(assistantMessageId)]: '' }));
            await executeRagPrompt(prompt, references, {
                appendUserMessage: false,
                clearComposer: false,
                force: true,
                chatIdOverride: sourceChatId,
                replaceAssistantMessageId: assistantMessageId,
                selectedSourcesOnly,
                rerunMessageId: userMessageId,
                rerunRequestToken: String(rerunData.rerun_request_token).trim(),
            });
        } catch (error) {
            console.error('Failed to rerun saved RAG response:', error);
            setRagBranchActionError(error?.isSafeForDisplay && error?.message
                ? error.message
                : 'Jane could not start the rerun. Please try again in this chat.');
        } finally {
            setRagBranchActionBusy(false);
        }
    };

    const handleForkAssistantResponse = async (message) => {
        if (!hasJaneUserIdentity) return;
        const chatId = Number(activeRagChatId);
        const messageId = Number(message?.id);
        if (!Number.isFinite(chatId) || chatId <= 0 || !Number.isFinite(messageId) || messageId <= 0) return;
        if (requestControllerRef.current || loading || ragSaving || forkingRagMessageId) return;

        const forkSessionId = ragSessionRef.current;
        const forkRunGeneration = ragRunGenerationRef.current;
        setForkingRagMessageId(message.id);
        setRagForkErrorByMessageId(prev => ({ ...prev, [String(message.id)]: '' }));
        try {
            const { data } = await fetchRagJsonWithTimeout(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({ action: 'fork_from_message', chat_id: chatId, message_id: messageId })
            }, {
                timeoutMs: RAG_SAVE_REQUEST_TIMEOUT_MS,
                failureMessage: 'Jane could not create the new chat. Please try again.',
                timeoutMessage: 'Jane took too long to create the new chat. Please try again.',
            });
            if (forkSessionId !== ragSessionRef.current
                || !isRagRunGenerationCurrent(forkRunGeneration, ragRunGenerationRef.current)) return;
            if (!data?.success || !data?.chat?.id) throw new Error('fork_failed');

            resetRagIncomingAnswerState();
            requestSequenceRef.current += 1;
            ragSessionRef.current += 1;
            ragRunGenerationRef.current += 1;
            ragForegroundChatLoadRef.current = null;
            ragMessagePageCacheRef.current = null;
            ragMessagePageEpochRef.current += 1;
            invalidateRagPendingSaveState();
            activeRagChatIdRef.current = data.chat.id;
            setActiveRagChatId(data.chat.id);
            setRagModelKey(normalizeRagModelKey(data.chat.model_key));
            setEditingRagChatTitle(false);
            setRagChatTitleDraft(data.chat.title || '');
            setRagMessages(normalizeRagMessages(data.messages || []));
            setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
            setRagMessagesLoadingOlder(false);
            setChunkPreview(null);
            setShowRagSourceHistory(false);
            setRagReferences([]);
            setRagReferenceContext(null);
            loadRagChats();
        } catch (error) {
            console.error('Failed to fork RAG chat:', error);
            setRagForkErrorByMessageId(prev => ({
                ...prev,
                [String(message.id)]: error?.isSafeForDisplay && error?.message
                    ? error.message
                    : 'Jane could not create the new chat. Please try again.',
            }));
        } finally {
            setForkingRagMessageId(null);
        }
    };

    const getRagMessageFeedbackRating = (message) => {
        const rating = String(message?.metadata?.feedback?.rating || message?.answer?.feedback?.rating || '').toLowerCase();
        return rating === 'up' || rating === 'down' ? rating : '';
    };

    const canRateAssistantResponse = (message) => {
        const messageId = Number(message?.id || 0);
        const queryText = String(message?.query_text || '').trim();
        return message?.role === 'assistant'
            && Number.isFinite(messageId)
            && messageId > 0
            && !message?.pending
            && !message?.externalRequest
            && queryText.length > 0;
    };

    const handleRateAssistantResponse = async (message, rating, requestedAction = '') => {
        if (!hasJaneUserIdentity) return;
        const normalizedRating = String(rating || '').toLowerCase();
        const chatId = Number(activeRagChatId || message?.chat_id || 0);
        const messageId = Number(message?.id || 0);
        const currentRating = getRagMessageFeedbackRating(message);
        const action = requestedAction === 'clear_feedback' || (!requestedAction && currentRating === normalizedRating)
            ? 'clear_feedback'
            : 'rate_message';
        if (!canRateAssistantResponse(message) || !['up', 'down'].includes(normalizedRating) || !Number.isFinite(chatId) || chatId <= 0) {
            return;
        }

        setRagFeedbackStatusByMessageId(prev => {
            const next = { ...prev };
            delete next[messageId];
            return next;
        });
        setRagFeedbackSavingByMessageId(prev => ({ ...prev, [messageId]: normalizedRating }));

        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action,
                    chat_id: chatId,
                    message_id: messageId,
                    ...(action === 'rate_message' ? { rating: normalizedRating } : {}),
                    path: `${window.location.pathname}${window.location.search}`,
                    user_agent: window.navigator?.userAgent || '',
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) throw new Error(data?.message || 'Failed to save feedback');

            const feedback = action === 'clear_feedback'
                ? null
                : (data.feedback || { rating: normalizedRating, message_id: messageId, chat_id: chatId });
            setRagMessages(prev => prev.map(currentMessage => (
                String(currentMessage.id) === String(messageId)
                    ? {
                        ...currentMessage,
                        metadata: {
                            ...(currentMessage.metadata || {}),
                            feedback,
                        },
                    }
                    : currentMessage
            )));
            setRagFeedbackStatusByMessageId(prev => ({
                ...prev,
                [messageId]: {
                    kind: 'success',
                    message: action === 'clear_feedback' ? 'Feedback removed.' : 'Feedback saved.',
                    action,
                    rating: normalizedRating,
                },
            }));
            window.setTimeout(() => {
                setRagFeedbackStatusByMessageId(prev => {
                    const currentStatus = prev[messageId];
                    if (currentStatus?.kind !== 'success' || currentStatus.action !== action || currentStatus.rating !== normalizedRating) {
                        return prev;
                    }
                    const next = { ...prev };
                    delete next[messageId];
                    return next;
                });
            }, 2400);
        } catch (error) {
            console.error('Failed to save Jane response feedback:', error);
            setRagFeedbackStatusByMessageId(prev => ({
                ...prev,
                [messageId]: {
                    kind: 'error',
                    message: 'Couldn\u2019t save feedback.',
                    action,
                    rating: normalizedRating,
                },
            }));
        } finally {
            setRagFeedbackSavingByMessageId(prev => {
                const next = { ...prev };
                delete next[messageId];
                return next;
            });
        }
    };

    const addRagReferenceFromDraft = () => {
        const rawValue = typeof ragReferenceDraft === 'string' ? ragReferenceDraft.trim() : '';
        if (!rawValue) return;
        setChunkPreview(null);

        const fileMatch = rawValue.match(/(?:file\/)?(\d+)(?:\D*)$/);
        const fileId = fileMatch ? fileMatch[1] : '';
        const isUrl = /^https?:\/\//i.test(rawValue);
        const url = fileId ? `https://casedb.co/file/${fileId}` : (isUrl ? rawValue : '');

        setRagReferences(prev => [
            ...prev,
            {
                id: `doc-${Date.now()}`,
                type: 'case_document',
                label: fileId ? `Case document #${fileId}` : rawValue,
                url,
                file_id: fileId || null
            }
        ]);
        setRagReferenceDraft('');
        setShowRagReferenceMenu(false);
    };

    const getRagReferenceSelection = useCallback(() => parseCaseLeadSelection(ragReferenceContext?.value || ''), [ragReferenceContext?.value]);

    const getRecentRagCaseReference = () => {
        for (let index = ragMessages.length - 1; index >= 0; index -= 1) {
            const message = ragMessages[index];
            if (message?.role === 'assistant') {
                const [caseReference] = getCaseReferenceFromAssistantMessage(message);
                if (caseReference?.case_id || caseReference?.id) return caseReference;
            }
            const messageReferences = Array.isArray(message?.references) ? message.references : [];
            const caseReference = messageReferences.find(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) > 0);
            if (caseReference) return caseReference;
        }
        return null;
    };

    const getRagReferencesForPrompt = (references = [], query = '') => {
        const selection = getRagReferenceSelection();
        const contextReference = selection?.type && selection.id
            ? {
                id: `${selection.type}-${selection.id}`,
                type: selection.type,
                label: ragReferenceContext?.label || `${selection.type === 'case' ? 'Case' : 'Lead'} #${selection.id}`,
                case_id: selection.type === 'case' ? selection.id : null,
                lead_id: selection.type === 'lead' ? selection.id : null,
                source: 'reference_context'
            }
            : null;
        const hasExplicitCaseCandidate = String(extractWorkspaceCaseCandidate(query || '') || '').trim() !== '';
        const pageContextReference = !contextReference && !hasExplicitCaseCandidate && ragUsePriorChat && ragUsePageContext ? getCurrentPageCaseReference() : null;
        const promptAlreadyHasCaseReference = Array.isArray(references) && references.some(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) > 0);
        const recentCaseReference = !contextReference
            && !pageContextReference
            && !promptAlreadyHasCaseReference
            && !hasExplicitCaseCandidate
            && ragUsePriorChat
            && isLikelyCaseBoundFollowupPrompt(query)
            ? getRecentRagCaseReference()
            : null;

        const contextReferences = [contextReference, pageContextReference, recentCaseReference].filter(Boolean);
        if (contextReferences.length === 0) return references;

        const mergedReferences = [...references];
        contextReferences.forEach((contextReferenceItem) => {
            const contextKey = `${contextReferenceItem.type}-${contextReferenceItem.case_id || contextReferenceItem.lead_id}`;
            const hasContext = mergedReferences.some((reference) => {
                const referenceType = reference?.type;
                const referenceId = referenceType === 'case'
                    ? reference?.case_id || reference?.id
                    : referenceType === 'lead'
                        ? reference?.lead_id || reference?.id
                        : null;
                return `${referenceType}-${referenceId}` === contextKey;
            });
            if (!hasContext) mergedReferences.unshift(contextReferenceItem);
        });

        return mergedReferences;
    };

    const getRagReferenceCaseId = useCallback(() => {
        const selection = getRagReferenceSelection();
        return selection?.type === 'case' ? selection.id : null;
    }, [getRagReferenceSelection]);

    const getRagReferenceLeadId = useCallback(() => {
        const selection = getRagReferenceSelection();
        return selection?.type === 'lead' ? selection.id : null;
    }, [getRagReferenceSelection]);

    const addRagReferenceFromDocument = (document) => {
        const fileId = document?.id || document?.file_id || document?.fileId;
        if (!fileId) return;
        setChunkPreview(null);

        const fileName = document?.name || document?.file_name || document?.filename || `File ${fileId}`;
        const selection = getRagReferenceSelection();

        setRagReferences(prev => {
            const normalizedFileId = Number(fileId);
            if (prev.some(reference => Number(reference.file_id) === normalizedFileId)) return prev;
            return [
                ...prev,
                {
                id: `doc-${fileId}-${Date.now()}`,
                type: 'case_document',
                label: fileName,
                url: `https://casedb.co/file/${fileId}`,
                file_id: normalizedFileId,
                file_name: fileName,
                file_path: document?.file_path || document?.path || document?.key || null,
                case_id: document?.case_id || document?.caseId || (selection?.type === 'case' ? selection.id : null),
                lead_id: document?.lead_id || document?.leadId || (selection?.type === 'lead' ? selection.id : null),
                source_label: document?.source_label || ragReferenceContext?.label || null
                }
            ];
        });
    };

    const addRagReferenceFromSource = (source, sourceLabel = null) => {
        if (!source || typeof source !== 'object') return false;
        const fileId = getSourceFileId(source) || source.file_id || source.id;
        if (fileId) {
            const fileName = source.file_name || source.name || source.title || source.label || `File ${fileId}`;
            addRagReferenceFromDocument({
                id: fileId,
                file_id: fileId,
                file_name: fileName,
                name: fileName,
                file_path: source.file_path || source.path || source.key || source.storage_key || null,
                case_id: source.case_id || source.caseId || null,
                lead_id: source.lead_id || source.leadId || null,
                source_label: sourceLabel || source.source_label || source.label || null,
            });
            return true;
        }

        const caseId = Number(source.case_id || (String(source.type || source.source_type || '').toLowerCase().includes('case') ? source.id : null));
        if (Number.isFinite(caseId) && caseId > 0) {
            setChunkPreview(null);
            setRagReferences(prev => {
                if (prev.some(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) === caseId)) return prev;
                const caseName = getCaseNameForDisplay(source, source.case_name || source.name || source.title || 'Case');
                return [
                    ...prev,
                    {
                        id: `case-${caseId}-${Date.now()}`,
                        type: 'case',
                        label: caseName && caseName !== 'Case' ? `Case #${caseId} · ${caseName}` : `Case #${caseId}`,
                        case_id: caseId,
                        source: 'pinned_source',
                    }
                ];
            });
            return true;
        }

        return false;
    };

    const fetchRagReferenceFolderContents = useCallback(async (currentFolder = '{{Name}}', page = 1, pageSize = 100) => {
        const caseId = getRagReferenceCaseId();
        if (!caseId) return null;

        const params = new URLSearchParams({
            folder_fetch: 'true',
            case_id: String(caseId),
            current_folder: currentFolder,
            page: String(page),
            page_size: String(pageSize),
            t: Date.now().toString()
        });

        const response = await fetch(`${API_BASE}/documents.php?${params}`, { credentials: 'include' });
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data?.message || 'Failed to load case documents');
        }
        return {
            items: data.items || [],
            folders: data.folders || [],
            files: data.files || [],
            metaData: data.metaData || [],
            pagination: data.pagination || {},
            currentFolder: data.current_folder || currentFolder,
            folderPrefix: data.folder_prefix || ''
        };
    }, [getRagReferenceCaseId]);

    const fetchRagCaseFolderContents = useCallback(async (caseId, currentFolder = '{{Name}}', page = 1, pageSize = 100) => {
        const numericCaseId = Number(caseId || 0);
        if (!Number.isFinite(numericCaseId) || numericCaseId <= 0) return null;

        const params = new URLSearchParams({
            folder_fetch: 'true',
            case_id: String(numericCaseId),
            current_folder: currentFolder || '{{Name}}',
            page: String(page),
            page_size: String(pageSize),
            t: Date.now().toString()
        });

        const response = await fetch(`${API_BASE}/documents.php?${params}`, { credentials: 'include' });
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data?.message || 'Failed to load case folders');
        }
        return {
            items: data.items || [],
            folders: data.folders || [],
            files: data.files || [],
            metaData: data.metaData || [],
            pagination: data.pagination || {},
            currentFolder: data.current_folder || currentFolder,
            folderPrefix: data.folder_prefix || ''
        };
    }, []);

    const normalizeConfirmableFolderPath = (path = '') => {
        const raw = String(path || '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
        if (!raw || raw === '{{Name}}') return raw === '{{Name}}' ? '{{Name}}' : '';
        return raw
            .replace(/^cases\/\d+\/(?:\{\{Name\}\}\/?)?/i, '')
            .replace(/^\{\{Name\}\}\/?/i, '')
            .replace(/^\/+|\/+$/g, '')
            .trim();
    };

    const getConfirmableFolderSelectionState = (actionDetails, payload = null) => {
        const resolvedPayload = payload || actionDetails?.payload || {};
        const required = Boolean(actionDetails?.folder_selection_required || actionDetails?.folder_picker || resolvedPayload?.folder_selection_required);
        const folderPath = normalizeConfirmableFolderPath(resolvedPayload?.folder_path || '');
        const caseId = Number(actionDetails?.folder_picker?.case_id || resolvedPayload?.case_id || 0);
        const caseName = String(actionDetails?.folder_picker?.case_name || resolvedPayload?.case_name || actionDetails?.subject || 'Case').trim() || 'Case';
        const initialPath = String(actionDetails?.folder_picker?.initial_path || '{{Name}}').trim() || '{{Name}}';
        return {
            required,
            folderPath,
            hasFolder: folderPath !== '',
            caseId,
            caseName,
            initialPath,
            displayFolder: folderPath === '{{Name}}' ? 'Case root' : folderPath,
        };
    };

    const handleConfirmableFolderSelection = (message, actionDetails, selectedPath) => {
        const editKey = String(message?.id || '');
        if (!editKey) return;
        const basePayload = confirmableActionEdits.get(editKey) || actionDetails?.payload || {};
        const folderPath = normalizeConfirmableFolderPath(selectedPath);
        if (!folderPath) return;
        setConfirmableActionEdits(prev => {
            const next = new Map(prev);
            next.set(editKey, {
                ...basePayload,
                folder_path: folderPath,
                folder_selection_required: false,
            });
            return next;
        });
        setRagFolderPickerActionMessageId(null);
    };

    const removeRagReference = (referenceId) => {
        setChunkPreview(null);
        setRagReferences(prev => prev.filter((reference) => reference.id !== referenceId));
    };

    const getPreSubmitRouteLabel = (query = '', references = []) => {
        const normalized = normalizeCapabilityPrompt(query);
        const hasFileReference = references.some(reference => Number(reference?.file_id) > 0);
        const hasSpreadsheetReference = references.some(reference => /\.(xlsx?|csv)$/i.test(String(reference?.file_name || reference?.name || reference?.label || '')));
        if (/\b(?:what\s+can\s+(?:you|jane|casedb)|what\s+are\s+your\s+(?:tools|capabilities|functions)|(?:list|show|explain)\s+(?:your\s+)?(?:tools|capabilities|functions)|help\s+menu|capability\s+list|tool\s+list|functionality\s+list)\b/.test(normalized)) return 'What Jane can do';
        if (hasSpreadsheetReference) return 'Spreadsheet review';
        if (hasFileReference) return 'Selected-file review';
        if (/\b(?:draft|prepare|generate|write)\b.*\b(?:demand|letter|settlement|discovery|mediation|template)\b/.test(normalized)) return 'Legal drafting';
        if (/\b(?:report|spreadsheet|csv|excel)\b/.test(normalized)) return 'Report preparation';
        if (/\b(?:progress|status|posture|summary|case|matter)\b/.test(normalized)) return 'Case review';
        return 'Document review';
    };

    const getJaneAgentDisplayDescription = (agent = {}) => {
        const title = String(agent.title || agent.name || '').toLowerCase();
        const tool = String(agent.tool || agent.category || '').toLowerCase();
        const description = String(agent.description || '').trim();
        const haystack = `${title} ${tool} ${description}`.toLowerCase();

        if (/\btranscrib|audio|recording|deposition audio\b/.test(haystack)) {
            return 'Upload or link an audio file, and Jane will generate a transcript file.';
        }
        if (/\bcalendar|deadline|scheduling order|hearing|trial setting\b/.test(haystack)) {
            return 'Select an order or case source, and Jane will draft calendar entries for review.';
        }
        if (/\bdraft|demand|letter|brief|settlement|discovery|mediation\b/.test(haystack)) {
            return 'Select a case or source set, and Jane will draft legal work product for review.';
        }
        if (/\bfield|extract|data entry|custom field|case field\b/.test(haystack)) {
            return 'Select a document and case, and Jane will stage editable field updates.';
        }
        if (/\bmissing|readiness|checklist\b/.test(haystack)) {
            return 'Select a case, and Jane will identify missing facts, documents, and next steps.';
        }
        if (/\btimeline|chronolog\b/.test(haystack)) {
            return 'Select a case, and Jane will build a sourced timeline of key events.';
        }
        if (/\bspreadsheet|excel|csv|workbook\b/.test(haystack)) {
            return 'Upload or link a spreadsheet, and Jane will review the data and return the requested output.';
        }
        if (/\breport\b/.test(haystack)) {
            return 'Describe the report you need, and Jane will prepare the matching CaseDB report.';
        }
        if (!description) return 'Use this agent to start a guided Jane workflow.';
        return description
            .replace(/\b(?:backend|runtime|S3|AWS|vector|hybrid|route|retrieval|metadata|CaseDB\s+files?)\b/gi, '')
            .replace(/\s{2,}/g, ' ')
            .trim() || 'Use this agent to start a guided Jane workflow.';
    };

    const isJaneWorkspaceAgent = (agent = {}) => {
        if (agent?.saved) return false;
        const id = String(agent.id || '').toLowerCase();
        const title = String(agent.title || '').toLowerCase();
        const description = String(agent.description || agent.displayDescription || '').toLowerCase();
        const routeGoal = String(agent.route_goal || agent.routeGoal || '').toLowerCase();
        const route = String(agent.route || '').toLowerCase();
        const searchText = `${id} ${title} ${routeGoal} ${route} ${description}`;

        if (routeGoal === 'workspace_navigation') return true;
        if (route.includes('workspace-navigation')) return true;
        if (/case\s*\/\s*\d+/.test(searchText)) return true;
        if (/\b(?:open|navigate|go to|take me to|pull up)\b.{0,80}\b(?:case\s+section|workspace\s+section|activity\s+feed|feed\s+drafting|case\s+calendar)\b/i.test(searchText)) return true;
        return false;
    };

    const getJaneAgentLibraryTypeMeta = (agent = {}) => {
        if (isJaneWorkspaceAgent(agent)) {
            return { label: 'Workspace Agent', icon: Navigation };
        }
        return { label: 'Prompt', icon: Quote };
    };

    const getJaneAgentLibraryTabMeta = (category = 'All') => {
        switch (category) {
            case 'Workspace Agents':
                return { label: 'Workspace Agents', icon: Navigation };
            case 'Prompts':
                return { label: 'Prompts', icon: Quote };
            case 'Bookmarks':
                return { label: 'Bookmarks', icon: Bookmark };
            default:
                return { label: 'All', icon: Layers };
        }
    };

    const buildSourceAwarePromptSuggestion = (query = '', references = []) => {
        const prompt = String(query || '').trim();
        if (prompt.length < 8 || !Array.isArray(references) || references.length === 0) return '';
        if (/Use the selected (?:sources?|files?).+primary evidence/i.test(prompt)) return '';
        const evidenceReferences = references.filter((reference) => (
            !['case', 'case_profile', 'case_summary'].includes(String(reference?.type || reference?.source_type || '').toLowerCase())
        ));
        const sourceLabels = evidenceReferences
            .map(reference => getRagReferenceDisplayLabel(reference))
            .map(label => String(label || '').replace(/^Referenced:\s*/i, '').trim())
            .filter(Boolean)
            .slice(0, 4);
        if (sourceLabels.length === 0) return '';
        const allReferencesAreFiles = evidenceReferences.every((reference) => (
            Number(reference?.file_id || reference?.fileId || 0) > 0
            || /(?:file|document|attachment|upload)/.test(String(reference?.type || reference?.source_type || '').toLowerCase())
        ));
        const sourceNoun = allReferencesAreFiles ? 'file' : 'source';
        const normalized = normalizeCapabilityPrompt(prompt);
        const requestedOutput = /\b(?:draft|write|prepare|generate)\b/.test(normalized)
            ? 'draft the requested output'
            : /\b(?:timeline|chronology)\b/.test(normalized)
                ? 'build the requested timeline'
                : /\b(?:summari[sz]e|summary|analy[sz]e|review)\b/.test(normalized)
                    ? 'produce the requested analysis'
                    : 'answer the request';
        const sourceText = sourceLabels.length === 1 ? sourceLabels[0] : `${sourceLabels.slice(0, -1).join(', ')} and ${sourceLabels[sourceLabels.length - 1]}`;
        return [
            prompt,
            '',
            `Use the selected ${sourceNoun}${sourceLabels.length === 1 ? '' : 's'} (${sourceText}) as the primary evidence. Before answering, make a short plan, then ${requestedOutput} using only grounded facts from the selected ${sourceNoun}${sourceLabels.length === 1 ? '' : 's'} and any explicitly linked case context. Preserve uncertainty, cite specific support for material claims, and flag anything that needs user review instead of filling gaps.`
        ].join('\n');
    };

    const renderRagPreSubmitContextStrip = () => {
        const pageContextReference = ragUsePriorChat ? getCurrentPageCaseReference() : null;
        const promptReferences = getRagReferencesForPrompt(ragReferences, chunkSearchDraft);
        const caseReference = promptReferences.find(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) > 0);
        const fileReferences = promptReferences.filter(reference => Number(reference?.file_id) > 0);
        const routeLabel = getPreSubmitRouteLabel(chunkSearchDraft, promptReferences);
        const sourceAwarePromptSuggestion = buildSourceAwarePromptSuggestion(chunkSearchDraft, promptReferences);
        const retrievalScopeLabel = fileReferences.length > 0
            ? (ragSelectedSourcesOnly ? 'Selected files only' : null)
            : caseReference
                ? (ragSelectedSourcesOnly ? 'Selected case only' : 'Selected case and related information')
                : 'Relevant CaseDB information allowed';
        const strictSourcesAvailable = Boolean(caseReference || fileReferences.length > 0);
        const effectivePriorChat = ragUsePriorChat && !ragSelectedSourcesOnly;
        const shouldShow = Boolean(pageContextReference || caseReference || fileReferences.length > 0 || chunkSearchDraft.trim() || !ragUsePriorChat);
        if (!shouldShow) return null;
        if (!ragPreflightExpanded) return null;

        return (
            <div className="library-rag-preflight" aria-label="Pre-submit Jane context">
                <div className="library-rag-preflight-row">
                    <span className="library-rag-preflight-label">Will use</span>
                    <span className="library-rag-preflight-pill">{routeLabel}</span>
                    {caseReference && <span className="library-rag-preflight-pill">{getRagReferenceDisplayLabel(caseReference)}</span>}
                    {fileReferences.length > 0 && <span className="library-rag-preflight-pill">{fileReferences.length} selected file{fileReferences.length === 1 ? '' : 's'}</span>}
                    <span className={`library-rag-preflight-pill${effectivePriorChat ? '' : ' muted'}`}>{effectivePriorChat ? 'Prior chat on' : 'Prior chat off'}</span>
                    {retrievalScopeLabel && <span className="library-rag-preflight-pill">{retrievalScopeLabel}</span>}
                </div>
                <div className="library-rag-preflight-actions">
                    <button
                        type="button"
                        onClick={() => strictSourcesAvailable && setRagSelectedSourcesOnly(prev => !prev)}
                        aria-pressed={ragSelectedSourcesOnly && strictSourcesAvailable}
                        disabled={!strictSourcesAvailable}
                        title={strictSourcesAvailable ? 'Restrict Jane to pinned files or the selected case for this prompt' : 'Pin a file or select a case to restrict Jane'}
                    >
                        {ragSelectedSourcesOnly && strictSourcesAvailable ? 'Selected sources only' : 'Use only selected sources'}
                    </button>
                    {pageContextReference && !ragReferenceContext && (
                        <button type="button" onClick={() => setRagUsePageContext(prev => !prev)} aria-pressed={ragUsePageContext}>
                            {ragUsePageContext ? 'Using page case' : 'Use page case'}
                        </button>
                    )}
                    <button type="button" onClick={() => setRagUsePriorChat(prev => !prev)} aria-pressed={effectivePriorChat} disabled={ragSelectedSourcesOnly}>
                        {effectivePriorChat ? 'Prior chat on' : 'Prior chat off'}
                    </button>
                    {sourceAwarePromptSuggestion && sourceAwarePromptSuggestion !== chunkSearchDraft.trim() && (
                        <button
                            type="button"
                            onClick={() => {
                                setChunkSearchDraft(sourceAwarePromptSuggestion);
                                setTimeout(() => inputRef.current?.focus(), 0);
                            }}
                            title="Rewrite this prompt to make the selected sources, plan, citations, and uncertainty rules explicit"
                        >
                            Improve with sources
                        </button>
                    )}
                    <button
                        type="button"
                        className="library-rag-preflight-collapse"
                        onClick={() => setRagPreflightExpanded(false)}
                        aria-expanded="true"
                        aria-label="Collapse Jane context preflight"
                        title="Collapse Jane context preflight"
                    >
                        <X size={13} aria-hidden="true" />
                    </button>
                </div>
            </div>
        );
    };

    const toggleCitationExpansion = (messageId) => {
        setExpandedCitationMessages(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    const getWorkspaceNavigationDetails = (message) => {
        if (!message) return null;
        const intelligence = message.metadata?.intelligence || null;
        const workspaceNavigation = message.metadata?.workspace_navigation || intelligence?.workspace_navigation || null;
        const routeTrace = intelligence?.route_trace || null;
        const route = String(workspaceNavigation?.route || routeTrace?.route || intelligence?.retrieval_mode || intelligence?.mode || '').trim();
        if (route !== 'workspace_navigation') return null;

        const caseId = Number(
            workspaceNavigation?.case_id
            || message.metadata?.case_id
            || intelligence?.workspace_session?.case_id
            || intelligence?.session?.case_id
            || intelligence?.plan?.case_id
            || routeTrace?.query_context?.case_id
            || 0
        );
        if (!Number.isFinite(caseId) || caseId <= 0) return null;

        const caseName = String(
            workspaceNavigation?.case_name
            || intelligence?.workspace_session?.case_name
            || intelligence?.session?.case_name
            || intelligence?.plan?.case_name
            || ''
        ).trim();
        const caseLabel = caseName || `Case ${caseId}`;
        const summary = String(workspaceNavigation?.summary || intelligence?.summary || message.answer?.answer_text || message.content || '').trim();

        return {
            caseId,
            caseName,
            caseLabel,
            title: String(workspaceNavigation?.title || '').trim() || `Open the ${caseLabel} case?`,
            subject: String(workspaceNavigation?.subject || '').trim() || 'Open case',
            summary,
            assistantText: String(workspaceNavigation?.assistant_text || message.answer?.answer_text || message.content || summary).trim(),
            actions: Array.isArray(workspaceNavigation?.actions)
                ? workspaceNavigation.actions
                : (Array.isArray(intelligence?.actions) ? intelligence.actions : []),
            prompt: String(workspaceNavigation?.prompt || intelligence?.prompt || intelligence?.query || '').trim(),
            routeReason: String(workspaceNavigation?.route_reason || routeTrace?.route_reason || intelligence?.retrieval_mode || '').trim(),
            status: String(workspaceNavigation?.status || intelligence?.session?.status || 'pending_permission').trim() || 'pending_permission',
            confirmedBy: workspaceNavigation?.confirmed_by || null,
            confirmedByUserId: workspaceNavigation?.confirmed_by_user_id || null,
        };
    };

    const buildWorkspaceNavigationMessage = (responseData, prompt) => {
        const workspaceSession = responseData?.workspace_session || responseData?.session || {};
        const intelligence = responseData?.intelligence || {};
        const caseId = Number(workspaceSession.case_id || intelligence?.plan?.case_id || intelligence?.route_trace?.query_context?.case_id || 0);
        const caseName = String(workspaceSession.case_name || intelligence?.plan?.case_name || '').trim();
        const caseLabel = caseName || `Case ${caseId}`;
        const summary = String(responseData?.summary || `Open ${caseLabel} to continue this workspace task.`).trim();
        const assistantText = String(responseData?.answer?.answer_text || summary).trim() || summary;
        const routeReason = String(intelligence?.route_trace?.route_reason || intelligence?.plan?.route_goal || 'workspace_navigation').trim();

        return {
            caseId,
            caseName,
            caseLabel,
            title: `Open the ${caseLabel} case?`,
            subject: 'Open case',
            summary,
            assistantText,
            answerPayload: {
                answer_text: assistantText,
                citations: [],
                source_descriptions: [],
            },
            workspaceNavigation: {
                route: 'workspace_navigation',
                case_id: caseId,
                case_name: caseName || null,
                prompt: String(prompt || intelligence?.query || '').trim(),
                title: `Open the ${caseLabel} case?`,
                subject: 'Open case',
                summary,
                assistant_text: assistantText,
                actions: Array.isArray(responseData?.actions) ? responseData.actions : [],
                route_reason: routeReason || 'workspace_navigation',
                status: 'pending_permission',
                confirmed_by: null,
                confirmed_by_user_id: null,
                confirmed_at: null,
            },
        };
    };

    const saveRagTurn = async ({ chatId, query, answer, responseData, references, sessionId, requestToken = '', clientSaveToken = '', selectedSourcesOnly = false, rerunMessageId = 0, rerunRequestToken = '', assistantMessageId = '', runGeneration = 0 }) => {
        // This guard also covers workspace/selection and persistence-retry
        // callers, not just the normal answer branch below.
        if (getRagTerminalPersistenceAction({
            response: responseData, runtimeIntent: ragRuntimeIntentRef.current,
            runGeneration: runGeneration || ragRunGenerationRef.current, actorId: getJaneUserId(user), chatId: Number(chatId || 0),
        }) !== 'save_legacy') return null;
        const stableClientSaveToken = String(clientSaveToken || '').trim() || createRagClientSaveToken();
        const retryArgs = { chatId, query, answer, responseData, references, requestToken, clientSaveToken: stableClientSaveToken, selectedSourcesOnly, rerunMessageId, rerunRequestToken, assistantMessageId };
        if (runGeneration > 0 && !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return null;
        if (!hasJaneUserIdentity) {
            recordRagPersistenceFailure(assistantMessageId, { type: 'save_turn', args: retryArgs }, 'Your CaseDB session could not be verified. Refresh the page before retrying.');
            return null;
        }
        const saveOperationId = ++ragSaveOperationRef.current;
        ragLocalTurnSavingRef.current = true;
        setRagSaving(true);
        const saveController = new AbortController();
        const saveTimeout = typeof window !== 'undefined'
            ? window.setTimeout(() => saveController.abort(), RAG_SAVE_REQUEST_TIMEOUT_MS)
            : null;
        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                signal: saveController.signal,
                body: JSON.stringify({
                    action: 'save_turn',
                    chat_id: chatId,
                    query,
                    answer,
                    files: responseData?.files || [],
                    intelligence: responseData?.intelligence || null,
                    references: references || [],
                    selected_sources_only: Boolean(selectedSourcesOnly),
                    request_token: requestToken || '',
                    client_save_token: stableClientSaveToken,
                    rerun_message_id: Number(rerunMessageId || 0) || undefined,
                    rerun_request_token: rerunMessageId > 0 ? rerunRequestToken : undefined,
                    model_key: normalizeRagModelKey(ragModelKey),
                    chat_scope: 'firm-wide'
                })
            });
            const responseText = await response.text();
            let data = null;
            try { data = responseText ? JSON.parse(responseText) : null; } catch (error) {}
            if (!response.ok || !data?.success) {
                if (data?.request_terminalized && ['outlook_response_stale', 'outlook_chat_not_private'].includes(String(data?.code || ''))) {
                    if (runGeneration > 0 && !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return null;
                    clearRagPersistenceFailure(assistantMessageId);
                    clearRagIncomingAnswerMessage(assistantMessageId);
                    if (sessionId !== undefined && sessionId !== ragSessionRef.current) return null;
                    if (Array.isArray(data.messages)) {
                        setRagMessages(normalizeRagMessages(data.messages));
                        setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
                    }
                    setRagActiveRequest(null);
                    setRagMessagesLoadingOlder(false);
                    loadRagChats();
                    return data;
                }
                throw new Error(data?.message || 'Could not save this Jane response.');
            }
            if (runGeneration > 0 && !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return null;
            clearRagPersistenceFailure(assistantMessageId);
            if (sessionId !== undefined && sessionId !== ragSessionRef.current) return;
            if (data.success) {
                const savedChatId = data.chat?.id || chatId || null;
                if (rerunMessageId > 0 && Number(savedChatId) !== Number(chatId)) {
                    throw new Error('The rerun could not be verified in this chat.');
                }
                activeRagChatIdRef.current = savedChatId;
                setActiveRagChatId(savedChatId);
                if (data.chat?.updated_at || data.chat?.last_message_at || data.chat?.created_at) {
                    ragLatestChatUpdatedAtRef.current = String(data.chat.updated_at || data.chat.last_message_at || data.chat.created_at || ragLatestChatUpdatedAtRef.current || '');
                }
                if (data.chat?.model_key) setRagModelKey(normalizeRagModelKey(data.chat.model_key));
                if (Array.isArray(data.messages)) {
                    const normalizedSavedMessages = normalizeRagMessages(data.messages);
                    if (rerunMessageId > 0 && data.rerun_assistant_message) {
                        normalizedSavedMessages.unshift(...normalizeRagMessages([data.rerun_assistant_message]));
                    }
                    if (rerunMessageId > 0 && !normalizedSavedMessages.some(message => (
                        message.role === 'assistant' && Number(message.id) === Number(assistantMessageId)
                    ))) {
                        throw new Error('The saved rerun response could not be verified.');
                    }
                    if (ragIncomingAnswerMessageIdsRef.current.size > 0) {
                        ragDeferredSavedMessagesRef.current = {
                            messages: normalizedSavedMessages,
                            rerunAssistantMessageId: rerunMessageId > 0 ? assistantMessageId : null,
                            chatId: savedChatId,
                            sessionId: sessionId ?? ragSessionRef.current,
                            runGeneration: runGeneration || ragRunGenerationRef.current,
                        };
                    } else {
                        setRagMessages(previous => rerunMessageId > 0
                            ? mergeRagRerunMessages(previous, normalizedSavedMessages, assistantMessageId)
                            : normalizedSavedMessages);
                    }
                    if (rerunMessageId <= 0) setRagMessagesHasMoreBefore(Boolean(data.message_pagination?.has_more_before));
                } else if (Number(data.latest_message_id || 0) > 0) {
                    ragLatestMessageIdRef.current = Math.max(ragLatestMessageIdRef.current, Number(data.latest_message_id || 0));
                }
                setRagMessagesLoadingOlder(false);
                setRagActiveRequest(null);
                if (rerunMessageId > 0 && ragInlineRerunRef.current?.generation === runGeneration) {
                    ragInlineRerunRef.current = null;
                }
                loadRagChats();
                return data;
            }
        } catch (error) {
            console.error('Error saving RAG chat turn:', error);
            if (runGeneration > 0 && !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return null;
            recordRagPersistenceFailure(
                assistantMessageId,
                { type: 'save_turn', args: retryArgs },
                error?.name === 'AbortError'
                    ? 'Saving took too long. This response is still visible, but it has not been saved to the chat.'
                    : 'This response is visible here but was not saved to the chat.'
            );
        } finally {
            if (saveTimeout) window.clearTimeout(saveTimeout);
            if (saveOperationId === ragSaveOperationRef.current) {
                ragLocalTurnSavingRef.current = false;
                if ((sessionId === undefined || sessionId === ragSessionRef.current)
                    && (runGeneration <= 0 || isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current))) setRagSaving(false);
            }
        }
        return null;
    };

    const handleRetryRagPersistence = async (messageId) => {
        const key = String(messageId || '');
        const retryEntry = ragPersistenceRetryRef.current.get(key);
        if (!key || !retryEntry || ragPersistenceStateByMessageId[key]?.status === 'retrying') return;
        setRagPersistenceStateByMessageId(prev => ({
            ...prev,
            [key]: { ...(prev[key] || {}), status: 'retrying' },
        }));
        if (retryEntry.type === 'save_turn') {
            await saveRagTurn({ ...retryEntry.args, sessionId: ragSessionRef.current });
            return;
        }
        if (retryEntry.type === 'update_message') {
            await persistRagMessageUpdate(retryEntry.args);
        }
    };

    const animateRagAssistantText = async ({ messageId, answerPayload, fullText, files: answerFiles = [], sources = [], metadata = null, sessionId, runGeneration = 0 }) => {
        if (sessionId !== ragSessionRef.current
            || (runGeneration > 0 && !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current))) return;

        setRagMessages(prev => prev.map((message) => (
            message.id === messageId
                ? (() => {
                    const intelligence = metadata?.intelligence || message.metadata?.intelligence || null;
                    const plannerSteps = getRagPlannerStepsFromMessage(message, intelligence);
                    return {
                        ...message,
                        pending: false,
                        content: fullText,
                        answer: { ...answerPayload, answer_text: fullText },
                        sources,
                        files: answerFiles,
                        metadata: metadata ? { ...(message.metadata || {}), ...metadata, intelligence: intelligence ? { ...intelligence, plannerSteps } : null, plannerSteps, streaming: false } : message.metadata,
                    };
                })()
                : message
        )));
    };

    const executeRagPrompt = async (trimmedQuery, referencesSnapshot = [], options = {}) => {
        if (!hasJaneUserIdentity) {
            setRagSpeechStatus('Your CaseDB session could not be verified. Refresh the page and try again.');
            return;
        }
        if (trimmedQuery.length < 2 || (loading && !options.force)) return;
        if (ragActiveRequest && !options.force) return;
        if (getOwnedRagRuntimeIntent()) {
            setRagCancelError('Jane’s previous run has not been reconciled. Reopen the chat to check its saved status before submitting again.');
            return;
        }
        const isInlineRerun = Number(options.rerunMessageId || 0) > 0;
        if (isInlineRerun && (
            Number(options.chatIdOverride) !== Number(activeRagChatIdRef.current)
            || !Number.isInteger(Number(options.chatIdOverride)) || Number(options.chatIdOverride) <= 0
            || !Number.isInteger(Number(options.replaceAssistantMessageId)) || Number(options.replaceAssistantMessageId) <= 0
            || !String(options.rerunRequestToken || '').trim()
        )) {
            setRagSpeechStatus('Jane could not verify the response to rerun in this chat. Reopen the chat and try again.');
            return;
        }
        const runGeneration = ++ragRunGenerationRef.current;
        invalidateRagPendingSaveState();
        if (!isInlineRerun) markRagThreadShouldAutoScroll();
        else {
            ragShouldAutoScrollRef.current = false;
            setRagThreadAtBottom(false);
        }
        const pendingRagActionTarget = getPendingRagActionTarget(ragMessages);
        const isPendingWorkspaceCaseFollowup = shouldUseDirectWorkspaceContinuation({
            pendingActionType: pendingRagActionTarget?.type,
            query: trimmedQuery,
        });
        // A fresh prompt belongs to Jane's model-selected tool loop. The direct
        // workspace endpoint is only a continuation for a case-choice card
        // that Jane has already returned; keyword matching here can otherwise
        // steal Outlook, drafting, reporting, and ordinary answer requests.
        const shouldPreflightWorkspace = isPendingWorkspaceCaseFollowup;

        const handleWorkspaceRedirect = async () => {
            try {
                const referenceSelection = getRagReferenceSelection();
                const selectedWorkspaceCaseId = referenceSelection?.type === 'case' && Number(referenceSelection.id) > 0
                    ? Number(referenceSelection.id)
                    : 0;
                const currentPageCaseReference = getCurrentPageCaseReference();
                const recentWorkspaceContext = ragMessages.slice(-12).map((message) => ({
                    role: message?.role || '',
                    content: message?.content || '',
                    query_text: message?.query_text || '',
                    answer: message?.answer || null,
                    metadata: message?.metadata || null,
                }));
                const workspaceResponse = await fetch(`${API_BASE}/jane-workspace.php`, {
                    method: 'POST',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    credentials: 'include',
                    body: JSON.stringify({
                        prompt: trimmedQuery,
                        explicit_case_candidate: isPendingWorkspaceCaseFollowup ? trimmedQuery : undefined,
                        case_id: selectedWorkspaceCaseId > 0 ? selectedWorkspaceCaseId : currentPageCaseReference?.case_id || undefined,
                        chat_id: activeRagChatId || getRagChatIdFromUrl() || undefined,
                        recent_messages: recentWorkspaceContext,
                        current_path: `${window.location.pathname}${window.location.search}`,
                    }),
                });
                const workspaceData = await workspaceResponse.json();
                if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return true;
                if (!workspaceResponse.ok || !workspaceData?.success) {
                    return false;
                }
                if (workspaceData.mode === 'case_selection_required') {
                    const caseMatches = Array.isArray(workspaceData.case_matches) ? workspaceData.case_matches : [];
                    const selectionUserMessage = {
                        id: `local-user-${Date.now()}`,
                        role: 'user',
                        content: trimmedQuery,
                        author_name: 'You',
                        user_id: userId,
                        created_at: new Date().toISOString(),
                    };
                    const matchSummary = caseMatches.length > 0
                        ? caseMatches.slice(0, 4).map((match, index) => `${index + 1}. ${String(match?.case_name || match?.name || `Case ${match?.case_id || ''}`).trim()}`).join('\n')
                        : 'Reply with the case ID or exact case name.';
                    const caseSelectionMessage = {
                        id: `workspace-case-selection-${Date.now()}`,
                        role: 'assistant',
                        content: `${String(workspaceData.message || 'Case context is required for this guided workspace action.').trim()}\n\n${matchSummary}`,
                        answer: {
                            answer_text: `${String(workspaceData.message || 'Case context is required for this guided workspace action.').trim()}\n\n${matchSummary}`,
                            citations: [],
                            source_descriptions: [],
                        },
                        sources: [],
                        files: [],
                        metadata: {
                            workspace_case_selection_required: true,
                            case_matches: caseMatches,
                            original_prompt: workspaceData.case_selection?.prompt || trimmedQuery,
                            case_selection: workspaceData.case_selection || null,
                        },
                        created_at: new Date().toISOString(),
                    };
                    setRagMessages(prev => [...prev, selectionUserMessage, caseSelectionMessage]);
                    const selectionChatId = activeRagChatId || await ensureRagChatForUpload(ragSessionRef.current);
                    await saveRagTurn({
                        chatId: selectionChatId,
                        query: trimmedQuery,
                        answer: caseSelectionMessage.answer,
                        responseData: { files: [], intelligence: workspaceData },
                        references: [],
                        assistantMessageId: caseSelectionMessage.id,
                        runGeneration,
                    });
                    if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return true;
                    scheduleRagThreadToBottom(3);
                    return true;
                }
                if (workspaceData.mode === 'contact_selection_required') {
                    const selectionUserMessage = {
                        id: `local-user-${Date.now()}`,
                        role: 'user',
                        content: trimmedQuery,
                        author_name: 'You',
                        user_id: userId,
                        created_at: new Date().toISOString(),
                    };
                    const answerText = String(workspaceData.message || 'I need you to confirm which contact to use.').trim();
                    const contactSelectionMessage = {
                        id: `workspace-contact-selection-${Date.now()}`,
                        role: 'assistant',
                        content: answerText,
                        answer: {
                            answer_text: answerText,
                            citations: [],
                            source_descriptions: [],
                        },
                        sources: [],
                        files: [],
                        metadata: {
                            workspace_contact_selection_required: true,
                            contact_matches: Array.isArray(workspaceData.contact_matches) ? workspaceData.contact_matches : [],
                            intelligence: workspaceData,
                        },
                        created_at: new Date().toISOString(),
                    };
                    setRagMessages(prev => [...prev, selectionUserMessage, contactSelectionMessage]);
                    const selectionChatId = activeRagChatId || await ensureRagChatForUpload(ragSessionRef.current);
                    await saveRagTurn({
                        chatId: selectionChatId,
                        query: trimmedQuery,
                        answer: contactSelectionMessage.answer,
                        responseData: { files: [], intelligence: workspaceData },
                        references: [],
                        assistantMessageId: contactSelectionMessage.id,
                        runGeneration,
                    });
                    if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return true;
                    scheduleRagThreadToBottom(3);
                    return true;
                }
                if (workspaceData.mode !== 'workspace_navigation' || !Number(workspaceData?.session?.case_id)) {
                    return false;
                }
                const workspaceNavigation = buildWorkspaceNavigationMessage(workspaceData, workspaceData.case_selection?.prompt || trimmedQuery);
                const workspaceActions = Array.isArray(workspaceData.actions) ? workspaceData.actions : [];
                const workspacePlannerSteps = Array.isArray(workspaceData.steps) ? workspaceData.steps : [];
                const workspaceRouteTrace = {
                    route: 'workspace_navigation',
                    route_reason: workspaceNavigation.workspaceNavigation.route_reason || 'workspace_navigation',
                    query_context: {
                        case_id: workspaceNavigation.caseId,
                        case_name: workspaceNavigation.caseName || null,
                        selected_sources_only: false,
                        reference_file_ids: [],
                    },
                    tools: Array.isArray(workspaceData.tools_used) ? workspaceData.tools_used : [],
                    evidence: { retrieval_mode: 'workspace_navigation' },
                };
                const selectionUserMessage = {
                    id: `local-user-${Date.now()}`,
                    role: 'user',
                    content: trimmedQuery,
                    author_name: 'You',
                    user_id: userId,
                    created_at: new Date().toISOString(),
                };
                const workspaceAssistantMessage = {
                    id: `workspace-navigation-${Date.now()}`,
                    role: 'assistant',
                    content: workspaceNavigation.assistantText,
                    answer: workspaceNavigation.answerPayload,
                    sources: [],
                    files: [],
                    metadata: {
                        plannerSteps: workspacePlannerSteps,
                        intelligence: {
                            ...(workspaceData.intelligence || {}),
                            summary: workspaceNavigation.summary,
                            answer: workspaceNavigation.answerPayload,
                            actions: workspaceActions,
                            session: workspaceData.session || workspaceNavigation.workspaceNavigation,
                            route_trace: workspaceRouteTrace,
                            plannerSteps: workspacePlannerSteps,
                            workspace_navigation: workspaceNavigation.workspaceNavigation,
                        },
                        workspace_navigation: workspaceNavigation.workspaceNavigation,
                    },
                    created_at: new Date().toISOString(),
                };
                setRagMessages(prev => [...prev, selectionUserMessage, workspaceAssistantMessage]);
                setWorkspaceNavigationPendingMessageIds(prev => {
                    const next = new Set(prev);
                    next.add(String(workspaceAssistantMessage.id));
                    return next;
                });
                const workspaceChatId = activeRagChatId || await ensureRagChatForUpload(ragSessionRef.current);
                if (workspaceChatId) {
                    const savedWorkspaceTurn = await saveRagTurn({
                        chatId: workspaceChatId,
                        query: trimmedQuery,
                        answer: workspaceNavigation.answerPayload,
                        responseData: {
                            ...workspaceData,
                            files: [],
                            intelligence: {
                                ...(workspaceData.intelligence || {}),
                                summary: workspaceNavigation.summary,
                                answer: workspaceNavigation.answerPayload,
                                actions: workspaceActions,
                                session: workspaceData.session || workspaceNavigation.workspaceNavigation,
                                route_trace: workspaceRouteTrace,
                                plannerSteps: workspacePlannerSteps,
                                workspace_navigation: workspaceNavigation.workspaceNavigation,
                            },
                        },
                        references: [],
                        assistantMessageId: workspaceAssistantMessage.id,
                        runGeneration,
                    });
                    if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return true;
                    const savedWorkspaceMessages = normalizeRagMessages(savedWorkspaceTurn?.messages || []);
                    const savedWorkspaceAssistant = [...savedWorkspaceMessages].reverse().find((savedMessage) => (
                        savedMessage.role === 'assistant'
                        && Number(savedMessage.answer?.workspace_navigation?.case_id || savedMessage.metadata?.workspace_navigation?.case_id || savedMessage.metadata?.intelligence?.workspace_navigation?.case_id || 0) === Number(workspaceNavigation.caseId)
                    ));
                    setWorkspaceNavigationPendingMessageIds(prev => {
                        const next = new Set(prev);
                        next.delete(String(workspaceAssistantMessage.id));
                        if (savedWorkspaceAssistant?.id) {
                            next.add(String(savedWorkspaceAssistant.id));
                        }
                        return next;
                    });
                }
                scheduleRagThreadToBottom(3);
                return true;
            } catch (workspaceError) {
                console.error('Error preflighting workspace redirect:', workspaceError);
                return false;
            }
        };

        if (!options.force && shouldPreflightWorkspace) {
            const redirectedToWorkspace = await handleWorkspaceRedirect();
            if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
            if (redirectedToWorkspace) {
                if (options.clearComposer !== false) {
                    setChunkSearchDraft('');
                }
                return;
            }
        }

        const promptReferences = filterReferencesForFreshTitleSearch(
            isInlineRerun ? referencesSnapshot : getRagReferencesForPrompt(referencesSnapshot, trimmedQuery),
            trimmedQuery
        );
        const promptHasSelectedContext = promptReferences.some(reference => (
            Number(reference?.file_id) > 0
            || (reference?.type === 'case' && Number(reference.case_id || reference.id) > 0)
        ));
        const selectedSourcesOnly = Boolean(options.selectedSourcesOnly ?? (ragSelectedSourcesOnly && promptHasSelectedContext));
        const currentJaneRunMode = normalizeJaneRunMode(options.janeRunModeOverride || janeRunMode);
        const immediatePlannerSteps = buildImmediateJanePlannerSteps(currentJaneRunMode, trimmedQuery, promptReferences, selectedSourcesOnly);
        const immediateRunPlan = buildImmediateJaneRunPlan(currentJaneRunMode, trimmedQuery, promptReferences);
        const rerunMessageId = Number(options.rerunMessageId || 0);
        // The visible Jane preamble is model-authored and request-bound. Until
        // the server publishes that control event, the planner/Thinking state
        // is application status—not assistant prose presented as model output.
        const janeOpeningText = '';

        const sessionId = ragSessionRef.current;
        let chatIdForRequest = options.chatIdOverride || activeRagChatId;
        if (!chatIdForRequest) {
            try {
                chatIdForRequest = await ensureRagChatForUpload(sessionId);
            } catch (error) {
                setRagSpeechStatus(error?.message || 'Jane could not create a chat for this request. Refresh the page and try again.');
                return;
            }
            if (sessionId !== ragSessionRef.current
                || !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
        }
        const replacementMessages = Array.isArray(options.replacementMessages) ? options.replacementMessages : null;
        setChunkSearchQuery(trimmedQuery);
        setFiles([]);
        setPagination(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalCount: 0 }));
        const replaceAssistantMessageId = options.replaceAssistantMessageId ? String(options.replaceAssistantMessageId) : '';
        const replacementAssistantSnapshot = replaceAssistantMessageId
            ? ragMessages.find((message) => String(message.id) === replaceAssistantMessageId) || null
            : null;
        const assistantCreatedAt = isInlineRerun && replacementAssistantSnapshot?.created_at
            ? replacementAssistantSnapshot.created_at : new Date().toISOString();
        const rerunMetadata = isInlineRerun ? { rerun_of_message_id: rerunMessageId } : {};
        const pendingAssistantId = replaceAssistantMessageId || `pending-assistant-${Date.now()}`;
        if (isInlineRerun && replacementAssistantSnapshot) {
            ragInlineRerunRef.current = {
                generation: runGeneration,
                chatId: Number(chatIdForRequest),
                requestToken: String(options.rerunRequestToken),
                snapshot: replacementAssistantSnapshot,
            };
            setRagActiveRequest({
                chat_id: Number(chatIdForRequest),
                request_token: String(options.rerunRequestToken),
                user_message_id: rerunMessageId,
                assistant_message_id: Number(replaceAssistantMessageId),
                user_id: userId,
                status: 'active',
                status_message: 'Starting response…',
            });
        }
        const shouldAppendUserMessage = !replaceAssistantMessageId && options.appendUserMessage !== false && rerunMessageId <= 0;
        const shouldClearComposer = options.clearComposer !== false;
        const visibleUserQuery = String(options.displayQuery || trimmedQuery).trim() || trimmedQuery;
        const responsePlanStartedAt = Date.now();
        const userMessage = {
            id: `local-user-${Date.now()}`,
            role: 'user',
            content: visibleUserQuery,
            references: promptReferences,
            metadata: selectedSourcesOnly ? { selected_sources_only: true } : undefined,
            author_name: 'You',
            user_id: userId,
            created_at: new Date().toISOString()
        };
        const pendingAssistant = {
            id: pendingAssistantId,
            role: 'assistant',
            content: buildRagSearchStatusMessage(trimmedQuery, promptReferences),
            pending: true,
            metadata: {
                ...rerunMetadata,
                jane_run_mode: currentJaneRunMode,
                janeOpeningText,
                plannerSteps: immediatePlannerSteps,
                runPlan: immediateRunPlan,
                responsePlanStartedAt,
                streaming_trace_disabled: currentJaneRunMode === 'fast',
            },
            created_at: assistantCreatedAt
        };

        if (replaceAssistantMessageId) {
            setRagMessages(prev => prev.map(message => (
                String(message.id) === replaceAssistantMessageId
                    ? { ...pendingAssistant, id: message.id }
                    : message
            )));
        } else {
            setRagMessages(prev => [...(replacementMessages || prev), ...(shouldAppendUserMessage ? [userMessage] : []), pendingAssistant]);
        }
        markRagIncomingAnswerMessage(pendingAssistantId);
        if (!isInlineRerun) scheduleRagThreadToBottom(4);
        if (shouldClearComposer) {
            setChunkSearchDraft('');
            setRagReferences([]);
        }
        if (!isInlineRerun) scheduleRagThreadToBottom(4);
        setShowRagReferenceMenu(false);

        let streamedAnswerText = '';
        let streamedAnswerPayload = null;
        let streamedRequestToken = '';
        let streamedAnswerTraceStarted = false;
        let streamedAnswerIsLegalDraft = false;
        let streamedAnswerContinuesInBackground = false;
        let streamedRunPlan = immediateRunPlan;
        let streamedPublicOpening = null;
        let streamedWorkHistory = [];
        const showBackgroundRunReceipt = (payload) => {
            const receiptRun = buildJaneBackgroundRunFromReceipt(payload, chatIdForRequest);
            if (!receiptRun) return null;
            setRagBackgroundDraftRuns(previousRuns => upsertJaneBackgroundRun(previousRuns, receiptRun));
            return receiptRun;
        };
        const responseData = await loadChunkMatches(1, trimmedQuery, {
            streamAnswer: true,
            chatIdOverride: chatIdForRequest,
            caseIdOverride: options.caseIdOverride,
            confirmedCaseId: options.confirmedCaseId,
            promptReferences,
            selectedSourcesOnly,
            rerunMessageId,
            rerunRequestToken: options.rerunRequestToken,
            // Inline reruns use the server-owned reserved target; the separate
            // reuse parameter is only for an existing continuation workflow.
            reuseAssistantMessageId: isInlineRerun ? undefined : replaceAssistantMessageId,
            janeRunModeOverride: currentJaneRunMode,
            onStreamEvent: (event) => {
                if (sessionId !== ragSessionRef.current
                    || !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
                const nextWorkHistory = mergeJaneStreamWorkHistory(streamedWorkHistory, event, streamedRequestToken || event.request_token || '');
                if (nextWorkHistory !== streamedWorkHistory) {
                    streamedWorkHistory = nextWorkHistory;
                    setRagMessages(previous => previous.map(message => String(message.id) === String(pendingAssistantId)
                        ? { ...message, metadata: { ...(message.metadata || {}), work_history: nextWorkHistory } }
                        : message));
                }
                const publicOpening = getJaneStreamOpening(event, streamedRequestToken);
                if (publicOpening) {
                    const boundPublicOpening = { ...publicOpening, ...(streamedRequestToken || event.request_token ? { request_token: streamedRequestToken || event.request_token } : {}) };
                    streamedPublicOpening = boundPublicOpening;
                    setRagMessages(previous => previous.map(message => String(message.id) === String(pendingAssistantId)
                        ? { ...message, metadata: { ...(message.metadata || {}), public_opening: boundPublicOpening, janeOpeningText: publicOpening.text } }
                        : message));
                }
                if (event.type === 'request_lock') {
                    applyAcceptedRagChatTitle(event, chatIdForRequest);
                    streamedRequestToken = event.request_token || '';
                    if (isInlineRerun && ragInlineRerunRef.current?.generation === runGeneration && streamedRequestToken) {
                        ragInlineRerunRef.current.requestToken = streamedRequestToken;
                    }
                    if (event.chat_id && !chatIdForRequest) {
                        chatIdForRequest = event.chat_id;
                    }
                    setRagMessages(prev => bindOptimisticRagRequest(prev, {
                        requestToken: streamedRequestToken,
                        localUserMessageId: userMessage.id,
                        localAssistantMessageId: pendingAssistantId,
                        userMessageId: event.user_message_id,
                        assistantMessageId: event.assistant_message_id,
                    }));
                    setRagActiveRequest({
                        ...(getOwnedRagRuntimeIntent(chatIdForRequest)?.acknowledgement || {}),
                        ...(getOwnedRagRuntimeIntent(chatIdForRequest) ? { runtime: 'agent_v2' } : {}),
                        chat_id: event.chat_id || chatIdForRequest,
                        request_token: streamedRequestToken,
                        user_message_id: Number(event.user_message_id || 0) || null,
                        assistant_message_id: Number(event.assistant_message_id || 0) || null,
                        query_text: String(event.query_text || trimmedQuery),
                        status_message: String(event.status_message || 'Starting response…'),
                        user_id: userId,
                        user_name: 'You',
                        status: 'active',
                    });
                }
                if (event.type === 'request_blocked') {
                    const blockedRequest = event.active_request || null;
                    setRagActiveRequest(blockedRequest);
                    if (blockedRequest) syncExternalRagRequestPreview(blockedRequest);
                    clearRagIncomingAnswerMessage(pendingAssistantId);
                    setRagMessages(prev => replacementAssistantSnapshot
                        ? prev.map((message) => String(message.id) === String(pendingAssistantId) ? replacementAssistantSnapshot : message)
                        : prev.filter((message) => String(message.id) !== String(pendingAssistantId) && String(message.id) !== String(userMessage.id)));
                }
                if (event.type === 'status' && event.message) {
                    const statusMessage = sanitizeJaneStatusMessage(event.message);
                    setRagMessages(prev => prev.map((message) => (
                        String(message.id) === String(pendingAssistantId) && (!message.answer || message.metadata?.streaming)
                            ? appendRagPlannerStep({ ...message, content: statusMessage }, { type: 'status', message: statusMessage })
                            : message
                    )));
                }

                if (event.type === 'planner') {
                    const plannerMessage = sanitizeJaneStatusMessage(event.message);
                    streamedRunPlan = mergeJaneRunPlanProgress(streamedRunPlan, event.run_plan || null);
                    setRagMessages(prev => prev.map((message) => (
                        String(message.id) === String(pendingAssistantId) && (!message.answer || message.metadata?.streaming)
                            ? (() => {
                                const nextMessage = appendRagPlannerStep(
                                    { ...message, content: plannerMessage || message.content },
                                    { type: 'planner', message: plannerMessage, steps: event.steps || [], mode: event.mode || null }
                                );
                                return {
                                    ...nextMessage,
                                    metadata: {
                                        ...(nextMessage.metadata || {}),
                                        runPlan: mergeJaneRunPlanProgress(nextMessage.metadata?.runPlan || null, event.run_plan || null),
                                        streaming: message.metadata?.streaming || nextMessage.metadata?.streaming || false,
                                    }
                                };
                            })()
                            : message
                    )));
                }

                if (event.type === 'run_plan') {
                    const runPlanMessage = sanitizeJaneStatusMessage(event.message);
                    streamedRunPlan = mergeJaneRunPlanProgress(streamedRunPlan, event.run_plan || event.plan || null);
                    setRagMessages(prev => prev.map((message) => (
                        String(message.id) === String(pendingAssistantId)
                            ? {
                                ...message,
                                content: runPlanMessage || message.content,
                                metadata: {
                                    ...(message.metadata || {}),
                                    runPlan: mergeJaneRunPlanProgress(message.metadata?.runPlan || null, event.run_plan || event.plan || null),
                                }
                            }
                            : message
                    )));
                }

                if (event.type === 'answer_start') {
                    streamedAnswerText = '';
                    streamedAnswerPayload = event.answer || { answer_text: '', citations: [], source_descriptions: [] };
                    const answerStartMessage = sanitizeJaneStatusMessage(event.message);
                    const answerStartIntelligence = event.intelligence || null;
                    streamedAnswerContinuesInBackground = Boolean(showBackgroundRunReceipt({
                        ...event,
                        answer: streamedAnswerPayload,
                        intelligence: answerStartIntelligence,
                    }));
                    streamedAnswerIsLegalDraft = isJaneLegalDraftPresentation({
                        answer: streamedAnswerPayload,
                        intelligence: answerStartIntelligence,
                    });
                    streamedRunPlan = mergeJaneRunPlanProgress(
                        streamedRunPlan,
                        event.run_plan || answerStartIntelligence?.run_plan || null
                    );
                    setRagMessages(prev => prev.map((message) => (
                        String(message.id) === String(pendingAssistantId)
                            ? (() => {
                                const intelligence = answerStartIntelligence;
                                const intelligenceWithOpening = withJanePublicOpening(intelligence, janeOpeningText, streamedPublicOpening, streamedRequestToken);
                                const answerStartStep = {
                                    type: 'status',
                                    message: answerStartMessage || 'I’m starting the answer from the selected plan and evidence.',
                                    id: `answer-start-${Date.now()}`,
                                };
                                const tracedMessage = appendRagPlannerStep(message, answerStartStep);
                                const plannerSteps = getRagPlannerStepsFromMessage(tracedMessage, intelligenceWithOpening);
                                return {
                                ...tracedMessage,
                                content: streamedAnswerIsLegalDraft ? answerStartMessage : '',
                                pending: false,
                                answer: streamedAnswerPayload,
                                sources: streamedAnswerPayload.citations || [],
                                files: event.files || [],
                                metadata: {
                                    ...(message.metadata || {}),
                                    janeOpeningText: intelligenceWithOpening.jane_opening_text,
                                    intelligence: { ...intelligenceWithOpening, plannerSteps },
                                    plannerSteps,
                                    runPlan: mergeJaneRunPlanProgress(
                                        message.metadata?.runPlan || intelligence?.run_plan || null,
                                        streamedRunPlan
                                    ),
                                    streaming: !streamedAnswerContinuesInBackground,
                                }
                            };
                            })()
                            : message
                    )));
                }

                if (event.type === 'answer_delta') {
                    if (!streamedAnswerContinuesInBackground && !streamedAnswerTraceStarted && event.text) {
                        streamedAnswerTraceStarted = true;
                        setRagMessages(prev => prev.map((message) => (
                            String(message.id) === String(pendingAssistantId)
                                ? appendRagPlannerStep(message, {
                                    type: 'status',
                                    message: RAG_FINAL_RESPONSE_DRAFTING_LABEL,
                                    id: `answer-drafting-${Date.now()}`,
                                })
                                : message
                        )));
                    }
                    streamedAnswerText += event.text || '';
                    if (streamedAnswerIsLegalDraft) {
                        return;
                    }
                    setRagMessages(prev => prev.map((message) => (
                        String(message.id) === String(pendingAssistantId)
                            ? {
                                ...message,
                                content: streamedAnswerText,
                                answer: {
                                    ...(streamedAnswerPayload || message.answer || {}),
                                    answer_text: streamedAnswerText
                                },
                                metadata: { ...(message.metadata || {}), janeOpeningText: message.metadata?.janeOpeningText || janeOpeningText, streaming: !streamedAnswerContinuesInBackground }
                            }
                            : message
                    )));
                }

                if (event.type === 'complete' && event.payload) {
                    // A rejected submission is not an answer failure. Keep
                    // the server's existing request visible for wait/Stop.
                    if (event.payload.blocked_by_active_request) return;
                    const terminalDisposition = getJaneTerminalResponseDisposition(event.payload);
                    const promptUnavailableAnswer = getJanePromptUnavailableAnswer(event.payload);
                    const completedReceiptRun = terminalDisposition.failed ? null : showBackgroundRunReceipt(event.payload);
                    streamedAnswerContinuesInBackground = streamedAnswerContinuesInBackground || Boolean(completedReceiptRun);
                    const responsePlanTiming = buildJaneResponsePlanTimingMetadata(event.payload, responsePlanStartedAt);
                    const finalAnswer = promptUnavailableAnswer || event.payload?.intelligence?.answer || event.payload?.answer || null;
                    const finalText = finalAnswer?.answer_text || streamedAnswerText;
                    const finalPayloadRunPlan = event.payload?.intelligence?.run_plan || event.payload?.run_plan || null;
                    const finalAnswerMode = String(finalAnswer?.mode || event.payload?.mode || '').trim().toLowerCase();
                    const continuesInBackground = !terminalDisposition.failed && (
                        Boolean(event.payload?.run_active)
                        || JANE_BACKGROUND_HANDOFF_MODES.has(finalAnswerMode)
                        || streamedAnswerContinuesInBackground
                    );
                    streamedRunPlan = mergeJaneRunPlanProgress(
                        streamedRunPlan,
                        terminalDisposition.failed
                            ? { ...(finalPayloadRunPlan || {}), status: terminalDisposition.runPlanStatus, steps: finalPayloadRunPlan?.steps || [] }
                            : continuesInBackground
                            ? finalPayloadRunPlan
                            : { ...(finalPayloadRunPlan || {}), status: terminalDisposition.runPlanStatus, steps: finalPayloadRunPlan?.steps || [] }
                    );
                    streamedWorkHistory = mergeJaneStreamWorkHistory(streamedWorkHistory, { type: 'run_plan', run_plan: streamedRunPlan }, streamedRequestToken);
                    if (finalAnswer || Array.isArray(event.payload?.files)) {
                        streamedAnswerPayload = finalAnswer || streamedAnswerPayload;
                        setRagMessages(prev => prev.map((message) => (
                            String(message.id) === String(pendingAssistantId)
                                ? (() => {
                                    const intelligence = event.payload?.intelligence || message.metadata?.intelligence || null;
                                    const intelligenceWithOpening = withJanePublicOpening(intelligence, janeOpeningText, streamedPublicOpening, streamedRequestToken);
                                    const completedMessage = terminalDisposition.completionMessage
                                        ? appendRagPlannerStep(message, {
                                            type: 'status',
                                            message: terminalDisposition.completionMessage,
                                            id: `answer-complete-${Date.now()}`,
                                        })
                                        : message;
                                    const completedRunPlan = mergeJaneRunPlanProgress(
                                        message.metadata?.runPlan || message.metadata?.intelligence?.run_plan || null,
                                        streamedRunPlan
                                    );
                                    const completedIntelligence = { ...intelligenceWithOpening, run_plan: completedRunPlan };
                                    const finalPlannerSteps = buildRagPlannerStepsFromIntelligence(completedIntelligence);
                                    const plannerSteps = finalPlannerSteps.length > 0
                                        ? finalPlannerSteps
                                        : getRagPlannerStepsFromMessage(completedMessage, completedIntelligence);
                                    return {
                                        ...completedMessage,
                                        content: finalText,
                                        pending: false,
                                        answer: finalAnswer || message.answer,
                                        sources: finalAnswer?.citations || message.sources || [],
                                        files: event.payload?.files || message.files || [],
                                        metadata: {
                                            ...(message.metadata || {}),
                                            ...responsePlanTiming,
                                            janeOpeningText: intelligenceWithOpening.jane_opening_text,
                                            intelligence: {
                                                ...completedIntelligence,
                                                plannerSteps,
                                                response_plan_elapsed_ms: responsePlanTiming.responsePlanElapsedMs ?? intelligenceWithOpening.response_plan_elapsed_ms ?? null,
                                                response_plan_completed_at: responsePlanTiming.responsePlanCompletedAt ?? intelligenceWithOpening.response_plan_completed_at ?? null,
                                            },
                                            plannerSteps,
                                            runPlan: completedRunPlan,
                                            ...(streamedWorkHistory.length > 0 ? { work_history: streamedWorkHistory } : {}),
                                            ...(terminalDisposition.failed ? {
                                                jane_error: true,
                                                jane_error_code: String(event.payload?.error_code || event.payload?.reason || promptUnavailableAnswer?.error_code || promptUnavailableAnswer?.failure_kind || 'jane_request_failed'),
                                                retryable: Boolean(event.payload?.retryable || promptUnavailableAnswer?.retryable),
                                            } : {}),
                                            streaming: false,
                                        }
                                    };
                                })()
                                : message
                        )));
                    }
                }
            }
        });
        if (sessionId !== ragSessionRef.current
            || !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
        const responseTerminalDisposition = getJaneTerminalResponseDisposition(responseData);
        if (responseData && !responseTerminalDisposition.failed) {
            streamedAnswerContinuesInBackground = streamedAnswerContinuesInBackground
                || Boolean(showBackgroundRunReceipt(responseData));
        }
        if (responseData?.chat_id && !chatIdForRequest) {
            chatIdForRequest = responseData.chat_id;
        }
        applyAcceptedRagChatTitle(responseData, chatIdForRequest);
        if (!responseData) {
            if (isInlineRerun) ragInlineRerunRef.current = null;
            setRagMessages(prev => replacementAssistantSnapshot
                ? prev.map((message) => String(message.id) === String(pendingAssistantId) ? replacementAssistantSnapshot : message)
                : prev.filter((message) => String(message.id) !== String(pendingAssistantId)));
            clearRagIncomingAnswerMessage(pendingAssistantId);
            setRagActiveRequest(null);
            return;
        }
        if (responseData.blocked_by_active_request) {
            if (isInlineRerun) ragInlineRerunRef.current = null;
            const blockedRequest = responseData.active_request || null;
            setRagActiveRequest(blockedRequest);
            setRagMessages(prev => replacementAssistantSnapshot
                ? prev.map(message => String(message.id) === String(pendingAssistantId) ? replacementAssistantSnapshot : message)
                : prev.filter(message => String(message.id) !== String(pendingAssistantId) && String(message.id) !== String(userMessage.id)));
            clearRagIncomingAnswerMessage(pendingAssistantId);
            if (blockedRequest) syncExternalRagRequestPreview(blockedRequest);
            return;
        }
        const responseRequestToken = String(responseData.request_token || '').trim();
        if (responseRequestToken) {
            streamedRequestToken = streamedRequestToken || responseRequestToken;
            setRagMessages(prev => bindOptimisticRagRequest(prev, {
                requestToken: responseRequestToken,
                localUserMessageId: userMessage.id,
                localAssistantMessageId: pendingAssistantId,
                userMessageId: responseData.user_message_id,
                assistantMessageId: responseData.assistant_message_id,
            }));
        }
        if (responseTerminalDisposition.failed) {
            const promptUnavailableAnswer = getJanePromptUnavailableAnswer(responseData);
            const failureText = getJaneRequestFailureMessage(responseData);
            const failureRunPlan = { ...(streamedRunPlan || immediateRunPlan || {}), status: 'failed' };
            const failureWorkHistory = mergeJaneStreamWorkHistory(streamedWorkHistory, { type: 'run_plan', run_plan: failureRunPlan }, streamedRequestToken);
            const failureTiming = buildJaneResponsePlanTimingMetadata(responseData, responsePlanStartedAt);
            setRagMessages(prev => prev.map((message) => (
                String(message.id) === String(pendingAssistantId)
                    ? (isInlineRerun && replacementAssistantSnapshot ? replacementAssistantSnapshot : {
                        ...message,
                        content: failureText,
                        pending: false,
                        // A failed generated explanation must not hide the
                        // existing access-filtered capability cards.
                        answer: responseData.answer?.mode === 'capability_registry_answer'
                            ? { ...responseData.answer, answer_text: failureText }
                            : { answer_text: failureText, citations: [], source_descriptions: [] },
                        sources: [],
                        files: [],
                        metadata: {
                            ...(message.metadata || {}),
                            ...failureTiming,
                            runPlan: failureRunPlan,
                            ...(failureWorkHistory.length > 0 ? { work_history: failureWorkHistory } : {}),
                            jane_error: true,
                            jane_error_code: String(responseData.error_code || responseData.reason || promptUnavailableAnswer?.error_code || promptUnavailableAnswer?.failure_kind || 'jane_request_failed'),
                            jane_error_status: Number(responseData.http_status || responseData.status || 0) || null,
                            retryable: Boolean(responseData.retryable || promptUnavailableAnswer?.retryable),
                            streaming: false,
                        },
                    })
                    : message
            )));
            if (isInlineRerun) {
                ragInlineRerunRef.current = null;
                setRagRerunErrorByMessageId(previous => ({ ...previous, [String(pendingAssistantId)]: failureText }));
            }
            clearRagIncomingAnswerMessage(pendingAssistantId);
            if (getOwnedRagRuntimeIntent(chatIdForRequest)) {
                await loadRagChat(chatIdForRequest, { silent: true, requireSnapshot: true });
                if (sessionId !== ragSessionRef.current || runGeneration !== ragRunGenerationRef.current) return;
                if (getOwnedRagRuntimeIntent(chatIdForRequest)) {
                    setRagCancelError('Jane could not verify this run’s saved status. Reopen the chat before trying another request.');
                }
            } else {
                setRagActiveRequest(null);
            }
            return;
        }
        if (responseData.run_active) {
            const activeText = sanitizeJaneStatusMessage(responseData.message) || 'I’m still working on this response.';
            setRagMessages(prev => prev.map((message) => (
                String(message.id) === String(pendingAssistantId)
                    ? {
                        ...message,
                        content: activeText,
                        pending: true,
                        metadata: {
                            ...(message.metadata || {}),
                            janeOpeningText: message.metadata?.janeOpeningText || janeOpeningText,
                            jane_agent_v2: {
                                run_id: responseData.run_id || null,
                                request_token: responseData.request_token || streamedRequestToken || '',
                                status: 'running',
                            },
                            streaming: false,
                        },
                    }
                    : message
            )));
            if (responseData.chat_id || chatIdForRequest) {
                setRagActiveRequest({
                    ...(getOwnedRagRuntimeIntent(chatIdForRequest)?.acknowledgement || {}),
                    ...(getOwnedRagRuntimeIntent(chatIdForRequest) ? { runtime: 'agent_v2' } : {}),
                    chat_id: responseData.chat_id || chatIdForRequest,
                    request_token: responseData.request_token || streamedRequestToken || '',
                    user_message_id: Number(responseData.user_message_id || 0) || null,
                    assistant_message_id: Number(responseData.assistant_message_id || 0) || null,
                    user_id: userId,
                    user_name: 'You',
                    status: 'active',
                    query_text: trimmedQuery,
                    status_message: activeText,
                });
            }
            return;
        }
        if (responseData.mode === 'workspace_navigation' && Number(responseData?.workspace_session?.case_id) > 0) {
            const workspaceNavigation = buildWorkspaceNavigationMessage(responseData, trimmedQuery);
            const workspacePlannerSteps = Array.isArray(responseData.steps) ? responseData.steps : [];
            const workspaceRouteTrace = {
                route: 'workspace_navigation',
                route_reason: workspaceNavigation.workspaceNavigation.route_reason || 'workspace_navigation',
                query_context: {
                    case_id: workspaceNavigation.caseId,
                    case_name: workspaceNavigation.caseName || null,
                    selected_sources_only: Boolean(selectedSourcesOnly),
                    reference_file_ids: (promptReferences || []).map((reference) => Number(reference?.file_id || 0)).filter((fileId) => Number.isFinite(fileId) && fileId > 0),
                },
                tools: Array.isArray(responseData.tools_used) ? responseData.tools_used : [],
                evidence: {
                    retrieval_mode: 'workspace_navigation',
                },
            };
            const workspaceSaveResponseData = {
                ...responseData,
                intelligence: {
                    ...(responseData.intelligence || {}),
                    summary: workspaceNavigation.summary,
                    answer: workspaceNavigation.answerPayload,
                    actions: Array.isArray(responseData.actions) ? responseData.actions : [],
                    session: responseData.session || workspaceNavigation.workspaceNavigation,
                    route_trace: workspaceRouteTrace,
                    plannerSteps: workspacePlannerSteps,
                    workspace_navigation: workspaceNavigation.workspaceNavigation,
                },
            };
            const workspaceAssistantMessage = {
                id: replaceAssistantMessageId || pendingAssistantId,
                role: 'assistant',
                content: workspaceNavigation.assistantText,
                answer: workspaceNavigation.answerPayload,
                sources: [],
                files: responseData?.files || [],
                metadata: {
                    ...rerunMetadata,
                    plannerSteps: workspacePlannerSteps,
                    intelligence: {
                        ...(responseData.intelligence || {}),
                        summary: workspaceNavigation.summary,
                        answer: workspaceNavigation.answerPayload,
                        actions: Array.isArray(responseData.actions) ? responseData.actions : [],
                        session: responseData.session || workspaceNavigation.workspaceNavigation,
                        route_trace: workspaceRouteTrace,
                        plannerSteps: workspacePlannerSteps,
                        workspace_navigation: workspaceNavigation.workspaceNavigation,
                    },
                    workspace_navigation: workspaceNavigation.workspaceNavigation,
                },
                created_at: assistantCreatedAt,
            };
            setWorkspaceNavigationPendingMessageIds(prev => {
                const next = new Set(prev);
                next.add(String(workspaceAssistantMessage.id));
                return next;
            });
            setRagMessages(prev => prev.map((message) => (
                String(message.id) === String(pendingAssistantId) ? workspaceAssistantMessage : message
            )));

            clearRagIncomingAnswerMessage(pendingAssistantId);
            const savedWorkspaceTurn = await saveRagTurn({
                chatId: chatIdForRequest,
                query: trimmedQuery,
                answer: workspaceNavigation.answerPayload,
                responseData: workspaceSaveResponseData,
                references: promptReferences,
                sessionId,
                requestToken: streamedRequestToken,
                assistantMessageId: workspaceAssistantMessage.id,
                runGeneration,
                selectedSourcesOnly,
                rerunMessageId,
                rerunRequestToken: options.rerunRequestToken,
            });
            if (!isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
            const savedWorkspaceMessages = normalizeRagMessages(isInlineRerun
                ? [savedWorkspaceTurn?.rerun_assistant_message].filter(Boolean)
                : savedWorkspaceTurn?.messages || []);
            const savedWorkspaceAssistant = [...savedWorkspaceMessages].reverse().find((savedMessage) => (
                savedMessage.role === 'assistant'
                && (!isInlineRerun || Number(savedMessage.id) === Number(replaceAssistantMessageId))
                && savedMessage.answer?.mode === 'workspace_navigation'
                && Number(savedMessage.answer?.workspace_navigation?.case_id || savedMessage.metadata?.workspace_navigation?.case_id || savedMessage.metadata?.intelligence?.workspace_navigation?.case_id || 0) === Number(workspaceNavigation.caseId)
            ));
            setWorkspaceNavigationPendingMessageIds(prev => {
                const next = new Set(prev);
                next.delete(String(pendingAssistantId));
                if (savedWorkspaceAssistant?.id) {
                    next.add(String(savedWorkspaceAssistant.id));
                }
                return next;
            });

            return;
        }
        if (responseData.mode === 'case_selection_required') {
            const caseMatches = Array.isArray(responseData.case_matches) ? responseData.case_matches : [];
            const matchSummary = caseMatches.length > 0
                ? caseMatches.slice(0, 4).map((match, index) => `${index + 1}. ${String(match?.case_name || match?.name || `Case ${match?.case_id || ''}`).trim()}`).join('\n')
                : 'Reply with the case ID or exact case name.';
            const answerText = `${String(responseData.message || 'Case context is required for this guided workspace action.').trim()}\n\n${matchSummary}`;
            const caseSelectionMessage = {
                id: replaceAssistantMessageId || pendingAssistantId,
                role: 'assistant',
                content: answerText,
                answer: {
                    answer_text: answerText,
                    citations: [],
                    source_descriptions: [],
                },
                sources: [],
                files: [],
                metadata: { ...rerunMetadata, workspace_case_selection_required: true, case_matches: caseMatches, original_prompt: trimmedQuery, case_selection: responseData.case_selection || null },
                created_at: assistantCreatedAt,
            };
            setRagMessages(prev => prev.map((message) => String(message.id) === String(pendingAssistantId) ? caseSelectionMessage : message));
            await saveRagTurn({
                chatId: chatIdForRequest,
                query: trimmedQuery,
                answer: caseSelectionMessage.answer,
                responseData: {
                    files: [],
                    intelligence: {
                        ...(responseData.intelligence || {}),
                        mode: 'case_selection_required',
                        case_matches: caseMatches,
                        original_prompt: trimmedQuery,
                        case_selection: responseData.case_selection || null,
                    },
                },
                references: promptReferences,
                sessionId,
                requestToken: streamedRequestToken,
                assistantMessageId: caseSelectionMessage.id,
                runGeneration,
                selectedSourcesOnly,
                rerunMessageId,
                rerunRequestToken: options.rerunRequestToken,
            });
            return;
        }
        const answer = responseData?.intelligence?.answer || null;
        const assistantText = answer?.answer_text || (
            responseData?.files?.length
                ? 'I found relevant source documents, but could not build a grounded answer from the retrieved chunks.'
                : 'I could not find matching indexed document context for that question.'
        );
        const answerPayload = answer || { answer_text: assistantText, citations: [], source_descriptions: [] };
        const responseIntelligence = responseData?.intelligence || null;
        const responseAnswerMode = String(answerPayload?.mode || responseData?.mode || '').trim().toLowerCase();
        const responseContinuesInBackground = Boolean(responseData?.run_active)
            || JANE_BACKGROUND_HANDOFF_MODES.has(responseAnswerMode)
            || streamedAnswerContinuesInBackground;
        const incomingResponseRunPlan = responseContinuesInBackground
            ? responseIntelligence?.run_plan
            : { ...(responseIntelligence?.run_plan || {}), status: 'complete', steps: responseIntelligence?.run_plan?.steps || [] };
        const responseRunPlan = mergeJaneRunPlanProgress(
            streamedRunPlan || immediateRunPlan || null,
            incomingResponseRunPlan || { status: 'complete', steps: [] }
        );
        const responseWorkHistory = mergeJaneStreamWorkHistory(streamedWorkHistory, { type: 'run_plan', run_plan: responseRunPlan }, streamedRequestToken);
        const responseIntelligenceWithRunPlan = {
            ...withJanePublicOpening(responseIntelligence, janeOpeningText, streamedPublicOpening, streamedRequestToken),
            run_plan: responseRunPlan,
            ...(responseWorkHistory.length > 0 ? { work_history: responseWorkHistory } : {}),
        };
        const responsePlannerSteps = buildRagPlannerStepsFromIntelligence(responseIntelligenceWithRunPlan);
        const responsePlanTiming = buildJaneResponsePlanTimingMetadata(responseData, responsePlanStartedAt);
        const responseIntelligenceWithPlanner = responseIntelligence
            ? { ...responseIntelligenceWithRunPlan, plannerSteps: responsePlannerSteps, response_plan_elapsed_ms: responsePlanTiming.responsePlanElapsedMs ?? responseIntelligence.response_plan_elapsed_ms ?? null, response_plan_completed_at: responsePlanTiming.responsePlanCompletedAt ?? responseIntelligence.response_plan_completed_at ?? null }
            : { ...responseIntelligenceWithRunPlan, plannerSteps: responsePlannerSteps, response_plan_elapsed_ms: responsePlanTiming.responsePlanElapsedMs ?? null, response_plan_completed_at: responsePlanTiming.responsePlanCompletedAt ?? null };
        const assistantMessage = {
            id: replaceAssistantMessageId || pendingAssistantId,
            role: 'assistant',
            content: assistantText,
            answer: answerPayload,
            sources: answerPayload.citations || [],
            files: responseData?.files || [],
            metadata: { ...rerunMetadata, janeOpeningText: responseIntelligenceWithPlanner.jane_opening_text, ...responsePlanTiming, intelligence: responseIntelligenceWithPlanner, plannerSteps: responsePlannerSteps, runPlan: responseRunPlan, ...(responseWorkHistory.length > 0 ? { work_history: responseWorkHistory } : {}) },
            created_at: assistantCreatedAt
        };

        const isLegalDraftResponse = isJaneLegalDraftPresentation({
            answer: answerPayload,
            intelligence: responseIntelligenceWithPlanner,
        });
        const shouldImmediatelyReconcileStructuredAnswer = shouldReconcileStructuredRagAnswerImmediately(
            answerPayload,
            responseIntelligenceWithPlanner
        );
        if (isLegalDraftResponse || shouldImmediatelyReconcileStructuredAnswer) {
            clearRagIncomingAnswerMessage(pendingAssistantId);
        }
        const shouldAnimateFullAnswer = !isLegalDraftResponse
            && !shouldImmediatelyReconcileStructuredAnswer
            && streamedAnswerText.trim() === '';
        if (shouldAnimateFullAnswer) {
            setRagMessages(prev => prev.map((message) => (
                String(message.id) === String(pendingAssistantId)
                    ? (() => {
                        const plannerSteps = getRagPlannerStepsFromMessage(message, responseIntelligenceWithPlanner);
                        return {
                        ...assistantMessage,
                        content: 'Building response from retrieved context…',
                        answer: null,
                        pending: true,
                        metadata: { ...(message.metadata || {}), ...assistantMessage.metadata, plannerSteps, streaming: false },
                    };
                    })()
                    : message
            )));
                    await animateRagAssistantText({
                messageId: assistantMessage.id,
                answerPayload,
                fullText: assistantText,
                files: responseData?.files || [],
                sources: answerPayload.citations || [],
                metadata: assistantMessage.metadata,
                sessionId,
                runGeneration,
            });
            if (sessionId !== ragSessionRef.current
                || !isRagRunGenerationCurrent(runGeneration, ragRunGenerationRef.current)) return;
        } else {
            setRagMessages(prev => prev.map((message) => (
                String(message.id) === String(pendingAssistantId)
                    ? (() => {
                        const plannerSteps = getRagPlannerStepsFromMessage(message, responseIntelligenceWithPlanner);
                        return { ...assistantMessage, metadata: { ...(message.metadata || {}), ...assistantMessage.metadata, plannerSteps, streaming: false } };
                    })()
                    : message
            )));
        }

        const persistenceAction = getRagTerminalPersistenceAction({
            response: responseData, runtimeIntent: ragRuntimeIntentRef.current,
            runGeneration, actorId: getJaneUserId(user), chatId: Number(chatIdForRequest || 0),
        });
        if (persistenceAction !== 'save_legacy') {
            clearRagIncomingAnswerMessage(pendingAssistantId);
            await loadRagChat(chatIdForRequest, { silent: true, requireSnapshot: true });
            if (sessionId !== ragSessionRef.current || runGeneration !== ragRunGenerationRef.current) return;
            if (getOwnedRagRuntimeIntent(chatIdForRequest)) {
                setRagCancelError('Jane’s output is displayed, but its saved terminal status could not be verified. Reopen the chat before submitting again.');
            }
            return;
        }
        await saveRagTurn({ chatId: chatIdForRequest, query: trimmedQuery, answer: answerPayload, responseData: { ...responseData, intelligence: responseIntelligenceWithPlanner }, references: promptReferences, sessionId, requestToken: streamedRequestToken, selectedSourcesOnly, rerunMessageId, rerunRequestToken: options.rerunRequestToken, assistantMessageId: assistantMessage.id || pendingAssistantId, runGeneration });
    };

    useEffect(() => {
        const pendingDockPrompt = ragPendingDockPromptSubmitRef.current;
        if (!pendingDockPrompt || pendingDockPrompt.submitted || !pendingDockPrompt.draftText) return;
        if (ragChatLoading || loading || ragActiveRequest) return;
        if (pendingDockPrompt.chatId && Number(activeRagChatId || 0) !== Number(pendingDockPrompt.chatId)) return;

        pendingDockPrompt.submitted = true;
        ragPendingDockPromptDraftRef.current = null;
        if (typeof window !== 'undefined') {
            if (pendingDockPrompt.draftKey) {
                window.sessionStorage.setItem(JANE_DOCK_PROMPT_CONSUMED_STORAGE_KEY, pendingDockPrompt.draftKey);
            }
            window.sessionStorage.removeItem(JANE_DOCK_PROMPT_DRAFT_STORAGE_KEY);
        }

        executeRagPrompt(pendingDockPrompt.draftText, [], { clearComposer: true }).catch((error) => {
            console.error('Error auto-submitting dock prompt draft:', error);
            ragPendingDockPromptSubmitRef.current = null;
        });
    }, [activeRagChatId, loading, ragActiveRequest, ragChatLoading, executeRagPrompt]);

    const handleChunkSearchSubmit = async (event) => {
        event.preventDefault();
        const trimmedQuery = chunkSearchDraft.trim();
        const submitDuringActiveRun = isRagResponseCancelable && trimmedQuery.length >= 2;
        if (ragChatFilesUploading || (isRagSendDisabled && !submitDuringActiveRun)) return;
        if (submitDuringActiveRun && (loading || requestControllerRef.current || ragActiveRequest || ragCanceling || ragCancelError)) {
            const stopped = await handleCancelRagResponse();
            if (!stopped) return;
        }
        await executeRagPrompt(trimmedQuery, [...ragReferences], { clearComposer: true, force: submitDuringActiveRun });
    };

    const getCurrentRagRunPrompt = () => {
        const draftPrompt = String(chunkSearchDraft || '').trim();
        if (draftPrompt.length >= 2) return draftPrompt;

        const activePrompt = String(ragActiveRequest?.query_text || ragActiveRequest?.prompt || '').trim();
        if (activePrompt.length >= 2) return activePrompt;

        for (let index = ragMessages.length - 1; index >= 0; index -= 1) {
            const message = ragMessages[index];
            if (message?.role === 'assistant' && (message.pending || message?.metadata?.streaming) && index > 0) {
                const promptMessage = ragMessages[index - 1];
                const pendingPrompt = String(promptMessage?.content || promptMessage?.query_text || '').trim();
                if (pendingPrompt.length >= 2) return pendingPrompt;
            }
        }

        const latestUserPrompt = [...ragMessages].reverse().find((message) => message?.role === 'user');
        return String(latestUserPrompt?.content || latestUserPrompt?.query_text || '').trim();
    };

    const handleRerouteCurrentRun = async () => {
        const prompt = getCurrentRagRunPrompt();
        setRagRunMenuOpen(false);
        if (prompt.length < 2 || ragSaving) return;
        if (loading || ragActiveRequest) {
            const stopped = await handleCancelRagResponse();
            if (!stopped) return;
        }
        await executeRagPrompt(prompt, [...ragReferences], {
            clearComposer: chunkSearchDraft.trim().length >= 2,
            force: true,
        });
    };

    const appendRagRunControlNotice = (content, metadata = {}) => {
        const answerText = String(content || '').trim();
        if (!answerText) return;
        const noticeMessage = {
            id: `run-control-${Date.now()}`,
            role: 'assistant',
            content: answerText,
            answer: {
                answer_text: answerText,
                citations: [],
                source_descriptions: [],
            },
            sources: [],
            files: [],
            metadata: {
                run_control: true,
                ...metadata,
            },
            created_at: new Date().toISOString(),
        };
        setRagMessages(prev => [...prev, noticeMessage]);
        scheduleRagThreadToBottom(3);
    };

    const appendLocalBackgroundRunTurn = (prompt, notice, metadata = {}) => {
        const promptText = String(prompt || '').trim();
        const noticeText = String(notice || '').trim();
        if (!promptText && !noticeText) return;
        const timestamp = Date.now();
        const messages = [];
        if (promptText) {
            messages.push({
                id: `run-control-user-${timestamp}`,
                role: 'user',
                content: promptText,
                query_text: promptText,
                author_name: user?.name || user?.user || '',
                user_id: userId,
                sources: [],
                files: [],
                metadata: {
                    run_control: true,
                    local_background_anchor: true,
                    ...metadata,
                },
                created_at: new Date().toISOString(),
            });
        }
        if (noticeText) {
            messages.push({
                id: `run-control-assistant-${timestamp}`,
                role: 'assistant',
                content: noticeText,
                answer: {
                    answer_text: noticeText,
                    citations: [],
                    source_descriptions: [],
                },
                sources: [],
                files: [],
                metadata: {
                    run_control: true,
                    local_background_anchor: true,
                    ...metadata,
                },
                created_at: new Date().toISOString(),
            });
        }
        setRagMessages(prev => [...prev, ...messages]);
        scheduleRagThreadToBottom(3);
    };

    const handleRunCurrentResponseInBackground = async () => {
        if (!hasJaneUserIdentity) return;
        const prompt = getCurrentRagRunPrompt();
        setRagRunMenuOpen(false);
        if (prompt.length < 2 || ragSaving || ragRunControlBackgroundBusyRef.current) return;
        ragRunControlBackgroundBusyRef.current = true;
        try {
            let chatIdForRun = activeRagChatId || null;
            if (!chatIdForRun) {
                chatIdForRun = await ensureRagChatForUpload();
            }
            const activeRequest = ragActiveRequest;
            if (loading || ragActiveRequest) {
                const stopped = await handleCancelRagResponse();
                if (!stopped) return;
            }
            const referenceSelection = getRagReferenceSelection();
            const selectedCaseId = referenceSelection?.type === 'case' && Number(referenceSelection.id) > 0
                ? Number(referenceSelection.id)
                : 0;
            const currentPageCaseReference = getCurrentPageCaseReference();
            const promptReferences = getRagReferencesForPrompt([...ragReferences], prompt);
            const referenceFileIds = Array.from(new Set(promptReferences
                .map((reference) => Number(reference?.file_id || 0))
                .filter((fileId) => Number.isFinite(fileId) && fileId > 0)));
            const response = await fetch(`${API_BASE}/rag-run-control.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'background',
                    prompt,
                    chat_id: chatIdForRun,
                    request_token: activeRequest?.request_token || '',
                    case_id: selectedCaseId > 0 ? selectedCaseId : (currentPageCaseReference?.case_id || undefined),
                    reference_file_ids: referenceFileIds,
                    selected_sources_only: Boolean(ragSelectedSourcesOnly && referenceFileIds.length > 0),
                    model_key: normalizeRagModelKey(ragModelKey),
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) {
                const unsupportedMessage = data?.unsupported
                    ? 'That task cannot continue in the background yet. Nothing else was started.'
                    : (data?.needs_case
                        ? 'Select the case first, then try running this task in the background again.'
                        : (data?.message || 'Could not queue the background run.'));
                appendRagRunControlNotice(unsupportedMessage, { error: true, run_control_response: data });
                return;
            }
            applyAcceptedRagChatTitle(data, chatIdForRun);
            const receiptRun = buildJaneBackgroundRunFromReceipt(data, chatIdForRun);
            if (receiptRun) {
                // The durable run identity is already sealed in the response;
                // show its card before waiting for chat reload or the first
                // proactive poll.
                setRagBackgroundDraftRuns(previousRuns => upsertJaneBackgroundRun(previousRuns, receiptRun));
            }
            const routeLabel = data?.detected?.capabilities?.label || 'Jane background run';
            const runId = Number(data?.run_id || 0);
            const anchorAssistantId = Number(data?.anchor_messages?.assistant_message_id || 0);
            if (anchorAssistantId > 0 && chatIdForRun) {
                await loadRagChat(chatIdForRun, { silent: true });
            } else {
                appendLocalBackgroundRunTurn(prompt, `I queued ${routeLabel.toLowerCase()} in the background${runId > 0 ? ` as run #${runId}` : ''}. You can keep chatting while I work.`, {
                    proactive_ai: {
                        run_id: runId,
                        task_id: Number(data?.task?.id || 0),
                        task_type: data?.task?.task_type || data?.detected?.capabilities?.task_type || 'background_job',
                    },
                    run_control_response: data,
                });
            }
            setChunkSearchDraft('');
            setRagReferences([]);
            await refreshBackgroundDraftRuns();
        } catch (error) {
            console.error('Error queueing background run:', error);
            appendRagRunControlNotice(error.message || 'Could not queue the background run.', { error: true });
        } finally {
            ragRunControlBackgroundBusyRef.current = false;
        }
    };

    const ensureRagChatForUpload = async (expectedSessionId = ragSessionRef.current) => {
        if (!hasJaneUserIdentity) throw new Error('Your CaseDB session could not be verified. Refresh the page and try again.');
        const currentChatId = activeRagChatIdRef.current || activeRagChatId;
        if (currentChatId) return currentChatId;

        const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
            method: 'POST',
            headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
            credentials: 'include',
            body: JSON.stringify({
                action: 'create',
                title: 'New chat',
                chat_scope: 'firm-wide',
                model_key: normalizeRagModelKey(ragModelKey),
            })
        });
        const data = await response.json();
        if (!data.success || !data.chat?.id) {
            throw new Error(data.message || 'Could not create a chat for the upload');
        }

        if (Number(expectedSessionId) === Number(ragSessionRef.current)) {
            activeRagChatIdRef.current = data.chat.id;
            setActiveRagChatId(data.chat.id);
            syncRagChatUrl(data.chat.id);
            if (data.chat?.model_key) setRagModelKey(normalizeRagModelKey(data.chat.model_key));
        }
        setRagChats(prev => sortRagChatsByUpdateTime([data.chat, ...prev.filter(chat => Number(chat.id) !== Number(data.chat.id))]));
        return data.chat.id;
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleUploadFiles = async (uploadedFiles) => {
        if (!uploadedFiles || uploadedFiles.length === 0) return;
        const uploadOperationId = ++ragUploadOperationRef.current;
        const uploadSessionId = ragSessionRef.current;
        const uploadSelection = getRagReferenceSelection();
        const uploadCaseId = uploadSelection?.type === 'case' && uploadSelection.id ? Number(uploadSelection.id) : 0;
        setRagChatFilesUploading(true);
        setRagSpeechStatus('');

        const selectedFiles = Array.from(uploadedFiles);
        let uploadChatId = activeRagChatId;
        try {
            uploadChatId = await ensureRagChatForUpload(uploadSessionId);
        } catch (error) {
            console.error('Error preparing chat upload:', error);
            selectedFiles.forEach((file, index) => {
                dispatch({ type: 'FAIL_UPLOAD', payload: { id: `${Date.now()}-${index}`, error: error.message || 'Upload failed' } });
            });
            if (ragUploadOperationRef.current === uploadOperationId) setRagChatFilesUploading(false);
            return;
        }
        const uploadContext = createRagUploadContext({
            chatId: uploadChatId,
            sessionId: uploadSessionId,
            operationId: uploadOperationId,
            caseId: uploadCaseId,
        });

        // Cap concurrency at 2 so large uploads don't exhaust the browser's
        // per-origin connection pool and starve every other request.
        const uploadQueue = selectedFiles.map((file, index) => ({ file, index }));
        const uploadOneChatFile = async ({ file, index }) => {
            const uploadId = `${Date.now()}-${index}`;
            const uploadFileName = String(file?.name || '').trim() || `upload-${uploadId}`;
            dispatch({
                type: 'START_UPLOAD',
                payload: { id: uploadId, fileName: uploadFileName, fileSize: file.size, operation: 'chat file upload' }
            });

            const formData = new FormData();
            formData.append('action', 'upload_file');
            formData.append('chat_id', uploadChatId);
            if (uploadCaseId > 0) {
                formData.append('case_id', String(uploadCaseId));
            }
            formData.append('original_file_name', uploadFileName);
            formData.append('file', file, uploadFileName);
            const nativeTablePreviewPromise = buildSpreadsheetNativePreviewFromBrowserFile(file, getFileExtension).catch((error) => {
                console.warn('Could not build local spreadsheet preview:', error);
                return null;
            });

            try {
                // Make sure a CSRF token exists; the global XHR interceptor in
                // utils/csrf.js attaches it on send(). Setting it manually here
                // too would append a duplicate header value and fail validation.
                await ensureCaseDbCsrfToken().catch(() => '');
                const data = await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE}/library-rag-chat.php?action=upload_file`);
                    xhr.withCredentials = true;
                    xhr.upload.addEventListener('progress', (event) => {
                        if (!event.lengthComputable) return;
                        const progress = Math.round((event.loaded / event.total) * 100);
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                id: uploadId,
                                progress,
                                statusMessage: progress >= 100 ? 'Finalizing upload on server...' : `${progress}%`,
                                loadedBytes: event.loaded,
                                totalBytes: event.total,
                                updatedAt: Date.now()
                            }
                        });
                    });
                    xhr.addEventListener('load', () => {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch (parseError) {
                            reject(new Error(`Upload failed (HTTP ${xhr.status})`));
                        }
                    });
                    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
                    xhr.timeout = 0; // Large chat files can take a while; no artificial cutoff.
                    xhr.send(formData);
                });
                if (!data.success || !data.file) {
                    throw new Error(data.message || 'Upload failed');
                }
                const nativeTablePreview = await nativeTablePreviewPromise;

                if (isRagUploadContextCurrent(uploadContext, {
                    activeChatId: activeRagChatIdRef.current,
                    sessionId: ragSessionRef.current,
                    operationId: ragUploadOperationRef.current,
                })) {
                    setRagReferences(prev => [
                        ...prev,
                        {
                            ...data.file,
                            id: data.file.id || `upload-${uploadId}`,
                            type: 'chat_upload',
                            label: data.file.file_name || data.file.name || data.file.label || uploadFileName,
                            ...(nativeTablePreview ? { native_table_preview: { ...nativeTablePreview, url: data.file.url || nativeTablePreview.url } } : {}),
                            note: 'Uploaded to this chat in S3.'
                        }
                    ]);
                }
                if (data.chat) {
                    setRagChats(prev => sortRagChatsByUpdateTime([data.chat, ...prev.filter(chat => Number(chat.id) !== Number(data.chat.id))]));
                }
                dispatch({ type: 'COMPLETE_UPLOAD', payload: { id: uploadId } });
            } catch (error) {
                console.error('Error uploading chat file:', error);
                dispatch({ type: 'FAIL_UPLOAD', payload: { id: uploadId, error: error.message || 'Upload failed' } });
            }
        };

        const uploadWorker = async () => {
            while (uploadQueue.length > 0) {
                const nextUpload = uploadQueue.shift();
                if (nextUpload) await uploadOneChatFile(nextUpload);
            }
        };
        await Promise.all(Array.from({ length: Math.min(2, uploadQueue.length) }, uploadWorker));
        if (isRagUploadContextCurrent(uploadContext, {
            activeChatId: activeRagChatIdRef.current,
            sessionId: ragSessionRef.current,
            operationId: ragUploadOperationRef.current,
        })) {
            setRagChatFilesUploading(false);
        }
    };

    const handleFileInputChange = (event) => {
        const selectedFiles = event.target.files;
        if (selectedFiles && selectedFiles.length > 0) handleUploadFiles(selectedFiles);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRagVoiceToggle = () => {
        if (!ragSpeechSupported) {
            setRagSpeechStatus('Voice dictation is not supported in this browser.');
            return;
        }

        if (ragListening && ragSpeechRecognitionRef.current) {
            ragSpeechRecognitionRef.current.stop();
            return;
        }

        const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new Recognition();
        ragSpeechRecognitionRef.current = recognition;
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            setRagListening(true);
            setRagSpeechStatus('Listening…');
        };
        recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
                .slice(event.resultIndex)
                .map(result => result?.[0]?.transcript || '')
                .join(' ')
                .trim();
            if (!transcript) return;
            setChunkSearchDraft(prev => `${prev}${prev.trim() ? ' ' : ''}${transcript}`);
        };
        recognition.onerror = (event) => {
            setRagSpeechStatus(event?.error === 'not-allowed' ? 'Microphone permission was denied.' : 'Voice dictation stopped.');
        };
        recognition.onend = () => {
            setRagListening(false);
            ragSpeechRecognitionRef.current = null;
            setRagSpeechStatus(prev => prev === 'Listening…' ? '' : prev);
        };

        try {
            recognition.start();
        } catch (error) {
            setRagListening(false);
            setRagSpeechStatus(error.message || 'Could not start voice dictation.');
        }
    };

    const handleImproveRagPrompt = async () => {
        if (!hasJaneUserIdentity) return;
        const draftPrompt = String(chunkSearchDraft || '').trim();
        if (draftPrompt.length < 2 || ragPromptImproving) return;

        setRagPromptImproving(true);
        setRagPromptImproveStatus('Improving prompt…');
        try {
            const promptReferences = getRagReferencesForPrompt(ragReferences, draftPrompt);
            const response = await fetch(`${API_BASE}/jane-improve-prompt.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    prompt: draftPrompt,
                    references: promptReferences.map(reference => ({
                        id: reference.id || reference.file_id || reference.case_id || '',
                        type: reference.type || reference.source_type || '',
                        label: getRagReferenceDisplayLabel(reference),
                        file_name: reference.file_name || reference.name || '',
                        case_name: reference.case_name || '',
                    })),
                    mode: janeRunMode,
                    selected_sources_only: Boolean(ragSelectedSourcesOnly && promptReferences.length > 0),
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Could not improve the prompt.');
            }
            const improvedPrompt = String(data.improved_prompt || '').trim();
            if (!improvedPrompt) {
                throw new Error('The improved prompt was empty.');
            }
            setChunkSearchDraft(improvedPrompt);
            setRagPromptImproveStatus('Prompt improved.');
            window.setTimeout(() => setRagPromptImproveStatus(''), 1800);
            window.requestAnimationFrame(() => inputRef.current?.focus());
        } catch (error) {
            console.error('Error improving Jane prompt:', error);
            setRagPromptImproveStatus(error.message || 'Could not improve the prompt.');
        } finally {
            setRagPromptImproving(false);
        }
    };

    const handleChunkResultOpen = (item, matchedChunk = null) => {
        const fileId = item?.id || item?.file_id;
        if (!fileId) return;
        const shouldUseChunkTarget = !isRagGeneratedOutputSource(item) && hasRagSourceChunkTarget({ ...item, matchedChunk });
        const nextPreview = {
            fileId,
            fileName: item?.name || item?.file_name || `File ${fileId}`,
            caseId: item?.case_id || null,
            caseName: item?.case_name || '',
            storageKey: item?.storage_key || item?.key || item?.file_path || item?.path || '',
            pageLabel: shouldUseChunkTarget && (matchedChunk?.page_start || matchedChunk?.page_end)
                ? `Page ${matchedChunk.page_start || matchedChunk.page_end}`
                : '',
            chunkIndex: shouldUseChunkTarget && Number.isFinite(Number(matchedChunk?.chunk_index)) ? Number(matchedChunk.chunk_index) : null,
            registryId: shouldUseChunkTarget ? (matchedChunk?.registry_id || item?.registry_id || null) : null,
            matchedChunk: shouldUseChunkTarget ? matchedChunk : null,
            sourceType: item?.source_type || item?.sourceType || matchedChunk?.source_type || '',
            chunkSource: item?.chunk_source || item?.chunkSource || matchedChunk?.chunk_source || '',
        };
        setShowRagSourceHistory(false);
        setRagCalendarPanelMessageId(null);
        setRagDraftPreviewMessageId(null);
        setTimelineEvidencePreview(null);
        setChunkPreview(null);
        window.requestAnimationFrame(() => setChunkPreview(nextPreview));
    };

    const handleCitationOpen = (citation, options = {}) => {
        const outlookReviewTarget = options?.outlookTarget || getOutlookCitationReviewTarget(citation);
        if (outlookReviewTarget) {
            setShowRagSourceHistory(false);
            setRagCalendarPanelMessageId(null);
            setRagDraftPreviewMessageId(null);
            setRagResponseDetailsMessageId(null);
            setTimelineEvidencePreview(null);
            setChunkPreview(null);
            setOutlookCitationReview({
                ...outlookReviewTarget,
                initialView: options?.outlookView === 'pdf' ? 'pdf' : (outlookReviewTarget.initialView || 'thread'),
            });
            return;
        }
        setOutlookCitationReview(null);
        const caseProfileUrl = getRagCitationHref(citation);
        if (isRagGeneratedOutputSource(citation) && caseProfileUrl) {
            openSafeRagWindow(caseProfileUrl);
            return;
        }
        const isTimelineEvidenceCitation = String(citation?.chunk_source || citation?.source_type || '').toLowerCase().includes('sourced_link_timeline');
        if (String(citation?.source_type || '').toLowerCase() === 'report' && caseProfileUrl) {
            openSafeRagWindow(caseProfileUrl);
            return;
        }
        if (!isTimelineEvidenceCitation && isCaseSummaryCitation(citation) && caseProfileUrl) {
            openSafeRagWindow(caseProfileUrl);
            return;
        }

        const fileId = citation?.file_id;
        if (!fileId) {
            setShowRagSourceHistory(false);
            setRagCalendarPanelMessageId(null);
            setChunkPreview(null);
            setRagDraftPreviewMessageId(null);
            setTimelineEvidencePreview(buildTimelineEvidencePreview(citation));
            return;
        }
        const shouldUseChunkTarget = !isRagGeneratedOutputSource(citation) && hasRagSourceChunkTarget(citation);
        const physicalPageTarget = shouldUseChunkTarget ? null : getRagSourcePhysicalPageTarget(citation);
        const chunk = {
            registry_id: citation.registry_id,
            chunk_index: citation.chunk_index,
            page_start: citation.page_start,
            page_end: citation.page_end,
            bbox_json: citation.bbox_json,
            chunk_source: citation.chunk_source,
            source_type: citation.source_type,
        };
        const item = {
            id: fileId,
            file_id: fileId,
            registry_id: citation.registry_id,
            name: citation.file_name || `File ${fileId}`,
            case_name: citation.case_name || '',
            case_id: citation.case_id || null,
            storage_key: citation.storage_key || citation.key || citation.file_path || '',
            source_type: citation.source_type || '',
            chunk_source: citation.chunk_source || '',
        };
        const nextPreview = {
            fileId,
            fileName: item.name,
            caseId: item.case_id,
            caseName: item.case_name,
            storageKey: item.storage_key,
            pageLabel: shouldUseChunkTarget ? (citation.label || '') : (physicalPageTarget
                ? (physicalPageTarget.page_start === physicalPageTarget.page_end
                    ? `Page ${physicalPageTarget.page_start}`
                    : `Pages ${physicalPageTarget.page_start}-${physicalPageTarget.page_end}`)
                : ''),
            chunkIndex: shouldUseChunkTarget && Number.isFinite(Number(citation.chunk_index)) ? Number(citation.chunk_index) : null,
            registryId: shouldUseChunkTarget ? (citation.registry_id || null) : null,
            matchedChunk: shouldUseChunkTarget ? chunk : physicalPageTarget,
            sourceType: citation.source_type || '',
            chunkSource: citation.chunk_source || '',
        };
        setShowRagSourceHistory(false);
        setRagCalendarPanelMessageId(null);
        setRagDraftPreviewMessageId(null);
        setTimelineEvidencePreview(null);
        setChunkPreview(null);
        window.requestAnimationFrame(() => setChunkPreview(nextPreview));
    };

    const handleOpenLegalDraftPreview = (message, actionDetails = null) => {
        const details = actionDetails || getConfirmableActionDetails(message);
        if (!getLegalDraftPreviewPayload(message, details)) return;
        const generatedDraftPreview = buildGeneratedDraftDocxPreview(message, details);
        setShowRagSourceHistory(false);
        setTimelineEvidencePreview(null);
        setRagResponseDetailsMessageId(null);
        setRagCalendarPanelMessageId(null);
        if (generatedDraftPreview) {
            setRagDraftPreviewMessageId(null);
            setChunkPreview(null);
            window.requestAnimationFrame(() => setChunkPreview(generatedDraftPreview));
            return;
        }
        setChunkPreview(null);
        setRagDraftPreviewMessageId(message.id);
    };

    const handlePromptReferenceOpen = async (event, reference) => {
        const href = getRagCitationHref(reference);
        const isRawS3Reference = /https?:\/\/(?:[^/]+\.)?s3[.-][^/]+\/|https?:\/\/casedb\.s3\.amazonaws\.com\//i.test(String(href || reference?.url || ''));
        const hasFileRoute = Number(getSourceFileId(reference) || reference?.file_id || 0) > 0;
        if (!isRawS3Reference || hasFileRoute) return;

        event.preventDefault();
        const rawUrl = String(reference?.url || href || '').trim();
        try {
            const response = await fetch(`${API_BASE}/rag/resolve-file-reference.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({ url: rawUrl }),
            });
            const data = await response.json().catch(() => ({}));
            const resolvedFileId = Number(data?.file_id || 0);
            if (!response.ok || !data?.success || !resolvedFileId) {
                throw new Error(data?.message || 'No matching CaseDB file found.');
            }
            openSafeRagWindow(getRagCitationHref({ file_id: resolvedFileId }));
        } catch (error) {
            setRagSpeechStatus('Jane could not verify access to that file. Refresh the page and try again.');
        }
    };

    const handleNativeTablePreviewOpen = (preview) => {
        const normalizedPreview = normalizePrivateFinancialArtifactUrl({
            artifact_kind: preview?.artifact_kind || (String(preview?.url || '').startsWith('/financial-report-download.php') ? 'financial_period_workbook' : ''),
            url: preview?.url,
        }, API_BASE);
        openSafeRagWindow(normalizedPreview?.url);
    };

    const handleRetryNativeTablePreview = (preview) => {
        const requestKey = String(preview?.requestKey || '').trim();
        if (!requestKey) return;
        ragTablePreviewRequestsRef.current.delete(requestKey);
        setRagTablePreviewCache(prev => {
            if (!prev[requestKey]) return prev;
            const next = { ...prev };
            delete next[requestKey];
            return next;
        });
    };

    const handleUseCapabilityExample = (example) => {
        const nextPrompt = String(example || '').trim();
        if (!nextPrompt) return;
        setChunkSearchDraft(nextPrompt);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const getActiveRagPromptReferences = () => getRagReferencesForPrompt(ragReferences, chunkSearchDraft);

    const handleUseJaneAgent = async (agent) => {
        if (!hasJaneUserIdentity) return;
        if (!agent?.id) return;
        setRagSpeechStatus(`Opening ${agent.title || 'Jane agent'}…`);
        try {
            const response = await fetch(`${API_BASE}/library-rag-chat.php`, {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'seed_agent_chat',
                    agent: {
                        id: agent.id,
                        title: agent.title,
                        type: agent.type,
                        tool: agent.tool || agent.category,
                        route: agent.route || '',
                        route_goal: agent.route_goal || agent.routeGoal || '',
                        category: agent.category,
                        description: agent.displayDescription || agent.description,
                        sourceRequired: agent.sourceRequired || agent.source_required || '',
                        prompt: agent.prompt || '',
                    },
                    chat_scope: 'firm-wide',
                    model_key: normalizeRagModelKey(ragModelKey),
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to open agent chat.');
            }
            const nextChatId = data.chat?.id || data.chat_id;
            setShowJaneAgentLibrary(false);
            setRagMobileNavOpen(false);
            setShowRagReferenceMenu(false);
            setShowRagDocumentPicker(false);
            setRagPreflightExpanded(false);
            setChunkSearchDraft('');
            setRagReferences([]);
            setRagReferenceContext(null);
            if (nextChatId) {
                await loadRagChats();
                await loadRagChat(nextChatId);
                setRagSpeechStatus('');
                window.setTimeout(() => inputRef.current?.focus(), 0);
            }
        } catch (error) {
            console.error('Error opening Jane agent chat:', error);
            setRagSpeechStatus(error.message || 'Failed to open agent chat.');
        }
    };

    const handleSaveCurrentPromptAsAgent = () => {
        const prompt = String(chunkSearchDraft || '').trim();
        if (prompt.length < 8) {
            setRagSpeechStatus('Write a prompt before saving it as an agent.');
            return;
        }
        const titleSource = prompt.split('\n').find(Boolean) || 'Saved Jane prompt';
        const title = titleSource.replace(/[.!?]+$/g, '').slice(0, 72);
        const nextAgent = buildSavedJaneAgent({ title, prompt });
        setSavedJaneAgents(prev => {
            const next = [nextAgent, ...prev].slice(0, 40);
            saveSavedJaneAgents(next);
            return next;
        });
        setJaneAgentLibraryCategory('Prompts');
        setShowJaneAgentLibrary(true);
        syncJaneAgentLibraryUrl();
        setRagMobileNavOpen(false);
        setRagSpeechStatus('Saved prompt to your Jane Agent Library.');
    };

    const syncJaneAgentBookmarks = async (agentIds) => {
        if (!hasJaneUserIdentity) {
            setRagSpeechStatus('Your CaseDB session could not be verified. Refresh the page before saving bookmarks.');
            return;
        }
        const normalizedIds = Array.from(new Set((Array.isArray(agentIds) ? agentIds : []).map(String).filter(Boolean)));
        savePinnedJaneAgentIds(normalizedIds);
        try {
            const response = await fetch(`${API_BASE}/jane-agent-library.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({ bookmarked_agent_ids: normalizedIds }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Could not save agent bookmark.');
            }
            if (Array.isArray(data.bookmarked_agent_ids)) {
                const savedIds = data.bookmarked_agent_ids.map(String).filter(Boolean);
                savePinnedJaneAgentIds(savedIds);
                setPinnedJaneAgentIds(savedIds);
            }
        } catch (error) {
            console.error('Error saving Jane Agent Library bookmarks:', error);
            setRagSpeechStatus(error.message || 'Could not save agent bookmark.');
        }
    };

    const handleToggleJaneAgentPin = (agentId) => {
        const normalizedId = String(agentId || '');
        if (!normalizedId) return;
        setPinnedJaneAgentIds(prev => {
            const existing = new Set(prev.map(String));
            if (existing.has(normalizedId)) {
                existing.delete(normalizedId);
            } else {
                existing.add(normalizedId);
            }
            const next = Array.from(existing);
            savePinnedJaneAgentIds(next);
            syncJaneAgentBookmarks(next);
            return next;
        });
    };

    const renderJaneAgentLibrary = () => {
        if (!showJaneAgentLibrary) return null;
        const references = getActiveRagPromptReferences();
        const libraryAgents = backendJaneAgents.length > 0 ? backendJaneAgents : JANE_AGENT_LIBRARY;
        const normalizedSavedAgents = savedJaneAgents.map(agent => ({
            ...agent,
            type: 'Prompt',
            category: 'Prompts',
            tool: agent.tool || 'Saved prompt',
            creator: agent.creator || 'You',
        }));
        const agents = [...normalizedSavedAgents, ...libraryAgents].map(agent => ({
            ...agent,
            type: isJaneWorkspaceAgent(agent) ? 'Workspace Agent' : 'Prompt',
            tool: agent.tool || agent.category || agent.title || 'Jane',
            creator: agent.creator || (agent.saved ? 'You' : 'Jane'),
            displayDescription: getJaneAgentDisplayDescription(agent),
        }));
        const pinnedSet = new Set(pinnedJaneAgentIds.map(String));
        const pinnedAgents = agents.filter(agent => pinnedSet.has(String(agent.id)));
        const workspaceAgents = agents.filter(agent => agent.type === 'Workspace Agent');
        const promptAgents = agents.filter(agent => agent.type === 'Prompt');
        const visibleAgents = janeAgentLibraryCategory === 'Bookmarks'
            ? pinnedAgents
            : janeAgentLibraryCategory === 'Workspace Agents'
                ? workspaceAgents
                : janeAgentLibraryCategory === 'Prompts'
                    ? promptAgents
                    : agents;
        return (
            <section className="library-rag-agent-library" aria-label="Jane Agent Library">
                <div className="library-rag-agent-library-toolbar" role="tablist" aria-label="Agent Library views">
                    <span>{agents.length} total agents</span>
                    <span>{pinnedAgents.length} bookmarked</span>
                    <span>{workspaceAgents.length} workspace agents</span>
                    <span>{promptAgents.length} prompts</span>
                    {/*<button type="button" className="library-rag-agent-library-save" onClick={handleSaveCurrentPromptAsAgent}>
                        <Save size={14} /> Save current prompt
                    </button>*/}
                </div>
                <div className="library-rag-agent-library-content">
                    {janeAgentLibraryError ? (
                        <div className="library-rag-agent-library-notice">{janeAgentLibraryError}</div>
                    ) : null}
                    <div className="library-rag-agent-library-table-wrap">
                        <table className="library-rag-agent-library-table">
                            <thead>
                                <tr>
                                    <th aria-label="Bookmark"></th>
                                    <th>Agent</th>
                                    <th>Type</th>
                                    <th>Tool</th>
                                    <th>Creator</th>
                                    <th>Use agent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleAgents.map((agent) => {
                                const ready = isJaneAgentSourceReady(agent, references);
                                const requirement = getJaneAgentSourceRequirementLabel(agent.sourceRequired);
                                const bookmarked = pinnedSet.has(String(agent.id));
                                const typeMeta = getJaneAgentLibraryTypeMeta(agent);
                                const TypeIcon = typeMeta.icon;
                                const UseIcon = typeMeta.icon;
                                return (
                                    <tr key={agent.id} className={ready ? '' : 'needs-source'}>
                                        <td>
                                            <button type="button" className={`library-rag-agent-library-bookmark${bookmarked ? ' bookmarked' : ''}`} onClick={() => handleToggleJaneAgentPin(agent.id)} title={bookmarked ? 'Remove bookmark' : 'Bookmark agent'} aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark agent'}>
                                                <Bookmark size={15} />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="library-rag-agent-library-agent-cell">
                                                <div className="library-rag-agent-library-agent-title-row">
                                                    <span>{agent.title}</span>
                                                </div>
                                                <span title={agent.displayDescription}>{agent.displayDescription}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="library-rag-agent-library-type-pill">
                                                <TypeIcon size={15} />
                                                <span>{typeMeta.label}</span>
                                            </span>
                                        </td>
                                        <td>{agent.tool}</td>
                                        <td>{agent.creator}</td>
                                        <td>
                                            <div className="library-rag-agent-library-actions">
                                                <button className='agent-use-btn' type="button" onClick={() => handleUseJaneAgent(agent)}>
                                                    <span>Use</span>
                                                </button>
                                                <span className="library-rag-agent-library-info" title={ready ? 'Ready to use.' : requirement} aria-label={ready ? 'Ready to use.' : requirement}>
                                                    <Info size={17} />
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                    {visibleAgents.length === 0 ? (
                        <div className="library-rag-agent-library-empty">
                            <strong>No agents here yet.</strong>
                            <span>{janeAgentLibraryCategory === 'Bookmarks' ? 'Bookmark agents from All to keep them here.' : 'Save a prompt from the composer or choose another view.'}</span>
                        </div>
                    ) : null}
                </div>
            </section>
        );
    };

    const handleOpenJaneAgentLibrary = () => {
        setShowJaneAgentLibrary(true);
        setShowRagReferenceMenu(false);
        setShowRagDocumentPicker(false);
        setShowRagSourceHistory(false);
        setChunkPreview(null);
        setTimelineEvidencePreview(null);
        setRagCalendarPanelMessageId(null);
        setRagDraftPreviewMessageId(null);
        setRagResponseDetailsMessageId(null);
        setRagPlannerPreviewClosed(true);
        setRagMobileNavOpen(false);
        syncJaneAgentLibraryUrl();
    };

    const renderChunkSectionContent = (item, matchedChunk, contextChunk) => {
        const text = getPlainChunkText(matchedChunk, contextChunk);
        if (shouldRenderSpreadsheetChunk(item, text, getFileExtension)) return renderSpreadsheetChunkTable(text);

        if (contextChunk?.is_match && matchedChunk?.highlighted_html) {
            return <div className="library-chunk-section-text" dangerouslySetInnerHTML={{ __html: sanitizeRagHighlightedHtml(matchedChunk.highlighted_html) }} />;
        }

        return <ChunkContextText contextChunk={contextChunk} />;
    };

    const hasLiteralSourceChunks = (item) => {
        if (!item || item.type === 'case_profile' || item.chunk_source === 'case_summary') return false;
        if (isRagGeneratedOutputSource(item)) return false;
        const registryId = Number(item.registry_id);
        if (!Number.isFinite(registryId) || registryId <= 0) return false;

        const matchedChunks = Array.isArray(item.matched_chunks) ? item.matched_chunks : [];
        return matchedChunks.some((matchedChunk) => {
            if (!matchedChunk || matchedChunk.chunk_source === 'case_summary') return false;
            const matchedRegistryId = Number(matchedChunk.registry_id || item.registry_id);
            const chunkIndex = Number(matchedChunk.chunk_index);
            return Number.isFinite(matchedRegistryId) && matchedRegistryId > 0 && Number.isFinite(chunkIndex) && chunkIndex >= 0;
        });
    };

    const renderSourceChunkTray = (items, messageId = 'latest', initiallyOpen = false) => {
        const chunkItems = Array.isArray(items) ? items.filter(hasLiteralSourceChunks) : [];
        if (chunkItems.length === 0) return null;

        return (
            <details className="library-rag-source-tray" open={initiallyOpen}>
                <summary>
                    <ChevronDown size={15} className="library-rag-source-tray-chevron" aria-hidden="true" />
                    <span>Source chunks for this answer ({formatValue(chunkItems.length)})</span>
                </summary>
                <div className="library-chunk-results">
                    {chunkItems.map((item) => {
                        const matchedChunks = Array.isArray(item?.matched_chunks) && item.matched_chunks.length > 0 ? item.matched_chunks : [];
                        const primaryMatch = matchedChunks[0] || null;

                        return (
                            <article key={`chunk-${messageId}-${item.registry_id || item.key || item.id}`} className="library-chunk-card" onClick={() => handleChunkResultOpen(item, primaryMatch)}>
                                <div className="library-chunk-card-header">
                                    <div className="library-chunk-card-topline">
                                        <span className="library-chunk-case-name">{item.case_name || 'Library document'}</span>
                                        <span className="library-chunk-date">{formatDate(item.lastModified || item.last_modified)}</span>
                                    </div>
                                    <h4 className="library-chunk-document-name">{item.name}</h4>
                                    <div className="library-chunk-card-meta">
                                        <span className="library-chunk-meta-pill">{item.first_primary_name || 'No first primary'}</span>
                                        {item.case_type_name && <span className="library-chunk-meta-pill">{item.case_type_name}</span>}
                                    </div>
                                </div>
                                <div className="library-chunk-card-body">
                                    {matchedChunks.map((matchedChunk, matchIndex) => {
                                        const contextChunks = Array.isArray(matchedChunk?.context_chunks) && matchedChunk.context_chunks.length > 0
                                            ? matchedChunk.context_chunks
                                            : [{
                                                chunk_index: matchedChunk?.chunk_index,
                                                chunk_text: matchedChunk?.grouped_text || matchedChunk?.text || matchedChunk?.snippet || '',
                                                is_match: true,
                                            }];

                                        return (
                                            <div
                                                key={`match-${messageId}-${item.registry_id || item.id}-${matchedChunk?.chunk_index ?? matchIndex}`}
                                                className="library-chunk-match-group"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleChunkResultOpen(item, matchedChunk);
                                                }}
                                            >
                                                {matchedChunks.length > 1 && (
                                                    <div className="library-chunk-match-label">
                                                        Match {matchIndex + 1}{Number.isFinite(Number(matchedChunk?.chunk_index)) ? ` · Chunk ${Number(matchedChunk.chunk_index) + 1}` : ''}
                                                    </div>
                                                )}
                                                {contextChunks.map((contextChunk, contextIndex) => (
                                                    <div
                                                        key={`context-${messageId}-${item.registry_id || item.id}-${matchedChunk?.chunk_index ?? matchIndex}-${contextChunk?.chunk_index ?? contextIndex}`}
                                                        className={`library-chunk-section${contextChunk?.is_match ? ' is-match' : ''}`}
                                                    >
                                                        {renderChunkSectionContent(item, matchedChunk, contextChunk)}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </details>
        );
    };

    const filteredRagChats = sortRagChatsByUpdateTime(ragChats).filter((chat) => (
        (cleanRagChatTitle(chat.title) || '').toLowerCase().includes(ragChatSearch.trim().toLowerCase())
    ));
    const activeRagChat = ragChats.find((chat) => Number(chat.id) === Number(activeRagChatId)) || null;
    const activeRagChatTitle = cleanRagChatTitle(activeRagChat?.title) || (activeRagChatId ? 'Untitled chat' : 'New chat');
    const activeRagChatCanEdit = !!activeRagChat && !activeRagChat.is_shared;
    const visibleRagBackgroundDraftRuns = filterBackgroundDraftRunsForMessages(ragBackgroundDraftRuns, ragMessages, activeRagChatId);
    const visibleRagReminderRuns = visibleRagBackgroundDraftRuns.filter(run => run.event_type === 'chat_reminder' || run.task?.task_type === 'chat_reminder');
    const backgroundWorkRuns = visibleRagBackgroundDraftRuns.filter(run => !visibleRagReminderRuns.includes(run));
    const unanchoredBackgroundRuns = backgroundWorkRuns.filter(run => !ragMessages.some(message => getBackgroundDraftRunIdForMessage(message) === Number(run.id)));
    const getInlineBackgroundRun = (message) => {
        if (message?.role !== 'assistant') return null;
        const runId = getBackgroundDraftRunIdForMessage(message);
        const polledRun = backgroundWorkRuns.find(run => Number(run.id) === runId);
        if (polledRun) return polledRun;
        const history = message?.metadata?.work_history || message?.metadata?.intelligence?.work_history;
        const receiptRun = buildJaneBackgroundRunFromReceipt({ answer: message.answer, intelligence: message?.metadata?.intelligence }, activeRagChatId);
        if (!receiptRun && !Array.isArray(history)) return buildJaneForegroundWorkRun(message);
        const plan = message?.metadata?.runPlan || message?.metadata?.intelligence?.run_plan;
        const status = String(message?.metadata?.backgroundRunStatus || plan?.status || receiptRun?.status || 'queued');
        return {
            ...(receiptRun || {}),
            id: runId || receiptRun?.id || `foreground-${message.id}`,
            status: status === 'complete' ? 'completed' : status,
            started_at: message?.metadata?.responsePlanStartedAt ? new Date(message.metadata.responsePlanStartedAt).toISOString() : null,
            finished_at: message?.metadata?.responsePlanCompletedAt,
            metadata: { ...(receiptRun?.metadata || {}), run_plan: plan, work_history: history },
        };
    };
    const renderInlineBackgroundRun = (run, message = null) => {
        const busy = ragBackgroundDraftBusyRunIds.has(getBackgroundDraftRunBusyKey(run.id));
        const taskType = String(run.task?.task_type || run.event_type || '').trim();
        const status = getJaneBackgroundRunStatus(run);
        const error = String(ragBackgroundDraftErrorByRunId[String(run.id)] || '').trim();
        const generatedFile = run.metadata?.generated_file;
        const fileUrl = generatedFile ? getSafeRagNavigationUrl(normalizePrivateFinancialArtifactUrl(generatedFile, API_BASE)?.url) : '';
        const resultUrl = getSafeRagNavigationUrl(run.metadata?.result_url);
        const showSave = status === 'awaiting_permission' && taskType !== 'case_timeline_build';
        const providerRecovery = getJaneProviderRecoveryAction(run);
        const actions = showSave || providerRecovery || fileUrl || resultUrl || error ? (
            <div className="library-rag-work-actions">
                {error && <span role="alert">{error}</span>}
                {providerRecovery && <JaneProviderRecoveryAction run={run} busy={busy} onContinue={handleContinueBackgroundRunWithNextProvider} />}
                {showSave && <button type="button" className="action alt" onClick={() => handleSaveBackgroundDraftRun(run)} disabled={busy}>{busy ? 'Saving...' : 'Save draft'}</button>}
                {status === 'completed' && fileUrl && <button type="button" className="action alt" onClick={() => openSafeRagWindow(fileUrl)}>Open</button>}
                {taskType === 'case_trial_analysis' && resultUrl && <button type="button" className="action alt" onClick={() => window.location.assign(resultUrl)}>Open</button>}
            </div>
        ) : null;
        return <JaneBackgroundWorkHistory key={getJaneBackgroundRunIdentity(run) || String(run.id)} run={run} message={message} nowMs={janeElapsedNow} actions={actions} />;
    };
    const ragMobileActionChat = ragChats.find((chat) => Number(chat.id) === Number(ragMobileActionChatId)) || null;
    const ragMobileActionPreviewMessages = ragMobileActionChat ? (ragMobilePreviewMessagesByChatId[String(ragMobileActionChat.id)] || []) : [];
    const ragMobileActionPreviewLoading = ragMobileActionChat && Number(ragMobilePreviewLoadingChatId) === Number(ragMobileActionChat.id);
    const ragSourceHistory = buildRagSourceHistory(ragMessages, { chatId: activeRagChatId });
    let activeRagPendingMessageIndex = -1;
    for (let index = ragMessages.length - 1; index >= 0; index -= 1) {
        const message = ragMessages[index];
        if (message?.role === 'assistant' && (message?.pending || message?.metadata?.streaming)) {
            activeRagPendingMessageIndex = index;
            break;
        }
    }
    const activeRagPendingMessage = activeRagPendingMessageIndex >= 0 ? ragMessages[activeRagPendingMessageIndex] : null;
    const activeRagPendingPromptMessage = activeRagPendingMessageIndex > 0 ? ragMessages[activeRagPendingMessageIndex - 1] : null;
    const activeRagPendingPlannerSteps = getRagPlannerStepsFromMessage(activeRagPendingMessage);
    const normalizedActiveRagPendingPlannerSteps = normalizeRagPlannerSteps(activeRagPendingPlannerSteps);
    const activeRagPlannerStatus = sanitizeJaneStatusMessage([...normalizedActiveRagPendingPlannerSteps].reverse().find(step => String(step?.message || '').trim())?.message || '');
    const activeRagPendingPrompt = String(activeRagPendingPromptMessage?.content || '').trim();
    const activeRagPendingStatus = sanitizeJaneStatusMessage(String(activeRagPlannerStatus || (activeRagPendingMessage?.metadata?.streaming ? 'Streaming answer…' : activeRagPendingMessage?.content || '')).trim());
    const activeRagPendingPlannerItemCount = countRagPlannerItems(activeRagPendingPlannerSteps);
    const activeRagThoughtPlannerSteps = activeRagPendingPlannerItemCount > 0
        ? activeRagPendingPlannerSteps
        : (activeRagPendingStatus ? [{ type: 'status', message: activeRagPendingStatus }] : []);
    const activeRagPendingPromptReferences = activeRagPendingPromptMessage
        ? getRagReferencesForPrompt(Array.isArray(activeRagPendingPromptMessage.references) ? activeRagPendingPromptMessage.references : [], activeRagPendingPrompt)
        : [];
    const activeRagContextParts = [
        `${getJaneRunModeLabel(activeRagPendingMessage?.metadata?.jane_run_mode || janeRunMode)} mode`,
        ragSelectedSourcesOnly ? 'Selected sources only' : 'Firm-wide context',
        ragUsePriorChat ? 'Prior chat on' : 'Prior chat off',
        ragUsePageContext ? 'Page context on' : 'Page context off',
    ].filter(Boolean);
    const activeRequestOwnerLabel = Number(ragActiveRequest?.user_id) === Number(userId)
        ? 'Your other tab'
        : (ragActiveRequest?.user_name || 'Another user');
    const canCancelOwnRagRequest = !!ragActiveRequest && Number(ragActiveRequest.user_id) === Number(userId);
    const isReservedRagRequest = ragActiveRequest?.status === 'reserved';
    const isLocalActiveRequest = !!ragActiveRequest && loading && Number(ragActiveRequest.user_id) === Number(userId);
    const isChatRequestBlocked = !!ragActiveRequest && !isLocalActiveRequest;
    const activeRagThought = isChatRequestBlocked ? {
        tone: 'locked',
        label: isReservedRagRequest ? 'Waiting to start' : 'I’m working',
        title: isReservedRagRequest ? 'A request is reserved but has not started' : Number(ragActiveRequest?.user_id) === Number(userId)
            ? 'I’m already responding in another tab'
            : 'I’m already responding in this chat',
        status: sanitizeJaneStatusMessage(ragActiveRequest?.status_message) || 'You can wait here; this chat will update when the response finishes.',
        prompt: String(ragActiveRequest?.query_text || '').trim(),
        plannerSteps: [],
        runPlan: null,
        badgeLabel: activeRequestOwnerLabel,
        contextLabel: activeRagContextParts.join(' · '),
    } : activeRagPendingMessage ? {
        tone: 'thinking',
        label: 'Active thought',
        title: activeRagPendingMessage?.metadata?.streaming ? 'I’m streaming the answer' : 'I’m working through this prompt',
        status: activeRagPendingStatus || 'Working…',
        prompt: activeRagPendingPrompt,
        plannerSteps: activeRagThoughtPlannerSteps,
        runPlan: activeRagPendingMessage?.metadata?.streaming_trace_disabled ? null : getJaneRunPlanFromMessage(activeRagPendingMessage, null, { liveTraceOnly: true }),
        badgeLabel: activeRagPendingPlannerItemCount > 0
            ? `${formatValue(activeRagPendingPlannerItemCount)} step${activeRagPendingPlannerItemCount === 1 ? '' : 's'}`
            : (activeRagPendingPromptReferences.length > 0
            ? `${formatValue(activeRagPendingPromptReferences.length)} source${activeRagPendingPromptReferences.length === 1 ? '' : 's'}`
            : 'Planning…'),
        contextLabel: activeRagContextParts.join(' · '),
    } : null;
    const toggleJaneTraceCollapsed = useCallback((messageId, currentlyCollapsed) => {
        const key = String(messageId || '');
        if (!key) return;
        setJaneTraceCollapsedByMessageId(prev => {
            const next = new Map(prev);
            next.set(key, !currentlyCollapsed);
            return next;
        });
    }, []);
    const togglePromptReferenceExpansion = useCallback((messageId) => {
        const key = String(messageId || '');
        if (!key) return;
        setExpandedPromptReferenceMessages(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);
    const currentRagRunPrompt = getCurrentRagRunPrompt();

    useEffect(() => {
        const messageId = ragResponseDetailsMessageId !== null ? String(ragResponseDetailsMessageId) : '';
        if (!messageId) return;
        if (ragRunInspectorByMessageId.has(messageId) || ragRunInspectorLoadingMessageIds.has(messageId)) return;

        const controller = new AbortController();
        setRagRunInspectorLoadingMessageIds(prev => {
            const next = new Set(prev);
            next.add(messageId);
            return next;
        });
        setRagRunInspectorErrorByMessageId(prev => {
            const next = new Map(prev);
            next.delete(messageId);
            return next;
        });

        fetch(`${API_BASE}/jane-run-inspector.php?message_id=${encodeURIComponent(messageId)}`, {
            method: 'GET',
            credentials: 'include',
            signal: controller.signal,
        })
            .then(async response => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok || data?.success === false) {
                    throw new Error(data?.message || `Inspector request failed (${response.status})`);
                }
                setRagRunInspectorByMessageId(prev => {
                    const next = new Map(prev);
                    next.set(messageId, data?.inspector || null);
                    return next;
                });
            })
            .catch(error => {
                if (error?.name === 'AbortError') return;
                setRagRunInspectorErrorByMessageId(prev => {
                    const next = new Map(prev);
                    next.set(messageId, error?.message || 'Failed to load Jane run inspector.');
                    return next;
                });
            })
            .finally(() => {
                setRagRunInspectorLoadingMessageIds(prev => {
                    const next = new Set(prev);
                    next.delete(messageId);
                    return next;
                });
            });

        return () => controller.abort();
    }, [ragResponseDetailsMessageId]);

    const ragResponseDetailsMessage = ragResponseDetailsMessageId !== null
        ? ragMessages.find(message => String(message.id) === String(ragResponseDetailsMessageId)) || null
        : null;
    const ragResponseDetailsMessageKey = ragResponseDetailsMessageId !== null ? String(ragResponseDetailsMessageId) : '';
    const ragResponseDetailsInspector = ragResponseDetailsMessageKey ? ragRunInspectorByMessageId.get(ragResponseDetailsMessageKey) || null : null;
    const ragResponseDetailsInspectorLoading = ragResponseDetailsMessageKey ? ragRunInspectorLoadingMessageIds.has(ragResponseDetailsMessageKey) : false;
    const ragResponseDetailsInspectorError = ragResponseDetailsMessageKey ? ragRunInspectorErrorByMessageId.get(ragResponseDetailsMessageKey) || '' : '';
    const ragResponseDetailsPlannerSteps = getRagPlannerStepsFromMessage(ragResponseDetailsMessage);
    const ragResponseDetailsRunPlan = getJaneRunPlanFromMessage(ragResponseDetailsMessage);
    const ragResponseDetailsIntelligence = ragResponseDetailsMessage?.metadata?.intelligence || null;
    const ragResponseDetailsTrace = ragResponseDetailsIntelligence?.route_trace || null;
    const ragResponseDetailsTurnContract = getJaneTurnContractFromIntelligence(ragResponseDetailsIntelligence);
    const ragResponseDetailsRows = buildRagTraceRows(ragResponseDetailsTrace);
    const ragResponseDetailsTurnRows = buildJaneTurnContractRows(ragResponseDetailsTurnContract);
    const ragResponseDetailsReadiness = Array.isArray(ragResponseDetailsTrace?.file_readiness) ? ragResponseDetailsTrace.file_readiness.filter(Boolean) : [];
    const ragResponseDetailsItemCount = countRagPlannerItems(ragResponseDetailsPlannerSteps) + ragResponseDetailsTurnRows.length + ragResponseDetailsRows.length + ragResponseDetailsReadiness.length;
    const ragResponseDetailsThought = ragResponseDetailsMessage ? {
        tone: 'response-details',
        label: 'Response details',
        title: 'How Jane prepared this answer',
        status: ragResponseDetailsTurnContract?.decision_class ? titleCaseRagTraceValue(ragResponseDetailsTurnContract.decision_class) : (ragResponseDetailsTrace?.route ? titleCaseRagTraceValue(ragResponseDetailsTrace.route) : 'Saved response metadata'),
        prompt: String(ragResponseDetailsMessage.query_text || '').trim(),
        plannerSteps: ragResponseDetailsPlannerSteps,
        runPlan: ragResponseDetailsRunPlan,
        trace: ragResponseDetailsTrace,
        turnContract: ragResponseDetailsTurnContract,
        inspector: ragResponseDetailsInspector,
        inspectorLoading: ragResponseDetailsInspectorLoading,
        inspectorError: ragResponseDetailsInspectorError,
        badgeLabel: ragResponseDetailsInspectorLoading ? 'Loading details' : `${formatValue(Math.max(1, ragResponseDetailsItemCount))} detail${Math.max(1, ragResponseDetailsItemCount) === 1 ? '' : 's'}`,
        contextLabel: 'Jane response',
    } : null;
    const ragCalendarPanelMessage = ragCalendarPanelMessageId !== null
        ? ragMessages.find(message => String(message.id) === String(ragCalendarPanelMessageId)) || null
        : null;
    const ragCalendarPanelAnswer = ragCalendarPanelMessage?.answer || null;
    const showRagCalendarPanel = !!ragCalendarPanelAnswer && Array.isArray(ragCalendarPanelAnswer.calendar_drafts) && ragCalendarPanelAnswer.calendar_drafts.length > 0;
    const ragDraftPreviewMessage = ragDraftPreviewMessageId !== null
        ? ragMessages.find(message => String(message.id) === String(ragDraftPreviewMessageId)) || null
        : null;
    const ragDraftPreviewActionDetails = getConfirmableActionDetails(ragDraftPreviewMessage);
    const ragDraftPreviewPayload = ragDraftPreviewMessage ? getLegalDraftPreviewPayload(ragDraftPreviewMessage, ragDraftPreviewActionDetails) : null;
    const ragDraftPreviewAnswer = ragDraftPreviewMessage?.answer || null;
    const ragDraftPreviewDraftIntent = ragDraftPreviewPayload?.draft_intent || ragDraftPreviewAnswer?.draft_intent || {};
    const ragDraftPreviewDocumentType = String(ragDraftPreviewDraftIntent?.document_type || ragDraftPreviewAnswer?.document_type || 'legal document').trim() || 'legal document';
    const ragDraftPreviewDraftText = String(ragDraftPreviewPayload?.draft_text || ragDraftPreviewAnswer?.draft_text || '').trim();
    const ragDraftPreviewSavedFile = ragDraftPreviewAnswer?.generated_docx || ragDraftPreviewActionDetails?.result || null;
    const ragDraftPreviewSavedUrl = getSafeRagNavigationUrl(ragDraftPreviewSavedFile?.url);
    const ragDraftPreviewGeneratedDocx = ragDraftPreviewMessage ? buildGeneratedDraftDocxPreview(ragDraftPreviewMessage, ragDraftPreviewActionDetails) : null;
    const ragDraftPreviewIsPending = ragDraftPreviewActionDetails?.status === 'pending_permission';
    const ragDraftPreviewCanSaveToChat = Boolean(
        ragDraftPreviewIsPending
        && ragDraftPreviewActionDetails?.type === 'save_legal_draft_docx'
        && ragDraftPreviewPayload?.allow_chat_destination
    );
    const ragDraftPreviewIsBusy = ragDraftPreviewMessage ? confirmableActionBusyMessageIds.has(String(ragDraftPreviewMessage.id)) : false;
    const ragDraftPreviewCommitBlocked = ragDraftPreviewIsPending && (!Number.isFinite(Number(ragDraftPreviewMessage?.id)) || ragSaving || ragDraftPreviewIsBusy);
    const showRagDraftPreviewPanel = !!ragDraftPreviewMessage && !!ragDraftPreviewDraftText && !ragDraftPreviewGeneratedDocx;
    const showOutlookCitationReview = !!outlookCitationReview;
    const showRagPlannerInPreview = !!activeRagThought && !isRagMobileViewport && !ragPlannerPreviewClosed;
    const showRagResponseDetailsPreview = !!ragResponseDetailsThought;
    const activePendingRagAction = getPendingRagActionTarget(ragMessages);
    const isPendingRagActionActive = !!activePendingRagAction;
    const isRagResponseCancelable = loading || !!requestControllerRef.current || canCancelOwnRagRequest || ragCanceling || Boolean(ragCancelError);
    const ragComposerDraftText = chunkSearchDraft.trim();
    const isRagComposerCancelMode = isRagResponseCancelable && ragComposerDraftText.length < 2;
    const isRagSendDisabled = !hasJaneUserIdentity || loading || ragSaving || ragChatFilesUploading || !!requestControllerRef.current || ragCanceling || Boolean(ragCancelError) || isChatRequestBlocked || isPendingRagActionActive;
    const showRagRunMenuControl = isRagResponseCancelable && !ragCanceling && !ragCancelError;
    const canUseRagRunMenu = showRagRunMenuControl && currentRagRunPrompt.length >= 2 && !ragSaving;

    const handleShowRagSourceHistory = () => {
        setShowRagSourceHistory(prev => {
            if (!prev) {
                setChunkPreview(null);
                setRagResponseDetailsMessageId(null);
                setRagCalendarPanelMessageId(null);
                setRagDraftPreviewMessageId(null);
            }
            return !prev;
        });
    };

    const handleOpenCalendarPanel = (messageId) => {
        setShowRagSourceHistory(false);
        setChunkPreview(null);
        setRagResponseDetailsMessageId(null);
        setRagDraftPreviewMessageId(null);
        setRagCalendarPanelMessageId(messageId);
    };

    const handleCloseRagPlannerPreview = () => {
        setCookie(RAG_PLANNER_PREVIEW_CLOSED_COOKIE, '1', 365);
        setRagPlannerPreviewClosed(true);
    };

    const handleOpenSourceHistoryEntry = (entry) => {
        if (entry.previewCitation) {
            setShowRagSourceHistory(false);
            setRagCalendarPanelMessageId(null);
            setRagDraftPreviewMessageId(null);
            handleCitationOpen(withOutlookCitationReviewContext(entry.previewCitation, entry.outlookReviewContext || {}));
            return;
        }
        if (entry.fileId) {
            const source = entry.source || {};
            const nextPreview = {
                fileId: entry.fileId,
                fileName: source.file_name || source.name || entry.title || `File ${entry.fileId}`,
                caseId: source.case_id || source.caseId || null,
                caseName: source.case_name || source.caseName || '',
                storageKey: source.storage_key || source.key || source.file_path || source.path || '',
                pageLabel: source.label || '',
                chunkIndex: Number.isFinite(Number(source.chunk_index)) ? Number(source.chunk_index) : null,
                registryId: source.registry_id || null,
                matchedChunk: Number.isFinite(Number(source.chunk_index)) ? {
                    registry_id: source.registry_id,
                    chunk_index: source.chunk_index,
                    page_start: source.page_start,
                    page_end: source.page_end,
                    bbox_json: source.bbox_json,
                    chunk_source: source.chunk_source,
                    source_type: source.source_type,
                } : null,
                sourceType: source.source_type || '',
                chunkSource: source.chunk_source || '',
            };
            setShowRagSourceHistory(false);
            setRagCalendarPanelMessageId(null);
            setRagDraftPreviewMessageId(null);
            setChunkPreview(null);
            window.requestAnimationFrame(() => setChunkPreview(nextPreview));
            return;
        }
        openSafeRagWindow(entry.url);
    };

    const handleMentionSourceInPrompt = (entry) => {
        if (!entry?.source) return;
        addRagReferenceFromSource(entry.source, entry.origin || null);
        setTimeout(() => inputRef.current?.focus(), 0);
    };
    const currentPageCaseReferenceForPlanner = getCurrentPageCaseReference();
    const ragFolderPickerActionMessage = ragFolderPickerActionMessageId
        ? ragMessages.find(message => String(message.id) === String(ragFolderPickerActionMessageId)) || null
        : null;
    const ragFolderPickerActionDetails = ragFolderPickerActionMessage ? getConfirmableActionDetails(ragFolderPickerActionMessage) : null;
    const ragFolderPickerPayload = ragFolderPickerActionMessage && ragFolderPickerActionDetails
        ? getConfirmableActionPayloadForMessage(ragFolderPickerActionMessage, ragFolderPickerActionDetails)
        : null;
    const ragFolderPickerSelection = ragFolderPickerActionDetails
        ? getConfirmableFolderSelectionState(ragFolderPickerActionDetails, ragFolderPickerPayload)
        : null;

    return (
        <div className={`page-container library-rag-page full nm ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
            <div className={`library-rag-chat-shell${ragMobileNavOpen ? ' mobile-nav-open' : ''}`}>
                <aside className={`library-rag-chat-sidebar${ragSidebarCollapsed ? ' collapsed' : ''}${ragSidebarTextHidden ? ' text-hidden' : ''}`} aria-label="RAG chat history">
                    <div className="library-rag-chat-sidebar-header">
                        <div className="library-rag-sidebar-brand">
                            <span alt="Jane logo" className="library-rag-logo" style={{ fontFamily: 'LINE Seed JP', fontSize: '1.5em', height: 'auto', paddingLeft: '10px' }}>Jane</span>
                            {ragSaving && <small className="library-rag-sidebar-text">Saving…</small>}
                        </div>
                        <button
                            type="button"
                            className="library-rag-sidebar-toggle"
                            onClick={() => setRagSidebarCollapsed(prev => !prev)}
                            aria-label={ragSidebarCollapsed ? 'Expand chat sidebar' : 'Collapse chat sidebar'}
                            aria-expanded={!ragSidebarCollapsed}
                        >
                            <RagSidebarMark className="library-rag-sidebar-icon library-rag-sidebar-panel-mark" />
                            <img src={ragSidebarLogo} alt="" className="library-rag-sidebar-min-logo" aria-hidden="true" />
                        </button>
                    </div>
                    <button type="button" className="library-rag-new-chat-button" onClick={handleNewRagChat}>
                        <SquarePen size={20} /> <span className="library-rag-sidebar-text">New chat</span>
                    </button>
                    <button
                        type="button"
                        className={`library-rag-new-chat-button library-rag-agent-nav-button${showJaneAgentLibrary ? ' active' : ''}`}
                        onClick={handleOpenJaneAgentLibrary}
                    >
                        <SquareLibrary size={20} /> <span className="library-rag-sidebar-text">Agent Library</span>
                    </button>
                    <div className="library-rag-chat-search">
                        <Search size={20} />
                        <input className="library-rag-sidebar-text" type="text" placeholder="Search chats" value={ragChatSearch} onChange={(event) => setRagChatSearch(event.target.value)} />
                    </div>
                    <div className="library-rag-chat-list">
                        {filteredRagChats.length === 0 && <div className="library-rag-chat-empty library-rag-sidebar-text">No saved chats found.</div>}
                        {filteredRagChats.map((chat) => (
                            <div key={`rag-chat-${chat.id}`} className="library-rag-chat-list-row">
                                <button
                                    type="button"
                                    className={`library-rag-chat-list-item${Number(activeRagChatId) === Number(chat.id) ? ' active' : ''}`}
                                    onClick={() => handleRagChatSelect(chat.id)}
                                >
                                    <span className="library-rag-sidebar-text">{cleanRagChatTitle(chat.title) || 'Untitled chat'}</span>
                                    {chat.is_shared && <small className="library-rag-sidebar-text">Shared with you</small>}
                                </button>
                                <div className="library-rag-chat-row-actions">
                                    {!chat.is_shared && (
                                        <button
                                            type="button"
                                            className="library-rag-chat-row-action library-rag-chat-share"
                                            onClick={(event) => handleOpenShareRagChat(event, chat)}
                                            aria-label={`Share ${cleanRagChatTitle(chat.title) || 'Untitled chat'}`}
                                            title="Share chat"
                                        >
                                            <Share2 size={15} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="library-rag-chat-row-action library-rag-chat-delete"
                                        onClick={(event) => handleRequestDeleteRagChat(event, chat)}
                                        aria-label={`${chat.is_shared ? 'Remove shared chat' : 'Delete'} ${cleanRagChatTitle(chat.title) || 'Untitled chat'}`}
                                        title={chat.is_shared ? 'Remove from my chats' : 'Delete chat'}
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>

                <div className={`library-rag-mobile-nav${ragMobileNavOpen ? ' open' : ''}`} aria-hidden={!ragMobileNavOpen}>
                    <button type="button" className="library-rag-mobile-nav-backdrop" onClick={() => setRagMobileNavOpen(false)} aria-label="Close chat menu" />
                    <aside className="library-rag-mobile-nav-panel" aria-label="Mobile RAG chat history">
                        <div className="library-rag-mobile-nav-header">
                            <div className="library-rag-sidebar-brand">
                                <img src={DBLogo} alt="CaseDB logo" className="library-rag-logo" style={{ filter: getCookie("theme") !== "dark" ? "invert(1)" : "none" }} />
                                {ragSaving && <small>Saving…</small>}
                            </div>
                            <button type="button" className="library-rag-mobile-nav-close" onClick={() => setRagMobileNavOpen(false)} aria-label="Close chat menu">
                                <X size={18} />
                            </button>
                        </div>
                        <button type="button" className="library-rag-new-chat-button" onClick={handleNewRagChat}>
                            <SquarePen size={20} /> <span>New chat</span>
                        </button>
                        <button
                            type="button"
                            className={`library-rag-new-chat-button library-rag-agent-nav-button${showJaneAgentLibrary ? ' active' : ''}`}
                            onClick={handleOpenJaneAgentLibrary}
                        >
                            <SquareLibrary size={20} /> <span>Agent Library</span>
                        </button>
                        <div className="library-rag-chat-search">
                            <Search size={20} />
                            <input type="text" placeholder="Search chats" value={ragChatSearch} onChange={(event) => setRagChatSearch(event.target.value)} />
                        </div>
                        <div className="library-rag-chat-list">
                            {filteredRagChats.length === 0 && <div className="library-rag-chat-empty">No saved chats found.</div>}
                            {filteredRagChats.map((chat) => (
                                <div
                                    key={`mobile-rag-chat-${chat.id}`}
                                    className={`library-rag-chat-list-row${Number(ragMobileActionChatId) === Number(chat.id) ? ' action-open' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className={`library-rag-chat-list-item${Number(activeRagChatId) === Number(chat.id) ? ' active' : ''}`}
                                        onClick={(event) => handleMobileChatClick(event, chat)}
                                        onPointerDown={() => handleMobileChatPressStart(chat)}
                                        onPointerUp={handleMobileChatPressEnd}
                                        onPointerLeave={handleMobileChatPressEnd}
                                        onPointerCancel={handleMobileChatPressEnd}
                                        onContextMenu={(event) => {
                                            event.preventDefault();
                                            openRagMobileChatActions(chat);
                                        }}
                                    >
                                        <span>{cleanRagChatTitle(chat.title) || 'Untitled chat'}</span>
                                        {chat.is_shared && <small>Shared with you</small>}
                                    </button>
                                </div>
                            ))}
                        </div>
                        {ragMobileActionChat && (
                            <div className="library-rag-mobile-chat-popover" role="dialog" aria-label={`${cleanRagChatTitle(ragMobileActionChat.title) || 'Untitled chat'} actions`}>
                                <div className="library-rag-mobile-chat-preview-window">
                                    <div className="library-rag-mobile-chat-popover-title">{cleanRagChatTitle(ragMobileActionChat.title) || 'Untitled chat'}</div>
                                    <div className="library-rag-mobile-chat-preview-thread">
                                        {ragMobileActionPreviewLoading ? (
                                            <div className="library-rag-mobile-chat-preview-status">Loading preview…</div>
                                        ) : ragMobileActionPreviewMessages.length > 0 ? ragMobileActionPreviewMessages.map((message) => {
                                            const previewText = getRagMobilePreviewMessageText(message);
                                            if (!previewText) return null;
                                            return (
                                                <div key={`mobile-preview-${ragMobileActionChat.id}-${message.id}`} className={`library-rag-mobile-chat-preview-message ${message.role}`}>
                                                    {previewText}
                                                </div>
                                            );
                                        }) : (
                                            <div className="library-rag-mobile-chat-preview-status">{getRagChatPreviewText(ragMobileActionChat)}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="library-rag-mobile-chat-popover-actions">
                                    {!ragMobileActionChat.is_shared && (
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                setRagMobileActionChatId(null);
                                                handleOpenShareRagChat(event, ragMobileActionChat);
                                            }}
                                        >
                                            <Share2 size={15} /> Share
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="danger"
                                        onClick={(event) => {
                                            setRagMobileActionChatId(null);
                                            handleRequestDeleteRagChat(event, ragMobileActionChat);
                                        }}
                                    >
                                        <Trash2 size={15} /> {ragMobileActionChat.is_shared ? 'Remove' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </aside>
                </div>

                <main className={`library-rag-chat-main${showJaneAgentLibrary ? ' agent-library-view' : ''}`} style={{ '--library-rag-composer-height': `${ragComposerHeight}px`, '--library-rag-active-request-height': `${ragActiveRequestHeight}px` }}>
                    {showJaneAgentLibrary ? (
                        <>
                            <header className="library-rag-chat-header" aria-label="Agent Library controls">
                                <div className="library-rag-mobile-top-controls">
                                    <button
                                        type="button"
                                        className="library-rag-mobile-menu-button"
                                        onClick={() => setRagMobileNavOpen(true)}
                                        aria-label="Open chat menu"
                                        aria-expanded={ragMobileNavOpen}
                                    >
                                        <TextAlignStart size={20} />
                                    </button>
                                </div>
                                <div className="library-rag-chat-header-title-block">
                                    <div className="library-rag-chat-title-button static">
                                        <h2>Agent Library</h2>
                                    </div>
                                </div>
                                <div className="library-rag-chat-header-controls">
                                    <div className="library-rag-chat-header-actions library-rag-agent-library-header-tabs">
                                        {['All', 'Workspace Agents', 'Prompts', 'Bookmarks'].map((option) => {
                                            const tabMeta = getJaneAgentLibraryTabMeta(option);
                                            const TabIcon = tabMeta.icon;
                                            return (
                                                <button
                                                    key={`library-header-tab-${option}`}
                                                    type="button"
                                                    className={`library-agent-header-action${janeAgentLibraryCategory === option ? ' active' : ''}`}
                                                    onClick={() => setJaneAgentLibraryCategory(option)}
                                                >
                                                    <TabIcon size={14} />
                                                    <span>{tabMeta.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </header>
                            {renderJaneAgentLibrary()}
                        </>
                    ) : (
                        <>
                            <header className="library-rag-chat-header" aria-label="Current chat controls">
                                <div className="library-rag-mobile-top-controls">
                                    <button
                                        type="button"
                                        className="library-rag-mobile-menu-button"
                                        onClick={() => setRagMobileNavOpen(true)}
                                        aria-label="Open chat menu"
                                        aria-expanded={ragMobileNavOpen}
                                    >
                                        <TextAlignStart size={20} />
                                    </button>
                                    <label className="library-rag-chat-model-control library-rag-mobile-model-control">
                                        <select value={ragModelKey} onChange={handleRagModelChange} disabled={ragChatSettingsSaving || (!!activeRagChat && activeRagChat.is_shared)} aria-label="Model">
                                            {RAG_MODEL_OPTIONS.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                                        </select>
                                    </label>
                                </div>
                                <div className="library-rag-chat-header-title-block">
                                    {editingRagChatTitle ? (
                                        <form
                                            className="library-rag-chat-title-form"
                                            onSubmit={(event) => {
                                                event.preventDefault();
                                                handleRagChatTitleSubmit();
                                            }}
                                        >
                                            <input
                                                className="library-rag-chat-title-input"
                                                value={ragChatTitleDraft}
                                                onChange={(event) => setRagChatTitleDraft(event.target.value)}
                                                onBlur={handleRagChatTitleSubmit}
                                                autoFocus
                                                disabled={ragChatSettingsSaving}
                                                aria-label="Chat title"
                                            />
                                        </form>
                                    ) : (
                                        <button
                                            type="button"
                                            className="library-rag-chat-title-button"
                                            onClick={() => {
                                                if (!activeRagChatCanEdit) return;
                                                setRagChatTitleDraft(activeRagChatTitle);
                                                setEditingRagChatTitle(true);
                                            }}
                                            disabled={!activeRagChatCanEdit || ragChatSettingsSaving}
                                            title={activeRagChatCanEdit ? 'Rename chat' : 'Only the chat owner can rename this chat'}
                                        >
                                            <span>{activeRagChatTitle}</span>
                                            {activeRagChatCanEdit && <Pen size={14} />}
                                        </button>
                                    )}
                                </div>

                                <div className="library-rag-chat-header-controls">
                                    <label className="library-rag-chat-model-control">
                                        <select value={ragModelKey} onChange={handleRagModelChange} disabled={ragChatSettingsSaving || (!!activeRagChat && activeRagChat.is_shared)}>
                                            {RAG_MODEL_OPTIONS.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                                        </select>
                                    </label>
                                    <div className="library-rag-chat-header-actions">
                                        <button
                                            type="button"
                                            className={`library-rag-header-action${showRagSourceHistory ? ' active' : ''}`}
                                            onClick={handleShowRagSourceHistory}
                                            disabled={ragMessages.length === 0}
                                            title="Show every source used in this chat"
                                        >
                                            <SquareLibrary size={16} />
                                            <span>Sources</span>
                                        </button>
                                        <ProactiveAiPlanner chatId={activeRagChatId} caseId={currentPageCaseReferenceForPlanner?.case_id || null} />
                                        {activeRagChat && !activeRagChat.is_shared && (
                                            <button
                                                type="button"
                                                className="library-rag-header-action"
                                                onClick={(event) => handleOpenShareRagChat(event, activeRagChat)}
                                                title="Share chat"
                                            >
                                                <Share2 size={16} />
                                            </button>
                                        )}
                                        {activeRagChat && (
                                            <button
                                                type="button"
                                                className="library-rag-header-action danger"
                                                onClick={(event) => handleRequestDeleteRagChat(event, activeRagChat)}
                                                title={activeRagChat.is_shared ? 'Remove from my chats' : 'Delete chat'}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </header>
                            {ragUserMessageRailItems.length > 1 && (
                                <div className="library-rag-thread-rail" aria-label="User messages in this chat" onMouseEnter={scrollRagRailOverviewToCurrent} onFocus={scrollRagRailOverviewToCurrent}>
                                    <div className="library-rag-thread-rail-items">
                                        {ragUserMessageRailItems.map((item) => (
                                            <button
                                                key={`rag-thread-rail-${item.id}`}
                                                type="button"
                                                className={`library-rag-thread-rail-item${item.id === String(ragActiveRailMessageId) ? ' active' : ''}${item.id === String(ragTopRailMessageId) ? ' current' : ''}`}
                                                onClick={() => handleRagRailJump(item.id)}
                                                title={item.label}
                                                aria-label={`Jump to user message ${item.index + 1}`}
                                            >
                                                <span aria-hidden="true" />
                                            </button>
                                        ))}
                                    </div>
                                    <div className="library-rag-thread-rail-overview" ref={ragThreadRailOverviewRef} aria-label="User message overview">
                                        {ragUserMessageRailItems.map((item) => (
                                            <button
                                                key={`rag-thread-rail-overview-${item.id}`}
                                                type="button"
                                                data-rag-rail-message-id={item.id}
                                                className={`library-rag-thread-rail-overview-item${item.id === String(ragTopRailMessageId) ? ' current' : ''}${item.id === String(ragActiveRailMessageId) ? ' active' : ''}`}
                                                onClick={() => handleRagRailJump(item.id)}
                                                title={item.label}
                                                aria-label={`Jump to user message ${item.index + 1}`}
                                            >
                                                <span>{item.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {visibleRagReminderRuns.length > 0 && (
                                <div className="library-rag-floating-job-stack" aria-label="Background tasks">
                                    {visibleRagReminderRuns.map((run) => {
                                        const status = String(run.status || 'queued');
                                        const isReminder = run.event_type === 'chat_reminder' || run.task?.task_type === 'chat_reminder';
                                        const taskType = String(run.task?.task_type || run.event_type || '').trim();
                                        const isTimelineBuild = taskType === 'case_timeline_build';
                                        const isJaneBackgroundRun = taskType === 'jane_background_run';
                                        const isCaseTrialAnalysis = taskType === 'case_trial_analysis';
                                        const presentation = getJaneBackgroundRunPresentation(run);
                                        const plannerModel = buildJaneBackgroundRunPlannerModel(run, ragMessages);
                                        const taskTitle = presentation.title;
                                        const busy = ragBackgroundDraftBusyRunIds.has(getBackgroundDraftRunBusyKey(run.id));
                                        const backgroundRunError = String(ragBackgroundDraftErrorByRunId[String(run.id)] || '').trim();
                                        const generatedFile = run.metadata?.generated_file || null;
                                        const normalizedGeneratedFile = generatedFile ? normalizePrivateFinancialArtifactUrl(generatedFile, API_BASE) : null;
                                        const generatedFileUrl = getSafeRagNavigationUrl(normalizedGeneratedFile?.url);
                                        const caseAnalysisResultUrl = getSafeRagNavigationUrl(run.metadata?.result_url);
                                        const scheduledLabel = isReminder ? formatDate(run.metadata?.scheduled_for || run.metadata?.scheduled_for_mysql || run.task?.next_run_at || run.next_run_at || run.metadata?.scheduled_for_utc_mysql) : '';
                                        const reminderChatId = Number(run.task?.chat_id || 0);
                                        const reminderFromChat = isReminder && reminderChatId > 0 && Number(activeRagChatId || 0) !== reminderChatId;
                                        const reminderText = String(run.metadata?.reminder_text || run.task?.prompt || taskTitle).trim();
                                        const isReady = status === 'awaiting_permission';
                                        const isWorking = ['queued', 'running'].includes(presentation.stateKey);
                                        const hasActiveRunStatus = status === 'queued' || status === 'running';
                                        const isCompleted = status === 'completed' && (isTimelineBuild || isJaneBackgroundRun || generatedFile);
                                        const isReminderDelivered = isReminder && status === 'completed';
                                        const reminderStateLabel = isReminderDelivered ? 'Completed' : 'Scheduled';
                                        const announcement = isReminder
                                            ? `${isReminderDelivered ? 'Reminder delivered' : 'Reminder scheduled'} — ${reminderText}`
                                            : presentation.announcement;
                                        return (
                                            <div
                                                key={`background-draft-run-${run.id}`}
                                                className={`library-rag-floating-job-card ${status} state-${isReminder ? (isReminderDelivered ? 'completed' : 'queued') : presentation.stateKey}`}
                                            >
                                                <div className="library-rag-floating-job-icon">
                                                    {isWorking ? <Loader2 size={16} className="spinner" /> : isReminder ? <BellRing size={16} /> : <Save size={16} />}
                                                </div>
                                                <div
                                                    className="library-rag-floating-job-copy"
                                                    role="status"
                                                    aria-live="polite"
                                                    aria-atomic="true"
                                                    aria-label={announcement}
                                                >
                                                    <div className="library-rag-floating-job-heading">
                                                        <strong>{isReminder ? (isReminderDelivered ? 'Reminder delivered' : 'Reminder scheduled') : taskTitle}{' '}</strong>
                                                        <span className={`library-rag-floating-job-state ${isReminder ? (isReminderDelivered ? 'completed' : 'queued') : presentation.stateKey}`}>
                                                            {isReminder ? reminderStateLabel : presentation.stateLabel}
                                                        </span>
                                                    </div>
                                                    <span className="library-rag-floating-job-status">{' '}{isReminder ? `${reminderText}${scheduledLabel && !isReminderDelivered ? ` · ${scheduledLabel}` : ''}${reminderFromChat ? ` · Chat ${reminderChatId}` : ''}` : presentation.statusText}</span>
                                                    {backgroundRunError && <span className="library-rag-floating-job-error" role="alert">{backgroundRunError}</span>}
                                                </div>
                                                <div className="library-rag-floating-job-actions">
                                                    {isReminder && !isReminderDelivered && <button type="button" className="action quaternary" onClick={() => handleCancelProactiveEvent(run)} disabled={busy}>{busy ? 'Canceling...' : 'Cancel'}</button>}
                                                    {isReminder && isReminderDelivered && reminderChatId > 0 && <button type="button" className="action alt" onClick={() => loadRagChat(reminderChatId)}>Open</button>}
                                                    {isReminder && isReminderDelivered && <button type="button" className="action-micro quaternary quiet" onClick={() => handleCancelProactiveEvent(run)} disabled={busy} aria-label="Dismiss reminder"><X size={14} /></button>}
                                                    {!isReminder && !isTimelineBuild && isReady && <button type="button" className="action alt" onClick={() => handleSaveBackgroundDraftRun(run)} disabled={busy}>{busy ? 'Saving...' : 'Save draft'}</button>}
                                                    {!isReminder && isCompleted && generatedFileUrl && <button type="button" className="action alt" onClick={() => openSafeRagWindow(generatedFileUrl)}>Open</button>}
                                                    {isCaseTrialAnalysis && caseAnalysisResultUrl && <button type="button" className="action alt" onClick={() => window.location.assign(caseAnalysisResultUrl)}>Open</button>}
                                                    {!isReminder && !hasActiveRunStatus && <button type="button" className="action-micro quaternary quiet" onClick={() => handleDismissBackgroundDraftRun(run)} disabled={busy} aria-label="Dismiss background run"><X size={14} /></button>}
                                                </div>
                                                {!isReminder && plannerModel.steps.length > 0 && (
                                                    <details className="library-rag-floating-job-progress">
                                                        <summary>
                                                            <ChevronDown size={14} aria-hidden="true" />
                                                            <span>Task steps</span>
                                                            <span className="library-rag-floating-job-progress-count">{plannerModel.steps.length}</span>
                                                        </summary>
                                                        <ol>
                                                            {plannerModel.steps.map((step, stepIndex) => {
                                                                const stepStatus = String(step?.status || 'pending').trim().toLowerCase();
                                                                const stepStatusLabel = stepStatus === 'complete'
                                                                    ? 'Done'
                                                                    : (stepStatus === 'running'
                                                                        ? 'In progress'
                                                                        : (stepStatus === 'failed' ? 'Failed' : 'Waiting'));
                                                                return (
                                                                    <li key={String(step?.id || `background-step-${stepIndex}`)} className={stepStatus}>
                                                                        <span className="library-rag-floating-job-step-marker" aria-hidden="true" />
                                                                        <span className="library-rag-floating-job-step-copy">
                                                                            <strong>{String(step?.label || `Step ${stepIndex + 1}`)}</strong>
                                                                            {step?.detail && <span>{String(step.detail)}</span>}
                                                                        </span>
                                                                        <span className="library-rag-floating-job-step-status">{stepStatusLabel}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ol>
                                                    </details>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="library-rag-chat-thread" ref={ragThreadRef}>
                                {ragChatLoading ? (
                                    <div className="library-rag-chat-loading"><Loader2 size={18} className="spinner" /> Loading chat…</div>
                                ) : ragMessages.length === 0 && unanchoredBackgroundRuns.length === 0 ? (
                                    <div className="library-rag-chat-welcome">
                                        <h3>{ragWelcomeGreeting}</h3>
                                        <p>Ask a question, link a case or file, or tell me what you want summarized.</p>
                                    </div>
                                ) : <>
                                    <div ref={ragOlderMessagesSentinelRef} className="library-rag-older-messages-sentinel" aria-hidden={!ragMessagesHasMoreBefore && !ragMessagesLoadingOlder}>
                                        {ragMessagesLoadingOlder ? <><Loader2 size={14} className="spinner" /> Loading older messages…</> : ragMessagesHasMoreBefore ? 'Scroll up to load older messages' : null}
                                    </div>
                                    {ragMessages.map((message, messageIndex) => {
                                    if (isJaneBackgroundHandoffShadowPrompt(message)) return null;
                                    const messageDateKey = getRagMessageDateKey(message?.created_at, ragMessageTimeZone);
                                    const previousMessageDateKey = messageIndex > 0
                                        ? getRagMessageDateKey(ragMessages[messageIndex - 1]?.created_at, ragMessageTimeZone)
                                        : '';
                                    const messageDateLabel = messageDateKey && messageDateKey !== previousMessageDateKey
                                        ? formatRagMessageDate(message?.created_at, ragMessageTimeZone)
                                        : '';
                                    const messageDateDivider = messageDateLabel ? (
                                        <div className="library-rag-message-date-divider" aria-label={`Messages from ${messageDateLabel}`}>
                                            <time dateTime={messageDateKey}>{messageDateLabel}</time>
                                        </div>
                                    ) : null;
                                    const shouldVirtualizeMessages = ragMessages.length > RAG_MESSAGE_VIRTUALIZE_THRESHOLD;
                                    const isMessageInRenderRange = messageIndex >= ragRenderedMessageRange.start && messageIndex <= ragRenderedMessageRange.end;
                                    if (shouldVirtualizeMessages && !isMessageInRenderRange) {
                                        return (
                                            <article
                                                key={`rag-message-${message.id}`}
                                                data-rag-message-id={String(message.id)}
                                                className={`library-rag-message ${message.role} library-rag-message-windowed${messageDateDivider ? ' has-date-divider' : ''}`}
                                            >
                                                {messageDateDivider}
                                                <div className="library-rag-message-windowed-placeholder">
                                                    <span>Message content hidden while off-screen</span>
                                                </div>
                                            </article>
                                        );
                                    }

                                    const answer = message.answer || null;
                                    const inlineBackgroundRun = getInlineBackgroundRun(message);
                                    const isBackgroundReceipt = !!inlineBackgroundRun && JANE_BACKGROUND_HANDOFF_MODES.has(String(answer?.mode || '').trim().toLowerCase());
                                    const outlookCitationContext = {
                                        chatId: Number(message.chat_id || activeRagChatId || 0) || null,
                                        assistantMessageId: Number(message.id || 0) || null,
                                    };
                                    const isLegalDraftPresentation = isJaneLegalDraftPresentation({
                                        answer,
                                        intelligence: message.metadata?.intelligence,
                                        metadata: message.metadata,
                                    });
                                    const citations = (Array.isArray(answer?.citations) ? answer.citations : Array.isArray(message.sources) ? message.sources : [])
                                        .map((source) => normalizePrivateFinancialArtifactUrl(source, API_BASE))
                                        .map((source) => withOutlookCitationReviewContext(source, outlookCitationContext));
                                    const answerText = normalizeRagAnswerText(answer?.answer_text || message.content || '');
                                    const displayedCitations = getDisplayedCitationsForAnswer(citations, answerText);
                                    const displayedSourceCards = getAnswerSourceCards(answer)
                                        .map((source) => withOutlookCitationReviewContext(source, outlookCitationContext));
                                    const messageFiles = (Array.isArray(message.files) ? message.files : []).map((source) => normalizePrivateFinancialArtifactUrl(source, API_BASE));
                                    const nonGeneratedMessageFiles = messageFiles.filter(file => !isRagGeneratedOutputSource(file));
                                    const displayedFileOutputSources = messageFiles
                                        .filter(isRagGeneratedOutputSource)
                                        .map((file, fileIndex) => ({
                                            ...file,
                                            number: file.number || file.citation_number || displayedSourceCards.length + displayedCitations.length + fileIndex + 1,
                                            source_type: file.source_type || file.sourceType || file.chunk_source || file.chunkSource || 'generated_file',
                                            chunk_source: file.chunk_source || file.chunkSource || file.source_type || file.sourceType || 'generated_file',
                                            file_id: Number(file.file_id || file.id || file.fileId || 0) || null,
                                            file_name: file.file_name || file.name || file.filename || '',
                                            title: file.title || file.file_name || file.name || file.filename || 'Generated file',
                                            label: file.label || file.file_name || file.name || file.filename || 'Generated file',
                                        }));
                                    const displayedSources = mergeDisplayedSources([...displayedSourceCards, ...displayedFileOutputSources], displayedCitations);
                                    const displayedOutputSources = uniqueRagGeneratedOutputSources(displayedFileOutputSources, displayedSources);
                                    const generatedOutputFileIds = new Set(displayedOutputSources.map(getRagSourceFileIdentity).filter(Boolean));
                                    const displayedEvidenceSources = (isLegalDraftPresentation ? [] : displayedSources).filter(source => {
                                        if (isRagGeneratedOutputSource(source)) return false;
                                        const fileId = getRagSourceFileIdentity(source);
                                        return !fileId || !generatedOutputFileIds.has(fileId);
                                    });
                                    const sourcedTimelineCitations = Array.isArray(answer?.citations) ? answer.citations : displayedCitations;
                                    const answerDisplayCaseName = getCaseNameForDisplay(
                                        citations.find((citation) => getCaseNameForDisplay(citation))
                                        || (Array.isArray(message.files) ? message.files.find((file) => getCaseNameForDisplay(file)) : null)
                                        || {},
                                        ''
                                    );
                                    const displayedAnswerText = replaceCaseNameToken(answerText, {}, answerDisplayCaseName);
                                    const sourceDescriptions = Array.isArray(answer?.source_descriptions) ? answer.source_descriptions : [];
                                    const draftSubject = String(answer?.draft_subject || '').trim();
                                    const isEmailDraft = answer?.draft_type === 'email' && draftSubject !== '';
                                    const isEditingUserMessage = message.role === 'user' && editingRagMessageId === message.id;
                                    const isSourcesOpen = expandedCitationMessages.has(message.id);
                                    const visibleEvidenceSources = isSourcesOpen ? displayedEvidenceSources : [];
                                    const citationListClassName = `library-rag-citation-list${isSourcesOpen ? ' expanded' : ''}`;
                                    const sourceDescriptionMap = new Map();
                                    sourceDescriptions.forEach((source) => {
                                        if (source?.citation_number) sourceDescriptionMap.set(String(source.citation_number), source);
                                        if (source?.source_id) sourceDescriptionMap.set(String(source.source_id), source);
                                    });
                                    const getSourceDetailForCitation = (citation) => (
                                        sourceDescriptionMap.get(String(citation.number))
                                        || sourceDescriptionMap.get(String(citation.original_number))
                                        || sourceDescriptionMap.get(String(citation.source_id))
                                        || null
                                    );
                                    const isAssistantStreaming = Boolean(message.metadata?.streaming);
                                    const isMessageBoundToActiveRequest = message.role === 'assistant'
                                        && isRagMessageBoundToActiveRequest(message, ragActiveRequest);
                                    const storedOpeningRequestToken = String(
                                        message.metadata?.request_token
                                        || message.request_token
                                        || message.metadata?.intelligence?.request_token
                                        || message.answer?.request_token
                                        || message.metadata?.jane_agent_v2?.request_token
                                        || ''
                                    ).trim();
                                    const openingRequestToken = isMessageBoundToActiveRequest
                                        ? String(ragActiveRequest?.request_token || '').trim()
                                        : storedOpeningRequestToken;
                                    const requiresRequestBoundOpening = isMessageBoundToActiveRequest
                                        || storedOpeningRequestToken !== '';
                                    const typedMessageOpening = requiresRequestBoundOpening
                                        ? (openingRequestToken ? getJanePublicOpening(message, openingRequestToken, true) : null)
                                        : getJanePublicOpening(message);
                                    const rawMessageJaneOpeningText = String(
                                        typedMessageOpening?.text
                                        || (!requiresRequestBoundOpening && (
                                            message.metadata?.janeOpeningText
                                            || message.metadata?.intelligence?.jane_opening_text
                                            || message.answer?.jane_opening_text
                                        ))
                                        || ''
                                    ).trim();
                                    const messageJaneOpeningText = isLegacyRagRerunOpeningText(rawMessageJaneOpeningText)
                                        ? '' : rawMessageJaneOpeningText;
                                    const foregroundRunActivity = getJaneForegroundRunActivity(message, answer);
                                    const messageHandedOffToBackground = foregroundRunActivity.handedOffToBackground;
                                    const shouldShowJaneRunPlan = message.role === 'assistant'
                                        && !inlineBackgroundRun
                                        && !messageHandedOffToBackground
                                        && !message.metadata?.streaming_trace_disabled
                                        && (message.pending || isAssistantStreaming || !!answer);
                                    const hasActiveBackgroundRunPlan = foregroundRunActivity.hasActiveRunPlan;
                                    const isCompletedAssistantAnswer = message.role === 'assistant' && !!answer && !message.pending && !isAssistantStreaming && !hasActiveBackgroundRunPlan;
                                    const isLiveAssistantThinking = foregroundRunActivity.isLiveAssistantThinking;
                                    const janeRunningTimeText = isLiveAssistantThinking ? getJaneResponsePlanRunningSummary(message, janeElapsedNow) : '';
                                    const janeRunPlan = shouldShowJaneRunPlan ? getJaneRunPlanFromMessage(message, null, { liveTraceOnly: isLiveAssistantThinking }) : null;
                                    const janeRunPlanMessageKey = String(message.id);
                                    const collapsedTraceOverride = janeTraceCollapsedByMessageId.get(janeRunPlanMessageKey);
                                    const shouldCollapseJaneTrace = !!janeRunPlan && (typeof collapsedTraceOverride === 'boolean'
                                        ? collapsedTraceOverride
                                        : shouldCollapseJaneRunPlanByDefault({
                                            isLiveAssistantThinking,
                                            isCompletedAssistantAnswer,
                                            isPendingWithoutAnswer: Boolean(message.pending && !answer),
                                            runPlanStatus: janeRunPlan.status,
                                        }));
                                    const isJaneTraceExpanded = !!janeRunPlan && !shouldCollapseJaneTrace;
                                    const handleJaneTraceToggle = () => toggleJaneTraceCollapsed(message.id, shouldCollapseJaneTrace);
                                    const messageHasResponseDetails = message.role === 'assistant' && !!answer && !isLegalDraftPresentation;
                                    const isResponseDetailsOpen = String(ragResponseDetailsMessageId) === String(message.id);
                                    const medicalChronology = getMedicalChronologyForMessage(message);
                                    const sourcedTimeline = getSourcedTimelineFromAnswer(answer);
                                    const readinessResult = getReadinessResultFromAnswer(answer);
                                    const caseEvidenceDigest = getCaseEvidenceDigestFromAnswer(answer);
                                    const caseAnalysisChatCard = getCaseAnalysisChatCardData(message, answer);
                                    const workspaceNavigationDetails = getWorkspaceNavigationDetails(message);
                                    const confirmableActionDetails = getConfirmableActionDetails(message);
                                    const workspaceCaseSelectionDetails = getWorkspaceCaseSelectionDetails(message);
                                    const promptReferences = getMessagePromptReferences(message, ragMessages[messageIndex + 1]);
                                    const promptReferencesExpanded = expandedPromptReferenceMessages.has(String(message.id));
                                    const visiblePromptReferences = promptReferencesExpanded ? promptReferences : promptReferences.slice(0, 2);
                                    const hiddenPromptReferenceCount = Math.max(0, promptReferences.length - visiblePromptReferences.length);
                                    const nativeTablePreviewReferences = message.role === 'user' ? promptReferences : [];
                                    const nativeTablePreviewMessage = {
                                        ...message,
                                        files: messageFiles,
                                        sources: citations,
                                        answer: answer ? { ...answer, citations } : answer,
                                    };
                                    const nativeTablePreviews = getNativeTablePreviewsForMessage(nativeTablePreviewMessage, ragTablePreviewCache, nativeTablePreviewReferences, getFileExtension, getRagCitationHref, {
                                        includeReports: true,
                                        includeSpreadsheets: message.role === 'user',
                                    });
                                    const nativeTablePreview = nativeTablePreviews[0] || null;
                                    const capabilityRegistryReview = answer?.mode === 'capability_registry_answer' && Array.isArray(answer?.capability_registry?.capabilities) && answer.capability_registry.capabilities.length > 0;
                                    const calendarDraftReview = ['scheduling_order_calendar_drafts_created', 'scheduling_order_calendar_pending_confirmation'].includes(String(answer?.mode || '')) && Array.isArray(answer?.calendar_drafts) && answer.calendar_drafts.length > 0;
                                    const isGeneratedNativeTableAnswer = !!nativeTablePreview && (
                                        nativeTablePreview.kind === 'report'
                                        || answer?.mode === 'generated_report'
                                        || answer?.generated_by === 'jane_report_generator'
                                        || Number(answer?.report_id || 0) > 0
                                    );
                                    const shouldShowCaseEvidenceDigest = !!caseEvidenceDigest && !isLegalDraftPresentation && !caseAnalysisChatCard && !medicalChronology && !sourcedTimeline && !readinessResult && !isGeneratedNativeTableAnswer && !capabilityRegistryReview && !calendarDraftReview && !isEmailDraft;
                                    const hasStructuredAnswerComponent = !!caseAnalysisChatCard || !!medicalChronology || !!sourcedTimeline || !!readinessResult || shouldShowCaseEvidenceDigest || isGeneratedNativeTableAnswer || capabilityRegistryReview || calendarDraftReview || isEmailDraft;
                                    const structuredAnswerComponentType = caseAnalysisChatCard ? 'case_analysis'
                                        : medicalChronology ? 'medical_chronology'
                                        : sourcedTimeline ? 'sourced_timeline'
                                            : readinessResult ? 'readiness'
                                                : shouldShowCaseEvidenceDigest ? 'case_evidence_digest'
                                                    : isGeneratedNativeTableAnswer ? 'native_table'
                                                        : capabilityRegistryReview ? 'capability_registry'
                                                            : calendarDraftReview ? 'calendar_draft'
                                                                : isEmailDraft ? 'email_draft'
                                                                    : '';
                                    const structuredAnswerComponentLabel = RAG_COMPONENT_VIEW_LABELS[structuredAnswerComponentType] || 'Component';
                                    const isStructuredAnswerTextView = hasStructuredAnswerComponent && textViewRagComponentTypes.has(structuredAnswerComponentType);
                                    const isDigestStructuredAnswer = structuredAnswerComponentType === 'case_evidence_digest';
                                    const hasSourceChunks = !isLegalDraftPresentation && nonGeneratedMessageFiles.some(hasLiteralSourceChunks);
                                    const messageHasSources = displayedEvidenceSources.length > 0 || displayedOutputSources.length > 0 || hasSourceChunks;
                                    const finalDisplayedAnswerText = getGeneratedReportDisplayText(displayedAnswerText, answer, nativeTablePreview);
                                    const hasIntegratedOutlookDigestSources = Boolean(parseOutlookDigestSummary(
                                        normalizeOutlookAnswerLines(finalDisplayedAnswerText, displayedCitations),
                                        displayedCitations
                                    ));
                                    const shouldAnimateAnswerText = message.role === 'assistant' && !!answer && ragIncomingAnswerMessageIds.has(String(message.id));
                                    const isIncomingAnswerComplete = !Boolean(message.metadata?.streaming);
                                    const messageAuthorLabel = getRagMessageAuthorLabel(message, user, activeRagChat);
                                    const messageTimeLabel = shouldDisplayRagMessageTime(message, {
                                        assistantResponseActive: isLiveAssistantThinking || shouldAnimateAnswerText,
                                    })
                                        ? formatRagMessageTime(message?.created_at, ragMessageTimeZone)
                                        : '';
                                    const messageTimestampMs = parseRagMessageTimestampMs(message?.created_at);
                                    const messageDateTimeValue = messageTimestampMs ? new Date(messageTimestampMs).toISOString() : undefined;
                                    const messagePersistenceState = ragPersistenceStateByMessageId[String(message.id)] || null;
                                    const isWorkspaceNavigationPending = workspaceNavigationDetails?.status === 'pending_permission' || workspaceNavigationPendingMessageIds.has(String(message.id));
                                    const isConfirmableActionPending = confirmableActionDetails?.status === 'pending_permission';
                                    const isConfirmableActionResolved = !!confirmableActionDetails && ['confirmed', 'declined'].includes(confirmableActionDetails.status);
                                    const isExplicitlyAuthorizedFinancialAction = confirmableActionDetails?.type === 'generate_financial_period_workbook'
                                        && String(confirmableActionDetails?.authorization_basis || confirmableActionDetails?.permission_state || '').trim() === 'explicit_user_request';
                                    const isConfirmableActionBusy = confirmableActionBusyMessageIds.has(String(message.id));
                                    const isConfirmableActionDeclining = confirmableActionDecliningMessageIds.has(String(message.id));
                                    const confirmableActionError = confirmableActionErrorByMessageId[String(message.id)] || '';
                                    const isWorkspaceCaseSelectionPending = workspaceCaseSelectionDetails?.status === 'pending_permission' && workspaceCaseSelectionDetails.caseMatches.length > 0;
                                    const isWorkspaceCaseSelectionBusy = confirmableActionBusyMessageIds.has(String(message.id));
                                    const hasPersistedMessageIdentity = Number.isInteger(Number(message.id)) && Number(message.id) > 0;
                                    const isWorkspaceCaseSelectionCommitBlocked = isWorkspaceCaseSelectionBusy || ragSaving || !hasPersistedMessageIdentity;
                                    const showCompletedAnswerExtras = !message.pending && !isWorkspaceNavigationPending && !isConfirmableActionPending && !isWorkspaceCaseSelectionPending;
                                    const showAssistantActions = message.role === 'assistant' && answer && !isBackgroundReceipt && !message.pending && !isWorkspaceNavigationPending && !isConfirmableActionPending && !isWorkspaceCaseSelectionPending;
                                    const assistantFeedbackRating = showAssistantActions ? getRagMessageFeedbackRating(message) : '';
                                    const assistantFeedbackAvailable = showAssistantActions && canRateAssistantResponse(message);
                                    const assistantFeedbackSaving = assistantFeedbackAvailable ? (ragFeedbackSavingByMessageId[Number(message.id)] || '') : '';
                                    const assistantFeedbackStatus = assistantFeedbackAvailable ? (ragFeedbackStatusByMessageId[Number(message.id)] || null) : null;
                                    const assistantForkError = message.role === 'assistant' ? (ragForkErrorByMessageId[String(message.id)] || '') : '';
                                    const assistantRerunError = message.role === 'assistant' ? (ragRerunErrorByMessageId[String(message.id)] || '') : '';
                                    const structuredPanelAction = (() => {
                                        if (!showAssistantActions) return null;
                                        if (caseAnalysisChatCard?.url) {
                                            return {
                                                label: 'Open',
                                                title: 'Open case analysis',
                                                ariaLabel: 'Open case analysis',
                                                active: false,
                                                onClick: () => navigate(caseAnalysisChatCard.url),
                                            };
                                        }
                                        if (calendarDraftReview) {
                                            const isOpen = String(ragCalendarPanelMessageId || '') === String(message.id);
                                            return {
                                                label: 'Panel',
                                                title: isOpen ? 'Calendar panel is open' : 'Open calendar panel',
                                                ariaLabel: isOpen ? 'Calendar panel is open' : 'Open calendar panel',
                                                active: isOpen,
                                                onClick: () => handleOpenCalendarPanel(message.id),
                                            };
                                        }
                                        if (nativeTablePreview?.url) {
                                            return {
                                                label: 'Open',
                                                title: 'Open source file',
                                                ariaLabel: 'Open source file',
                                                active: false,
                                                onClick: () => handleNativeTablePreviewOpen(nativeTablePreview),
                                            };
                                        }
                                        return null;
                                    })();
                                    const workspaceNavigationProgressText = isWorkspaceNavigationPending ? 'Awaiting user action.' : '';
                                    const isWorkspaceNavigationCommitBlocked = isWorkspaceNavigationPending && (!hasPersistedMessageIdentity || ragSaving || isConfirmableActionBusy);
                                    const confirmableActionPayload = isConfirmableActionPending ? getConfirmableActionPayloadForMessage(message, confirmableActionDetails) : null;
                                    const legalDraftPreviewPayload = isConfirmableActionPending ? getLegalDraftPreviewPayload(message, confirmableActionDetails) : null;
                                    const isOversizedDiscoveryDraftAnswer = confirmableActionDetails?.type === 'save_legal_draft_docx'
                                        && String(answer?.generated_by || '').trim() === 'discovery_objection_review'
                                        && (answerText.length > 5000 || answerText.includes('DRAFT—ATTORNEY REVIEW REQUIRED'));
                                    const shouldSuppressLegalDraftAnswerText = confirmableActionDetails?.type === 'save_legal_draft_docx'
                                        && (confirmableActionDetails?.status === 'pending_permission' || isOversizedDiscoveryDraftAnswer);
                                    const isFieldReviewConfirmableAction = Boolean(confirmableActionPayload?.field_review && Array.isArray(confirmableActionPayload?.field_writes));
                                    const canSaveLegalDraftToChat = Boolean(
                                        isConfirmableActionPending
                                        && confirmableActionDetails?.type === 'save_legal_draft_docx'
                                        && confirmableActionPayload?.allow_chat_destination
                                    );
                                    const confirmableFolderSelection = isConfirmableActionPending ? getConfirmableFolderSelectionState(confirmableActionDetails, confirmableActionPayload) : { required: false, hasFolder: true };
                                    const isFolderSelectionConfirmableAction = Boolean(confirmableFolderSelection.required);
                                    const isConfirmableActionCommitBlocked = isConfirmableActionPending && (!hasPersistedMessageIdentity || ragSaving || isConfirmableActionBusy || (isFieldReviewConfirmableAction && getConfirmableFieldIncludedCount(confirmableActionPayload) === 0) || (isFolderSelectionConfirmableAction && !confirmableFolderSelection.hasFolder));
                                    const renderResponseSourceCards = (sources, sectionKey) => (
                                        sources.map((citation, sourceIndex) => {
                                            const sourceDetail = getSourceDetailForCitation(citation);
                                            const isCaseSummarySource = isCaseSummaryCitation(citation);
                                            const citationCaseName = getCaseNameForDisplay(citation, getCaseNameForDisplay(sourceDetail));
                                            const sourceTypeLabel = getSourceTypeLabel(citation.source_type || citation.type || citation.chunk_source || '');
                                            const detailText = uniqueDisplayParts(
                                                isCaseSummarySource
                                                    ? [citationCaseName, citation.first_primary_name]
                                                    : [
                                                        replaceCaseNameToken(citation.excerpt, citation, citationCaseName),
                                                        replaceCaseNameToken(sourceDetail?.description, citation, citationCaseName),
                                                        replaceCaseNameToken(citation.case_name, citation, citationCaseName),
                                                        replaceCaseNameToken(citation.label, citation, citationCaseName),
                                                        replaceCaseNameToken(citation.folder_path || sourceDetail?.folder_path, citation, citationCaseName),
                                                    ]
                                            ).join(' · ');
                                            const citationHref = getRagCitationHref(citation);
                                            const caseSourceTitle = getCaseSourceTitle(citation);
                                            const citationFileId = Number(getSourceFileId(citation) || citation.file_id || 0);
                                            const citationCaseId = Number(citation.case_id || (isCaseSummarySource ? citation.id : 0));
                                            const citationPinned = citationFileId > 0
                                                ? ragReferences.some(reference => Number(reference.file_id) === citationFileId)
                                                : (Number.isFinite(citationCaseId) && citationCaseId > 0 && ragReferences.some(reference => reference?.type === 'case' && Number(reference.case_id || reference.id) === citationCaseId));
                                            const sourceTitle = citation.title || citation.file_name || citation.label || (citationFileId > 0 ? `File ${citationFileId}` : sourceTypeLabel || `Source ${sourceIndex + 1}`);
                                            const sourceNumber = citation.number || citation.citation_number || sourceIndex + 1;
                                            const sourceMarker = String(citation.source_type || citation.type || citation.chunk_source || '').toLowerCase();
                                            const isFeedSource = /feed|activity|note|email/.test(sourceMarker);
                                            const isFileSource = citationFileId > 0 || /file|document|attachment|docx|pdf|chunk/.test(sourceMarker);
                                            const isKeyValueSource = !isCaseSummarySource && (
                                                /field|fact|custom|fusion|case_data|lead_data|structured/.test(sourceMarker)
                                                || (!isFileSource && !isFeedSource && !citation.url && detailText)
                                            );
                                            const duplicateTitlePattern = sourceTitle
                                                ? new RegExp(`(^|\\s·\\s)${String(sourceTitle).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s·\\s|$)`, 'i')
                                                : null;
                                            const valueText = isKeyValueSource && detailText && duplicateTitlePattern
                                                ? detailText
                                                    .replace(duplicateTitlePattern, ' · ')
                                                    .replace(/(?:^|\s)·(?:\s|$)/g, ' · ')
                                                    .replace(/^\s*·\s*|\s*·\s*$/g, '')
                                                    .trim()
                                                : detailText;
                                            const displaySourceTitle = isKeyValueSource && valueText ? valueText : sourceTitle;
                                            const displaySourceDetail = isKeyValueSource && valueText
                                                ? sourceTitle
                                                : (isFeedSource ? 'Feed' : (isFileSource ? 'File' : detailText));
                                            const isOutputCard = String(sectionKey || '').startsWith('inline-output') || isRagGeneratedOutputSource(citation);
                                            const citationTitleText = isOutputCard
                                                ? String(citation.file_name || citation.title || citation.label || displaySourceTitle || '').trim()
                                                : String(displaySourceTitle || caseSourceTitle || displaySourceDetail || '').trim();
                                            const isOutlookSource = isOutlookCitation(citation);
                                            if (isOutlookSource) {
                                                return (
                                                    <OutlookEmailCitationCard
                                                        truncateSummary={false}
                                                        key={`rag-outlook-email-${sectionKey}-${message.id}-${citation.source_id || sourceNumber}-${sourceIndex}`}
                                                        citation={citation}
                                                        sourceNumber={sourceNumber}
                                                        onOpen={(source, outlookTarget) => handleCitationOpen(source, {
                                                            outlookTarget,
                                                            outlookView: 'pdf',
                                                        })}
                                                    />
                                                );
                                            }
                                            const citationCardInteractionProps = {
                                                role: citationHref ? 'link' : 'button',
                                                tabIndex: 0,
                                                onClick: (event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleCitationOpen(citation);
                                                },
                                                onKeyDown: (event) => {
                                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleCitationOpen(citation);
                                                },
                                            };

                                            return (
                                                <div
                                                    key={`rag-citation-${sectionKey}-${message.id}-${citation.source_id || sourceNumber}-${citation.file_id || citation.case_id || citation.url || sourceIndex}`}
                                                    className={`library-rag-citation${isCaseSummarySource ? ' case-source' : ''}`}
                                                    title={citationTitleText || undefined}
                                                    {...citationCardInteractionProps}
                                                >
                                                    {!isOutputCard && !isOutlookSource && <button
                                                        type="button"
                                                        className={`library-rag-source-pin${citationPinned ? ' pinned' : ''}`}
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            addRagReferenceFromSource(citation, 'Pinned from answer source');
                                                            setTimeout(() => inputRef.current?.focus(), 0);
                                                        }}
                                                        aria-label={`${citationPinned ? 'Pinned' : 'Pin'} ${displaySourceTitle || caseSourceTitle || `Source ${sourceNumber}`} to prompt`}
                                                        title={citationPinned ? 'Pinned to prompt' : 'Pin to prompt'}
                                                    >
                                                        <Pin size={16} />
                                                    </button>}
                                                    {isCaseSummarySource ? (
                                                        <span className="library-rag-case-source-card">
                                                            <span className={getContactInitialsClassName(citation.case_id)}>{caseSourceTitle.charAt(0)}</span>
                                                            <span className="library-rag-case-source-text">
                                                                <span className="library-rag-citation-title">{caseSourceTitle}</span>
                                                                <span className="library-rag-citation-detail always-visible">{[citation.case_id, citation.first_primary_name].filter(Boolean).join(' · ')}</span>
                                                            </span>
                                                            <span className="library-rag-citation-number case-source-number">[{citation.number}]</span>
                                                        </span>
                                                    ) : (
                                                        <span className="library-rag-citation-main">
                                                            <span className="library-rag-citation-number">[{citation.number}]</span>
                                                            <span className="library-rag-citation-copy">
                                                                <span className="library-rag-citation-title-row">
                                                                    <span className="library-rag-citation-title">{displaySourceTitle}</span>
                                                                </span>
                                                                {displaySourceDetail && <span className="library-rag-citation-detail always-visible">{displaySourceDetail}</span>}
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })
                                    );
                                    return (
                                        <article key={`rag-message-${message.id}`} data-rag-message-id={String(message.id)} className={`library-rag-message ${message.role}${messageDateDivider ? ' has-date-divider' : ''}${janeRunPlan ? ' has-live-trace' : ''}${String(ragActiveRailMessageId) === String(message.id) ? ' rail-active' : ''}${message.role === 'user' && ragMobileUserActionMessageId === message.id ? ' user-action-open' : ''}`} style={{ marginBottom: message.role === 'assistant' && ragMessages[messageIndex + 1]?.role === 'assistant' ? '20px' : '' }}>
                                            {messageDateDivider}
                                            <div className="library-rag-message-body">
                                                {inlineBackgroundRun && messageJaneOpeningText && <p className="library-rag-jane-opening-text library-rag-work-opening">{messageJaneOpeningText}</p>}
                                                {inlineBackgroundRun && renderInlineBackgroundRun(inlineBackgroundRun, message)}
                                                {isBackgroundReceipt || (inlineBackgroundRun && message.pending && !answer) ? null : message.pending && !answer ? (
                                                    <div className={`library-rag-message-pending${janeRunPlan ? ' has-run-plan' : ''}`}>
                                                        {!janeRunPlan ? (
                                                            <div className="library-rag-message-pending-header">
                                                                <div className="library-rag-message-pending-title">
                                                                    <RagThinkingIndicator />
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        {!inlineBackgroundRun && messageJaneOpeningText ? (
                                                            <p className="library-rag-jane-opening-text">{messageJaneOpeningText}</p>
                                                        ) : null}
                                                        {janeRunPlan ? renderJaneRunPlan(janeRunPlan, {
                                                            compact: !isJaneTraceExpanded,
                                                            collapsed: shouldCollapseJaneTrace,
                                                            collapsible: true,
                                                            onToggleCollapsed: handleJaneTraceToggle,
                                                            pending: true,
                                                            showActiveThinking: isLiveAssistantThinking,
                                                            activeThinkingLabel: isAssistantStreaming ? RAG_FINAL_RESPONSE_DRAFTING_LABEL : RAG_THINKING_LABEL,
                                                            summaryText: ['Working through response plan', janeRunningTimeText].filter(Boolean).join(' · '),
                                                            runningTimeText: janeRunningTimeText,
                                                            thoughtTrace: true,
                                                        }) : null}
                                                    </div>
                                                ) : message.role === 'assistant' && answer ? (
                                                    <section className="library-rag-answer-card" aria-label="AI response">
                                                        {!inlineBackgroundRun && messageJaneOpeningText ? (
                                                            <p className="library-rag-jane-opening-text final">{messageJaneOpeningText}</p>
                                                        ) : null}
                                                        {janeRunPlan ? (
                                                            <div className="library-rag-message-streaming-trace">
                                                                {renderJaneRunPlan(janeRunPlan, {
                                                                    compact: !isJaneTraceExpanded,
                                                                    collapsed: shouldCollapseJaneTrace,
                                                                    collapsible: true,
                                                                    onToggleCollapsed: handleJaneTraceToggle,
                                                                    pending: isLiveAssistantThinking,
                                                                    complete: isCompletedAssistantAnswer,
                                                                    showActiveThinking: isLiveAssistantThinking,
                                                                    activeThinkingLabel: isAssistantStreaming ? RAG_FINAL_RESPONSE_DRAFTING_LABEL : RAG_THINKING_LABEL,
                                                                    summaryText: isAssistantStreaming
                                                                        ? ['Final response in progress', janeRunningTimeText].filter(Boolean).join(' · ')
                                                                        : janeRunPlan.status === 'failed'
                                                                            ? 'Response plan stopped'
                                                                            : isLiveAssistantThinking
                                                                                ? ['Working through response plan', janeRunningTimeText].filter(Boolean).join(' · ')
                                                                                : getJaneResponsePlanCompleteSummary(message),
                                                                    runningTimeText: janeRunningTimeText,
                                                                    thoughtTrace: true,
                                                                })}
                                                            </div>
                                                        ) : null}
                                                        {caseAnalysisChatCard && !isStructuredAnswerTextView ? (
                                                            <CaseAnalysisChatCard data={caseAnalysisChatCard} onOpen={navigate} />
                                                        ) : null}
                                                        {medicalChronology && !isStructuredAnswerTextView ? (
                                                            <MedicalChronologyReview chronology={medicalChronology} />
                                                        ) : null}
                                                        {sourcedTimeline && !isStructuredAnswerTextView ? (
                                                            <SourcedTimelineReview timeline={sourcedTimeline} citations={sourcedTimelineCitations} onCitationOpen={handleCitationOpen} messageId={String(message.id)} expandText={false} />
                                                        ) : null}
                                                        {readinessResult && !isStructuredAnswerTextView ? (
                                                            <ReadinessEvidenceReview result={readinessResult} onCitationOpen={handleCitationOpen} />
                                                        ) : null}
                                                        {shouldShowCaseEvidenceDigest && !isStructuredAnswerTextView ? (
                                                            <CaseEvidenceDigestReview digest={caseEvidenceDigest} onCitationOpen={handleCitationOpen} />
                                                        ) : null}
                                                        {!isStructuredAnswerTextView ? <NativeRagTablePreview previews={nativeTablePreviews} onOpen={handleNativeTablePreviewOpen} onRetry={handleRetryNativeTablePreview} /> : null}
                                                        {!isStructuredAnswerTextView && capabilityRegistryReview ? (
                                                            <JaneCapabilityExplorer answer={answer} onUseExample={handleUseCapabilityExample} />
                                                        ) : !isStructuredAnswerTextView && calendarDraftReview ? (
                                                            <SchedulingOrderCalendarDraftReview answer={answer} onOpenCalendarPanel={() => handleOpenCalendarPanel(message.id)} />
                                                        ) : isEmailDraft && !isStructuredAnswerTextView ? (
                                                            <div className="library-rag-email-draft">
                                                                <div className="library-rag-email-draft-subject">
                                                                    <span className="library-rag-email-draft-subject-text">{draftSubject}</span>
                                                                    <button
                                                                        type="button"
                                                                        className="library-rag-email-draft-send"
                                                                        onClick={() => openRagEmailDraftWindow(draftSubject, finalDisplayedAnswerText)}
                                                                        aria-label="Open email draft"
                                                                        title="Open email draft"
                                                                    >
                                                                        <Send size={16} />
                                                                    </button>
                                                                </div>
                                                                <div className="library-rag-email-draft-content">
                                                                    <div className="library-rag-answer-text">
                                                                        <AnimatedRagAnswerContent
                                                                            text={finalDisplayedAnswerText}
                                                                            answerMode={answer?.mode}
                                                                            citations={displayedCitations}
                                                                            messageId={message.id}
                                                                            onCitationOpen={handleCitationOpen}
                                                                            animate={shouldAnimateAnswerText}
                                                                            complete={isIncomingAnswerComplete}
                                                                            onAnimationComplete={() => clearRagIncomingAnswerMessage(message.id)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : shouldSuppressLegalDraftAnswerText || (hasStructuredAnswerComponent && !isStructuredAnswerTextView && !isDigestStructuredAnswer) ? null : (
                                                            <div className="library-rag-answer-text">
                                                                <AnimatedRagAnswerContent
                                                                    text={finalDisplayedAnswerText}
                                                                    answerMode={answer?.mode}
                                                                    citations={displayedCitations}
                                                                    messageId={message.id}
                                                                    onCitationOpen={handleCitationOpen}
                                                                    animate={shouldAnimateAnswerText}
                                                                    complete={isIncomingAnswerComplete}
                                                                    onAnimationComplete={() => clearRagIncomingAnswerMessage(message.id)}
                                                                />
                                                            </div>
                                                        )}
                                                        {isConfirmableActionPending && !isWorkspaceNavigationPending && (
                                                            <>
                                                                <div className="library-rag-navigation-status pending">{confirmableActionDetails.pendingText}</div>
                                                                <div className={`library-rag-navigation-permission-box${getConfirmableActionPayloadForMessage(message, confirmableActionDetails)?.field_review ? ' field-review' : ''}${isFolderSelectionConfirmableAction ? ' folder-selection' : ''}`} role="group" aria-label={confirmableActionDetails.title}>
                                                                    <div className="library-rag-navigation-permission-title">
                                                                        <span>{confirmableActionDetails.title}</span>
                                                                        {legalDraftPreviewPayload && (
                                                                            <button
                                                                                type="button"
                                                                                className="library-rag-navigation-permission-preview"
                                                                                onClick={() => handleOpenLegalDraftPreview(message, confirmableActionDetails)}
                                                                                aria-label={`Preview ${confirmableActionDetails.subject}`}
                                                                                title="Preview draft"
                                                                            >
                                                                                <GalleryVerticalEnd size={13} />
                                                                                <span>{String(ragDraftPreviewMessageId) === String(message.id) ? 'Preview Open' : 'Preview'}</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-copy">
                                                                        <strong>{confirmableActionDetails.subject}</strong>
                                                                        {confirmableActionDetails.summary && (
                                                                            <span className="library-rag-navigation-permission-summary">
                                                                                <span className="library-rag-navigation-permission-summary-icon">{renderPermissionActionIcon(confirmableActionDetails.type)}</span>
                                                                                <span className='library-rag-navigation-permission-summary-text'>{confirmableActionDetails.summary}</span>
                                                                            </span>
                                                                        )}
                                                                        {isFolderSelectionConfirmableAction && (
                                                                            <div className="library-rag-folder-selection-action">
                                                                                <span className="library-rag-folder-selection-label">
                                                                                    Destination folder:
                                                                                    <strong>{confirmableFolderSelection.hasFolder ? confirmableFolderSelection.displayFolder : 'Not selected'}</strong>
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="library-rag-navigation-permission-preview"
                                                                                    onClick={() => setRagFolderPickerActionMessageId(message.id)}
                                                                                    disabled={isConfirmableActionBusy || !confirmableFolderSelection.caseId}
                                                                                    aria-label="Choose destination folder"
                                                                                    title="Choose destination folder"
                                                                                >
                                                                                    <Folder size={13} />
                                                                                    <span>{confirmableFolderSelection.hasFolder ? 'Change folder' : 'Choose folder'}</span>
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        {renderConfirmableFieldReview(message, confirmableActionDetails)}
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-actions">
                                                                        <button
                                                                            type="button"
                                                                            className={`action alt perm-action${isConfirmableActionBusy && !isConfirmableActionDeclining ? ' is-busy' : ''}`}
                                                                            onClick={() => handleConfirmConfirmableAction(message)}
                                                                            disabled={isConfirmableActionCommitBlocked}
                                                                            aria-busy={isConfirmableActionBusy}
                                                                        >
                                                                            {isConfirmableActionBusy && !isConfirmableActionDeclining ? (
                                                                                <>
                                                                                    <Loader2 size={14} className="spinner" aria-hidden="true" />
                                                                                    <span>{confirmableActionDetails.type.startsWith('save_') ? 'Saving' : 'Working'}</span>
                                                                                </>
                                                                            ) : confirmableActionDetails.confirmLabel}
                                                                        </button>
                                                                        {canSaveLegalDraftToChat && (
                                                                            <button
                                                                                type="button"
                                                                                className="action quaternary perm-secondary"
                                                                                onClick={() => handleConfirmConfirmableAction(message, { destinationOverride: 'chat' })}
                                                                                disabled={isConfirmableActionBusy || ragSaving || !hasPersistedMessageIdentity}
                                                                            >
                                                                                Save to chat
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="action quaternary perm-secondary"
                                                                            onClick={() => handleCancelConfirmableAction(message)}
                                                                            disabled={isConfirmableActionBusy || ragSaving || !hasPersistedMessageIdentity}
                                                                        >
                                                                            {isConfirmableActionDeclining ? (
                                                                                <>
                                                                                    <Loader2 size={14} className="spinner" aria-hidden="true" />
                                                                                    <span>Canceling</span>
                                                                                </>
                                                                            ) : confirmableActionDetails.cancelLabel}
                                                                        </button>
                                                                    </div>
                                                                    {confirmableActionError && (
                                                                        <div className="library-rag-navigation-action-error" role="alert">
                                                                            {confirmableActionError}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                        {isConfirmableActionResolved && !isExplicitlyAuthorizedFinancialAction && !isWorkspaceNavigationPending && (
                                                            <div className={`library-rag-navigation-status${confirmableActionDetails.status === 'confirmed' ? ' confirmed' : ''}`}>
                                                                {confirmableActionDetails.status === 'confirmed'
                                                                    ? `${confirmableActionDetails.confirmed_by || 'You'} confirmed ${confirmableActionDetails.subject}.`
                                                                    : getDeclinedConfirmableActionText(confirmableActionDetails)}
                                                            </div>
                                                        )}
                                                        {isWorkspaceCaseSelectionPending && (
                                                            <>
                                                                <div className="library-rag-navigation-status pending">{workspaceCaseSelectionDetails.pendingText}</div>
                                                                <div className="library-rag-navigation-permission-box" role="group" aria-label={workspaceCaseSelectionDetails.title}>
                                                                    <div className="library-rag-navigation-permission-title">
                                                                        <span>{workspaceCaseSelectionDetails.title}</span>
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-copy">
                                                                        <strong>{workspaceCaseSelectionDetails.subject}</strong>
                                                                        <span className="library-rag-navigation-permission-summary">
                                                                            <span className="library-rag-navigation-permission-summary-icon">{renderPermissionActionIcon('workspace_navigation')}</span>
                                                                            <span className="library-rag-navigation-permission-summary-text">{workspaceCaseSelectionDetails.summary || 'Choose the case to continue this workspace action.'}</span>
                                                                        </span>
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-actions case-selection">
                                                                        {workspaceCaseSelectionDetails.caseMatches.slice(0, 6).map((caseMatch, caseMatchIndex) => {
                                                                            const caseId = Number(caseMatch.case_id || caseMatch.id || 0);
                                                                            const caseName = String(caseMatch.case_name || caseMatch.name || `Case ${caseId}`).trim();
                                                                            return (
                                                                                <button
                                                                                    key={`case-selection-${message.id}-${caseId || caseMatchIndex}`}
                                                                                    type="button"
                                                                                    className="action quaternary perm-action"
                                                                                    onClick={() => handleConfirmWorkspaceCaseSelection(message, caseMatch)}
                                                                                    disabled={isWorkspaceCaseSelectionCommitBlocked || !caseId}
                                                                                    title={caseId ? `Use case ${caseId}` : 'Case ID unavailable'}
                                                                                >
                                                                                    {isWorkspaceCaseSelectionBusy ? 'Working...' : (ragSaving || !hasPersistedMessageIdentity ? 'Saving...' : `${caseName}${caseId ? ` #${caseId}` : ''}`)}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                    {confirmableActionError && (
                                                                        <div className="library-rag-navigation-action-error" role="alert">
                                                                            {confirmableActionError}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                        {isWorkspaceNavigationPending && (
                                                            <>
                                                                <div className="library-rag-navigation-status pending">{workspaceNavigationProgressText}</div>
                                                                <div className="library-rag-navigation-permission-box" role="group" aria-label={`Permission to navigate to ${workspaceNavigationDetails.caseLabel}`}>
                                                                    <div className="library-rag-navigation-permission-title">
                                                                        <span>{workspaceNavigationDetails.title}</span>
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-copy">
                                                                        <strong>{workspaceNavigationDetails.subject}</strong>
                                                                        <span className="library-rag-navigation-permission-summary">
                                                                            <span className="library-rag-navigation-permission-summary-icon">{renderPermissionActionIcon('workspace_navigation')}</span>
                                                                            <span className="library-rag-navigation-permission-summary-text">{workspaceNavigationDetails.summary}</span>
                                                                        </span>
                                                                    </div>
                                                                    <div className="library-rag-navigation-permission-actions">
                                                                        <button type="button" className="action alt perm-action" onClick={() => handleConfirmWorkspaceNavigation(message)} disabled={isWorkspaceNavigationCommitBlocked}>{isConfirmableActionBusy && !isConfirmableActionDeclining ? 'Opening...' : (ragSaving || !hasPersistedMessageIdentity ? 'Saving...' : 'Allow navigation')}</button>
                                                                        <button type="button" className="action quaternary perm-secondary" onClick={() => handleCancelWorkspaceNavigation(message)} disabled={isWorkspaceNavigationCommitBlocked}>
                                                                            {isConfirmableActionDeclining ? <><Loader2 size={14} className="spinner" aria-hidden="true" /> Canceling</> : 'Cancel'}
                                                                        </button>
                                                                    </div>
                                                                    {confirmableActionError && <div className="library-rag-navigation-action-error" role="alert">{confirmableActionError}</div>}
                                                                </div>
                                                            </>
                                                        )}
                                                        {!isWorkspaceNavigationPending && workspaceNavigationDetails && (
                                                            <div className={`library-rag-navigation-status${workspaceNavigationDetails.status === 'confirmed' ? ' confirmed' : ''}`}>
                                                                {workspaceNavigationDetails.status === 'confirmed'
                                                                    ? 'You confirmed navigation.'
                                                                    : workspaceNavigationDetails.status === 'declined'
                                                                        ? 'User action cancelled.'
                                                                        : `This message can take you directly to ${workspaceNavigationDetails.caseLabel}.`}
                                                            </div>
                                                        )}
                                                        {showCompletedAnswerExtras && displayedOutputSources.length > 0 && (
                                                            <div className="library-rag-output-files" aria-label="Generated output files">
                                                                <span className="library-rag-output-files-title">Output</span>
                                                                <div className="library-rag-output-files-list">
                                                                    {renderResponseSourceCards(displayedOutputSources, 'inline-output')}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {showCompletedAnswerExtras && !hasIntegratedOutlookDigestSources && isSourcesOpen && visibleEvidenceSources.length > 0 && (
                                                            <div className="library-rag-citations">
                                                                {visibleEvidenceSources.length > 0 && (
                                                                    <div className="library-rag-citation-group sources">
                                                                        <span className="library-rag-citations-title">Sources</span>
                                                                        <div className={citationListClassName}>
                                                                            {renderResponseSourceCards(visibleEvidenceSources, 'sources')}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {showCompletedAnswerExtras && !hasIntegratedOutlookDigestSources && isSourcesOpen && renderSourceChunkTray(nonGeneratedMessageFiles, message.id, false)}
                                                    </section>
                                                ) : isEditingUserMessage ? (
                                                    <form className="library-rag-inline-edit" onSubmit={(event) => handleInlineRagEditSubmit(event, message, messageIndex)}>
                                                        <textarea
                                                            value={editingRagDraft}
                                                            onChange={(event) => setEditingRagDraft(event.target.value)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Escape') {
                                                                    event.preventDefault();
                                                                    handleCancelInlineRagEdit();
                                                                }
                                                                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    handleInlineRagEditSubmit(event, message, messageIndex);
                                                                }
                                                            }}
                                                            autoFocus
                                                            rows={Math.min(8, Math.max(2, editingRagDraft.split('\n').length))}
                                                        />
                                                        <div className="library-rag-inline-edit-actions">
                                                            <button type="button" className="action quaternary" onClick={handleCancelInlineRagEdit}>Cancel</button>
                                                            <button type="submit" className="action alt" disabled={editingRagDraft.trim().length < 2}>Submit</button>
                                                        </div>
                                                    </form>
                                                ) : (
                                                    <>
                                                        <NativeRagTablePreview previews={nativeTablePreviews} onOpen={handleNativeTablePreviewOpen} onRetry={handleRetryNativeTablePreview} />
                                                        <div
                                                            className="library-rag-message-text"
                                                            onPointerDown={message.role === 'user' ? () => handleMobileUserMessagePressStart(message) : undefined}
                                                            onPointerUp={message.role === 'user' ? handleMobileUserMessagePressEnd : undefined}
                                                            onPointerLeave={message.role === 'user' ? handleMobileUserMessagePressEnd : undefined}
                                                            onPointerCancel={message.role === 'user' ? handleMobileUserMessagePressEnd : undefined}
                                                            onContextMenu={message.role === 'user' ? (event) => {
                                                                event.preventDefault();
                                                                openRagMobileUserActions(message);
                                                            } : undefined}
                                                        >
                                                            {(message.content || '').replace(/https?:\/\/(?:www\.)?casedb\.co\/(?:case|file|lead|document)\/\d+/gi, '').replace(/https?:\/\/(?:www\.)?casedb\.co\/reports\?[^\s]+/gi, '').split('\n').map(l => l.trim()).filter(Boolean).map((line, lineIndex) => (
                                                                <p key={`rag-message-line-${message.id}-${lineIndex}`}>{line}</p>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                                {message.role === 'user' && promptReferences.length > 0 && (
                                                    <div className={`library-rag-reference-list in-message${promptReferences.length > 2 ? ' has-more' : ''}${promptReferencesExpanded ? ' expanded' : ''}`}>
                                                        {visiblePromptReferences.map((reference) => {
                                                            const referenceHref = getRagCitationHref(reference);
                                                            return referenceHref ? (
                                                                <a
                                                                    key={`message-reference-${message.id}-${reference.id}`}
                                                                    href={referenceHref}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="library-rag-reference-chip"
                                                                    onClick={(event) => handlePromptReferenceOpen(event, reference)}
                                                                >
                                                                    {renderRagReferenceChipLabel(reference)}
                                                                </a>
                                                            ) : (
                                                                <span key={`message-reference-${message.id}-${reference.id}`} className="library-rag-reference-chip">{renderRagReferenceChipLabel(reference)}</span>
                                                            );
                                                        })}
                                                        {promptReferences.length > 2 && (
                                                            <button
                                                                type="button"
                                                                className="library-rag-reference-more"
                                                                onClick={() => togglePromptReferenceExpansion(message.id)}
                                                                aria-expanded={promptReferencesExpanded}
                                                            >
                                                                {promptReferencesExpanded ? 'Less' : `More +${hiddenPromptReferenceCount}`}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {message.role === 'assistant' && messagePersistenceState && (
                                                    <div className="library-rag-persistence-notice" role="alert">
                                                        <span>{messagePersistenceState.status === 'retrying' ? 'Saving this response again…' : messagePersistenceState.message}</span>
                                                        {messagePersistenceState.status !== 'retrying' && (
                                                            <button type="button" onClick={() => handleRetryRagPersistence(message.id)}>Retry save</button>
                                                        )}
                                                        {messagePersistenceState.status === 'retrying' && <Loader2 size={14} className="spinner" aria-hidden="true" />}
                                                    </div>
                                                )}
                                                {message.role === 'user' && (
                                                    <div className="library-rag-message-footer user">
                                                        {(messageAuthorLabel || messageTimeLabel) && (
                                                            <div className="library-rag-message-meta">
                                                                {messageAuthorLabel && <span className="library-rag-message-author">{messageAuthorLabel}</span>}
                                                                {messageAuthorLabel && messageTimeLabel && <span aria-hidden="true">·</span>}
                                                                {messageTimeLabel && <time dateTime={messageDateTimeValue}>{messageTimeLabel}</time>}
                                                            </div>
                                                        )}
                                                        {!isEditingUserMessage && <div className="library-rag-user-actions" aria-label="User message actions">
                                                            <button
                                                                type="button"
                                                                className={copiedUserMessageId === message.id ? 'copied' : ''}
                                                                onClick={() => {
                                                                    setRagMobileUserActionMessageId(null);
                                                                    handleCopyUserMessage(message);
                                                                }}
                                                                aria-label={copiedUserMessageId === message.id ? 'Copied message' : 'Copy message'}
                                                                title={copiedUserMessageId === message.id ? 'Copied' : 'Copy message'}
                                                            >
                                                                <Copy size={16} />
                                                                <span className="library-rag-action-label">{copiedUserMessageId === message.id ? 'Copied' : 'Copy'}</span>
                                                            </button>
                                                            <button type="button" onClick={() => {
                                                                setRagMobileUserActionMessageId(null);
                                                                handleEditUserMessage(message, messageIndex);
                                                            }} aria-label="Edit message" title="Edit message">
                                                                <Pen size={16} />
                                                                <span className="library-rag-action-label">Edit</span>
                                                            </button>
                                                        </div>}
                                                    </div>
                                                )}
                                                {message.role === 'assistant' && (
                                                    <div className="library-rag-message-footer assistant">
                                                        {showAssistantActions && <div className="library-rag-assistant-actions" aria-label="AI response actions">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRedoAssistantResponse(messageIndex)}
                                                            disabled={loading || ragSaving || !!requestControllerRef.current || !!forkingRagMessageId || !hasPersistedMessageIdentity}
                                                            aria-label="Retry this response"
                                                            title="Retry this response"
                                                        >
                                                            <RefreshCw size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleForkAssistantResponse(message)}
                                                            disabled={loading || ragSaving || !!requestControllerRef.current || !!forkingRagMessageId || !Number.isFinite(Number(message.id))}
                                                            aria-label="Fork chat from this response"
                                                            title="Fork chat from this response"
                                                        >
                                                            {forkingRagMessageId === message.id ? <Loader2 size={16} className="spinner" /> : <Split size={16} className="library-rag-fork-icon" />}
                                                        </button>
                                                        {assistantFeedbackAvailable && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={assistantFeedbackRating === 'up' ? 'active' : ''}
                                                                    onClick={() => handleRateAssistantResponse(message, 'up')}
                                                                    disabled={!!assistantFeedbackSaving}
                                                                    aria-label={assistantFeedbackRating === 'up' ? 'Remove helpful rating' : 'Mark response helpful'}
                                                                    aria-pressed={assistantFeedbackRating === 'up'}
                                                                    title={assistantFeedbackRating === 'up' ? 'Remove helpful rating' : 'Helpful'}
                                                                >
                                                                    {assistantFeedbackSaving === 'up' ? <Loader2 size={16} className="spinner" /> : <ThumbsUp size={16} />}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={assistantFeedbackRating === 'down' ? 'active' : ''}
                                                                    onClick={() => handleRateAssistantResponse(message, 'down')}
                                                                    disabled={!!assistantFeedbackSaving}
                                                                    aria-label={assistantFeedbackRating === 'down' ? 'Remove not helpful rating' : 'Mark response not helpful'}
                                                                    aria-pressed={assistantFeedbackRating === 'down'}
                                                                    title={assistantFeedbackRating === 'down' ? 'Remove not helpful rating' : 'Not helpful'}
                                                                >
                                                                    {assistantFeedbackSaving === 'down' ? <Loader2 size={16} className="spinner" /> : <ThumbsDown size={16} />}
                                                                </button>
                                                                {assistantFeedbackStatus && (
                                                                    <span
                                                                        className={`library-rag-feedback-status ${assistantFeedbackStatus.kind}`}
                                                                        role={assistantFeedbackStatus.kind === 'error' ? 'alert' : 'status'}
                                                                    >
                                                                        {assistantFeedbackStatus.message}
                                                                        {assistantFeedbackStatus.kind === 'error' && (
                                                                            <button
                                                                                type="button"
                                                                                className="library-rag-feedback-retry"
                                                                                onClick={() => handleRateAssistantResponse(message, assistantFeedbackStatus.rating, assistantFeedbackStatus.action)}
                                                                                disabled={!!assistantFeedbackSaving}
                                                                            >
                                                                                Retry
                                                                            </button>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                        {messageHasSources && !hasIntegratedOutlookDigestSources && (
                                                            <button
                                                                type="button"
                                                                className={isSourcesOpen ? 'active' : ''}
                                                                onClick={() => toggleCitationExpansion(message.id)}
                                                                aria-label={isSourcesOpen ? 'Hide response sources' : 'Show response sources'}
                                                                aria-expanded={isSourcesOpen}
                                                                title={isSourcesOpen ? 'Hide sources' : 'Show sources'}
                                                            >
                                                                <SquareLibrary size={16} />
                                                            </button>
                                                        )}
                                                        {messageHasResponseDetails && (
                                                            <button
                                                                type="button"
                                                                className={isResponseDetailsOpen ? 'active' : ''}
                                                                onClick={() => {
                                                                    setShowRagSourceHistory(false);
                                                                    setChunkPreview(null);
                                                                    setRagResponseDetailsMessageId(prev => String(prev) === String(message.id) ? null : message.id);
                                                                }}
                                                                aria-label={isResponseDetailsOpen ? 'Hide response details' : 'Show response details'}
                                                                aria-expanded={isResponseDetailsOpen}
                                                                title={isResponseDetailsOpen ? 'Hide response details' : 'Show response details'}
                                                            >
                                                                <Info size={16} />
                                                            </button>
                                                        )}
                                                        {structuredPanelAction ? (
                                                            <button
                                                                type="button"
                                                                className={structuredPanelAction.active ? 'active' : ''}
                                                                onClick={structuredPanelAction.onClick}
                                                                aria-label={structuredPanelAction.ariaLabel}
                                                                aria-expanded={structuredPanelAction.active}
                                                                title={structuredPanelAction.title}
                                                            >
                                                                <GalleryVerticalEnd size={16} />
                                                                <span className="library-rag-action-label">{structuredPanelAction.label}</span>
                                                            </button>
                                                        ) : null}
                                                        {hasStructuredAnswerComponent && finalDisplayedAnswerText.trim() ? (
                                                            <button
                                                                type="button"
                                                                className={isStructuredAnswerTextView ? 'active' : ''}
                                                                onClick={() => toggleRagComponentTextView(structuredAnswerComponentType)}
                                                                aria-label={isStructuredAnswerTextView ? `See ${structuredAnswerComponentLabel} responses as components` : `See ${structuredAnswerComponentLabel} responses as text`}
                                                                title={isStructuredAnswerTextView ? `See ${structuredAnswerComponentLabel} as component` : `See ${structuredAnswerComponentLabel} as text`}
                                                            >
                                                                <TextAlignStart size={16} />
                                                                <span className="library-rag-action-label">{isStructuredAnswerTextView ? 'Component' : 'Text'}</span>
                                                            </button>
                                                        ) : null}
                                                        {workspaceNavigationDetails && !isWorkspaceNavigationPending && (
                                                            <button
                                                                type="button"
                                                                className="library-rag-navigation-action"
                                                                onClick={() => handleConfirmWorkspaceNavigation(message)}
                                                                aria-label={`Navigate to ${workspaceNavigationDetails.caseLabel}`}
                                                                title={`Navigate to ${workspaceNavigationDetails.caseLabel}`}
                                                            >
                                                                <ExternalLink size={16} />
                                                                <span className="library-rag-action-label">Navigate</span>
                                                            </button>
                                                        )}
                                                        </div>}
                                                        {assistantForkError && (
                                                            <span className="library-rag-feedback-status error" role="alert">
                                                                <span>{assistantForkError}</span>
                                                                <button
                                                                    type="button"
                                                                    className="library-rag-feedback-retry"
                                                                    onClick={() => handleForkAssistantResponse(message)}
                                                                    disabled={!!forkingRagMessageId}
                                                                >
                                                                    Retry
                                                                </button>
                                                            </span>
                                                        )}
                                                        {assistantRerunError && (
                                                            <span className="library-rag-feedback-status error" role="alert">{assistantRerunError}</span>
                                                        )}
                                                        {(messageAuthorLabel || messageTimeLabel) && shouldShowJaneAssistantAttribution(message, inlineBackgroundRun) && (
                                                            <div className="library-rag-message-meta">
                                                                {messageAuthorLabel && <span className="library-rag-message-author">{messageAuthorLabel}</span>}
                                                                {messageAuthorLabel && messageTimeLabel && <span aria-hidden="true">·</span>}
                                                                {messageTimeLabel && <time dateTime={messageDateTimeValue}>{messageTimeLabel}</time>}
                                                                {messageTimeLabel && isRagRerunMessage(message) && <span className="library-rag-message-rerun">Reran</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                                </>}
                                {!ragChatLoading && unanchoredBackgroundRuns.map(run => (
                                    <article key={`background-work-${getJaneBackgroundRunIdentity(run)}`} className="library-rag-message assistant">
                                        <div className="library-rag-message-body">{renderInlineBackgroundRun(run)}</div>
                                    </article>
                                ))}
                            </div>

                            {pendingRagChatDelete && (
                                <Modal
                                    title={pendingRagChatDelete.is_shared ? 'Remove shared chat?' : 'Delete chat?'}
                                    onClose={handleCancelDeleteRagChat}
                                    footer={
                                        <div className="modal-footer-actions">
                                            <button type="button" className="action alt" onClick={handleCancelDeleteRagChat} disabled={ragChatDeleteBusy}>Cancel</button>
                                            <button type="button" className="action danger" onClick={handleConfirmDeleteRagChat} disabled={ragChatDeleteBusy}>
                                                {ragChatDeleteBusy ? <><Loader2 size={15} className="spinner" /> {pendingRagChatDelete.is_shared ? 'Removing…' : 'Deleting…'}</> : (pendingRagChatDelete.is_shared ? 'Remove chat' : 'Delete chat')}
                                            </button>
                                        </div>
                                    }
                                >
                                    <div className="modal-content-wrapper library-rag-delete-modal">
                                        <p>
                                            {pendingRagChatDelete.is_shared
                                                ? `Remove “${cleanRagChatTitle(pendingRagChatDelete.title) || 'Untitled chat'}” from your chat list?`
                                                : `Delete “${cleanRagChatTitle(pendingRagChatDelete.title) || 'Untitled chat'}”? This cannot be undone.`}
                                        </p>
                                        {ragChatDeleteError && <div className="library-rag-delete-error" role="alert">{ragChatDeleteError} Nothing was removed.</div>}
                                    </div>
                                </Modal>
                            )}

                            {pendingRagEdit && (
                                <Modal
                                    title="Edit in a new chat?"
                                    onClose={handleCancelRagEditConfirm}
                                    footer={
                                        <div className="modal-footer-actions">
                                            <button type="button" className="action alt" onClick={handleCancelRagEditConfirm} disabled={ragBranchActionBusy}>Cancel</button>
                                            <button type="button" className="action" onClick={handleConfirmRagEdit} disabled={ragBranchActionBusy}>
                                                {ragBranchActionBusy ? <><Loader2 size={15} className="spinner" /> Starting…</> : 'Edit in new chat'}
                                            </button>
                                        </div>
                                    }
                                >
                                    <div className="modal-content-wrapper library-rag-edit-modal-content">
                                        <p>Jane will start a new chat from before this request and use your edited prompt. This conversation will stay intact.</p>
                                        <div className="library-rag-edit-preview">{pendingRagEdit.prompt}</div>
                                        {ragBranchActionError && <div className="library-rag-branch-error" role="alert">{ragBranchActionError}</div>}
                                    </div>
                                </Modal>
                            )}

                            {pendingRagRedo && (
                                <Modal
                                    title="Rerun this response?"
                                    onClose={handleCancelRagRedoConfirm}
                                    footer={
                                        <div className="modal-footer-actions">
                                            <button type="button" className="action alt" onClick={handleCancelRagRedoConfirm} disabled={ragBranchActionBusy}>Cancel</button>
                                            <button type="button" className="action" onClick={handleConfirmRagRedo} disabled={ragBranchActionBusy}>
                                                {ragBranchActionBusy ? <><Loader2 size={15} className="spinner" /> Starting…</> : 'Rerun'}
                                            </button>
                                        </div>
                                    }
                                >
                                    <div className="modal-content-wrapper library-rag-edit-modal-content">
                                        <p>Jane will replace this response here. Your other messages will stay in this chat.</p>
                                        <div className="library-rag-edit-preview">{pendingRagRedo.prompt}</div>
                                        {ragBranchActionError && <div className="library-rag-branch-error" role="alert">{ragBranchActionError}</div>}
                                    </div>
                                </Modal>
                            )}

                            {sharingRagChat && (
                                <Modal
                                    title="Share chat"
                                    onClose={handleCloseShareRagChat}
                                    wide
                                    overflowy={true}
                                    footer={
                                        <div className="modal-footer-actions">
                                            <button type="button" className="action alt" onClick={handleCloseShareRagChat} disabled={shareChatSaving}>Cancel</button>
                                            <button
                                                type="button"
                                                className="action"
                                                onClick={handleShareRagChatSubmit}
                                                disabled={shareChatSaving || (selectedShareUserIds.length === 0 && !(sharingRagChat.shared_user_ids || sharingRagChat.shared_users || []).length)}
                                            >
                                                {shareChatSaving ? 'Saving…' : 'Save sharing'}
                                            </button>
                                        </div>
                                    }
                                >
                                    <div className="modal-content-wrapper library-rag-share-modal">
                                        <p>Give one or more users access to “{cleanRagChatTitle(sharingRagChat.title) || 'Untitled chat'}”. They’ll see the same live chat thread and can continue it.</p>
                                        <div className="library-rag-share-picker">
                                            <div className="library-rag-share-search-wrap">
                                                <label className="subtext" htmlFor="library-rag-share-user-search">Add Users</label>
                                                <input
                                                    id="library-rag-share-user-search"
                                                    className="form-control"
                                                    type="text"
                                                    value={shareSearch}
                                                    onFocus={() => setShowShareUserDropdown(true)}
                                                    onBlur={() => setTimeout(() => setShowShareUserDropdown(false), 150)}
                                                    onChange={(event) => {
                                                        setShareSearch(event.target.value);
                                                        setShareHighlightIndex(0);
                                                        setShowShareUserDropdown(true);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        const filtered = getFilteredShareUsers();
                                                        if (event.key === 'ArrowDown') {
                                                            event.preventDefault();
                                                            setShareHighlightIndex(index => (index + 1) % Math.max(filtered.length, 1));
                                                        } else if (event.key === 'ArrowUp') {
                                                            event.preventDefault();
                                                            setShareHighlightIndex(index => (index - 1 + Math.max(filtered.length, 1)) % Math.max(filtered.length, 1));
                                                        } else if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            addShareUser(filtered[shareHighlightIndex]);
                                                        } else if (event.key === 'Escape') {
                                                            setShowShareUserDropdown(false);
                                                        }
                                                    }}
                                                    placeholder="Type to search users..."
                                                    autoFocus
                                                />
                                                {showShareUserDropdown && (
                                                    <div className="autocomplete-dropdown library-rag-share-dropdown">
                                                        {(() => {
                                                            const filtered = getFilteredShareUsers();
                                                            if (filtered.length === 0) return <div className="dropdown-empty">No matches</div>;
                                                            return filtered.map((shareUser, index) => (
                                                                <div
                                                                    key={shareUser.id}
                                                                    className={`dropdown-item ${index === shareHighlightIndex ? 'highlight' : ''}`}
                                                                    onMouseDown={() => addShareUser(shareUser)}
                                                                >
                                                                    {shareUser.name || `User ${shareUser.id}`} <span className="subtext">(@{shareUser.user || shareUser.id})</span>
                                                                </div>
                                                            ));
                                                        })()}
                                                    </div>
                                                )}
                                                <div className="library-rag-share-hint">Search by name, username, or ID. Selected values are saved as user IDs.</div>
                                            </div>
                                            <div className="library-rag-share-selected">
                                                <label className="subtext">Shared with</label>
                                                {selectedShareUserIds.map((id, index) => {
                                                    const selectedUser = shareUsers.find(shareUser => Number(shareUser.id) === id);
                                                    return (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            className={`library-rag-share-user-chip ${index === selectedShareUserIds.length - 1 ? 'active' : ''}`}
                                                            onClick={() => removeShareUser(id)}
                                                            title="Remove user"
                                                        >
                                                            {selectedUser ? `${selectedUser.name || `User ${id}`} (@${selectedUser.user || id})` : `User ID ${id}`}
                                                        </button>
                                                    );
                                                })}
                                                {selectedShareUserIds.length === 0 && <div className="library-rag-share-empty">No users selected</div>}
                                            </div>
                                        </div>
                                        {shareChatStatus && <div className="library-rag-share-status">{shareChatStatus}</div>}
                                    </div>
                                </Modal>
                            )}

                            {!ragThreadAtBottom && (
                                <button
                                    type="button"
                                    className={`library-rag-scroll-bottom-button${isChatRequestBlocked || ragCanceling || ragCancelError ? ' above-active-request' : ''}`}
                                    onClick={scrollRagThreadToBottom}
                                    aria-label="Scroll to latest message"
                                    title="Scroll to latest message"
                                >
                                    <ArrowDown size={18} />
                                </button>
                            )}

                            {(isChatRequestBlocked || ragCanceling || ragCancelError) && (
                                <div className="library-rag-active-request-banner" ref={ragActiveRequestBannerRef}>
                                    <span className="library-rag-active-request-copy">
                                        <strong>{ragCanceling ? 'Stopping the current response…' : (ragCancelError || (isReservedRagRequest
                                            ? 'A request is reserved but has not started.'
                                            : `${activeRequestOwnerLabel} already has Jane working in this chat.`))}</strong>
                                    </span>
                                    {isChatRequestBlocked && canCancelOwnRagRequest && (
                                        <button
                                            type="button"
                                            className="library-rag-feedback-retry"
                                            onClick={handleCancelRagResponse}
                                            disabled={ragCanceling}
                                            aria-label={isReservedRagRequest ? 'Cancel reserved request' : 'Stop active request'}
                                        >
                                            {isReservedRagRequest ? 'Cancel request' : 'Stop'}
                                        </button>
                                    )}
                                </div>
                            )}
                            <form ref={ragComposerRef} className={`library-rag-composer${chunkSearchDraft ? ' has-clear' : ''}${ragListening ? ' listening' : ''}${ragPreflightExpanded ? ' preflight-open' : ''}`} onSubmit={handleChunkSearchSubmit}>
                                <div className="library-rag-composer-tools">
                                    <button
                                        type="button"
                                        className="library-rag-composer-tool"
                                        onClick={handleUploadClick}
                                        disabled={!hasJaneUserIdentity || ragChatLoading || ragChatFilesUploading}
                                        aria-label="Upload files to this chat"
                                        title="Upload files to this chat"
                                    >
                                        {ragChatFilesUploading ? <Loader2 size={16} className="spinner" /> : <Paperclip size={16} />}
                                        <span>Files</span>
                                    </button>
                                    <div
                                        className="library-rag-reference-wrap"
                                        ref={ragReferenceMenuRef}
                                    >
                                        <button type="button" className="library-rag-composer-tool" onClick={() => {
                                            setShowJaneAgentLibrary(false);
                                            setShowRagReferenceMenu(prev => !prev);
                                        }} aria-label="Open CaseDB source picker" title="Add CaseDB sources"><SquareLibrary size={16} /><span>Sources</span></button>
                                        {showRagReferenceMenu && (
                                            <div className="library-rag-reference-menu">
                                                <div className="library-rag-reference-linker">
                                                    <label>Link case document</label>
                                                    <RagReferenceContextSelect
                                                        value={ragReferenceContext}
                                                        onChange={(option) => {
                                                            setChunkPreview(null);
                                                            setRagReferenceContext(option);
                                                            setRagReferenceDraft('');
                                                        }}
                                                    />
                                                    {ragReferenceContext && (
                                                        <div className="library-rag-reference-picker">
                                                            <DocumentSearch
                                                                value={ragReferenceDraft}
                                                                onChange={(document) => {
                                                                    setChunkPreview(null);
                                                                    if (document) {
                                                                        setRagReferenceDraft(document);
                                                                        addRagReferenceFromDocument(document);
                                                                    } else {
                                                                        setRagReferenceDraft('');
                                                                    }
                                                                }}
                                                                case_id={getRagReferenceCaseId()}
                                                                lead_id={getRagReferenceLeadId()}
                                                                caseName={ragReferenceContext.label}
                                                            />
                                                            {getRagReferenceCaseId() && (
                                                                <button
                                                                    type="button"
                                                                    className="action tertiary"
                                                                    onClick={() => {
                                                                        setChunkPreview(null);
                                                                        setShowRagDocumentPicker(true);
                                                                    }}
                                                                >
                                                                    Browse...
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="library-rag-reference-manual">
                                                        <input
                                                            type="text"
                                                            placeholder="Or paste file ID / casedb.co/file URL"
                                                            value={typeof ragReferenceDraft === 'string' ? ragReferenceDraft : ''}
                                                            onChange={(event) => {
                                                                setChunkPreview(null);
                                                                setRagReferenceDraft(event.target.value);
                                                            }}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    addRagReferenceFromDraft();
                                                                }
                                                            }}
                                                        />
                                                        <button type="button" onClick={addRagReferenceFromDraft}>Add</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={`library-rag-composer-tool icon-only library-rag-preflight-info${ragPreflightExpanded ? ' active' : ''}`}
                                        onClick={() => setRagPreflightExpanded(prev => !prev)}
                                        aria-expanded={ragPreflightExpanded}
                                        aria-label={ragPreflightExpanded ? 'Hide Jane context options' : 'Show Jane context options'}
                                        title={ragPreflightExpanded ? 'Hide Jane context options' : 'Show Jane context options'}
                                    >
                                        <Info size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        className="library-rag-composer-tool"
                                        onClick={handleImproveRagPrompt}
                                        disabled={ragPromptImproving || chunkSearchDraft.trim().length < 2}
                                        aria-label="Improve this prompt"
                                        title="Rewrite this prompt with more detail before sending"
                                    >
                                        {ragPromptImproving ? <Loader2 size={16} className="spinner" /> : <Astroid size={16} />}
                                        <span>Improve</span>
                                    </button>
                                </div>
                                {renderRagPreSubmitContextStrip()}
                                <div className="library-rag-composer-actions">
                                    <label className="library-rag-composer-speed-control" title={JANE_RUN_MODE_OPTIONS.find(option => option.key === janeRunMode)?.description || 'Jane run speed'}>
                                        <select value={janeRunMode} onChange={handleJaneRunModeChange} aria-label="Jane run speed">
                                            {JANE_RUN_MODE_OPTIONS.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="library-rag-composer-speed-chevron" aria-hidden="true" />
                                    </label>
                                    <button
                                        type="button"
                                        className={`library-rag-composer-tool icon-only${ragListening ? ' active' : ''}`}
                                        onClick={handleRagVoiceToggle}
                                        aria-label={ragListening ? 'Stop voice dictation' : 'Start voice dictation'}
                                        title={ragSpeechSupported ? (ragListening ? 'Stop dictation' : 'Dictate prompt') : 'Voice dictation is not supported in this browser'}
                                    >
                                        {ragSpeechSupported ? <Mic size={16} /> : <MicOff size={16} />}
                                    </button>
                                    <div className={`library-rag-primary-action-group${showRagRunMenuControl ? ' has-run-menu' : ''}`}>
                                        <button
                                            type={isRagComposerCancelMode ? 'button' : 'submit'}
                                            className="library-rag-action-button primary-rag-action"
                                            disabled={isRagComposerCancelMode ? ragCanceling : (ragComposerDraftText.length < 2 || (isRagSendDisabled && !isRagResponseCancelable))}
                                            onClick={isRagComposerCancelMode ? handleCancelRagResponse : undefined}
                                            aria-label={isRagComposerCancelMode ? (ragCanceling ? 'Stopping response' : (ragCancelError ? 'Try stopping response again' : 'Stop response')) : 'Send message'}
                                            title={isRagComposerCancelMode ? (ragCanceling ? 'Stopping response' : (ragCancelError ? 'Try stopping response again' : 'Stop response')) : 'Send message'}
                                        >
                                            {isRagComposerCancelMode ? (ragCanceling ? <Loader2 size={16} className="spinner" aria-hidden="true" /> : <StopSquare size={16} fill={'var(--text-color)'} />) : <ArrowUp size={16} />}
                                        </button>
                                        {showRagRunMenuControl && (
                                            <div
                                                className="library-rag-run-menu-wrap"
                                                onMouseEnter={() => {
                                                    if (canUseRagRunMenu) setRagRunMenuOpen(true);
                                                }}
                                                onMouseLeave={() => {
                                                    setRagRunMenuOpen(false);
                                                }}
                                                onFocus={() => {
                                                    if (canUseRagRunMenu) setRagRunMenuOpen(true);
                                                }}
                                                onBlur={() => {
                                                    window.setTimeout(() => setRagRunMenuOpen(false), 120);
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className={`library-rag-action-button library-rag-run-menu-trigger${ragRunMenuOpen ? ' active' : ''}`}
                                                    disabled={!canUseRagRunMenu}
                                                    onClick={() => setRagRunMenuOpen(prev => !prev)}
                                                    aria-label="Open run options"
                                                    aria-expanded={ragRunMenuOpen}
                                                    title="Run options"
                                                >
                                                    <ChevronDown size={16} />
                                                </button>
                                                {ragRunMenuOpen && (
                                                    <div className="library-rag-run-menu" role="menu">
                                                        <button
                                                            type="button"
                                                            role="menuitem"
                                                            disabled={!canUseRagRunMenu}
                                                            onMouseDown={(event) => event.preventDefault()}
                                                            onClick={handleRerouteCurrentRun}
                                                        >
                                                            <span>Reroute the current run</span>
                                                            <small>Cancel and re-plan the prompt through Jane again.</small>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            role="menuitem"
                                                            disabled={!canUseRagRunMenu}
                                                            onMouseDown={(event) => event.preventDefault()}
                                                            onClick={handleRunCurrentResponseInBackground}
                                                        >
                                                            <span>Run current response in background</span>
                                                            <small>Best for full timelines and other long CaseDB scans.</small>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="library-rag-composer-body">
                                    {ragReferences.length > 0 && (
                                        <div className="library-rag-reference-list">
                                            {ragReferences.map((reference) => {
                                                const referenceLabel = getRagReferenceDisplayLabel(reference);
                                                return (
                                                    <span key={`active-reference-${reference.id}`} className="library-rag-reference-chip">
                                                        {renderRagReferenceChipLabel(reference)}
                                                        <button type="button" onClick={() => removeRagReference(reference.id)} aria-label={`Remove ${referenceLabel}`}>×</button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <textarea
                                        ref={inputRef}
                                        placeholder="Message Jane…"
                                        value={chunkSearchDraft}
                                        onChange={(event) => setChunkSearchDraft(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' && !event.shiftKey) {
                                                event.preventDefault();
                                                handleChunkSearchSubmit(event);
                                            }
                                        }}
                                        rows={1}
                                    />
                                    {(!hasJaneUserIdentity || ragSpeechStatus || ragPromptImproveStatus) && (
                                        <div className="library-rag-composer-status" role={!hasJaneUserIdentity ? 'alert' : 'status'}>
                                            {!hasJaneUserIdentity ? 'Your CaseDB session could not be verified. Refresh the page to use Jane.' : (ragPromptImproveStatus || ragSpeechStatus)}
                                        </div>
                                    )}
                                </div>
                                {/*chunkSearchDraft && <button type="button" className="library-rag-composer-clear" onClick={() => setChunkSearchDraft('')} aria-label="Clear prompt"><X size={16} /></button>*/}
                            </form>

                            {showRagDocumentPicker && getRagReferenceCaseId() && (
                                <MoveFolderPicker
                                    isOpen={showRagDocumentPicker}
                                    onClose={() => {
                                        setChunkPreview(null);
                                        setShowRagDocumentPicker(false);
                                    }}
                                    onSelect={(path, file) => {
                                        setChunkPreview(null);
                                        addRagReferenceFromDocument({
                                            ...file,
                                            file_path: file?.file_path || file?.key || path,
                                            name: file?.name || file?.file_name || path?.split('/').pop()
                                        });
                                        setShowRagDocumentPicker(false);
                                    }}
                                    fetchFolderContents={fetchRagReferenceFolderContents}
                                    caseName={ragReferenceContext?.label || 'Case'}
                                    currentFolderPath="{{Name}}"
                                    initialPath="{{Name}}"
                                    initialSelection=""
                                    selectMode="file"
                                    title="Choose case document"
                                    instructions="Navigate the case folders and select a file to add as direct RAG context."
                                    confirmLabel="Add Reference"
                                />
                            )}
                            {ragFolderPickerActionMessage && ragFolderPickerActionDetails && ragFolderPickerSelection?.caseId > 0 && (
                                <MoveFolderPicker
                                    isOpen={Boolean(ragFolderPickerActionMessage)}
                                    onClose={() => setRagFolderPickerActionMessageId(null)}
                                    onSelect={(path) => handleConfirmableFolderSelection(ragFolderPickerActionMessage, ragFolderPickerActionDetails, path)}
                                    fetchFolderContents={(currentFolder, page, pageSize) => fetchRagCaseFolderContents(ragFolderPickerSelection.caseId, currentFolder, page, pageSize)}
                                    caseName={ragFolderPickerSelection.caseName}
                                    currentFolderPath={ragFolderPickerSelection.initialPath || '{{Name}}'}
                                    initialPath={ragFolderPickerSelection.initialPath || '{{Name}}'}
                                    initialSelection={ragFolderPickerSelection.folderPath === '{{Name}}' ? '{{Name}}' : (ragFolderPickerSelection.folderPath ? `{{Name}}/${ragFolderPickerSelection.folderPath}` : '')}
                                    selectMode="folder"
                                    title="Choose destination folder"
                                    instructions="Navigate the case folders and choose where Jane should save the selected document."
                                    confirmLabel="Use Folder"
                                />
                            )}
                        </>
                    )}
                </main>
                {(showOutlookCitationReview || showRagSourceHistory || chunkPreview || timelineEvidencePreview || showRagDraftPreviewPanel || showRagPlannerInPreview || showRagResponseDetailsPreview || showRagCalendarPanel) && (
                    <aside className={`library-rag-preview-panel${showOutlookCitationReview ? ' outlook-review' : ''}`} aria-label={showOutlookCitationReview ? 'Cited Outlook email reviewer' : (showRagDraftPreviewPanel ? 'Draft document preview' : showRagCalendarPanel ? 'Case calendar planner' : showRagResponseDetailsPreview ? 'Jane response details' : (showRagPlannerInPreview ? 'Jane active planner' : (showRagSourceHistory ? 'Chat source history' : (timelineEvidencePreview ? 'Timeline source evidence' : 'Source document preview'))))}>
                        <div className="library-rag-preview-stack">
                            {showRagResponseDetailsPreview && ragResponseDetailsThought && (
                                <section className="library-rag-preview-planner response-details" aria-label="Jane response details">
                                    <div className="library-rag-active-thought-topline">
                                        <div className="library-rag-active-thought-heading">
                                            <span className="library-rag-active-thought-eyebrow">{ragResponseDetailsThought.label}</span>
                                            <h3 className="library-rag-active-thought-title">{ragResponseDetailsThought.title}</h3>
                                        </div>
                                        <button
                                            type="button"
                                            className="library-rag-preview-close"
                                            onClick={() => setRagResponseDetailsMessageId(null)}
                                            aria-label="Close response details"
                                            title="Close response details"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                    {ragResponseDetailsThought.prompt && (
                                        <div className="library-rag-active-thought-prompt">
                                            <span className="library-rag-active-thought-prompt-label">Prompt</span>
                                            <p>{ragResponseDetailsThought.prompt}</p>
                                        </div>
                                    )}
                                    <div className="library-rag-active-thought-meta">
                                        <span className="library-rag-active-thought-chip">{ragResponseDetailsThought.contextLabel}</span>
                                        {ragResponseDetailsThought.status && <span className="library-rag-active-thought-chip">{ragResponseDetailsThought.status}</span>}
                                        <span className="library-rag-active-thought-chip">{ragResponseDetailsThought.badgeLabel}</span>
                                    </div>
                                    {ragResponseDetailsThought.inspectorLoading && (
                                        <div className="library-rag-run-inspector-loading">
                                            <Loader2 size={16} className="spinner" />
                                            <span>Loading response details…</span>
                                        </div>
                                    )}
                                    {ragResponseDetailsThought.inspectorError && (
                                        <div className="library-rag-run-inspector-error">
                                            {ragResponseDetailsThought.inspectorError}
                                        </div>
                                    )}
                                    {ragResponseDetailsThought.runPlan ? renderJaneRunPlan(ragResponseDetailsThought.runPlan, { compact: false, complete: true }) : null}
                                    {renderJaneRunInspector(ragResponseDetailsThought.inspector, { hideRepeatedExecutionDetails: true })}
                                    {renderRagResponseDetailsBreakdown(ragResponseDetailsThought.plannerSteps, ragResponseDetailsThought.trace, ragResponseDetailsThought.turnContract)}
                                </section>
                            )}
                            {showRagPlannerInPreview && activeRagThought && (
                                <section className={`library-rag-preview-planner ${activeRagThought.tone}`} aria-label={activeRagThought.label} aria-live="polite">
                                    <div className="library-rag-active-thought-topline">
                                        <div className="library-rag-active-thought-heading">
                                            <span className="library-rag-active-thought-eyebrow">{activeRagThought.label}</span>
                                            <h3 className="library-rag-active-thought-title">{activeRagThought.title}</h3>
                                        </div>
                                        <button
                                            type="button"
                                            className="library-rag-preview-close"
                                            onClick={handleCloseRagPlannerPreview}
                                            aria-label="Close planner preview"
                                            title="Close planner preview"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                    {activeRagThought.prompt && (
                                        <div className="library-rag-active-thought-prompt">
                                            <span className="library-rag-active-thought-prompt-label">{activeRagThought.tone === 'locked' ? 'Request' : 'Prompt'}</span>
                                            <p>{activeRagThought.prompt}</p>
                                        </div>
                                    )}
                                    <div className="library-rag-active-thought-meta">
                                        <span className="library-rag-active-thought-chip">{activeRagThought.contextLabel}</span>
                                        {activeRagThought.status && <span className="library-rag-active-thought-chip">{activeRagThought.status}</span>}
                                        <span className="library-rag-active-thought-chip">{activeRagThought.badgeLabel}</span>
                                    </div>
                                    {activeRagThought.runPlan ? renderJaneRunPlan(activeRagThought.runPlan, { compact: false, pending: true }) : null}
                                    {renderRagPlanner(activeRagThought.plannerSteps)}
                                </section>
                            )}
                            {(showOutlookCitationReview || showRagSourceHistory || chunkPreview || timelineEvidencePreview || showRagDraftPreviewPanel || showRagCalendarPanel) && (
                                <div className="library-rag-preview-body">
                                    {showOutlookCitationReview ? (
                                        <OutlookCitationReviewerPanel
                                            target={outlookCitationReview}
                                            onClose={() => setOutlookCitationReview(null)}
                                        />
                                    ) : showRagDraftPreviewPanel ? (
                                        <>
                                            <div className="library-rag-preview-header">
                                                <div className="library-rag-preview-heading">
                                                    <div className="library-rag-preview-title">Draft {ragDraftPreviewDocumentType}</div>
                                                    <div className="library-rag-preview-meta">Review before saving DOCX</div>
                                                </div>
                                                <div className="library-rag-preview-actions" aria-label="Draft preview actions">
                                                    {ragDraftPreviewSavedUrl && (
                                                        <a
                                                            className="library-rag-preview-control"
                                                            href={ragDraftPreviewSavedUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            aria-label="Open saved draft DOCX"
                                                            title="Open saved DOCX"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </a>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="library-rag-preview-control"
                                                        onClick={() => setRagDraftPreviewMessageId(null)}
                                                        aria-label="Close draft preview"
                                                        title="Close preview"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="library-rag-draft-preview-scroll">
                                                <div className="library-rag-draft-preview-page">
                                                    <div className="library-rag-answer-text library-rag-draft-preview-text">
                                                        {renderRagAnswerContent(ragDraftPreviewDraftText, Array.isArray(ragDraftPreviewAnswer?.citations) ? ragDraftPreviewAnswer.citations : [], `draft-preview-${ragDraftPreviewMessage.id}`, handleCitationOpen)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="library-rag-draft-preview-actions">
                                                {ragDraftPreviewIsPending ? (
                                                    <>
                                                        <button type="button" className="action alt perm-action" onClick={() => handleConfirmConfirmableAction(ragDraftPreviewMessage)} disabled={ragDraftPreviewCommitBlocked}>{ragDraftPreviewIsBusy ? 'Saving...' : (ragDraftPreviewActionDetails?.confirmLabel || 'Save DOCX')}</button>
                                                        {ragDraftPreviewCanSaveToChat && (
                                                            <button
                                                                type="button"
                                                                className="action quaternary perm-secondary"
                                                                onClick={() => handleConfirmConfirmableAction(ragDraftPreviewMessage, { destinationOverride: 'chat' })}
                                                                disabled={ragDraftPreviewIsBusy}
                                                            >
                                                                Save to chat
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="action quaternary perm-secondary"
                                                            onClick={() => handleCancelConfirmableAction(ragDraftPreviewMessage)}
                                                            disabled={ragDraftPreviewIsBusy}
                                                        >
                                                            {ragDraftPreviewActionDetails?.cancelLabel || 'Do not save'}
                                                        </button>
                                                    </>
                                                ) : ragDraftPreviewSavedUrl ? (
                                                    <a className="library-rag-preview-toggle" href={ragDraftPreviewSavedUrl} target="_blank" rel="noreferrer">Open saved DOCX</a>
                                                ) : (
                                                    <span>Draft preview only.</span>
                                                )}
                                            </div>
                                        </>
                                    ) : showRagCalendarPanel ? (
                                        <JaneCalendarPlannerPanel key={`calendar-panel-${ragCalendarPanelMessageId}`} answer={ragCalendarPanelAnswer} apiBase={API_BASE} onClose={() => setRagCalendarPanelMessageId(null)} />
                                    ) : showRagSourceHistory ? (
                                        <>
                                            <div className="library-rag-preview-header">
                                                <div className="library-rag-preview-heading">
                                                    <div className="library-rag-preview-title">Chat sources</div>
                                                    <div className="library-rag-preview-meta">Chronological first use across this chat</div>
                                                </div>
                                                <div className="library-rag-preview-actions" aria-label="Source history actions">
                                                    <button
                                                        type="button"
                                                        className="library-rag-preview-control"
                                                        onClick={() => setShowRagSourceHistory(false)}
                                                        aria-label="Close source history"
                                                        title="Close sources"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="library-rag-source-history">
                                                {ragSourceHistory.length === 0 ? (
                                                    <div className="library-rag-source-history-empty">No sources have been used in this chat yet.</div>
                                                ) : ragSourceHistory.map((entry, index) => (
                                                    <div
                                                        key={entry.key}
                                                        className="library-rag-source-history-item"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="library-rag-source-history-main"
                                                            onClick={() => handleOpenSourceHistoryEntry(entry)}
                                                        >
                                                            <span className="library-rag-source-history-index">{index + 1}</span>
                                                            <span className="library-rag-source-history-body">
                                                                <span className="library-rag-source-history-title">
                                                                {entry.feedAttachmentSubject ? (
                                                                    <><em>{entry.feedAttachmentSubject}</em> - attachment</>
                                                                ) : entry.caseDisplay ? (
                                                                    <>{entry.caseDisplay.caseName} · <span className="small-text" style={{ color: 'var(--subtext-color)' }}>{entry.caseDisplay.caseId}</span></>
                                                                ) : entry.title}
                                                                </span>
                                                                <span className="library-rag-source-history-meta">
                                                                {entry.type} · {entry.origin}{entry.date ? ` · ${formatDate(entry.date)}` : ''}
                                                                </span>
                                                                {entry.detail && <span className="library-rag-source-history-detail">{entry.detail}</span>}
                                                            </span>
                                                        </button>
                                                        {entry.fileId && !isOutlookCitation(entry.source) && (
                                                            <button
                                                                type="button"
                                                                className="library-rag-source-history-action"
                                                                onClick={() => handleMentionSourceInPrompt(entry)}
                                                                aria-label={`Pin ${entry.title} to prompt`}
                                                                title="Pin to prompt"
                                                            >
                                                                <Pin size={14} />
                                                                <span>Pin</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : timelineEvidencePreview ? (
                                        <TimelineEvidencePreview preview={timelineEvidencePreview} onClose={() => setTimelineEvidencePreview(null)} />
                                    ) : (
                                        <>
                                            <div className="library-rag-preview-header">
                                                <div className="library-rag-preview-heading">
                                                    <div className="library-rag-preview-title" title={chunkPreview.fileName || `File ${chunkPreview.fileId}`}>
                                                        {chunkPreview.fileName || `File ${chunkPreview.fileId}`}
                                                    </div>
                                                    <div className="library-rag-preview-meta">
                                                        {buildPreviewDetailParts(chunkPreview).join(' · ') || 'Source document preview'}
                                                    </div>
                                                </div>
                                                <div className="library-rag-preview-actions" aria-label="Source preview actions">
                                                    <a
                                                        className="library-rag-preview-control"
                                                        href={buildCaseDocumentLocationUrl(chunkPreview)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        aria-label={`Open ${chunkPreview.fileName || 'source document'} in its case documents location`}
                                                        title="Open case document location"
                                                    >
                                                        <ExternalLink size={16} />
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="library-rag-preview-control"
                                                        onClick={() => setChunkPreview(null)}
                                                        aria-label="Close source preview"
                                                        title="Close preview"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            <LibraryRagPdfPreview key={`${chunkPreview.fileId}-${chunkPreview.registryId}-${chunkPreview.chunkIndex}`} preview={chunkPreview} apiBase={API_BASE} />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </aside>
                )}
            </div>
        </div>
    );
};

// Identity changes and the existing reauthentication dialog end the previous
// view's lifetime, including previews, in-flight reads and local message state.
// Keep profile-only updates stable and leave background server tasks untouched.
const AuthScopedAI = ({ user, authSuspended = false }) => (
    authSuspended ? null : <AI key={getJaneUserId(user) ?? 'no-actor'} user={user} />
);

export default AuthScopedAI;
